import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";

import {
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import {
  addDoc,
  collection,
  doc,
  getDocs,
  getDoc,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import TallargeLogoFull from "../../assets/Tallarge_final.png";
import cargoCategoryIconGeneral from "../../assets/tallarge-icon-general-logistics.png";
import cargoCategoryIconContainer from "../../assets/tallarge-icon-container-port.png";
import cargoCategoryIconChemical from "../../assets/tallarge-icon-chemical-hazmat.png";
import cargoCategoryIconWaste from "../../assets/tallarge-icon-waste-recycling.png";
import cargoCategoryIconSoil from "../../assets/tallarge-icon-soil-aggregate.png";
import cargoCategoryIconSteel from "../../assets/tallarge-icon-steel-heavy-equipment.png";
import cargoCategoryIconColdChain from "../../assets/tallarge-icon-cold-chain.png";
import cargoCategoryIconAnimal from "../../assets/tallarge-icon-live-animal.png";
import cargoCategoryIconVehicles from "../../assets/tallarge-icon-vehicles-machinery.png";
import inspectionCategoryIcon from "../../assets/tallarge-inspection-icon-clean-v3.png";
import swapStopsIcon from "../../assets/swap_stops.ico";
import {
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { auth, db } from "../../firebase";
import {
  directoryConfig,
  directoryPageId,
  hydrateDirectoryPage,
  sortDirectoryItems,
} from "./commonDirectory";
import { readVehicleDirectory } from "./vehicleDirectory";

const COMPANY_API_URL =
  "https://asia-east1-chat3test-59045.cloudfunctions.net/createCompanyAccount";
const TRACKING_API_URL = `https://asia-east1-${auth.app.options.projectId}.cloudfunctions.net/createTrackingLink`;

function trackingUrlDiagnostics(value) {
  const urlText = text(value);
  if (!urlText) {
    return { present: false, parseable: false, host: "", isTrackPath: false, tokenKeyPresent: false, tokenValuePresent: false, tokenLength: 0, queryKeys: [] };
  }
  try {
    const parsed = new URL(urlText, "https://tallarge.com");
    const token = text(parsed.searchParams.get("token"));
    return {
      present: true,
      parseable: true,
      host: parsed.hostname,
      isTrackPath: parsed.pathname.replace(/\/+$/, "") === "/track",
      tokenKeyPresent: parsed.searchParams.has("token"),
      tokenValuePresent: Boolean(token),
      tokenLength: token.length,
      queryKeys: Array.from(parsed.searchParams.keys()),
    };
  } catch {
    return { present: true, parseable: false, host: "", isTrackPath: false, tokenKeyPresent: false, tokenValuePresent: false, tokenLength: 0, queryKeys: [] };
  }
}

function normalizeTrackingUrl(value) {
  const rawUrl = text(value);
  if (!rawUrl) {
    throw new Error("追蹤服務未回傳可用的配送追蹤網址。");
  }

  let parsed;
  try {
    parsed = new URL(rawUrl);
  } catch {
    throw new Error("追蹤服務回傳的網址格式無效，未寄出通知。");
  }

  const token = text(parsed.searchParams.get("token"));
  const queryKeys = Array.from(parsed.searchParams.keys());
  const isValid =
    parsed.protocol === "https:" &&
    parsed.hostname === "tallarge.com" &&
    parsed.pathname === "/track" &&
    parsed.searchParams.getAll("token").length === 1 &&
    queryKeys.length === 1 &&
    queryKeys[0] === "token" &&
    Boolean(token) &&
    token.length <= 512 &&
    !/\s/.test(token);

  if (!isValid) {
    throw new Error("追蹤服務未回傳 Tallarge /track?token= 連結，未寄出通知。");
  }

  return `https://tallarge.com/track?token=${encodeURIComponent(token)}`;
}

function isCanonicalTrackingUrl(value) {
  try {
    const rawUrl = text(value);
    return Boolean(rawUrl) && normalizeTrackingUrl(rawUrl) === rawUrl;
  } catch {
    return false;
  }
}

async function requestCreateTrackingLink({
  orgId,
  dateKey,
  applicationId,
  scopeKey,
  recipientEmail,
  recipientProfileId,
  expiresAt,
}) {
  const currentUser = auth.currentUser;
  if (!currentUser?.uid) throw new Error("尚未登入，無法建立配送查看連結。");
  const idToken = await currentUser.getIdToken();
  const response = await fetch(TRACKING_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      action: "createTrackingLink",
      orgId,
      dateKey,
      applicationId,
      scopeKey,
      recipientEmail,
      recipientProfileId,
      expiresAt: expiresAt.toISOString(),
    }),
  });
  const result = await response.json().catch(() => ({}));
  console.info("[CreateInternalFleetJob] createTrackingLink response", {
    httpStatus: response.status,
    httpOk: response.ok,
    payloadOk: result?.ok === true,
    responseFields: Object.keys(result || {}).sort(),
    linkFields: Object.keys(result?.link && typeof result.link === "object" ? result.link : {}).sort(),
    trackingUrl: trackingUrlDiagnostics(result?.trackingUrl),
  });
  if (!response.ok || !result.ok || !result.trackingUrl) {
    throw new Error(result.message || "配送查看連結建立失敗。");
  }
  return result;
}

async function requestHereGeocode(address, orgId, signal) {
  const currentUser = auth.currentUser;
  if (!currentUser?.uid)
    throw new Error("尚未登入 Firebase，無法取得地址定位。");
  if (!orgId) throw new Error("目前登入帳號沒有 orgId，無法取得地址定位。");

  const idToken = await currentUser.getIdToken();
  const response = await fetch(COMPANY_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      action: "geocodeAddress",
      orgId,
      address: text(address),
    }),
    signal,
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result.ok) {
    throw new Error(result.message || "HERE 地址查詢失敗。");
  }
  return result.results?.[0] || null;
}

async function requestHereReverseGeocode(lat, lng, orgId, signal) {
  const currentUser = auth.currentUser;
  if (!currentUser?.uid)
    throw new Error("尚未登入 Firebase，無法取得地址定位。");
  if (!orgId) throw new Error("目前登入帳號沒有 orgId，無法取得地址定位。");

  const idToken = await currentUser.getIdToken();
  const response = await fetch(COMPANY_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      action: "reverseGeocode",
      orgId,
      lat: Number(lat),
      lng: Number(lng),
    }),
    signal,
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result.ok) {
    throw new Error(result.message || "HERE 反向地址查詢失敗。");
  }
  return result.result || null;
}

async function reverseGeocodeWithHere(lat, lng, orgId, signal, fallback) {
  try {
    const result = await requestHereReverseGeocode(lat, lng, orgId, signal);
    if (result?.address) {
      const parsedAddress = splitTaiwanPostalPrefix(result.address);
      return {
        ...fallback,
        text: parsedAddress.address,
        postalCode:
          text(result.postalCode) ||
          parsedAddress.postalCode ||
          text(fallback?.postalCode),
        lat: Number(result.lat ?? lat),
        lng: Number(result.lng ?? lng),
      };
    }
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    console.warn("[Tallarge Address] HERE reverse fallback:", error?.message);
  }
  return fallback;
}

async function enrichLocationWithHere(base, address, orgId, signal) {
  try {
    const result = await requestHereGeocode(address, orgId, signal);
    if (!result) return base;
    return {
      ...base,
      lat: Number.isFinite(Number(result.lat)) ? Number(result.lat) : base.lat,
      lng: Number.isFinite(Number(result.lng)) ? Number(result.lng) : base.lng,
      postalCode: text(result.postalCode) || text(base.postalCode),
    };
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    console.warn("[Tallarge Address] HERE fallback:", error?.message);
    return base;
  }
}

const text = (value) => String(value ?? "").trim();
const pad = (value) => String(value).padStart(2, "0");
const splitTaiwanPostalPrefix = (value) => {
  const raw = text(value);
  const match = raw.match(/^(?:台灣|臺灣)\s*(\d{3,6})\s*/);
  return match
    ? { postalCode: match[1], address: raw.slice(match[0].length).trim() }
    : { postalCode: "", address: raw };
};

const normalizeHouseNumber = (value) => {
  const houseNumber = text(value);
  return /^\d+$/.test(houseNumber) ? `${houseNumber}號` : houseNumber;
};

function coordsOK(value) {
  const lat = Number(value?.lat);
  const lng = Number(value?.lng);
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180 &&
    !(Math.abs(lat) < 0.000001 && Math.abs(lng) < 0.000001)
  );
}

function normalizeAddressMatchKey(value) {
  return splitTaiwanPostalPrefix(value)
    .address
    .toUpperCase()
    .replace(/[臺台灣]/g, "台")
    .replace(/[\s　,，。．.、;；:：#＃\-－_]/g, "")
    .replace(/號$/g, "")
    .trim();
}

function commonAddressText(item) {
  return text(
    item?.addressText ||
      item?.address ||
      item?.fullAddress ||
      item?.formattedAddress ||
      item?.text,
  );
}

function commonAddressCoords(item) {
  const lat = Number(item?.lat ?? item?.latitude);
  const lng = Number(item?.lng ?? item?.longitude ?? item?.lon);
  return coordsOK({ lat, lng }) ? { lat, lng } : null;
}

async function loadCommonAddressDirectoryItems(orgId) {
  if (!orgId) return [];
  const config = directoryConfig("addresses");
  const items = [];
  for (let pageNo = 1; pageNo <= 100; pageNo += 1) {
    const snapshot = await getDoc(
      doc(db, "orgs", orgId, config.collection, directoryPageId(pageNo)),
    );
    const page = hydrateDirectoryPage(snapshot, pageNo);
    if (!page.exists) break;
    items.push(...page.items);
    if (page.items.length < config.pageCapacity) break;
  }
  return sortDirectoryItems(items);
}

function dateText(dateValue) {
  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())}`;
}

function dateTime(dateValue, timeValue) {
  const value = new Date(`${dateValue}T${timeValue || "00:00"}`);
  return Number.isNaN(value.getTime()) ? null : value;
}

function timeText(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function firestoreDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (Number.isFinite(Number(value?.seconds))) {
    return new Date(
      Number(value.seconds) * 1000 + Number(value.nanoseconds || 0) / 1000000,
    );
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function inputDateValue(value) {
  const date = firestoreDate(value);
  return date
    ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    : "";
}

function inputTimeValue(value) {
  const raw = text(value);
  const directTime = raw.match(/^(\d{1,2}):(\d{2})/);
  if (directTime) return `${pad(directTime[1])}:${directTime[2]}`;
  const date = firestoreDate(value);
  return date ? timeText(date) : "";
}

const CARGO_STATUS_OPTIONS = [
  ["PENDING_PICKUP", "待取貨"],
  ["PICKED_UP", "已取貨"],
  ["IN_TRANSIT", "運送中"],
  ["DELIVERED", "已送達"],
  ["EXCEPTION", "異常"],
];
const CARGO_STATUS_LABELS = Object.fromEntries(CARGO_STATUS_OPTIONS);
const STOP_OPERATION_OPTIONS = [
  ["PICKUP", "取貨"],
  ["DELIVERY", "送貨"],
  ["PICKUP_DELIVERY", "取貨＋送貨"],
];
const STOP_OPERATION_LABELS = Object.fromEntries(STOP_OPERATION_OPTIONS);
const CARGO_OPERATION_OPTIONS = [
  ["PICKUP", "取貨"],
  ["DELIVERY", "送貨"],
];
const CARGO_OPERATION_LABELS = Object.fromEntries(CARGO_OPERATION_OPTIONS);
const CARGO_CATEGORY_OPTIONS = [
  { id: "A_GENERAL_LOGISTICS", code: "NONE", label: "一般", subtitle: "一般物流、配送等，皆可使用", icon: cargoCategoryIconGeneral, version: 1 },
  { id: "B_CONTAINER_PORT", code: "B", label: "貨櫃", subtitle: "貨櫃與港口物流", icon: cargoCategoryIconContainer, version: 1 },
  { id: "C_CHEMICAL_HAZMAT", code: "C", label: "危險品", subtitle: "化學品與危險物品", icon: cargoCategoryIconChemical, version: 1 },
  { id: "D_WASTE_RECYCLING", code: "D", label: "廢棄物", subtitle: "廢棄物與資源回收", icon: cargoCategoryIconWaste, version: 1 },
  { id: "E_SOIL_AGGREGATE", code: "E", label: "土石方", subtitle: "土石方、砂石與礦物", icon: cargoCategoryIconSoil, version: 1 },
  { id: "F_STEEL_HEAVY_EQUIPMENT", code: "F", label: "大型設備", subtitle: "鋼鐵、重型與大型設備", icon: cargoCategoryIconSteel, version: 1 },
  { id: "G_COLD_CHAIN", code: "G", label: "冷鏈", subtitle: "冷鏈與溫控貨物", icon: cargoCategoryIconColdChain, version: 1 },
  { id: "H_LIVE_ANIMAL", code: "H", label: "活體動物", subtitle: "活體動物運輸", icon: cargoCategoryIconAnimal, version: 1 },
  { id: "I_VEHICLES_MACHINERY", code: "I", label: "車輛機械", subtitle: "汽機車、車輛與機械", icon: cargoCategoryIconVehicles, version: 1 },
];
const DEFAULT_CARGO_CATEGORY_ID = CARGO_CATEGORY_OPTIONS[0].id;
const CARGO_CATEGORY_BY_ID = Object.fromEntries(
  CARGO_CATEGORY_OPTIONS.map((category) => [category.id, category]),
);

const WORK_DOMAIN_BY_CATEGORY_ID = {
  A_GENERAL_LOGISTICS: "NONE",
  B_CONTAINER_PORT: "CONTAINER",
  C_CHEMICAL_HAZMAT: "HAZARDOUS_MATERIAL",
  D_WASTE_RECYCLING: "WASTE",
  E_SOIL_AGGREGATE: "SOIL_AGGREGATE",
  F_STEEL_HEAVY_EQUIPMENT: "HEAVY_EQUIPMENT",
  G_COLD_CHAIN: "COLD_CHAIN",
  H_LIVE_ANIMAL: "LIVE_ANIMAL",
  I_VEHICLES_MACHINERY: "VEHICLE_MACHINERY",
};
const CATEGORY_ID_BY_WORK_DOMAIN = Object.fromEntries(
  Object.entries(WORK_DOMAIN_BY_CATEGORY_ID).map(([categoryId, domain]) => [
    domain,
    categoryId,
  ]),
);
const WORK_DOMAIN_VALUES = new Set(Object.values(WORK_DOMAIN_BY_CATEGORY_ID));

function normalizeWorkDomain(value, fallback = "NONE") {
  const raw = text(value).toUpperCase();
  if (WORK_DOMAIN_VALUES.has(raw)) return raw;
  const categoryId = normalizeCargoCategory(raw, "");
  return WORK_DOMAIN_BY_CATEGORY_ID[categoryId] || fallback;
}

function categoryIdForWorkDomain(value) {
  return CATEGORY_ID_BY_WORK_DOMAIN[normalizeWorkDomain(value)] ||
    DEFAULT_CARGO_CATEGORY_ID;
}

function normalizeWorkType(value) {
  const raw = text(value).toUpperCase();
  return raw === "INSPECTION"
    ? "INSPECTION"
    : normalizeCargoCategory(raw, DEFAULT_CARGO_CATEGORY_ID);
}

const FIRESTORE_APPLICATION_SAFE_BYTES = Math.floor(1024 * 1024 * 0.7);

function serializedFirestoreSizeBytes(value) {
  const serialized = JSON.stringify(value);
  return new window.TextEncoder().encode(serialized || "null").length;
}

function hasMeaningfulValue(value) {
  if (value === false || value === 0) return true;
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return Boolean(value.trim());
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(compactRecord(value)).length > 0;
  return true;
}


function preserveDriverExecutionData(nextStops, latestStops) {
  const latestById = new Map(
    (Array.isArray(latestStops) ? latestStops : []).map((stop) => [text(stop?.id).toUpperCase(), stop]),
  );
  const stopFields = [
    "status", "executionStatus", "startedAt", "arrivedAt", "completedAt",
    "arrivalAt", "completionAt", "exceptionAt", "exceptionByUid",
    "exceptionReason", "exceptionRecipientIds", "exceptionContentIds",
    "exceptionRecords", "proof", "progress", "activityLog", "execution", "executionPhotos", "executionVideos", "currentStopId",
    "completedByUid", "startedByUid",
  ];
  const recipientFields = ["proof", "activityLog", "exceptionRecords", "execution"];
  const contentFields = [
    "status", "executionStatus", "startedAt", "completedAt", "proof",
    "activityLog", "execution", "executionPhotos", "executionVideos", "exceptionRecords",
  ];
  return (Array.isArray(nextStops) ? nextStops : []).map((nextStop) => {
    const latestStop = latestById.get(text(nextStop?.id).toUpperCase());
    if (!latestStop) return nextStop;
    const mergedStop = { ...nextStop };
    stopFields.forEach((field) => {
      if (latestStop[field] !== undefined) mergedStop[field] = latestStop[field];
    });
    mergedStop.recipients = (Array.isArray(nextStop?.recipients) ? nextStop.recipients : []).map((nextRecipient) => {
      const latestRecipient = (Array.isArray(latestStop?.recipients) ? latestStop.recipients : []).find(
        (candidate) => text(candidate?.id || candidate?.recipientId) === text(nextRecipient?.id || nextRecipient?.recipientId),
      );
      if (!latestRecipient) return nextRecipient;
      const mergedRecipient = { ...nextRecipient };
      recipientFields.forEach((field) => {
        if (latestRecipient[field] !== undefined) mergedRecipient[field] = latestRecipient[field];
      });
      mergedRecipient.contents = (Array.isArray(nextRecipient?.contents) ? nextRecipient.contents : []).map((nextContent) => {
        const latestContent = (Array.isArray(latestRecipient?.contents) ? latestRecipient.contents : []).find(
          (candidate) => text(candidate?.id || candidate?.contentId) === text(nextContent?.id || nextContent?.contentId),
        );
        if (!latestContent) return nextContent;
        const mergedContent = { ...nextContent };
        contentFields.forEach((field) => {
          if (latestContent[field] !== undefined) mergedContent[field] = latestContent[field];
        });
        return mergedContent;
      });
      return mergedRecipient;
    });
    return mergedStop;
  });
}

function compactRecord(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => compactRecord(item))
      .filter((item) => hasMeaningfulValue(item));
  }
  if (!value || typeof value !== "object" || value instanceof Date) return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => !["file", "preview", "previewUrl"].includes(key))
      .map(([key, item]) => [key, compactRecord(item)])
      .filter(([, item]) => hasMeaningfulValue(item)),
  );
}


function cargoReportDayFromStartAt(value, fallback = "") {
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-");
  }
  return text(fallback);
}

function cargoReportItemId(applicationId, stopId, recipientId, contentId) {
  return [applicationId, stopId, recipientId, contentId]
    .map((value) => text(value).replaceAll("/", "_"))
    .join("__");
}

function cargoReportItems(applicationId, stops = []) {
  return stops.flatMap((stop) =>
    (Array.isArray(stop?.recipients) ? stop.recipients : []).flatMap((recipient) =>
      (Array.isArray(recipient?.contents) ? recipient.contents : []).map((content) => {
        const stopId = text(stop?.id).toUpperCase();
        const recipientId = text(recipient?.id || recipient?.recipientId);
        const contentId = text(content?.id || content?.contentId);
        return {
          id: cargoReportItemId(applicationId, stopId, recipientId, contentId),
          data: {
            reportType: "cargo",
            applicationId,
            stopId,
            recipientId,
            recipientRole: ["PICKUP", "取貨"].includes(
              text(content?.operationType || recipient?.operationType || stop?.operationType).toUpperCase(),
            )
              ? "sender"
              : "receiver",
            recipientName: text(
              recipient?.name || recipient?.displayName || recipient?.company || recipient?.companyName,
            ),
            recipientPhone: text(recipient?.phone || recipient?.tel || recipient?.mobile),
            contentId,
            cargoName: text(content?.name || content?.cargoName || content?.itemName),
            quantity: content?.quantity ?? "",
            unit: text(content?.unit) || "件",
            operationType: text(
              content?.operationType || recipient?.operationType || stop?.operationType,
            ).toUpperCase(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
        };
      }),
    ),
  ).filter((item) => item.id && item.id !== "____");
}

const CARGO_REPORT_CHUNK_TARGET_BYTES = 700000;
function buildCargoReportChunks(items = []) {
  const chunks = [];
  let current = {};
  const pushCurrent = () => {
    if (!Object.keys(current).length) return;
    chunks.push(current);
    current = {};
  };
  items.forEach((item) => {
    const candidate = { ...current, [item.id]: {
      applicationId: text(item.data?.applicationId),
      stopId: text(item.data?.stopId),
      recipientId: text(item.data?.recipientId),
      recipientRole: text(item.data?.recipientRole),
      recipientName: text(item.data?.recipientName),
      recipientPhone: text(item.data?.recipientPhone),
      contentId: text(item.data?.contentId),
      cargoName: text(item.data?.cargoName),
      quantity: item.data?.quantity ?? "",
      unit: text(item.data?.unit) || "件",
      operationType: text(item.data?.operationType),
    } };
    if (Object.keys(current).length &&
        new TextEncoder().encode(JSON.stringify(candidate)).length > CARGO_REPORT_CHUNK_TARGET_BYTES) {
      pushCurrent();
      current = { [item.id]: candidate[item.id] };
    } else {
      current = candidate;
    }
  });
  pushCurrent();
  return chunks;
}


function attendanceReportItemId(applicationId, driverId) {
  return [applicationId, driverId]
    .map((value) => text(value).replaceAll("/", "_"))
    .join("__");
}

function buildAttendanceReportItem(applicationId, workDateKey, payload, jobMode) {
  const driverId = text(payload?.driver?.uid);
  if (!driverId) return null;
  const jobType = jobMode === "single"
    ? text(payload?.workType) === "INSPECTION"
      ? "INSPECTION"
      : text(payload?.workDomain)
    : text(payload?.workDomain);
  return {
    id: attendanceReportItemId(applicationId, driverId),
    data: {
      reportType: "attendance",
      applicationId,
      workDateKey,
      driverId,
      driverName: text(payload?.driver?.name),
      driverPhone: text(payload?.driver?.phone),
      jobName: text(payload?.applicationName),
      jobMode,
      jobType,
      attendanceStatus: "assigned",
      workStatus: "not_started",
      startedAt: null,
      endedAt: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
  };
}

const ATTENDANCE_REPORT_CHUNK_TARGET_BYTES = 700000;
function buildAttendanceReportChunks(items = []) {
  const chunks = [];
  let current = {};
  const pushCurrent = () => {
    if (!Object.keys(current).length) return;
    chunks.push(current);
    current = {};
  };
  items.forEach((item) => {
    const candidateItem = {
      applicationId: text(item.data?.applicationId),
      workDateKey: text(item.data?.workDateKey),
      driverId: text(item.data?.driverId),
      driverName: text(item.data?.driverName),
      driverPhone: text(item.data?.driverPhone),
      jobName: text(item.data?.jobName),
      jobMode: text(item.data?.jobMode),
      jobType: text(item.data?.jobType),
      attendanceStatus: text(item.data?.attendanceStatus),
      workStatus: text(item.data?.workStatus),
      startedAt: item.data?.startedAt ?? null,
      endedAt: item.data?.endedAt ?? null,
      createdAt: item.data?.createdAt ?? null,
      updatedAt: item.data?.updatedAt ?? null,
    };
    const candidate = { ...current, [item.id]: candidateItem };
    if (
      Object.keys(current).length &&
      new TextEncoder().encode(JSON.stringify(candidate)).length >
        ATTENDANCE_REPORT_CHUNK_TARGET_BYTES
    ) {
      pushCurrent();
      current = { [item.id]: candidateItem };
    } else {
      current = candidate;
    }
  });
  pushCurrent();
  return chunks;
}

function normalizeParty(value = {}, prefix = "PARTY") {
  const source = value && typeof value === "object" ? value : {};
  return {
    [`${prefix.toLowerCase()}Id`]: text(
      source[`${prefix.toLowerCase()}Id`] || source.id,
    ),
    name: text(source.name || source.displayName),
    company: text(source.company || source.companyName),
    phone: text(source.phone),
    email: text(source.email),
    addressText: text(source.addressText || source.address),
    note: text(source.note),
    [`${prefix.toLowerCase()}ProfileId`]: text(
      source[`${prefix.toLowerCase()}ProfileId`] || source.profileId,
    ),
  };
}

function recipientContents(recipient) {
  return Array.isArray(recipient?.contents) ? recipient.contents : [];
}

function recipientPrimaryContent(recipient) {
  return recipientContents(recipient)[0] || createCargoItem(recipient?.operationType);
}

function recipientContentCount(recipient) {
  return recipientContents(recipient).length;
}

function cargoRecordDraft(recipient, fallbackOperation = "DELIVERY") {
  const primary = recipientPrimaryContent(recipient);
  const recipientFields = { ...(recipient || {}) };
  delete recipientFields.sender;
  const draft = normalizeCargoItem(
    {
      ...recipientFields,
      ...primary,
      id: text(recipient?.id || recipient?.recipientId),
      operationType: recipient?.operationType || primary?.operationType,
      accessRequirements: {
        ...(recipient?.accessRequirements || {}),
        documents: recipient?.documents ||
          recipient?.accessRequirements?.documents ||
          primary?.accessRequirements?.documents ||
          [],
      },
      photos: recipient?.photos || primary?.photos || [],
    },
    text(recipient?.id || recipient?.recipientId),
    fallbackOperation,
  );
  return {
    ...draft,
    recipientId: text(recipient?.id || recipient?.recipientId),
    cargoNumber: text(recipient?.cargoNumber),
    sender: normalizeParty(primary?.sender, "SENDER"),
    contents: recipientContents(recipient).map((content) => ({
      ...content,
      sender: normalizeParty(content?.sender, "SENDER"),
    })),
    documents: normalizeDocumentProofs(
      recipient?.documents || recipient?.accessRequirements?.documents,
      true,
      true,
    ),
  };
}

function applyCargoRecordDraft(
  recipient,
  draft,
  fallbackOperation = "DELIVERY",
  selectedContentId = "",
) {
  const recipientId = text(recipient?.id || recipient?.recipientId);
  const existingContents = recipientContents(recipient);
  const operationType = normalizeCargoOperation(
    draft?.operationType || recipient?.operationType,
    fallbackOperation,
  );
  const targetId = text(selectedContentId);
  const contents = (existingContents.length
    ? existingContents
    : [createCargoItem(operationType)]
  ).map((content, index) => {
    const contentId = text(content?.id || content?.contentId);
    const isTarget = targetId ? contentId === targetId : index === 0;
    const contentOperation = isTarget
      ? normalizeCargoOperation(
          draft?.operationType || content?.operationType,
          content?.operationType || operationType,
        )
      : normalizeCargoOperation(content?.operationType, operationType);
    return normalizeCargoItem(
      isTarget
        ? {
            ...content,
            name: text(draft?.name),
            quantity: draft?.quantity ?? "",
            unit: text(draft?.unit) || "件",
            note: text(draft?.note),
            sender: normalizeParty(draft?.sender ?? content?.sender, "SENDER"),
            operationType: contentOperation,
            domainDetails: compactRecord(draft?.domainDetails || draft?.typeDetails),
            cargoDetails: compactRecord(draft?.cargoDetails),
            deliveryDetails: compactRecord(draft?.deliveryDetails),
            accessRequirements: compactRecord(draft?.accessRequirements),
            documents: normalizeDocumentProofs(
              draft?.accessRequirements?.documents || draft?.documents,
              true,
              true,
            ),
            payment: compactRecord(draft?.payment),
            execution: compactExecution(draft?.execution),
            currentLocation: normalizeCargoLocation(draft?.currentLocation),
            parkedLocation: normalizeParkedLocation(draft?.parkedLocation),
            status: optionalCargoStatus(draft?.status),
            photos: Array.isArray(content.photos) ? content.photos : [],
          }
        : { ...content, operationType: contentOperation },
      recipientId,
      contentOperation,
    );
  });
  const nextDocuments = normalizeDocumentProofs(
    draft?.accessRequirements?.documents || draft?.documents,
    true,
    true,
  );
  const next = {
    ...recipient,
    id: recipientId,
    recipientId,
    cargoNumber: text(draft?.cargoNumber),
    operationType,
    contents,
    cargoDetails: normalizeCargoDetails(draft),
    domainDetails: {
      ...(draft?.domainDetails || draft?.typeDetails || {}),
      ...(text(draft?.containerId)
        ? { containerNumber: text(draft.containerId) }
        : {}),
    },
    deliveryDetails: normalizeDeliveryDetails(draft).deliveryDetails,
    accessRequirements: normalizeDeliveryDetails(draft, true).accessRequirements,
    payment: normalizeDeliveryDetails(draft).payment,
    execution: normalizeDeliveryDetails(draft).execution,
    documents: nextDocuments,
    photos: Array.isArray(draft?.photos) ? draft.photos : [],
    currentLocation: normalizeCargoLocation(draft?.currentLocation),
    parkedLocation: normalizeParkedLocation(draft?.parkedLocation),
    status: optionalCargoStatus(draft?.status),
  };
  delete next.sender;
  if (next.accessRequirements) {
    next.accessRequirements = {
      ...next.accessRequirements,
      documents: nextDocuments,
    };
  }
  return next;
}

function normalizeStopOperation(value, fallback = "DELIVERY") {
  const operation = text(value).toUpperCase();
  return STOP_OPERATION_LABELS[operation] ? operation : fallback;
}

function normalizeCargoCategory(value, fallback = DEFAULT_CARGO_CATEGORY_ID) {
  const raw = text(value).toUpperCase();
  if (CARGO_CATEGORY_BY_ID[raw]) return raw;
  const match = CARGO_CATEGORY_OPTIONS.find(
    (category) => category.code === raw || category.label === text(value),
  );
  return match?.id || fallback;
}

function cargoCategoryMeta(value) {
  return CARGO_CATEGORY_BY_ID[normalizeCargoCategory(value)] || CARGO_CATEGORY_OPTIONS[0];
}

function recipientHasOperation(recipient, operation, fallback = "DELIVERY") {
  const target = normalizeCargoOperation(operation, fallback);
  const contents = recipientContents(recipient);
  if (!contents.length) {
    return normalizeCargoOperation(recipient?.operationType, fallback) === target;
  }
  return contents.some(
    (content) => normalizeCargoOperation(content?.operationType, fallback) === target,
  );
}

function recipientOperationType(recipient, fallback = "DELIVERY") {
  const contents = recipientContents(recipient);
  const operations = (contents.length
    ? contents.map((content) => content?.operationType)
    : [recipient?.operationType]
  )
    .map((operation) => text(operation).toUpperCase())
    .filter((operation) => CARGO_OPERATION_LABELS[operation]);
  const hasPickup = operations.includes("PICKUP");
  const hasDelivery = operations.includes("DELIVERY");
  if (hasPickup && hasDelivery) return "PICKUP_DELIVERY";
  if (hasPickup) return "PICKUP";
  if (hasDelivery) return "DELIVERY";
  return normalizeCargoOperation(recipient?.operationType, fallback);
}

function deriveStopOperation(value, fallback = "DELIVERY") {
  const recipients = Array.isArray(value) ? value : value?.recipients;
  const operations = (recipients || [])
    .map((recipient) => recipientOperationType(recipient, fallback))
    .flatMap((operation) =>
      operation === "PICKUP_DELIVERY" ? ["PICKUP", "DELIVERY"] : [operation],
    );
  const hasPickup = operations.includes("PICKUP");
  const hasDelivery = operations.includes("DELIVERY");
  if (hasPickup && hasDelivery) return "PICKUP_DELIVERY";
  if (hasPickup) return "PICKUP";
  if (hasDelivery) return "DELIVERY";
  return normalizeStopOperation(fallback, "DELIVERY");
}

function nullableNumber(value) {
  if (value === null || value === undefined || text(value) === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function normalizeCargoDetails(value = {}) {
  const source = value && typeof value === "object" ? value : {};
  const nested = source.cargoDetails && typeof source.cargoDetails === "object"
    ? source.cargoDetails
    : {};
  const measurements = source.measurements || nested.measurements || {};
  const weight = source.weight || nested.weight || {};
  return {
    cargoBusinessFamily: text(source.cargoBusinessFamily || nested.cargoBusinessFamily),
    materialClass: text(source.materialClass || nested.materialClass),
    transportUnit: text(source.transportUnit || nested.transportUnit),
    measurements: {
      lengthMm: nullableNumber(measurements.lengthMm),
      widthMm: nullableNumber(measurements.widthMm),
      heightMm: nullableNumber(measurements.heightMm),
      volumeM3: nullableNumber(measurements.volumeM3),
    },
    weight: {
      grossWeightKg: nullableNumber(weight.grossWeightKg),
      netWeightKg: nullableNumber(weight.netWeightKg),
      tareWeightKg: nullableNumber(weight.tareWeightKg),
      verifiedGrossMassKg: nullableNumber(weight.verifiedGrossMassKg),
    },
    handlingProfiles: Array.isArray(source.handlingProfiles || nested.handlingProfiles)
      ? (source.handlingProfiles || nested.handlingProfiles).map(text).filter(Boolean)
      : [],
    complianceProfiles: Array.isArray(source.complianceProfiles || nested.complianceProfiles)
      ? (source.complianceProfiles || nested.complianceProfiles).map(text).filter(Boolean)
      : [],
    containerId: text(source.containerId || nested.containerId) || null,
  };
}

function normalizeDocumentProofs(
  value,
  preservePreview = false,
  preserveFile = false,
) {
  if (!Array.isArray(value)) return [];
  return value.map((document) => ({
    id: text(document?.id),
    documentName: text(document?.documentName || document?.content),
    assetId: text(document?.assetId),
    ...(preservePreview || preserveFile
      ? { fileName: text(document?.fileName || document?.originalName) }
      : {}),
    fileType: text(document?.fileType),
    contentType: text(document?.contentType || document?.fileType),
    fileSize: nullableNumber(document?.fileSize),
    url: text(document?.url),
    storagePath: text(document?.storagePath),
    uploadedAt: text(document?.uploadedAt),
    ...(preservePreview ? { previewUrl: text(document?.previewUrl) } : {}),
    ...(preserveFile && document?.file ? { file: document.file } : {}),
  }));
}

function normalizeDeliveryDetails(value = {}, preserveTransientFiles = false) {
  const source = value && typeof value === "object" ? value : {};
  const delivery = source.deliveryDetails || {};
  const access = source.accessRequirements || {};
  const category = source.categoryDeliveryDetails || {};
  const payment = source.payment || {};
  const execution = source.execution || {};
  return {
    deliveryDetails: {
      packagingType: text(delivery.packagingType),
      packageCount: nullableNumber(delivery.packageCount),
      palletCount: nullableNumber(delivery.palletCount),
      weightKg: nullableNumber(delivery.weightKg),
      volumeM3: nullableNumber(delivery.volumeM3),
      batchOrSerial: text(delivery.batchOrSerial),
      unloadingMethod: text(delivery.unloadingMethod),
      tailgateRequirement: text(delivery.tailgateRequirement),
      forkliftRequirement: text(delivery.forkliftRequirement),
      handlingPersonnelRequirement: text(delivery.handlingPersonnelRequirement),
      appointmentTime: text(delivery.appointmentTime),
      siteContactName: text(delivery.siteContactName),
      siteContactPhone: text(delivery.siteContactPhone),
      specialInstructions: text(delivery.specialInstructions),
    },
    accessRequirements: {
      entryPermitRequirement: text(access.entryPermitRequirement),
      permitType: text(access.permitType),
      permitNumber: text(access.permitNumber),
      permitIssuer: text(access.permitIssuer),
      permitValidFrom: text(access.permitValidFrom),
      permitValidTo: text(access.permitValidTo),
      driverCredentialRequirement: text(access.driverCredentialRequirement),
      vehicleCredentialRequirement: text(access.vehicleCredentialRequirement),
      appointmentRequirement: text(access.appointmentRequirement),
      gateContact: text(access.gateContact),
      returnPermitRequirement: text(access.returnPermitRequirement),
      accessInstructions: text(access.accessInstructions),
      documents: normalizeDocumentProofs(
        access.documents,
        preserveTransientFiles,
        preserveTransientFiles,
      ),
    },
    categoryDeliveryDetails: { ...category },
    payment: {
      collectionAmount: nullableNumber(payment.collectionAmount),
      paymentAmount: nullableNumber(payment.paymentAmount),
      paymentMethod: text(payment.paymentMethod),
      receiptRequirement: text(payment.receiptRequirement),
      note: text(payment.note),
    },
    execution: {
      receivingStatus: text(execution.receivingStatus) || "待配送",
      actualQuantity: nullableNumber(execution.actualQuantity),
      receivedQuantity: nullableNumber(execution.receivedQuantity),
      rejectedQuantity: nullableNumber(execution.rejectedQuantity),
      damagedQuantity: nullableNumber(execution.damagedQuantity),
      signedBy: text(execution.signedBy),
      signedAt: text(execution.signedAt),
      signatureNote: text(execution.signatureNote),
      exceptionNote: text(execution.exceptionNote),
      photos: Array.isArray(execution.photos) ? execution.photos : [],
    },
  };
}

function cargoDetailField(key, label, path, options = {}) {
  return { key, label, path, ...options };
}

function cargoDetailSection(id, title, fields) {
  return { id, title, fields };
}

const CARGO_DETAIL_STATUS_OPTIONS = [["", "待設定"], ...CARGO_STATUS_OPTIONS];

const DELIVERY_DETAIL_COMMON_SECTIONS = [
  cargoDetailSection("deliveryBasics", "本站配送內容", [
    cargoDetailField("allocatedName", "配送內容", "name"),
    cargoDetailField("allocatedQuantity", "配送數量", "quantity", { type: "number" }),
    cargoDetailField("allocatedUnit", "配送單位", "unit"),
    cargoDetailField("deliveryNote", "配送備註", "note", { type: "textarea", wide: true }),
  ]),
  cargoDetailSection("packaging", "包裝與貨物明細", [
    cargoDetailField("packagingType", "包裝方式", "deliveryDetails.packagingType"),
    cargoDetailField("packageCount", "包裝件數", "deliveryDetails.packageCount", { type: "number", suffix: "件" }),
    cargoDetailField("palletCount", "棧板數", "deliveryDetails.palletCount", { type: "number", suffix: "板" }),
    cargoDetailField("weightKg", "重量", "deliveryDetails.weightKg", { type: "number", suffix: "kg" }),
    cargoDetailField("volumeM3", "體積", "deliveryDetails.volumeM3", { type: "number", suffix: "m³" }),
    cargoDetailField("batchOrSerial", "批號／序號", "deliveryDetails.batchOrSerial"),
  ]),
  cargoDetailSection("documents", "文件證明", []),
  cargoDetailSection("payment", "代收／代付", [
    cargoDetailField("collectionAmount", "代收金額", "payment.collectionAmount", { type: "number", suffix: "元" }),
    cargoDetailField("paymentAmount", "代付金額", "payment.paymentAmount", { type: "number", suffix: "元" }),
    cargoDetailField("paymentMethod", "付款方式", "payment.paymentMethod"),
    cargoDetailField("receiptRequirement", "收據／發票需求", "payment.receiptRequirement"),
    cargoDetailField("paymentNote", "金流備註", "payment.note", { type: "textarea", wide: true }),
  ]),
  cargoDetailSection("execution", "收貨與簽收資訊", [
    cargoDetailField("receivingStatus", "收件狀態", "execution.receivingStatus"),
    cargoDetailField("actualQuantity", "實際配送數量", "execution.actualQuantity", { type: "number" }),
    cargoDetailField("receivedQuantity", "實際收貨數量", "execution.receivedQuantity", { type: "number" }),
    cargoDetailField("rejectedQuantity", "拒收數量", "execution.rejectedQuantity", { type: "number" }),
    cargoDetailField("damagedQuantity", "破損數量", "execution.damagedQuantity", { type: "number" }),
    cargoDetailField("signedBy", "簽收人", "execution.signedBy"),
    cargoDetailField("signedAt", "簽收時間", "execution.signedAt", { type: "datetime-local" }),
    cargoDetailField("signatureNote", "簽收資訊", "execution.signatureNote", { type: "textarea", wide: true }),
    cargoDetailField("exceptionNote", "異常說明", "execution.exceptionNote", { type: "textarea", wide: true }),
  ]),
];

const DELIVERY_DETAIL_CATEGORY_SECTIONS = {
  A_GENERAL_LOGISTICS: cargoDetailSection("category", "一般物流配送細節", [
    cargoDetailField("productBatch", "品項批號／效期", "categoryDeliveryDetails.productBatch"),
    cargoDetailField("packageCondition", "包裝交付狀態", "categoryDeliveryDetails.packageCondition"),
    cargoDetailField("deliveryProof", "交付證明", "categoryDeliveryDetails.deliveryProof", { type: "textarea", wide: true }),
    cargoDetailField("customerHandlingNote", "客戶交接備註", "categoryDeliveryDetails.customerHandlingNote", { type: "textarea", wide: true }),
  ]),
  B_CONTAINER_PORT: cargoDetailSection("category", "貨櫃配送細節", [
    cargoDetailField("deliveryForm", "整櫃／拆櫃", "categoryDeliveryDetails.deliveryForm"),
    cargoDetailField("containerCondition", "櫃況", "categoryDeliveryDetails.containerCondition"),
    cargoDetailField("sealCondition", "封條狀態", "categoryDeliveryDetails.sealCondition"),
    cargoDetailField("terminalRequirement", "場站要求", "categoryDeliveryDetails.terminalRequirement", { type: "textarea", wide: true }),
    cargoDetailField("deconsolidationNote", "拆櫃／分貨備註", "categoryDeliveryDetails.deconsolidationNote", { type: "textarea", wide: true }),
    cargoDetailField("returnContainerRequirement", "還櫃要求", "categoryDeliveryDetails.returnContainerRequirement"),
    cargoDetailField("yardName", "場站／碼頭名稱", "categoryDeliveryDetails.yardName"),
  ]),
  C_CHEMICAL_HAZMAT: cargoDetailSection("category", "化學危險品配送細節", [
    cargoDetailField("packageCondition", "包裝狀態", "categoryDeliveryDetails.packageCondition"),
    cargoDetailField("receivingArea", "收貨區域", "categoryDeliveryDetails.receivingArea"),
    cargoDetailField("documentHandover", "文件交接", "categoryDeliveryDetails.documentHandover", { type: "textarea", wide: true }),
    cargoDetailField("sdsHandover", "SDS 交接紀錄", "categoryDeliveryDetails.sdsHandover"),
    cargoDetailField("containerCondition", "容器狀態", "categoryDeliveryDetails.containerCondition"),
    cargoDetailField("leakageCheck", "洩漏檢查紀錄", "categoryDeliveryDetails.leakageCheck", { type: "textarea", wide: true }),
    cargoDetailField("safetyRequirement", "收貨方安全要求", "categoryDeliveryDetails.safetyRequirement", { type: "textarea", wide: true }),
  ]),
  D_WASTE_RECYCLING: cargoDetailSection("category", "廢棄物配送細節", [
    cargoDetailField("actualWeightKg", "實際重量", "categoryDeliveryDetails.actualWeightKg", { type: "number", suffix: "kg" }),
    cargoDetailField("containerCount", "桶／容器數", "categoryDeliveryDetails.containerCount", { type: "number", suffix: "個" }),
    cargoDetailField("manifestNumber", "聯單／流向編號", "categoryDeliveryDetails.manifestNumber"),
    cargoDetailField("receivingFacility", "接收場所", "categoryDeliveryDetails.receivingFacility"),
    cargoDetailField("weighbridgeNumber", "過磅單號", "categoryDeliveryDetails.weighbridgeNumber"),
    cargoDetailField("flowNote", "流向確認備註", "categoryDeliveryDetails.flowNote", { type: "textarea", wide: true }),
  ]),
  E_SOIL_AGGREGATE: cargoDetailSection("category", "土石方／砂石配送細節", [
    cargoDetailField("actualTons", "實際噸數", "categoryDeliveryDetails.actualTons", { type: "number", suffix: "噸" }),
    cargoDetailField("weighbridgeTons", "過磅重量", "categoryDeliveryDetails.weighbridgeTons", { type: "number", suffix: "噸" }),
    cargoDetailField("unloadingArea", "卸貨區域", "categoryDeliveryDetails.unloadingArea"),
    cargoDetailField("materialType", "材料類型／級配", "categoryDeliveryDetails.materialType"),
    cargoDetailField("soilTicketNumber", "土單／聯單號", "categoryDeliveryDetails.soilTicketNumber"),
    cargoDetailField("siteReceivingNote", "工地收料狀態", "categoryDeliveryDetails.siteReceivingNote", { type: "textarea", wide: true }),
  ]),
  F_STEEL_HEAVY_EQUIPMENT: cargoDetailSection("category", "鋼鐵／重型設備配送細節", [
    cargoDetailField("liftingMethod", "吊掛方式", "categoryDeliveryDetails.liftingMethod"),
    cargoDetailField("craneRequirement", "吊車需求", "categoryDeliveryDetails.craneRequirement"),
    cargoDetailField("entryRoute", "進場路線", "categoryDeliveryDetails.entryRoute", { type: "textarea", wide: true }),
    cargoDetailField("turnRestriction", "車輛迴轉限制", "categoryDeliveryDetails.turnRestriction"),
    cargoDetailField("securingMethod", "綁紮與固定方式", "categoryDeliveryDetails.securingMethod", { type: "textarea", wide: true }),
    cargoDetailField("escortPermitNumber", "超限／前導通行證", "categoryDeliveryDetails.escortPermitNumber"),
    cargoDetailField("unloadingEquipment", "卸貨設備", "categoryDeliveryDetails.unloadingEquipment"),
    cargoDetailField("groundBearingRequirement", "地面承重要求", "categoryDeliveryDetails.groundBearingRequirement"),
  ]),
  G_COLD_CHAIN: cargoDetailSection("category", "冷鏈配送細節", [
    cargoDetailField("arrivalTemperatureC", "到貨溫度", "categoryDeliveryDetails.arrivalTemperatureC", { type: "number", suffix: "°C" }),
    cargoDetailField("targetTemperatureC", "溫度要求", "categoryDeliveryDetails.targetTemperatureC", { type: "number", suffix: "°C" }),
    cargoDetailField("coldOrFrozenState", "冷藏／冷凍狀態", "categoryDeliveryDetails.coldOrFrozenState"),
    cargoDetailField("temperatureRecord", "溫度紀錄", "categoryDeliveryDetails.temperatureRecord", { type: "textarea", wide: true }),
    cargoDetailField("storageArea", "冷藏／冷凍庫位置", "categoryDeliveryDetails.storageArea"),
    cargoDetailField("immediateStorageRequirement", "立即入庫要求", "categoryDeliveryDetails.immediateStorageRequirement"),
    cargoDetailField("temperatureExceptionNote", "溫度異常備註", "categoryDeliveryDetails.temperatureExceptionNote", { type: "textarea", wide: true }),
  ]),
  H_LIVE_ANIMAL: cargoDetailSection("category", "活體動物配送細節", [
    cargoDetailField("deliveredAnimalCount", "實際交付數量", "categoryDeliveryDetails.deliveredAnimalCount", { type: "number", suffix: "隻" }),
    cargoDetailField("injuryOrDeathCount", "受傷／死亡數量", "categoryDeliveryDetails.injuryOrDeathCount", { type: "number", suffix: "隻" }),
    cargoDetailField("healthDocument", "健康／防疫文件", "categoryDeliveryDetails.healthDocument"),
    cargoDetailField("receivingFacility", "接收場所", "categoryDeliveryDetails.receivingFacility"),
    cargoDetailField("isolationRequirement", "隔離要求", "categoryDeliveryDetails.isolationRequirement"),
    cargoDetailField("handoverRecord", "交接紀錄", "categoryDeliveryDetails.handoverRecord", { type: "textarea", wide: true }),
  ]),
  I_VEHICLES_MACHINERY: cargoDetailSection("category", "汽機車／機械配送細節", [
    cargoDetailField("vin", "車身號碼／VIN", "categoryDeliveryDetails.vin"),
    cargoDetailField("engineNumber", "引擎號碼", "categoryDeliveryDetails.engineNumber"),
    cargoDetailField("mileageKm", "里程", "categoryDeliveryDetails.mileageKm", { type: "number", suffix: "km" }),
    cargoDetailField("fuelOrBattery", "油量／電池狀態", "categoryDeliveryDetails.fuelOrBattery"),
    cargoDetailField("exteriorHandover", "外觀交接", "categoryDeliveryDetails.exteriorHandover", { type: "textarea", wide: true }),
    cargoDetailField("keyCount", "鑰匙數量", "categoryDeliveryDetails.keyCount", { type: "number", suffix: "把" }),
    cargoDetailField("accessoryList", "配件清單", "categoryDeliveryDetails.accessoryList", { type: "textarea", wide: true }),
    cargoDetailField("deliveryDocument", "交車文件", "categoryDeliveryDetails.deliveryDocument", { type: "textarea", wide: true }),
  ]),
};

function deliveryDetailSectionsForCategory(value) {
  const category = DELIVERY_DETAIL_CATEGORY_SECTIONS[normalizeCargoCategory(value)];
  return [...DELIVERY_DETAIL_COMMON_SECTIONS, ...(category ? [category] : [])];
}

const CARGO_DETAIL_CATEGORY_SECTIONS = {
  A_GENERAL_LOGISTICS: [
    cargoDetailSection("packaging", "包裝與搬運", [
      cargoDetailField("packagingType", "包裝型式", "typeDetails.packagingType"),
      cargoDetailField("fragile", "易碎品", "typeDetails.fragile", {
        type: "select",
        options: [["NO", "否"], ["YES", "是"]],
      }),
      cargoDetailField("stackable", "可堆疊", "typeDetails.stackable", {
        type: "select",
        options: [["YES", "是"], ["NO", "否"]],
      }),
      cargoDetailField("keepDry", "保持乾燥", "typeDetails.keepDry", {
        type: "select",
        options: [["NO", "否"], ["YES", "是"]],
      }),
      cargoDetailField("verticalOnly", "需保持直立", "typeDetails.verticalOnly", {
        type: "select",
        options: [["NO", "否"], ["YES", "是"]],
      }),
      cargoDetailField("tailgateRequired", "需要尾門／搬運人員", "typeDetails.tailgateRequired", {
        type: "select",
        options: [["NO", "否"], ["YES", "是"]],
      }),
    ]),
    cargoDetailSection("category", "一般物流資料", [
      cargoDetailField("productType", "貨物品項類型", "typeDetails.productType"),
      cargoDetailField("declaredValue", "申報貨值", "typeDetails.declaredValue", { type: "number", suffix: "元" }),
      cargoDetailField("handlingNote", "搬運注意事項", "typeDetails.handlingNote", { type: "textarea", wide: true }),
    ]),
  ],
  B_CONTAINER_PORT: [
    cargoDetailSection("container", "貨櫃本體", [
      cargoDetailField("containerId", "貨櫃號", "containerId", { placeholder: "輸入貨櫃號" }),
      cargoDetailField("containerType", "櫃型", "typeDetails.containerType", {
        type: "select",
        options: [["DRY", "乾貨櫃"], ["REEFER", "冷凍櫃"], ["OPEN_TOP", "開頂櫃"], ["FLAT_RACK", "平板櫃"], ["ISO_TANK", "ISO Tank"]],
      }),
      cargoDetailField("sealNumber", "封條號", "typeDetails.sealNumber"),
      cargoDetailField("verifiedGrossMassKg", "VGM 重量", "weight.verifiedGrossMassKg", { type: "number", suffix: "kg" }),
      cargoDetailField("pickupLocationNote", "提櫃地點", "typeDetails.pickupLocationNote"),
    ]),
    cargoDetailSection("contents", "貨櫃內容物", [
      cargoDetailField("contentsName", "內容物名稱", "typeDetails.contentsName"),
      cargoDetailField("contentsCount", "內容物件數", "typeDetails.contentsCount", { type: "number", suffix: "件" }),
      cargoDetailField("contentsPackaging", "內容物包裝型式", "typeDetails.contentsPackaging"),
      cargoDetailField("isHazmat", "是否危險品", "typeDetails.isHazmat", {
        type: "select",
        options: [["NO", "否"], ["YES", "是"]],
      }),
      cargoDetailField("setTemperatureC", "冷凍設定溫度", "typeDetails.setTemperatureC", { type: "number", suffix: "°C" }),
      cargoDetailField("cargoOwner", "貨主／委託人", "typeDetails.cargoOwner"),
    ]),
  ],
  C_CHEMICAL_HAZMAT: [
    cargoDetailSection("hazmat", "危險品資料", [
      cargoDetailField("materialName", "化學品名稱", "typeDetails.materialName"),
      cargoDetailField("physicalState", "物態", "typeDetails.physicalState", {
        type: "select",
        options: [["GAS", "氣體"], ["LIQUID", "液體"], ["SOLID", "固體"]],
      }),
      cargoDetailField("casNumber", "CAS No.", "typeDetails.casNumber"),
      cargoDetailField("unNumber", "UN No.", "typeDetails.unNumber"),
      cargoDetailField("hazardClass", "危險類別", "typeDetails.hazardClass"),
      cargoDetailField("packingGroup", "包裝等級", "typeDetails.packingGroup"),
      cargoDetailField("concentration", "濃度／含量", "typeDetails.concentration"),
      cargoDetailField("sdsVersion", "SDS 版本日期", "typeDetails.sdsVersion", { type: "date" }),
    ]),
    cargoDetailSection("protection", "合規與防護", [
      cargoDetailField("vehicleMarking", "車體危險標示", "typeDetails.vehicleMarking"),
      cargoDetailField("ppe", "隨車 PPE／防護用品", "typeDetails.ppe", { type: "textarea", wide: true }),
      cargoDetailField("emergencyContact", "緊急聯絡方式", "typeDetails.emergencyContact"),
      cargoDetailField("transportPermit", "危險品運送許可／證明", "typeDetails.transportPermit"),
    ]),
  ],
  D_WASTE_RECYCLING: [
    cargoDetailSection("waste", "廢棄物資料", [
      cargoDetailField("wasteCode", "廢棄物代碼", "typeDetails.wasteCode"),
      cargoDetailField("wasteType", "廢棄物名稱／類型", "typeDetails.wasteType"),
      cargoDetailField("sourceCompany", "來源公司", "typeDetails.sourceCompany"),
      cargoDetailField("manifestNumber", "聯單號碼", "typeDetails.manifestNumber"),
      cargoDetailField("sourceLocation", "來源地點", "typeDetails.sourceLocation"),
      cargoDetailField("treatmentDestination", "處理／再利用目的地", "typeDetails.treatmentDestination"),
      cargoDetailField("treatmentMethod", "處理方式", "typeDetails.treatmentMethod"),
      cargoDetailField("gpsRequired", "GPS 列管", "typeDetails.gpsRequired", {
        type: "select",
        options: [["YES", "是"], ["NO", "否"]],
      }),
    ]),
    cargoDetailSection("weighing", "過磅與交接", [
      cargoDetailField("weighbridgeNumber", "磅單號碼", "typeDetails.weighbridgeNumber"),
      cargoDetailField("actualWeightKg", "預估／實際重量", "typeDetails.actualWeightKg", { type: "number", suffix: "kg" }),
      cargoDetailField("receiptResult", "目的地收受結果", "typeDetails.receiptResult", { type: "textarea", wide: true }),
    ]),
  ],
  E_SOIL_AGGREGATE: [
    cargoDetailSection("soil", "土石方與砂石資料", [
      cargoDetailField("materialType", "土石／砂石種類", "typeDetails.materialType"),
      cargoDetailField("sourceSite", "來源工地／出土地點", "typeDetails.sourceSite"),
      cargoDetailField("destination", "收土地點／目的地", "typeDetails.destination"),
      cargoDetailField("manifestNumber", "土單／電子聯單號", "typeDetails.manifestNumber"),
      cargoDetailField("estimatedWeightTons", "預估重量", "typeDetails.estimatedWeightTons", { type: "number", suffix: "噸" }),
      cargoDetailField("estimatedVolumeM3", "預估體積", "typeDetails.estimatedVolumeM3", { type: "number", suffix: "m³" }),
      cargoDetailField("weighbridgeNumber", "過磅單號", "typeDetails.weighbridgeNumber"),
      cargoDetailField("coverRequired", "需要覆蓋防飛散", "typeDetails.coverRequired", {
        type: "select",
        options: [["YES", "是"], ["NO", "否"]],
      }),
    ]),
  ],
  F_STEEL_HEAVY_EQUIPMENT: [
    cargoDetailSection("heavyEquipment", "鋼鐵與大型設備資料", [
      cargoDetailField("equipmentName", "設備／貨物名稱", "typeDetails.equipmentName"),
      cargoDetailField("lengthM", "長度", "typeDetails.lengthM", { type: "number", suffix: "m" }),
      cargoDetailField("widthM", "寬度", "typeDetails.widthM", { type: "number", suffix: "m" }),
      cargoDetailField("heightM", "高度", "typeDetails.heightM", { type: "number", suffix: "m" }),
      cargoDetailField("centerOfGravity", "重心位置", "typeDetails.centerOfGravity"),
      cargoDetailField("liftPoints", "吊點／吊裝方式", "typeDetails.liftPoints"),
      cargoDetailField("securingMethod", "固定方式", "typeDetails.securingMethod", { type: "textarea", wide: true }),
      cargoDetailField("permitNumber", "臨時通行證號", "typeDetails.permitNumber"),
      cargoDetailField("escortRequired", "需要前導／後衛車", "typeDetails.escortRequired", {
        type: "select",
        options: [["NO", "否"], ["YES", "是"]],
      }),
    ]),
  ],
  G_COLD_CHAIN: [
    cargoDetailSection("temperature", "溫控資料", [
      cargoDetailField("setTemperatureC", "設定溫度", "typeDetails.setTemperatureC", { type: "number", suffix: "°C" }),
      cargoDetailField("minTemperatureC", "可接受最低溫", "typeDetails.minTemperatureC", { type: "number", suffix: "°C" }),
      cargoDetailField("maxTemperatureC", "可接受最高溫", "typeDetails.maxTemperatureC", { type: "number", suffix: "°C" }),
      cargoDetailField("preCoolTemperatureC", "預冷溫度", "typeDetails.preCoolTemperatureC", { type: "number", suffix: "°C" }),
      cargoDetailField("temperatureRecorder", "溫度紀錄器", "typeDetails.temperatureRecorder", {
        type: "select",
        options: [["NO", "無"], ["YES", "有（需回收）"], ["LIVE", "有（即時上傳）"]],
      }),
      cargoDetailField("temperatureLogRequired", "需要溫度紀錄", "typeDetails.temperatureLogRequired", {
        type: "select",
        options: [["YES", "是"], ["NO", "否"]],
      }),
      cargoDetailField("temperatureNote", "溫控注意事項", "typeDetails.temperatureNote", { type: "textarea", wide: true }),
    ]),
  ],
  H_LIVE_ANIMAL: [
    cargoDetailSection("animal", "活體動物資料", [
      cargoDetailField("species", "動物種類", "typeDetails.species"),
      cargoDetailField("breed", "品種／級別", "typeDetails.breed"),
      cargoDetailField("animalCount", "數量", "typeDetails.animalCount", { type: "number", suffix: "隻" }),
      cargoDetailField("totalWeightKg", "總重量", "typeDetails.totalWeightKg", { type: "number", suffix: "kg" }),
      cargoDetailField("loadingDensity", "裝載密度", "typeDetails.loadingDensity"),
      cargoDetailField("ventilation", "通風需求", "typeDetails.ventilation"),
      cargoDetailField("waterPlan", "飲水／休息安排", "typeDetails.waterPlan", { type: "textarea", wide: true }),
      cargoDetailField("healthDocument", "健康／防疫文件", "typeDetails.healthDocument"),
      cargoDetailField("disinfectionRecord", "清洗消毒紀錄", "typeDetails.disinfectionRecord"),
    ]),
  ],
  I_VEHICLES_MACHINERY: [
    cargoDetailSection("vehicle", "汽機車與機械資料", [
      cargoDetailField("brand", "品牌", "typeDetails.brand"),
      cargoDetailField("model", "車型／機械型號", "typeDetails.model"),
      cargoDetailField("vin", "車身號碼 VIN", "typeDetails.vin"),
      cargoDetailField("plateNumber", "車牌號碼", "typeDetails.plateNumber"),
      cargoDetailField("mileageKm", "里程", "typeDetails.mileageKm", { type: "number", suffix: "km" }),
      cargoDetailField("selfPropelled", "可自行行駛", "typeDetails.selfPropelled", {
        type: "select",
        options: [["YES", "是"], ["NO", "否"]],
      }),
      cargoDetailField("batteryStatus", "電池／燃油狀態", "typeDetails.batteryStatus"),
      cargoDetailField("keyCount", "鑰匙數量", "typeDetails.keyCount", { type: "number", suffix: "把" }),
      cargoDetailField("damageDescription", "外觀損傷與車況", "typeDetails.damageDescription", { type: "textarea", wide: true }),
      cargoDetailField("securingMethod", "裝載與固定方式", "typeDetails.securingMethod", { type: "textarea", wide: true }),
    ]),
  ],
};

function cargoDetailSectionsForCategory(value) {
  const common = [
    cargoDetailSection("basic", "基本資料", [
      cargoDetailField("name", "貨物名稱", "name"),
      cargoDetailField("quantity", "數量", "quantity", { type: "number" }),
      cargoDetailField("unit", "單位", "unit"),
      cargoDetailField("operationType", "貨物作業", "operationType", {
        type: "select",
        options: CARGO_OPERATION_OPTIONS,
      }),
      cargoDetailField("status", "狀態", "status", {
        type: "select",
        options: CARGO_DETAIL_STATUS_OPTIONS,
      }),
      cargoDetailField("note", "貨物描述／備註", "note", { type: "textarea", wide: true }),
    ]),
    cargoDetailSection("weight", "重量與尺寸", [
      cargoDetailField("grossWeightKg", "毛重", "weight.grossWeightKg", { type: "number", suffix: "kg" }),
      cargoDetailField("netWeightKg", "淨重", "weight.netWeightKg", { type: "number", suffix: "kg" }),
      cargoDetailField("lengthMm", "長度", "measurements.lengthMm", { type: "number", suffix: "mm" }),
      cargoDetailField("widthMm", "寬度", "measurements.widthMm", { type: "number", suffix: "mm" }),
      cargoDetailField("heightMm", "高度", "measurements.heightMm", { type: "number", suffix: "mm" }),
      cargoDetailField("volumeM3", "材積", "measurements.volumeM3", { type: "number", suffix: "m³" }),
    ]),
  ];
  const tail = [
    cargoDetailSection("documents", "文件證明", []),
    cargoDetailSection("payment", "代收／代付", [
      cargoDetailField("codAmount", "代收金額", "typeDetails.codAmount", { type: "number", suffix: "元" }),
      cargoDetailField("payAmount", "代付金額", "typeDetails.payAmount", { type: "number", suffix: "元" }),
      cargoDetailField("paymentNote", "收付款備註", "typeDetails.paymentNote", { type: "textarea", wide: true }),
    ]),
  ];
  return [
    ...common,
    ...(CARGO_DETAIL_CATEGORY_SECTIONS[normalizeCargoCategory(value)] || []),
    ...tail,
  ];
}

function readCargoDetailPath(source, path) {
  return text(path)
    .split(".")
    .filter(Boolean)
    .reduce((current, key) => current?.[key], source);
}

function writeCargoDetailPath(source, path, value) {
  const keys = text(path).split(".").filter(Boolean);
  if (!keys.length) return source;
  const next = { ...(source || {}) };
  let cursor = next;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    cursor[key] = { ...(cursor[key] || {}) };
    cursor = cursor[key];
  });
  return next;
}

function cloneCargoDetailDraft(recipient, recipientId = "") {
  const source = {
    ...(recipient || {}),
    id: text(recipientId || recipient?.id || recipient?.recipientId),
  };
  const draft = cargoRecordDraft(source, recipient?.operationType);
  const primary = recipientPrimaryContent(source);
  return {
    ...draft,
    cargoNumber: text(source.cargoNumber),
    sender: { ...normalizeParty(primary.sender, "SENDER") },
    contents: recipientContents(source).map((content) => ({ ...content })),
    name: text(primary.name),
    quantity: primary.quantity ?? "",
    unit: text(primary.unit) || "件",
    note: text(primary.note),
    measurements: { ...(draft.measurements || {}) },
    weight: { ...(draft.weight || {}) },
    typeDetails: { ...(draft.typeDetails || source.domainDetails || {}) },
    deliveryDetails: { ...(draft.deliveryDetails || {}) },
    accessRequirements: {
      ...(draft.accessRequirements || {}),
      documents: [...(draft.documents || draft.accessRequirements?.documents || [])],
    },
    categoryDeliveryDetails: { ...(draft.categoryDeliveryDetails || {}) },
    payment: { ...(draft.payment || {}) },
    execution: {
      ...(draft.execution || {}),
      photos: [...(draft.execution?.photos || [])],
    },
    handlingProfiles: [...(draft.handlingProfiles || [])],
    complianceProfiles: [...(draft.complianceProfiles || [])],
    photos: [...(source.photos || [])],
  };
}

function defaultCargoOperation(stopOperation) {
  const operation = normalizeStopOperation(stopOperation);
  return operation === "PICKUP" ? "PICKUP" : "DELIVERY";
}

function normalizeCargoOperation(value, fallbackOperation = "DELIVERY") {
  const operation = text(value).toUpperCase();
  if (CARGO_OPERATION_LABELS[operation]) return operation;
  return defaultCargoOperation(fallbackOperation);
}

function normalizeImportOperation(value, fallback = "") {
  const raw = text(value).toUpperCase().replace(/[＋+、/\s]/g, "_");
  if (["PICKUP", "取貨", "收件"].includes(raw)) return "PICKUP";
  if (["DELIVERY", "送貨", "派送", "配送"].includes(raw)) return "DELIVERY";
  if (["PICKUP_DELIVERY", "取貨_送貨", "收件_派送"].includes(raw))
    return "PICKUP_DELIVERY";
  return fallback;
}
function normalizeImportWindowType(value) {
  const raw = text(value).toUpperCase().replace(/[\s_-]/g, "");
  if (["硬性", "硬性時間", "HARD", "HARDTIME"].includes(raw)) return "HARD";
  if (["軟性", "軟性時間", "SOFT", "SOFTTIME"].includes(raw)) return "SOFT";
  return "NONE";
}
function compactImportHeader(value) {
  return text(value)
    .replace(/[（(].*?[）)]/g, "")
    .replace(/[\s_\-\/]/g, "")
    .toLowerCase();
}
const IMPORT_FIELD_ALIASES = {
  stationIndex: ["站點編號", "index", "seq", "序號"],
  address: ["地址", "配送地址", "收件地址", "address"],
  timeWindowType: ["時間類型", "time_window_type", "時段類型"],
  earliestStart: ["最早開始", "earliest_start", "最早時間"],
  latestStart: ["最晚開始", "latest_start", "最晚時間"],
  serviceMinutes: ["作業時間(分鐘)", "作業時間", "service_minutes"],
  recipientId: ["收件人編號", "聯絡人編號", "recipient_id"],
  cargoNumber: ["貨物編號", "貨單編號", "cargo_number", "cargo_no"],
  recipientName: ["收件人姓名", "姓名", "聯絡人", "recipient_name"],
  company: ["公司", "公司名稱", "company"],
  phone: ["電話", "聯絡電話", "手機", "phone"],
  email: ["email", "電子郵件", "電子信箱", "信箱"],
  cargoOperation: ["貨物作業", "任務類型", "cargo_operation", "cargo_type"],
  cargoName: ["貨物名稱", "貨物名稱&件數", "cargo_name", "items"],
  quantity: ["數量", "貨物件數", "件數", "quantity"],
  unit: ["單位", "unit"],
  note: ["貨物備註", "備註", "note"],
};
function importHeaderMap(headers) {
  const map = {};
  Object.entries(IMPORT_FIELD_ALIASES).forEach(([field, aliases]) => {
    const aliasSet = new Set(aliases.map(compactImportHeader));
    const index = headers.findIndex((header) =>
      aliasSet.has(compactImportHeader(header)),
    );
    if (index >= 0) map[field] = index;
  });
  return map;
}
function parseImportCsv(source) {
  const rows = [];
  let row = [];
  let cell = "";
  let quoted = false;
  const value = String(source || "").replace(/^\uFEFF/, "");
  for (let index = 0; index < value.length; index += 1) {
    const char = value[index];
    const next = value[index + 1];
    if (char === '"' && quoted && next === '"') {
      cell += '"';
      index += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === "," && !quoted) {
      row.push(cell);
      cell = "";
    } else if ((char === "\n" || char === "\r") && !quoted) {
      if (char === "\r" && next === "\n") index += 1;
      row.push(cell);
      if (row.some((item) => text(item))) rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += char;
    }
  }
  row.push(cell);
  if (row.some((item) => text(item))) rows.push(row);
  return rows;
}
function importRowsFromFile(file, source) {
  if (file.name.toLowerCase().endsWith(".csv")) return parseImportCsv(source);
  const workbook = XLSX.read(source, { type: "array" });
  const candidates = workbook.SheetNames.map((sheetName) =>
    XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
      header: 1,
      defval: "",
    }),
  );
  const rows = candidates.find((candidate) => {
    const header = candidate[0] || [];
    return Object.keys(importHeaderMap(header)).length >= 6;
  });
  if (!rows) throw new Error("Excel 找不到包含匯入欄位的工作表。");
  return rows;
}
function parseFleetImportRows(rawRows) {
  if (!Array.isArray(rawRows) || rawRows.length < 2)
    throw new Error("檔案沒有可匯入的資料列。");
  const headers = rawRows[0].map((value, index) => text(value) || `欄位${index + 1}`);
  const map = importHeaderMap(headers);
  const required = [
    ["stationIndex", "站點編號"],
    ["address", "地址"],
    ["recipientId", "收件人編號"],
    ["recipientName", "收件人姓名"],
    ["email", "Email"],
    ["cargoOperation", "貨物作業"],
    ["cargoName", "貨物名稱"],
    ["quantity", "數量"],
    ["unit", "單位"],
  ];
  const missing = required.filter(([key]) => map[key] === undefined).map(([, label]) => label);
  if (missing.length) throw new Error(`缺少必要欄位：${missing.join("、")}`);
  const read = (cells, key) =>
    map[key] === undefined ? "" : text(cells[map[key]]);
  const parsed = [];
  rawRows.slice(1).forEach((cells, rowIndex) => {
    if (!cells.some((value) => text(value))) return;
    const errors = [];
    const stationIndex = Number(read(cells, "stationIndex"));
    const quantity = Number(read(cells, "quantity").replace(/,/g, ""));
    const cargoOperation = normalizeImportOperation(read(cells, "cargoOperation"));
    if (!Number.isInteger(stationIndex) || stationIndex <= 0) errors.push("站點編號必須是正整數");
    if (!text(read(cells, "address"))) errors.push("缺少地址");
    if (!text(read(cells, "recipientId"))) errors.push("缺少收件人編號");
    if (!text(read(cells, "recipientName"))) errors.push("缺少收件人姓名");
    if (!text(read(cells, "email"))) errors.push("缺少收件人 Email");
    if (!cargoOperation) errors.push("貨物作業必須是取貨或送貨");
    if (!text(read(cells, "cargoName"))) errors.push("缺少貨物名稱");
    if (!Number.isFinite(quantity) || quantity <= 0) errors.push("數量必須大於 0");
    if (!text(read(cells, "unit"))) errors.push("缺少單位");
    const windowType = normalizeImportWindowType(read(cells, "timeWindowType"));
    parsed.push({
      rowNumber: rowIndex + 2,
      stationIndex,
      address: read(cells, "address"),
      timeWindowType: windowType,
      earliestStart: read(cells, "earliestStart"),
      latestStart: read(cells, "latestStart"),
      serviceMinutes: Math.max(0, Number(read(cells, "serviceMinutes")) || 10),
      recipientId: read(cells, "recipientId"),
      cargoNumber: read(cells, "cargoNumber"),
      recipientName: read(cells, "recipientName"),
      company: read(cells, "company"),
      phone: read(cells, "phone"),
      email: read(cells, "email"),
      cargoOperation,
      cargoName: read(cells, "cargoName"),
      quantity: Number.isFinite(quantity) ? quantity : "",
      unit: read(cells, "unit"),
      note: read(cells, "note"),
      errors,
    });
  });
  if (!parsed.length) throw new Error("檔案沒有可匯入的資料列。");
  return parsed;
}
function buildImportPreview(rows) {
  const stations = new Map();
  rows.forEach((row) => {
    const stationKey = String(row.stationIndex);
    let station = stations.get(stationKey);
    if (!station) {
      station = {
        stationIndex: row.stationIndex,
        address: row.address,
        timeWindowType: row.timeWindowType,
        earliestStart: row.earliestStart,
        latestStart: row.latestStart,
        serviceMinutes: row.serviceMinutes,
        importErrors: [],
        recipients: [],
      };
      stations.set(stationKey, station);
    }
    station.importErrors.push(
      ...(row.errors || []).map((error) => `第 ${row.rowNumber} 列：${error}`),
    );
    const existingRecipient = station.recipients.find(
      (recipient) => recipient.id === row.recipientId,
    );
    const recipient = existingRecipient || {
      id: row.recipientId,
      recipientId: row.recipientId,
      cargoNumber: row.cargoNumber,
      operationType: row.cargoOperation,
      name: row.recipientName,
      company: row.company,
      phone: row.phone,
      email: row.email,
      contents: [],
    };
    if (!existingRecipient) station.recipients.push(recipient);
    const cargo = {
      id: `import-${row.rowNumber}-${text(row.cargoName)}`,
      operationType: row.cargoOperation,
      name: row.cargoName,
      quantity: row.quantity,
      unit: row.unit,
      note: row.note,
    };
    recipient.contents.push(cargo);
  });
  return Array.from(stations.values()).sort((a, b) => a.stationIndex - b.stationIndex);
}
function validateImportPreview(stations) {
  const errors = [];
  stations.forEach((station) => {
    (station.recipients || []).forEach((recipient) => {
      if (!text(recipient.name)) {
        errors.push(`站點 ${station.stationIndex} 的收件人姓名未填寫`);
      }
      if (!text(recipient.email)) {
        errors.push(`站點 ${station.stationIndex} 的收件人 Email 未填寫`);
      }
      const recordOperation = recipientOperationType(recipient, "DELIVERY");
      recipientContents(recipient).forEach((item) => {
        const itemOperation = normalizeCargoOperation(
          item.operationType,
          recordOperation,
        );
        if (!CARGO_OPERATION_LABELS[itemOperation]) {
          errors.push(
            `站點 ${station.stationIndex} 的貨物「${text(item.name) || "未命名"}」作業方向無效`,
          );
        }
      });
    });
  });
  return Array.from(new Set(errors));
}

function createEmptyImportPreview() {
  return [
    {
      stationIndex: 1,
      address: "",
      timeWindowType: "NONE",
      earliestStart: "",
      latestStart: "",
      serviceMinutes: 10,
      importErrors: [],
      recipients: [
        {
          id: "",
          recipientId: "",
          cargoNumber: "",
          operationType: "PICKUP",
          name: "",
          company: "",
          phone: "",
          email: "",
          contents: [
            {
              id: `import-empty-${makeRandomId().slice(0, 8)}`,
              operationType: "PICKUP",
              name: "",
              quantity: "",
              unit: "件",
              note: "",
            },
          ],
        },
      ],
    },
  ];
}

const DEFAULT_RECIPIENT_EMAIL_TEMPLATE = `您好，{{recipientName}}

{{workName}} 配送工作已建立
工作編號：{{workId}}
配送日期：{{startAt}}
預計抵達時段：{{arrivalWindow}}
地址：{{stopAddress}}
貨物清單：{{cargoSummary}}

您可以點擊下方連結查看配送資訊與貨物狀態：
{{trackingUrl}}

此信由 Tallarge 車隊管理系統發送。`;

function normalizeRecipientNotificationSettings(value = {}) {
  return {
    emailEnabled: value.emailEnabled !== false,
    emailTemplate: DEFAULT_RECIPIENT_EMAIL_TEMPLATE,
  };
}

function renderRecipientEmailTemplate(template, values = {}) {
  return text(template).replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, (_, key) =>
    text(values[key]),
  );
}
function parseRecipientEmailDraft(draft, fallback = {}) {
  const raw = text(draft);
  const valueAfter = (labels, endLabels = []) => {
    const starts = (Array.isArray(labels) ? labels : [labels])
      .map((label) => ({ label, index: raw.indexOf(label) }))
      .filter((item) => item.index >= 0)
      .sort((a, b) => a.index - b.index);
    if (!starts.length) return "";
    const contentStart = starts[0].index + starts[0].label.length;
    const endPositions = endLabels
      .map((label) => raw.indexOf(label, contentStart))
      .filter((position) => position >= 0);
    const end = endPositions.length ? Math.min(...endPositions) : raw.length;
    return raw.slice(contentStart, end).trim();
  };
  const lines = raw.split(/\n/).map((line) => line.trim());
  const workNameLine = lines.find((line) => line.includes("工作已建立"));
  const recipientLine = lines.find((line) => /^您好[，,]/.test(line));
  const cargoLabels = ["貨物清單：", "貨物清單:", "貨物摘要：", "貨物摘要:"];
  return {
    ...fallback,
    recipientName:
      recipientLine?.replace(/^您好[，,]\s*/, "") || fallback.recipientName,
    workName:
      workNameLine?.replace(/\s*配送?工作已建立.*$/, "").trim() ||
      fallback.workName,
    startAt:
      valueAfter([
        "配送日期與時間：",
        "配送日期與時間:",
        "配送日期：",
        "配送日期:",
      ], ["\n"]) || fallback.startAt,
    stopAddress:
      valueAfter(
        ["地址：", "地址:", "配送站點：", "配送站點:"],
        cargoLabels,
      ) || fallback.stopAddress,
    cargoSummary:
      valueAfter(cargoLabels, ["您可以點擊", "您可以點選", "此信由"])
        .replace(/\n+/g, " ")
        .trim() || fallback.cargoSummary,
  };
}

function escapeEmailHtml(value) {
  return text(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function emailHtmlLines(value) {
  return escapeEmailHtml(value).replace(/\n/g, "<br />");
}

function hasRecipientEmailCargoItem(item) {
  return Boolean(
    text(item?.name) || Number(item?.quantity) > 0 || text(item?.note),
  );
}

function renderRecipientDeliveryEmailHtml({
  recipientName,
  trackingUrl,
  summary,
  items = [],
  customBody,
}) {
  const safeName = escapeEmailHtml(recipientName || "您好");
  const scheduleLabel = emailHtmlLines(
    recipientEmailArrivalSummary(summary?.stops || []),
  );
  const safeUrl = escapeEmailHtml(trackingUrl || "https://tallarge.com");
  const addressRows = (summary?.stops || [])
    .map((stop) => text(stop.address))
    .filter(Boolean)
    .map(
      (address) => `
        <tr>
          <td style="padding:12px 0;border-bottom:1px solid #e8eef3;vertical-align:top;color:#64788c;font-size:13px;line-height:1.6;word-break:break-word;">
            <strong style="color:#163e63;">地址：</strong>${escapeEmailHtml(address)}
          </td>
        </tr>`,
    )
    .join("");
  const visibleItems = items.filter(hasRecipientEmailCargoItem);
  const cargo = visibleItems
    .map(
      (item) => `
        <tr>
          <td style="padding:9px 0;border-bottom:1px solid #e8eef3;color:#365a77;font-size:13px;">${escapeEmailHtml(item.name || "未命名貨物")}</td>
          <td style="padding:9px 0;border-bottom:1px solid #e8eef3;color:#163e63;text-align:right;font-size:13px;font-weight:700;">${escapeEmailHtml(`${item.quantity || 0} ${item.unit || "件"}`)}</td>
        </tr>`,
    )
    .join("");
  const cargoCountLabel = visibleItems.length
    ? `${visibleItems.length} 項`
    : "尚未填寫";
  const customBlock = customBody
    ? `<tr><td style="padding:0 30px 22px;"><div style="padding:16px 18px;border:1px solid #e5edf3;border-radius:10px;background:#fbfdff;color:#536a7e;font-size:13px;line-height:1.7;">${emailHtmlLines(customBody)}</div></td></tr>`
    : "";
  return `<!doctype html><html><body style="margin:0;background:#f2f7fb;font-family:Arial,'Noto Sans TC','Microsoft JhengHei',sans-serif;color:#163e63;"><table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#f2f7fb;padding:28px 12px;"><tr><td align="center"><table role="presentation" width="640" cellspacing="0" cellpadding="0" style="width:100%;max-width:640px;background:#fff;border-radius:14px;overflow:hidden;box-shadow:0 8px 28px rgba(21,55,82,.08);"><tr><td style="padding:26px 32px 22px;border-bottom:1px solid #edf2f6;"><img src="https://tallarge.com/static/media/Tallarge_final.43c62f7bd14af576d68e.png" width="175" alt="Tallarge" style="display:block;width:175px;max-width:58%;height:auto;border:0;" /></td></tr><tr><td style="padding:34px 32px 30px;background:#eef7ff;"><div style="font-size:18px;font-weight:700;color:#123d63;">您好，${safeName}</div><div style="margin-top:10px;font-size:31px;line-height:1.2;font-weight:800;letter-spacing:-.04em;color:#123d63;">您的配送工作已建立</div><div style="margin-top:14px;color:#627c93;font-size:14px;line-height:1.7;">我們已為您安排本次配送。<br />您可以查看地址與貨物清單。</div></td></tr><tr><td style="padding:26px 30px 30px;"><div style="font-size:22px;font-weight:800;color:#123d63;margin-bottom:18px;">本次配送摘要</div><table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#f7fbfe;border-radius:10px;padding:4px 0;"><tr><td width="50%" align="center" style="padding:14px 8px;border-right:1px solid #dfeaf2;"><div style="font-size:11px;color:#70869a;">預計抵達時段</div><strong style="display:block;margin-top:7px;font-size:16px;line-height:1.4;color:#123d63;">${scheduleLabel}</strong></td><td width="50%" align="center" style="padding:14px 8px;"><div style="font-size:11px;color:#70869a;">貨物</div><strong style="display:block;margin-top:7px;font-size:18px;color:#123d63;">${cargoCountLabel}</strong></td></tr></table><div style="height:18px;"></div><table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border:1px solid #e2ebf1;border-radius:10px;padding:0 18px;"><tr><td style="padding:18px 0 7px;font-size:16px;font-weight:800;color:#123d63;">地址</td></tr>${addressRows || `<tr><td style="padding:10px 0 18px;color:#75899b;font-size:13px;">尚未提供地址。</td></tr>`}</table><div style="height:18px;"></div><table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border:1px solid #e2ebf1;border-radius:10px;padding:0 18px;"><tr><td style="padding:18px 0 7px;font-size:16px;font-weight:800;color:#123d63;">貨物清單</td></tr>${cargo || `<tr><td style="padding:10px 0 18px;color:#75899b;font-size:13px;">尚未提供貨物資料。</td></tr>`}</table></td></tr>${customBlock}<tr><td style="padding:0 30px 30px;"><a href="${safeUrl}" style="display:block;padding:14px 18px;border-radius:8px;background:#f58220;color:#fff;text-align:center;text-decoration:none;font-size:16px;font-weight:800;">查看全部配送資訊&nbsp;&nbsp;→</a></td></tr><tr><td style="padding:18px 30px 26px;border-top:1px solid #edf2f6;text-align:center;color:#8a9aaa;font-size:11px;line-height:1.6;">此信由 Tallarge 車隊管理系統發送。<br />若您不認識這筆配送，請聯絡寄件方。</td></tr></table></td></tr></table></body></html>`;
}

function normalizeRecipientEmail(value) {
  return text(value).toLowerCase();
}
function stableRecipientProfileId(orgId, email) {
  const normalizedEmail = normalizeRecipientEmail(email);
  if (!normalizedEmail) return "";
  const source = `${text(orgId)}|${normalizedEmail}`;
  let first = 2166136261;
  let second = 16777619;
  for (let index = 0; index < source.length; index += 1) {
    const code = source.charCodeAt(index);
    first ^= code;
    first = Math.imul(first, 16777619);
    second ^= code + index;
    second = Math.imul(second, 2246822519);
  }
  return `rp_${(first >>> 0).toString(36)}${(second >>> 0).toString(36)}`;
}
function stopAddressText(stop) {
  return (
    text(stop?.text) ||
    [stop?.city, stop?.suburb, stop?.road, stop?.houseNumber]
      .map(text)
      .filter(Boolean)
      .join("") ||
    "未提供地址"
  );
}
function cargoQuantity(item) {
  const quantity = Number(item?.quantity);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
}
function cargoStatusCounts(items = []) {
  return items.reduce(
    (counts, item) => {
      const status = cargoStatusCode(item?.status);
      counts[status] = (counts[status] || 0) + 1;
      return counts;
    },
    {
      PENDING_PICKUP: 0,
      PICKED_UP: 0,
      IN_TRANSIT: 0,
      DELIVERED: 0,
      EXCEPTION: 0,
    },
  );
}
function aggregateCargoStatus(counts) {
  if (counts.EXCEPTION > 0) return "EXCEPTION";
  if (counts.IN_TRANSIT > 0) return "IN_TRANSIT";
  if (counts.PENDING_PICKUP > 0) return "PENDING_PICKUP";
  if (counts.PICKED_UP > 0) return "PICKED_UP";
  return "DELIVERED";
}
function recipientGroupSummary(group) {
  const items = group.items || [];
  const counts = cargoStatusCounts(items);
  const totalQuantity = items.reduce((sum, item) => sum + cargoQuantity(item), 0);

  const cargoItems = items.map((item) => ({
    id: text(item?.id || item?.contentId || item?.itemId),
    name: text(item?.name || item?.itemName || item?.cargoName),
    quantity: cargoQuantity(item),
    unit: text(item?.unit) || "件",
    operationType: normalizeCargoOperation(item?.operationType, "DELIVERY"),
    stopId: text(item?.stopId),
    order: Number(item?.order || 0),
    status: cargoStatusCode(item?.status),
  }));
  const operationTypes = new Set(cargoItems.map((item) => item.operationType));
  const hasPickup = operationTypes.has("PICKUP") || operationTypes.has("PICKUP_DELIVERY");
  const hasDelivery = operationTypes.has("DELIVERY") || operationTypes.has("PICKUP_DELIVERY");
  const operationType = hasPickup && hasDelivery
    ? "PICKUP_DELIVERY"
    : hasPickup
      ? "PICKUP"
      : "DELIVERY";
  const stopMap = new Map();
  group.stopRecords.forEach((record) => {
    const existing = stopMap.get(record.stopId);
    if (existing) {
      existing.cargoCount += record.cargoCount;
      existing.cargoQuantity += record.cargoQuantity;
      existing.statusCounts = cargoStatusCounts(
        items.filter((item) => item.stopId === record.stopId),
      );
    } else {
      stopMap.set(record.stopId, {
        stopId: record.stopId,
        order: record.order,
        address: record.address,
        cargoCount: record.cargoCount,
        cargoQuantity: record.cargoQuantity,
        status: record.status,
      });
    }
  });
  return {
    stopCount: stopMap.size,
    cargoCount: items.length,
    cargoQuantity: totalQuantity,
    operationType,
    cargoItems,
    status: aggregateCargoStatus(counts),
    statusCounts: counts,
    stops: Array.from(stopMap.values()).sort(
      (a, b) => Number(a.order || 0) - Number(b.order || 0),
    ),
  };
}

function optionalCargoStatus(value) {
  const code = text(value).toUpperCase();
  return CARGO_STATUS_LABELS[code] ? code : "";
}
function cargoStatusCode(value) {
  const code = optionalCargoStatus(value);
  return code || "PENDING_PICKUP";
}
function cargoStatusLabel(value) {
  const code = optionalCargoStatus(value);
  return code ? CARGO_STATUS_LABELS[code] : "未設定";
}

function inputDateTimeLocalValue(value) {
  const date = firestoreDate(value);
  return date
    ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
    : "";
}

function normalizeCargoLocation(value) {
  if (!coordsOK(value)) return null;
  return {
    lat: Number(value.lat),
    lng: Number(value.lng),
    source: text(value.source),
    updatedAt: value.updatedAt || null,
    vehicleId: text(value.vehicleId),
    driverId: text(value.driverId),
  };
}

function normalizeParkedLocation(value) {
  if (!value || (!text(value.name) && !coordsOK(value))) return null;
  return {
    name: text(value.name),
    lat: coordsOK(value) ? Number(value.lat) : null,
    lng: coordsOK(value) ? Number(value.lng) : null,
    parkedAt: value.parkedAt || null,
    parkedByUid: text(value.parkedByUid),
    note: text(value.note),
  };
}

function serializeParkedLocation(value) {
  const location = normalizeParkedLocation(value);
  return location
    ? {
        ...location,
        parkedAt:
          location.parkedAt instanceof Date
            ? location.parkedAt
            : firestoreDate(location.parkedAt) || location.parkedAt,
      }
    : null;
}


function editablePhotoSignature(photo) {
  const file = photo?.file;
  return {
    url: text(photo?.url),
    path: text(photo?.path),
    name: text(photo?.name || file?.name),
    type: text(photo?.type || file?.type),
    file: file
      ? {
          name: text(file.name),
          size: Number(file.size) || 0,
          lastModified: Number(file.lastModified) || 0,
        }
      : null,
  };
}

function compactExecution(value) {
  const execution = compactRecord(value || {});
  if (execution.receivingStatus === "待配送") {
    delete execution.receivingStatus;
  }
  return compactRecord(execution);
}

function qrTokenFor(value, fallback = "") {
  return text(
    value?.identification?.qrToken ||
      value?.qrToken ||
      value?.identification?.token ||
      fallback,
  );
}

function qrPayloadFor(kind, token) {
  return JSON.stringify({ v: 3, kind, token: text(token) });
}

function formalContentRecords(recipient) {
  return recipientContents(recipient)
    .filter(
      (content) =>
        text(content?.name) ||
        text(content?.quantity) ||
        text(content?.unit) ||
        text(content?.note),
    )
    .map((content) => {
      const contentId = canonicalCargoCode(content?.contentId || content?.id);
      const contentQrToken = qrTokenFor(content, makeRandomId());
      const record = {
        contentId,
        identification: {
          qrEnabled: true,
          qrToken: contentQrToken,
          qrPayload: qrPayloadFor("cargo-item", contentQrToken),
        },
        operationType: normalizeCargoOperation(content?.operationType, "DELIVERY"),
        name: text(content?.name),
        quantity: Number(content?.quantity),
        unit: text(content?.unit),
        sender: compactRecord(normalizeParty(content?.sender, "SENDER")),
      };
      if (text(content?.note)) record.note = text(content.note);
      if (Array.isArray(content?.photos) && content.photos.length) {
        record.photos = content.photos.map((photo) => ({ ...photo }));
      }
      const detailBlocks = {
        domainDetails: compactRecord(content?.domainDetails || content?.typeDetails),
        cargoDetails: compactRecord(content?.cargoDetails),
        deliveryDetails: compactRecord(content?.deliveryDetails),
        accessRequirements: compactRecord(content?.accessRequirements),
        documents: compactRecord(content?.documents),
        payment: compactRecord(content?.payment),
        execution: (() => {
          const execution = compactExecution(content?.execution);
          if (execution && typeof execution === "object") delete execution.photos;
          return execution;
        })(),
        currentLocation: compactRecord(content?.currentLocation),
        parkedLocation: compactRecord(serializeParkedLocation(content?.parkedLocation)),
        status: optionalCargoStatus(content?.status),
      };
      Object.entries(detailBlocks).forEach(([key, value]) => {
        if (hasMeaningfulValue(value)) record[key] = value;
      });
      return compactRecord(record);
    });
}

function formalRecipientRecord({
  recipient,
  applicationId,
  operationType,
  workDomain,
  originalRecipient,
}) {
  const recipientId = canonicalRecipientCode(recipient?.id || recipient?.recipientId);
  const operation = recipientOperationType(recipient, operationType);
  const contents = formalContentRecords(recipient);
  const primary = recipientPrimaryContent(recipient);
  const recipientQrToken = qrTokenFor(recipient, makeRandomId());
  const record = {
    recipientId,
    operationType: operation,
    name: text(recipient?.name),
    company: text(recipient?.company),
    phone: text(recipient?.phone),
    email: text(recipient?.email),
    contents,
    status:
      optionalCargoStatus(recipient?.status) ||
      (operation === "PICKUP" ? "PENDING_PICKUP" : "PENDING_DELIVERY"),
    identification: {
      qrEnabled: true,
      qrToken: recipientQrToken,
      qrPayload: qrPayloadFor("cargo-manifest", recipientQrToken),
    },
  };
  const cargoDetails = {
    ...(recipient?.cargoDetails || normalizeCargoDetails(primary)),
  };
  delete cargoDetails.typeDetails;
  delete cargoDetails.containerId;
  const domainDetails = {
    ...(recipient?.domainDetails || recipient?.typeDetails || {}),
    ...(text(recipient?.containerId)
      ? { containerNumber: text(recipient.containerId) }
      : {}),
  };
  const accessRequirements = { ...(recipient?.accessRequirements || {}) };
  const optionalBlocks = {
    sourceCargoRef: recipient?.sourceCargoRef
      ? compactRecord({
          ...recipient.sourceCargoRef,
          ...(text(recipient.sourceCargoRef.source).toLowerCase() === "cargoinventory"
            ? {}
            : { jobId: text(recipient.sourceCargoRef.jobId) || text(applicationId) }),
        })
      : undefined,
    domainDetails:
      normalizeWorkDomain(workDomain) === "NONE"
        ? undefined
        : compactRecord(domainDetails),
    cargoDetails: compactRecord(cargoDetails),
    deliveryDetails: compactRecord(recipient?.deliveryDetails),
    accessRequirements: compactRecord(accessRequirements),
    payment: compactRecord(recipient?.payment),
    execution: (() => {
      const execution = compactExecution(recipient?.execution);
      if (execution && typeof execution === "object") delete execution.photos;
      return execution;
    })(),
    currentLocation: compactRecord(recipient?.currentLocation),
    parkedLocation: compactRecord(
      serializeParkedLocation(recipient?.parkedLocation),
    ),
    recipientProfileId: text(recipient?.recipientProfileId),
    addressText: text(recipient?.addressText),
    note: text(recipient?.note),
    notificationSettings: compactRecord(recipient?.notificationSettings),
  };
  Object.entries(optionalBlocks).forEach(([key, value]) => {
    if (hasMeaningfulValue(value)) record[key] = value;
  });
  if (originalRecipient?.identification && !record.identification) {
    record.identification = originalRecipient.identification;
  }
  return record;
}

function editableSnapshot({
  applicationName,
  workType,
  schedule,
  startPointEnabled,
  stops,
  selectedMemberIds,
  assignments,
}) {
  return JSON.stringify({
    applicationName: text(applicationName),
    workType: normalizeWorkType(workType),
    workDomain:
      normalizeWorkType(workType) === "INSPECTION"
        ? "NONE"
        : normalizeWorkDomain(workType),
    schedule: {
      date: text(schedule?.date),
      startTime: text(schedule?.startTime),
      hasRange: schedule?.hasRange === true,
      endTime: text(schedule?.endTime),
    },
    startPointEnabled: startPointEnabled === true,
    selectedMemberIds: (selectedMemberIds || []).map(text),
    assignments: Object.fromEntries(
      Object.entries(assignments || {})
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([uid, value]) => [
          uid,
          {
            vehicleId: text(value?.vehicleId),
            trailerIds: assignmentTrailerIds(value),
            containerIds: assignmentContainerIds(value),
            equipmentIds: assignmentEquipmentIds(value),
          },
        ]),
    ),
    stops: (stops || []).map((stop) => ({
      id: text(stop?.id).toUpperCase(),
      operationType: normalizeStopOperation(stop?.operationType),
      order: Number.isFinite(Number(stop?.order)) ? Number(stop.order) : null,
      timeWindowType: text(stop?.timeWindowType),
      earliestStart: text(stop?.earliestStart),
      latestStart: text(stop?.latestStart),
      serviceMinutes: Number(stop?.serviceMinutes) || 0,
      text: text(stop?.text),
      lat: Number.isFinite(Number(stop?.lat)) ? Number(stop.lat) : null,
      lng: Number.isFinite(Number(stop?.lng)) ? Number(stop.lng) : null,
      postalCode: text(stop?.postalCode),
      recipients: (stop?.recipients || []).map((recipient) => ({
        id: text(recipient?.id),
        name: text(recipient?.name),
        phone: text(recipient?.phone),
        company: text(recipient?.company),
        email: text(recipient?.email),
        note: text(recipient?.note),
        recipientId: text(recipient?.id || recipient?.recipientId),
        cargoNumber: text(recipient?.cargoNumber),
        operationType: recipientOperationType(recipient, stop?.operationType),
        contents: recipientContents(recipient).map((content) => ({
          id: text(content?.id || content?.contentId),
          operationType: normalizeCargoOperation(
            content?.operationType,
            recipient?.operationType || stop?.operationType,
          ),
          sender: normalizeParty(content?.sender, "SENDER"),
          name: text(content?.name),
          quantity: text(content?.quantity),
          unit: text(content?.unit),
          note: text(content?.note),
          domainDetails: compactRecord(content?.domainDetails || content?.typeDetails),
          cargoDetails: compactRecord(content?.cargoDetails),
          deliveryDetails: compactRecord(content?.deliveryDetails),
          accessRequirements: compactRecord(content?.accessRequirements),
          documents: normalizeDocumentProofs(content?.documents || content?.accessRequirements?.documents),
          payment: compactRecord(content?.payment),
          execution: compactExecution(content?.execution),
          currentLocation: normalizeCargoLocation(content?.currentLocation),
          parkedLocation: serializeParkedLocation(content?.parkedLocation),
          status: optionalCargoStatus(content?.status),
          photos: (content?.photos || []).map(editablePhotoSignature),
        })),
        domainDetails: compactRecord(recipient?.domainDetails || recipient?.typeDetails),
        cargoDetails: compactRecord(recipient?.cargoDetails),
        deliveryDetails: compactRecord(recipient?.deliveryDetails),
        accessRequirements: compactRecord(recipient?.accessRequirements),
        documents: normalizeDocumentProofs(recipient?.documents),
        payment: compactRecord(recipient?.payment),
        execution: compactExecution(recipient?.execution),
        photos: (recipient?.photos || []).map(editablePhotoSignature),
        parkedLocation: serializeParkedLocation(recipient?.parkedLocation),
      })),
      photos: (stop?.photos || []).map(editablePhotoSignature),
    })),
  });
}

function formatAddress(item, fallback = {}) {
  const address = item?.address || {};
  const postalCode = text(address.postcode || address.postalCode);
  const cityValue = text(address.city);
  const countyValue = text(address.county);
  const city = /[市縣]$/.test(cityValue)
    ? cityValue
    : /[市縣]$/.test(countyValue)
      ? countyValue
      : "";
  const suburb = text(address.suburb || address.town);
  const road = text(
    address.locality ||
      address.road ||
      address.city_district ||
      address.neighbourhood,
  );
  const houseNumber = normalizeHouseNumber(address.house_number);
  const lat = Number(item?.lat ?? fallback.lat);
  const lng = Number(item?.lon ?? item?.lng ?? fallback.lng);
  const formatted = [city, suburb, road, houseNumber].filter(Boolean).join("");
  return {
    text: formatted,
    city,
    suburb,
    road,
    houseNumber,
    postalCode,
    lat,
    lng,
  };
}

async function reverseGeocode(lat, lng, signal) {
  const params = new URLSearchParams({
    format: "jsonv2",
    lat: String(lat),
    lon: String(lng),
    addressdetails: "1",
    zoom: "18",
    "accept-language": "zh-TW",
  });
  const response = await fetch(
    `https://nominatim.openstreetmap.org/reverse?${params.toString()}`,
    { signal, headers: { Accept: "application/json" } },
  );
  if (!response.ok) throw new Error(`Nominatim ${response.status}`);
  const data = await response.json();
  const normalized = formatAddress(data, { lat, lng });
  if (!coordsOK(normalized) || !normalized.text) throw new Error("找不到地址");
  return normalized;
}

async function searchAddress(
  queryText,
  signal,
  limit = 5,
  allowRoadFallback = true,
) {
  const q = splitTaiwanPostalPrefix(queryText).address;
  if (!q) return [];
  const roadQuery = q.replace(/\s*\d+(?:[-之]\d+)?號?\s*$/, "").trim();

  const params = new URLSearchParams({
    format: "jsonv2",
    q,
    addressdetails: "1",
    countrycodes: "tw",
    limit: String(limit),
    dedupe: "1",
    "accept-language": "zh-TW",
  });
  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?${params.toString()}`,
    { signal, headers: { Accept: "application/json" } },
  );
  if (!response.ok) throw new Error(`Nominatim ${response.status}`);
  const data = await response.json();

  if (!Array.isArray(data)) {
    if (allowRoadFallback && roadQuery && roadQuery !== q) {
      return searchAddress(roadQuery, signal, limit, false);
    }
    return [];
  }
  const normalizedResults = data
    .map((item, index) => ({
      id: text(item.place_id) || `address-${index}`,
      display: text(item.display_name),
      normalized: formatAddress(item),
    }))
    .filter((item) => coordsOK(item.normalized) && item.normalized.text);
  if (
    normalizedResults.length === 0 &&
    allowRoadFallback &&
    roadQuery &&
    roadQuery !== q
  ) {
    return searchAddress(roadQuery, signal, limit, false);
  }
  return normalizedResults;
}

function isStartStop(stop) {
  return text(stop?.id).toUpperCase() === "SRT";
}

function isEndStop(stop) {
  return text(stop?.id).toUpperCase() === "END";
}

function createCargoItem(operationType = "DELIVERY") {
  return {
    id: makeCargoCode(),
    contentId: makeCargoCode(),
    operationType: defaultCargoOperation(operationType),
    name: "",
    quantity: "",
    unit: "件",
    note: "",
    sender: normalizeParty({}, "SENDER"),
    ...normalizeCargoDetails(),
    ...normalizeDeliveryDetails(),
    photos: [],
    status: "",
    currentLocation: null,
    parkedLocation: null,
  };
}

function createRecipient(operationType = "DELIVERY") {
  const id = makeRecipientCode();
  return {
    id,
    recipientId: id,
    cargoNumber: "",
    operationType: defaultCargoOperation(operationType),
    name: "",
    phone: "",
    company: "",
    email: "",
    addressText: "",
    note: "",
    notificationSettings: normalizeRecipientNotificationSettings(),
    contents: [{ ...createCargoItem(operationType), recipientId: id }],
  };
}

function makeRandomId() {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from(
    { length: 20 },
    () => chars[Math.floor(Math.random() * chars.length)],
  ).join("");
}

function makeAssetUuid() {
  const cryptoApi =
    typeof window !== "undefined" ? window.crypto : null;

  if (typeof cryptoApi?.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  if (typeof cryptoApi?.getRandomValues !== "function") {
    throw new Error("此環境不支援安全亂數，無法產生資產 UUID。");
  }

  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (value) =>
    value.toString(16).padStart(2, "0"),
  );
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex
    .slice(6, 8)
    .join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
}


const PUBLIC_CODE_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const RECIPIENT_CODE_LENGTH = 10;
const CARGO_CODE_LENGTH = 12;

function makeUpperCode(length) {
  return Array.from(
    { length },
    () => PUBLIC_CODE_CHARS[Math.floor(Math.random() * PUBLIC_CODE_CHARS.length)],
  ).join("");
}

function makeRecipientCode() {
  return makeUpperCode(RECIPIENT_CODE_LENGTH);
}

function makeCargoCode() {
  return makeUpperCode(CARGO_CODE_LENGTH);
}

function stableUpperCode(value, length) {
  const seed = text(value);
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return Array.from({ length }, (_, index) => {
    const mixed = Math.imul(hash ^ (index * 2654435761), 2246822519);
    return PUBLIC_CODE_CHARS[(mixed >>> 0) % PUBLIC_CODE_CHARS.length];
  }).join("");
}

function canonicalRecipientCode(value) {
  const raw = text(value);
  const candidate = raw.toUpperCase();
  return /^[A-Z]{10}$/.test(candidate)
    ? candidate
    : raw
      ? stableUpperCode(raw, RECIPIENT_CODE_LENGTH)
      : makeRecipientCode();
}

function canonicalCargoCode(value) {
  const raw = text(value);
  const candidate = raw.toUpperCase();
  return /^[A-Z]{12}$/.test(candidate)
    ? candidate
    : raw
      ? stableUpperCode(raw, CARGO_CODE_LENGTH)
      : makeCargoCode();
}

function cargoMediaKey(stopId, recipientId, contentId) {
  return `${text(stopId).toUpperCase()}:${canonicalRecipientCode(recipientId)}:${canonicalCargoCode(contentId)}`;
}

function cloneCargoContentForNewStop(content, recipientId) {
  const {
    photos: _photos,
    documents: _documents,
    execution: _execution,
    status: _status,
    currentLocation: _currentLocation,
    parkedLocation: _parkedLocation,
    ...details
  } = content || {};
  const accessRequirements = { ...(details.accessRequirements || {}) };
  delete accessRequirements.documents;
  return {
    ...details,
    id: makeCargoCode(),
    contentId: makeCargoCode(),
    recipientId: text(recipientId),
    operationType: "DELIVERY",
    photos: [],
    documents: [],
    accessRequirements,
  };
}

function createStop(stopKind, existingStops = [], forcedId = "") {
  const isStart = stopKind === "start";
  const operationType = isStart ? "PICKUP" : "DELIVERY";
  return {
    id: forcedId || (isStart ? "SRT" : "END"),
    order: existingStops.length + 1,
    timeWindowType: "NONE",
    earliestStart: "",
    latestStart: "",
    serviceMinutes: 0,
    operationType,
    text: "",
    lat: null,
    lng: null,
    verified: false,
    city: "",
    suburb: "",
    road: "",
    houseNumber: "",
    postalCode: "",
    distanceFromPreviousKm: isStart ? 0 : null,
    durationFromPreviousMinutes: isStart ? 0 : null,
    recipients: [],
    photos: [],
  };
}

function normalizeCargoItem(
  item = {},
  recipientId = "",
  fallbackOperation = "DELIVERY",
) {
  const contentId = text(item.contentId || item.id) || makeRandomId();
  return {
    ...item,
    id: contentId,
    contentId,
    operationType: normalizeCargoOperation(
      text(item.operationType) || fallbackOperation,
    ),
    recipientId: text(item.recipientId) || recipientId,
    name: text(item.name),
    quantity: item.quantity ?? "",
    unit: text(item.unit) || "件",
    note: text(item.note),
    sender: normalizeParty(item.sender, "SENDER"),
    ...normalizeCargoDetails(item),
    ...normalizeDeliveryDetails(item, true),
    status: optionalCargoStatus(item.status),
    currentLocation: normalizeCargoLocation(item.currentLocation),
    parkedLocation: normalizeParkedLocation(item.parkedLocation),
    photos: Array.isArray(item.photos)
      ? item.photos.map((photo) =>
          typeof photo === "string"
            ? { url: photo, preview: photo }
            : { ...photo, preview: photo.preview || photo.url },
        )
      : [],
  };
}

function normalizeRecipients(stop = {}) {
  if (!Array.isArray(stop.recipients)) return [];
  const stopOperation = normalizeStopOperation(stop.operationType);
  return stop.recipients.map((recipient) => {
    const id = text(recipient.id || recipient.recipientId) || makeRandomId();
    const recipientOperation = normalizeCargoOperation(
      recipient.operationType || recipientContents(recipient)[0]?.operationType,
      stopOperation,
    );
    const contents = recipientContents(recipient).map((item) => ({
      ...normalizeCargoItem(item, id, recipientOperation),
      operationType: recipientOperation,
    }));
    const primary = contents[0] || createCargoItem(recipientOperation);
    const normalizedRecipient = { ...recipient };
    delete normalizedRecipient.sender;
    return {
      ...normalizedRecipient,
      id,
      recipientId: id,
      cargoNumber: text(recipient.cargoNumber),
      operationType: recipientOperation,
      name: text(recipient.name),
      phone: text(recipient.phone),
      company: text(recipient.company),
      email: text(recipient.email),
      addressText: text(recipient.addressText),
      note: text(recipient.note),
      notificationSettings: normalizeRecipientNotificationSettings(
        recipient.notificationSettings,
      ),
      contents: contents.length ? contents : [{ ...primary, recipientId: id }],
    };
  });
}

function resetCopiedJobExecutionData(stops = []) {
  return (Array.isArray(stops) ? stops : []).map((sourceStop) => {
    const stop = { ...(sourceStop || {}) };
    [
      "proof", "progress", "execution", "status", "currentLocation",
      "parkedLocation", "startedAt", "arrivedAt", "completedAt",
      "finishedAt", "signedAt", "signedByUid", "qrScannedAt",
      "qrScannedByUid",
    ].forEach((key) => delete stop[key]);
    stop.recipients = (Array.isArray(sourceStop?.recipients) ? sourceStop.recipients : []).map((sourceRecipient) => {
      const recipient = { ...(sourceRecipient || {}) };
      [
        "status", "execution", "currentLocation", "parkedLocation",
        "sourceCargoRef", "identification", "qrToken", "qrPayload",
      ].forEach((key) => delete recipient[key]);
      const recipientId = makeRecipientCode();
      recipient.id = recipientId;
      recipient.recipientId = recipientId;
      recipient.contents = recipientContents(sourceRecipient).map((sourceContent) => {
        const content = { ...(sourceContent || {}) };
        [
          "status", "execution", "currentLocation", "parkedLocation",
          "sourceCargoRef", "identification", "qrToken", "qrPayload",
        ].forEach((key) => delete content[key]);
        const contentId = makeCargoCode();
        return { ...content, id: contentId, contentId, recipientId };
      });
      return recipient;
    });
    return stop;
  });
}

function normalizeStops(stops = []) {
  const hasExplicitStart = stops.some((stop) => isStartStop(stop));
  return stops.map((sourceStop, index, list) => {
    const stop = { ...(sourceStop || {}) };
    delete stop.role;
    delete stop.sequence;
    delete stop.stationIndex;
    delete stop.importOrder;
    delete stop.deliveryNote;
    const isStart = hasExplicitStart && index === 0;
    const rawId = text(stop.id).toUpperCase();
    const stopId = isStart
      ? "SRT"
      : hasExplicitStart && list.length === 2 && index === 1
        ? "END"
        : rawId || "END";
    const operationType = normalizeStopOperation(
      stop.operationType,
      isStart ? "PICKUP" : "DELIVERY",
    );
    const normalizedRecipients = normalizeRecipients({ ...stop, operationType });
    const derivedOperationType = deriveStopOperation(
      normalizedRecipients,
      operationType,
    );
    return {
      ...stop,
      id: stopId,
      order: index + 1,
      operationType: derivedOperationType,
      timeWindowType: text(stop.timeWindowType) || "NONE",
      earliestStart: text(stop.earliestStart),
      latestStart: text(stop.latestStart),
      serviceMinutes: Number(stop.serviceMinutes) || 0,
      distanceFromPreviousKm: isStart ? 0 : (stop.distanceFromPreviousKm ?? null),
      durationFromPreviousMinutes: isStart ? 0 : (stop.durationFromPreviousMinutes ?? null),
      recipients: normalizedRecipients,
      photos: Array.isArray(stop.photos) ? stop.photos : [],
    };
  });
}
function hasExplicitStartStop(stops = []) {
  return (stops || []).some((stop) => isStartStop(stop));
}

function sortStopsBySavedOrder(stops = []) {
  return (stops || [])
    .map((stop, index) => ({ stop, index }))
    .sort((left, right) => {
      const leftIsStart = isStartStop(left.stop);
      const rightIsStart = isStartStop(right.stop);
      if (leftIsStart !== rightIsStart) return leftIsStart ? -1 : 1;
      const leftOrder = Number(left.stop?.order);
      const rightOrder = Number(right.stop?.order);
      const leftHasOrder = Number.isFinite(leftOrder) && leftOrder > 0;
      const rightHasOrder = Number.isFinite(rightOrder) && rightOrder > 0;
      if (leftHasOrder && rightHasOrder && leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }
      if (leftHasOrder !== rightHasOrder) return leftHasOrder ? -1 : 1;
      return left.index - right.index;
    })
    .map(({ stop }) => stop);
}

function normalizeRouteStops(stops = []) {
  return normalizeStops(stops);
}

function applyRouteLegs(stops, legs = []) {
  return normalizeRouteStops(stops).map((stop, index) => {
    if (index === 0) {
      return {
        ...stop,
        distanceFromPreviousKm: 0,
        durationFromPreviousMinutes: 0,
      };
    }
    const leg = legs[index - 1];
    return {
      ...stop,
      distanceFromPreviousKm: leg
        ? Number((Number(leg.distance || 0) / 1000).toFixed(2))
        : null,
      durationFromPreviousMinutes: leg
        ? Math.max(1, Math.round(Number(leg.duration || 0) / 60))
        : null,
    };
  });
}

function routeFromOSRM(route, orderedStops) {
  return {
    stops: applyRouteLegs(orderedStops, route?.legs || []),
    legs: Array.isArray(route?.legs) ? route.legs : [],
    path: Array.isArray(route?.geometry?.coordinates)
      ? route.geometry.coordinates
          .map(([lng, lat]) => [Number(lat), Number(lng)])
          .filter(([lat, lng]) => coordsOK({ lat, lng }))
      : [],
    km: Number(route?.distance || 0) / 1000,
    min: Number(route?.duration || 0) / 60,
  };
}

async function fetchRoute(stops, signal) {
  const ordered = normalizeRouteStops(stops);
  if (ordered.length < 2 || ordered.some((stop) => !coordsOK(stop))) {
    throw new Error("請先完成所有站點定位");
  }
  const coords = ordered.map((stop) => `${stop.lng},${stop.lat}`).join(";");
  const response = await fetch(
    `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson&steps=false`,
    { signal },
  );
  if (!response.ok) throw new Error(`OSRM ${response.status}`);
  const data = await response.json();
  if (!data?.routes?.[0]) throw new Error("找不到可行駛的物流路線");
  return routeFromOSRM(data.routes[0], ordered);
}

function recipientCargoRecordSummary(recipient, stopOperation = "DELIVERY") {
  const contents = recipientContents(recipient);
  const operationType = recipientOperationType(recipient, stopOperation);
  const names = contents.map((content) => text(content?.name)).filter(Boolean);
  const quantity = contents.reduce(
    (sum, content) => sum + (Number(content?.quantity) || 0),
    0,
  );
  return {
    ...recipient,
    id: text(recipient?.id || recipient?.recipientId),
    cargoNumber: text(recipient?.cargoNumber),
    operationType,
    name: names.join("、") || "未命名貨物",
    quantity,
    unit: contents.length === 1 ? text(contents[0]?.unit) : "項",
    contents,
  };
}

function routeCargoKey(item) {
  const inventoryCargoId = text(item?.inventoryCargoId);
  if (inventoryCargoId) return `cargo-inventory:${inventoryCargoId}`;
  const sourceCargoNumber = text(item?.sourceCargoRef?.cargoNumber);
  const cargoNumber = sourceCargoNumber || text(item?.cargoNumber);
  if (cargoNumber) return `cargo-number:${cargoNumber}`;
  const sourceRecipientId = text(item?.sourceCargoRef?.recipientId);
  const recipientId = text(item?.recipientId || item?.id);
  const groupRecipientId = sourceRecipientId || recipientId;
  return groupRecipientId ? `recipient:${groupRecipientId}` : "";
}

function routeCargoEventRecords(record) {
  if (text(record?.sourceCargoRef?.source).toLowerCase() !== "cargoinventory") return [record];
  const contents = Array.isArray(record?.contents) ? record.contents : [];
  if (!contents.length) return [record];
  return contents.map((content) => ({
    ...record,
    inventoryCargoId: text(content?.contentId || content?.id),
    operationType: normalizeCargoOperation(content?.operationType, record.operationType),
    name: text(content?.name) || record.name,
    quantity: content?.quantity ?? record.quantity,
    unit: text(content?.unit) || record.unit,
    contents: [content],
  }));
}

function cargoSourceKey(item) {
  const sourceCargoNumber = text(item?.sourceCargoRef?.cargoNumber);
  const cargoNumber = sourceCargoNumber || text(item?.cargoNumber);
  return cargoNumber ? `cargo-number:${cargoNumber}` : routeCargoKey(item);
}

function cargoSourceSummary(stops = []) {
  const sources = new Map();
  stops.forEach((stop, stopIndex) => {
    (stop?.recipients || []).forEach((recipient) => {
      const record = recipientCargoRecordSummary(recipient, stop?.operationType);
      const key = cargoSourceKey(record);
      if (!key) return;
      const hasPickup = recipientHasOperation(recipient, "PICKUP", stop?.operationType);
      const hasDelivery = recipientHasOperation(recipient, "DELIVERY", stop?.operationType);
      if (!sources.has(key)) {
        sources.set(key, {
          key,
          cargoNumber: text(record.cargoNumber),
          name: record.name,
          unit: record.unit || "件",
          quantity: record.quantity,
          recipient,
          pickupStops: new Map(),
          deliveryStops: new Map(),
          itemCount: 0,
        });
      }
      const source = sources.get(key);
      source.itemCount += 1;
      const stopKey = text(stop?.id) || String(stopIndex);
      const stopInfo = {
        id: stopKey,
        label:
          text(stop?.text) || `配送站 ${stopIndex + 1}`,
        index: Number(stop?.order ?? stopIndex + 1),
      };
      if (hasPickup) source.pickupStops.set(stopKey, stopInfo);
      if (hasDelivery) source.deliveryStops.set(stopKey, stopInfo);
    });
  });
  return Array.from(sources.values()).map((source) => ({
    ...source,
    pickupList: Array.from(source.pickupStops.values()),
    deliveryList: Array.from(source.deliveryStops.values()),
    pickupCount: source.pickupStops.size,
    deliveryCount: source.deliveryStops.size,
  }));
}

function routePlanningInputKey(stops, schedule, startPointEnabled) {
  return JSON.stringify({
    version: 3,
    startPointEnabled: startPointEnabled === true,
    startTime: text(schedule?.startTime),
    stops: (stops || []).map((stop, position) => ({
      position,
      id: text(stop?.id).toUpperCase(),
      order: Number.isFinite(Number(stop?.order)) ? Number(stop.order) : position + 1,
      operationType: normalizeStopOperation(stop?.operationType),
      address: text(stop?.text),
      lat: Number.isFinite(Number(stop?.lat)) ? Number(stop.lat) : null,
      lng: Number.isFinite(Number(stop?.lng)) ? Number(stop.lng) : null,
      timeWindowType: text(stop?.timeWindowType).toUpperCase() || "NONE",
      earliestStart: text(stop?.earliestStart),
      latestStart: text(stop?.latestStart),
      serviceMinutes: Number(stop?.serviceMinutes) || 0,
      cargoRecords: (stop?.recipients || []).map((recipient) => ({
        recipientId: text(recipient?.id || recipient?.recipientId),
        cargoNumber: text(recipient?.cargoNumber),
        sourceCargoRef: compactRecord(recipient?.sourceCargoRef),
        operationType: recipientOperationType(recipient, stop?.operationType),
        contents: recipientContents(recipient).map((item) => ({
          contentId: text(item?.contentId || item?.id),
          operationType: normalizeCargoOperation(
            item?.operationType,
            recipient?.operationType || stop?.operationType,
          ),
          name: text(item?.name),
          quantity: text(item?.quantity),
          unit: text(item?.unit),
          note: text(item?.note),
        })),
      })),
    })),
  });
}

function routeClockMinutes(value) {
  const match = text(value).match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function formatRouteClockMinutes(value) {
  if (!Number.isFinite(Number(value))) return "—";
  const totalMinutes = Math.max(0, Math.round(Number(value))) % (24 * 60);
  return `${pad(Math.floor(totalMinutes / 60))}:${pad(totalMinutes % 60)}`;
}

function formatRouteDurationMinutes(value) {
  const numeric = Number(value);
  const totalMinutes = Number.isFinite(numeric)
    ? Math.max(0, Math.round(numeric))
    : 0;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours ? `${hours} 小時 ${minutes} 分鐘` : `${minutes} 分鐘`;
}

function routeEtaDurationsFromStops(stops = []) {
  const durations = {};
  let elapsedMinutes = 0;
  let canEstimate = true;
  stops.forEach((stop) => {
    const stopId = text(stop?.id).toUpperCase();
    const rawDuration = stop?.durationFromPreviousMinutes;
    const duration = rawDuration == null || rawDuration === ""
      ? null
      : Number(rawDuration);
    if (!Number.isFinite(duration) || duration < 0) {
      canEstimate = false;
      return;
    }
    elapsedMinutes += duration * 1.2;
    if (canEstimate && stopId) durations[stopId] = Math.round(elapsedMinutes);
  });
  return durations;
}

const RECIPIENT_EMAIL_ETA_FALLBACK = "預計抵達時段將於成員出發後更新";

function formatRecipientEmailArrivalWindow(timestamp) {
  const estimated = new Date(timestamp);
  if (!Number.isFinite(estimated.getTime())) return "";
  const center = new Date(estimated.getTime());
  center.setMinutes(0, 0, 0);
  const from = new Date(center.getTime() - 60 * 60 * 1000);
  const to = new Date(center.getTime() + 60 * 60 * 1000);
  const dateLabel = (value) => `${value.getMonth() + 1}/${value.getDate()}`;
  const periodLabel = (value) => {
    const hour = value.getHours();
    if (hour < 6) return "凌晨";
    if (hour < 12) return "上午";
    if (hour < 18) return "下午";
    return "晚上";
  };
  const timeLabel = (value) =>
    `${value.getHours() % 12 || 12}:${pad(value.getMinutes())}`;
  const sameDate =
    from.getFullYear() === to.getFullYear() &&
    from.getMonth() === to.getMonth() &&
    from.getDate() === to.getDate();
  const samePeriod = periodLabel(from) === periodLabel(to);
  const endPrefix = [
    sameDate ? "" : dateLabel(to),
    samePeriod && sameDate ? "" : periodLabel(to),
  ]
    .filter(Boolean)
    .join(" ");
  return `${dateLabel(from)} ${periodLabel(from)} ${timeLabel(from)}–${
    endPrefix ? `${endPrefix} ` : ""
  }${timeLabel(to)}`;
}

function recipientEmailArrivalSummary(stops = []) {
  if (stops.length === 1) {
    return text(stops[0]?.arrivalWindow) || RECIPIENT_EMAIL_ETA_FALLBACK;
  }
  if (stops.length > 1) {
    return stops
      .map(
        (stop) =>
          `配送站 ${stop.order || ""}：${
            text(stop.arrivalWindow) || RECIPIENT_EMAIL_ETA_FALLBACK
          }`.trim(),
      )
      .join("\n");
  }
  return RECIPIENT_EMAIL_ETA_FALLBACK;
}

function buildEmailArrivalWindowsByStopId(stops = [], startAt, routeIsValid) {
  if (!routeIsValid || !Array.isArray(stops) || !stops.length) return {};
  const scheduledStart = firestoreDate(startAt);
  if (!scheduledStart) return {};
  let cursor = scheduledStart.getTime();
  const windows = {};
  for (const stop of stops) {
    const rawDuration = stop?.durationFromPreviousMinutes;
    const duration = rawDuration == null || rawDuration === ""
      ? null
      : Number(rawDuration);
    if (!Number.isFinite(duration) || duration < 0) break;
    cursor += duration * 1.2 * 60 * 1000;
    const stopId = text(stop?.id).toUpperCase();
    if (stopId) windows[stopId] = formatRecipientEmailArrivalWindow(cursor);
    const earliest = routeClockMinutes(stop?.earliestStart);
    if (earliest != null) {
      const earliestAt = new Date(cursor);
      earliestAt.setHours(Math.floor(earliest / 60), earliest % 60, 0, 0);
      if (earliestAt.getTime() > cursor) cursor = earliestAt.getTime();
    }
    const serviceMinutes = Number(stop?.serviceMinutes);
    if (Number.isFinite(serviceMinutes) && serviceMinutes > 0) {
      cursor += serviceMinutes * 60 * 1000;
    }
  }
  return windows;
}

function buildRouteRelationModel(stops) {
  const groups = new Map();
  const errors = [];

  stops.forEach((stop, stopIndex) => {
    (stop?.recipients || []).forEach((recipient) => {
      const summary = recipientCargoRecordSummary(recipient, stop?.operationType);
      routeCargoEventRecords(summary).forEach((record) => {
        const key = routeCargoKey(record);
        if (!key) {
          errors.push(`第 ${stop.order ?? stopIndex + 1} 站的貨物缺少可識別資料`);
          return;
        }
        if (!record.contents.length) {
          errors.push(`第 ${stop.order ?? stopIndex + 1} 站的貨物沒有內容物`);
          return;
        }
        const contents = record.contents || [];
        const operationBuckets = new Map();
        contents.forEach((content) => {
          const operation = normalizeCargoOperation(
            content?.operationType,
            record.operationType,
          );
          const bucket = operationBuckets.get(operation) || [];
          bucket.push(content);
          operationBuckets.set(operation, bucket);
        });
        if (!operationBuckets.size) {
          operationBuckets.set(record.operationType, []);
        }
        const events = groups.get(key) || [];
        operationBuckets.forEach((bucket, operation) => {
          const names = bucket.map((content) => text(content?.name)).filter(Boolean);
          const quantity = bucket.reduce(
            (sum, content) => sum + (Number(content?.quantity) || 0),
            0,
          );
          events.push({
            key,
            stopIndex,
            stopId: stop.id,
            operation,
            name: names.join("、") || record.name,
            quantity: quantity || record.quantity,
            unit: bucket.length === 1 ? text(bucket[0]?.unit) || record.unit : "項",
            hasSourceReference: Boolean(record.sourceCargoRef),
            recipientId: text(record.id),
            cargoNumber: text(record.cargoNumber),
          });
        });
        groups.set(key, events);
      });
    });
  });

  const relations = [];
  groups.forEach((events, groupKey) => {
    const pickups = events.filter((event) => event.operation === "PICKUP");
    const deliveries = events.filter((event) => event.operation === "DELIVERY");
    const supportsOneToMany =
      pickups.length === 1 &&
      deliveries.length >= 1 &&
      events.some((event) => event.hasSourceReference);
    if (
      !pickups.length ||
      !deliveries.length ||
      (!supportsOneToMany && pickups.length !== deliveries.length)
    ) {
      errors.push(
        `${events[0]?.name || "未命名貨物"} 的取貨與送貨事件數量不完整`,
      );
      return;
    }
    const pairs = supportsOneToMany
      ? deliveries.map((delivery) => [pickups[0], delivery])
      : deliveries.map((delivery, index) => [pickups[index], delivery]);
    pairs.forEach(([pickup, delivery], index) => {
      relations.push({
        cargoKey: supportsOneToMany ? groupKey : `${groupKey}#${index}`,
        cargoNumber: pickup.cargoNumber || delivery.cargoNumber,
        name: pickup.name || delivery.name,
        quantity: delivery.quantity || pickup.quantity,
        unit: pickup.unit || delivery.unit,
        pickupIndex: pickup.stopIndex,
        deliveryIndex: delivery.stopIndex,
        pickupStopId: pickup.stopId,
        deliveryStopId: delivery.stopId,
        sameStation: pickup.stopIndex === delivery.stopIndex,
      });
    });
  });

  const byPickup = new Map();
  const byDelivery = new Map();
  const deliveryCountByCargoKey = new Map();
  relations.forEach((relation) => {
    const pickupList = byPickup.get(relation.pickupIndex) || [];
    pickupList.push(relation);
    byPickup.set(relation.pickupIndex, pickupList);
    const deliveryList = byDelivery.get(relation.deliveryIndex) || [];
    deliveryList.push(relation);
    byDelivery.set(relation.deliveryIndex, deliveryList);
    deliveryCountByCargoKey.set(
      relation.cargoKey,
      (deliveryCountByCargoKey.get(relation.cargoKey) || 0) + 1,
    );
  });

  return {
    relations,
    byPickup,
    byDelivery,
    deliveryCountByCargoKey,
    errors: Array.from(new Set(errors)),
    cycles: findRoutePrecedenceCycles(relations),
  };
}

function findRoutePrecedenceCycles(relations) {
  const graph = new Map();
  relations
    .filter((relation) => !relation.sameStation)
    .forEach((relation) => {
      const next = graph.get(relation.pickupIndex) || [];
      next.push(relation.deliveryIndex);
      graph.set(relation.pickupIndex, next);
    });
  const state = new Map();
  const stack = [];
  const cycles = [];
  const seen = new Set();

  const visit = (node) => {
    state.set(node, 1);
    stack.push(node);
    (graph.get(node) || []).forEach((target) => {
      if (state.get(target) === 1) {
        const start = stack.indexOf(target);
        const cycle = [...stack.slice(start), target];
        const key = cycle.slice().sort((a, b) => a - b).join(",");
        if (!seen.has(key)) {
          seen.add(key);
          cycles.push(cycle);
        }
      } else if (!state.get(target)) {
        visit(target);
      }
    });
    stack.pop();
    state.set(node, 2);
  };

  Array.from(graph.keys()).forEach((node) => {
    if (!state.get(node)) visit(node);
  });
  return cycles;
}

function getRoutePlanningContext(stops) {
  const first = stops[0];
  const hasCargo = (first?.recipients || []).some((recipient) =>
    recipientContents(recipient).some((item) => text(item?.name)),
  );
  const hasSeparateOrigin =
    text(first?.id).toUpperCase() === "SRT" && !hasCargo && stops.length >= 2;
  const originIndex = hasSeparateOrigin ? 0 : null;
  const firstServiceIndex = hasSeparateOrigin ? 1 : 0;
  const serviceIndices = Array.from(
    { length: Math.max(0, stops.length - firstServiceIndex) },
    (_, offset) => firstServiceIndex + offset,
  );
  return { originIndex, firstServiceIndex, serviceIndices };
}

function routeOrderIsComplete(order, planningContext) {
  const expected = new Set([
    ...(planningContext.originIndex == null ? [] : [planningContext.originIndex]),
    ...planningContext.serviceIndices,
  ]);
  const actual = new Set(order || []);
  return (
    order?.length === expected.size &&
    actual.size === expected.size &&
    Array.from(expected).every((index) => actual.has(index))
  );
}

function routeStopHasCargoOperation(stop, operation) {
  return (stop?.recipients || []).some((recipient) =>
    recipientContents(recipient).some(
      (item) => normalizeCargoOperation(item?.operationType, "DELIVERY") === operation,
    ),
  );
}

function routeRelationViolations(order, relationModel) {
  const position = new Map(order.map((index, positionIndex) => [index, positionIndex]));
  return relationModel.relations
    .filter((relation) => !relation.sameStation)
    .filter(
      (relation) =>
        (position.get(relation.pickupIndex) ?? Infinity) >=
        (position.get(relation.deliveryIndex) ?? Infinity),
    )
    .map((relation) => ({
      ...relation,
      message: `${relation.name} 的取貨站 ${relation.pickupIndex + 1} 必須在送貨站 ${relation.deliveryIndex + 1} 前面`,
    }));
}

function routeCandidateStatus(stopIndex, visited, onboard, relationModel) {
  if (visited.has(stopIndex)) return null;
  const deliveries = (relationModel.byDelivery.get(stopIndex) || []).filter(
    (relation) => !relation.sameStation,
  );
  if (deliveries.some((relation) => !onboard.has(relation.cargoKey))) return null;
  const pickups = (relationModel.byPickup.get(stopIndex) || []).filter(
    (relation) => !relation.sameStation,
  );
  const sameStation = (relationModel.byDelivery.get(stopIndex) || []).filter(
    (relation) => relation.sameStation,
  );
  const hasPickup = pickups.length > 0 || sameStation.some((relation) => relation.pickupIndex === stopIndex);
  const hasDelivery = deliveries.length > 0 || sameStation.length > 0;
  return {
    deliveries,
    pickups,
    sameStation,
    role: hasPickup && hasDelivery ? "pickup_delivery" : hasPickup ? "pickup" : hasDelivery ? "delivery" : "neutral",
  };
}

function applyRouteCargoState(
  status,
  onboard,
  completed,
  remainingDeliveries = new Map(),
) {
  status.deliveries.forEach((relation) => {
    const remaining = remainingDeliveries.get(relation.cargoKey) || 1;
    if (remaining > 1) {
      remainingDeliveries.set(relation.cargoKey, remaining - 1);
    } else {
      remainingDeliveries.delete(relation.cargoKey);
      onboard.delete(relation.cargoKey);
      completed.add(relation.cargoKey);
    }
  });
  status.sameStation.forEach((relation) => completed.add(relation.cargoKey));
  status.pickups.forEach((relation) => onboard.set(relation.cargoKey, relation));
}

function routeMatrixValue(matrix, fromIndex, toIndex) {
  const value = matrix?.durations?.[fromIndex]?.[toIndex];
  return value == null || !Number.isFinite(Number(value))
    ? Number.POSITIVE_INFINITY
    : Number(value);
}

function routeMatrixDistance(matrix, fromIndex, toIndex) {
  const value = matrix?.distances?.[fromIndex]?.[toIndex];
  return value == null || !Number.isFinite(Number(value)) ? 0 : Number(value);
}

async function fetchRouteMatrix(stops, signal) {
  const ordered = normalizeStops(stops);
  if (ordered.length < 2 || ordered.some((stop) => !coordsOK(stop))) {
    throw new Error("請先完成所有站點定位，再建立 OSRM Matrix");
  }
  const coords = ordered.map((stop) => `${stop.lng},${stop.lat}`).join(";");
  const response = await fetch(
    `https://router.project-osrm.org/table/v1/driving/${coords}?annotations=duration,distance`,
    { signal },
  );
  if (!response.ok) throw new Error(`OSRM Matrix ${response.status}`);
  const data = await response.json();
  const durations = Array.isArray(data?.durations) ? data.durations : [];
  const distances = Array.isArray(data?.distances) ? data.distances : [];
  if (
    data?.code !== "Ok" ||
    durations.length !== ordered.length ||
    distances.length !== ordered.length ||
    durations.some((row) => !Array.isArray(row) || row.length !== ordered.length) ||
    distances.some((row) => !Array.isArray(row) || row.length !== ordered.length)
  ) {
    throw new Error("OSRM Matrix 回傳的節點數量不完整");
  }
  return { stops: ordered, durations, distances };
}

function nextRouteTick() {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

function routeTravelValue(order, position, matrix, routeStops, routeLegs) {
  if (position === 0) return { seconds: 0, meters: 0 };
  const leg = routeLegs?.[position - 1];
  if (leg) {
    return {
      seconds: Math.max(0, Number(leg.duration || 0)),
      meters: Math.max(0, Number(leg.distance || 0)),
    };
  }
  if (Array.isArray(routeStops) && routeStops[position]) {
    return {
      seconds: Math.max(0, Number(routeStops[position].durationFromPreviousMinutes || 0) * 60),
      meters: Math.max(0, Number(routeStops[position].distanceFromPreviousKm || 0) * 1000),
    };
  }
  const from = order[position - 1];
  const to = order[position];
  return {
    seconds: routeMatrixValue(matrix, from, to),
    meters: routeMatrixDistance(matrix, from, to),
  };
}

function evaluateRouteOrder(
  order,
  stops,
  relationModel,
  matrix,
  startTime = "00:00",
  planningContext = getRoutePlanningContext(stops),
  routeStops = null,
  routeLegs = null,
) {
  const normalizedOrder = order.slice();
  const startMinutes = routeClockMinutes(startTime) ?? 0;
  let currentMinutes = startMinutes;
  let totalDrivingSeconds = 0;
  let totalDistanceMeters = 0;
  let waitingMinutes = 0;
  let serviceMinutes = 0;
  let hardLateCount = 0;
  let hardLateMinutes = 0;
  let softLateCount = 0;
  let softLateMinutes = 0;
  const onboard = new Map();
  const completed = new Set();
  const remainingDeliveries = new Map(
    relationModel.deliveryCountByCargoKey || [],
  );
  const cargoFlowViolations = [];
  const rows = [];
  let previousIndex = null;

  normalizedOrder.forEach((stopIndex, position) => {
    const stop = stops[stopIndex];
    if (!stop) return;
    if (planningContext.originIndex != null && position === 0 && stopIndex === planningContext.originIndex) {
      rows.push({
        position: position + 1,
        stopIndex,
        index: Number(stop.order ?? stopIndex),
        label: stop.text || "取貨站",
        arrivalMinutes: currentMinutes,
        serviceStartMinutes: currentMinutes,
        departureMinutes: currentMinutes,
        waitingMinutes: 0,
        lateMinutes: 0,
        travelMinutesFromPrevious: 0,
        distanceKmFromPrevious: 0,
        cargoBefore: [],
        cargoEvents: [],
        cargoAfter: [],
      });
      previousIndex = stopIndex;
      return;
    }

    const travel = routeTravelValue(
      normalizedOrder,
      position,
      matrix,
      routeStops,
      routeLegs,
    );
    if (!Number.isFinite(travel.seconds)) {
      cargoFlowViolations.push({
        type: "NO_ROUTE_EDGE",
        message: `找不到第 ${previousIndex + 1} 站到第 ${stopIndex + 1} 站的路徑`,
      });
      return;
    }
    const arrivalMinutes = currentMinutes + travel.seconds / 60;
    const earliest = routeClockMinutes(stop.earliestStart);
    const latest = routeClockMinutes(stop.latestStart);
    const serviceStart = earliest == null ? arrivalMinutes : Math.max(arrivalMinutes, earliest);
    const waiting = Math.max(0, serviceStart - arrivalMinutes);
    const late = latest == null ? 0 : Math.max(0, serviceStart - latest);
    const windowType = text(stop.timeWindowType).toUpperCase() || "NONE";
    if (late && windowType === "HARD") {
      hardLateCount += 1;
      hardLateMinutes += late;
    } else if (late && windowType === "SOFT") {
      softLateCount += 1;
      softLateMinutes += late;
    }

    const visited = new Set(normalizedOrder.slice(0, position));
    const status = routeCandidateStatus(stopIndex, visited, onboard, relationModel);
    const cargoBefore = Array.from(onboard.keys());
    const cargoEvents = [];
    const blocked = (relationModel.byDelivery.get(stopIndex) || []).filter(
      (relation) => !relation.sameStation && !onboard.has(relation.cargoKey),
    );
    blocked.forEach((relation) => {
      cargoFlowViolations.push({
        type: "DELIVERY_BEFORE_PICKUP",
        ...relation,
        message: `${relation.name} 在第 ${stopIndex + 1} 站送貨，但尚未在第 ${relation.pickupIndex + 1} 站取貨`,
      });
      cargoEvents.push({ action: "DELIVERY_BLOCKED", ...relation });
    });
    if (status) {
      status.deliveries.forEach((relation) => {
        const picked = onboard.get(relation.cargoKey);
        if (!picked) return;
        const remaining = remainingDeliveries.get(relation.cargoKey) || 1;
        if (remaining > 1) {
          remainingDeliveries.set(relation.cargoKey, remaining - 1);
        } else {
          remainingDeliveries.delete(relation.cargoKey);
          onboard.delete(relation.cargoKey);
          completed.add(relation.cargoKey);
        }
        cargoEvents.push({ action: "DELIVERY", ...relation, sourceIndex: picked.pickupIndex });
      });
      status.sameStation.forEach((relation) => {
        completed.add(relation.cargoKey);
        cargoEvents.push({ action: "SAME_STATION_COMPLETE", ...relation });
      });
      status.pickups.forEach((relation) => {
        onboard.set(relation.cargoKey, relation);
        cargoEvents.push({ action: "PICKUP", ...relation });
      });
    }

    const service = Math.max(0, Number(stop.serviceMinutes) || 0);
    const departureMinutes = serviceStart + service;
    totalDrivingSeconds += travel.seconds;
    totalDistanceMeters += travel.meters;
    waitingMinutes += waiting;
    serviceMinutes += service;
    rows.push({
      position: position + 1,
      stopIndex,
      index: Number(stop.order ?? stopIndex),
      label: stop.text || `站點 ${stopIndex + 1}`,
      arrivalMinutes,
      serviceStartMinutes: serviceStart,
      departureMinutes,
      waitingMinutes: waiting,
      lateMinutes: late,
      travelMinutesFromPrevious: travel.seconds / 60,
      distanceKmFromPrevious: travel.meters / 1000,
      cargoBefore,
      cargoEvents,
      cargoAfter: Array.from(onboard.keys()),
    });
    currentMinutes = departureMinutes;
    previousIndex = stopIndex;
  });

  relationModel.relations.forEach((relation) => {
    if (!relation.sameStation && !completed.has(relation.cargoKey)) {
      cargoFlowViolations.push({
        type: "DELIVERY_NOT_COMPLETED",
        ...relation,
        message: `${relation.name} 尚未完成送貨`,
      });
    }
  });
  const relationViolations = routeRelationViolations(normalizedOrder, relationModel);
  const firstServicePosition = planningContext.originIndex != null ? 1 : 0;
  const firstService = stops[normalizedOrder[firstServicePosition]];
  const firstStopViolation = routeStopHasCargoOperation(firstService, "PICKUP") ? 0 : 1;
  const totalMinutes = Math.max(0, currentMinutes - startMinutes);
  const score = [
    firstStopViolation,
    cargoFlowViolations.length,
    hardLateCount,
    hardLateMinutes,
    relationViolations.length,
    softLateCount,
    softLateMinutes,
    totalMinutes,
    totalDrivingSeconds,
    totalDistanceMeters,
  ];
  const cargoFeasible =
    firstStopViolation === 0 &&
    cargoFlowViolations.length === 0 &&
    relationViolations.length === 0 &&
    onboard.size === 0;
  return {
    feasible: cargoFeasible && hardLateCount === 0,
    cargoFeasible,
    order: normalizedOrder,
    rows,
    firstStopViolationCount: firstStopViolation,
    relationViolations,
    relationViolationCount: relationViolations.length,
    cargoFlowViolations,
    cargoFlowViolationCount: cargoFlowViolations.length,
    onboardCargoAtEnd: Array.from(onboard.keys()),
    hardLateCount,
    hardLateMinutes,
    softLateCount,
    softLateMinutes,
    waitingMinutes,
    serviceMinutes,
    travelMinutes: totalDrivingSeconds / 60,
    totalDistanceKm: totalDistanceMeters / 1000,
    totalMinutes,
    score,
    precedenceIssues: [
      ...relationViolations.map((item) => item.message),
      ...cargoFlowViolations.map((item) => item.message),
    ],
    routeApiConfirmed: Array.isArray(routeStops),
  };
}

function compareRouteScore(left, right) {
  const a = left?.score || [];
  const b = right?.score || [];
  for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
    const difference = Number(a[index] || 0) - Number(b[index] || 0);
    if (difference !== 0) return difference;
  }
  return 0;
}

function routeStackNearestOrder(indices, matrix, startIndex, stops) {
  const remaining = new Set(indices);
  const order = [];
  let current = Number.isInteger(startIndex) ? startIndex : null;

  while (remaining.size) {
    const next = Array.from(remaining).sort((left, right) => {
      const leftTravel = current == null
        ? 0
        : routeMatrixValue(matrix, current, left);
      const rightTravel = current == null
        ? 0
        : routeMatrixValue(matrix, current, right);
      return leftTravel - rightTravel ||
        (routeClockMinutes(stops[left]?.latestStart) ?? 24 * 60) -
          (routeClockMinutes(stops[right]?.latestStart) ?? 24 * 60) ||
        left - right;
    })[0];
    order.push(next);
    remaining.delete(next);
    current = next;
  }
  return order;
}

function routeStackDeadlineOrder(indices, matrix, startIndex, stops) {
  const remaining = new Set(indices);
  const order = [];
  let current = Number.isInteger(startIndex) ? startIndex : null;

  while (remaining.size) {
    const next = Array.from(remaining).sort((left, right) => {
      const leftLatest = routeClockMinutes(stops[left]?.latestStart);
      const rightLatest = routeClockMinutes(stops[right]?.latestStart);
      const leftHasWindow = leftLatest == null ? 1 : 0;
      const rightHasWindow = rightLatest == null ? 1 : 0;
      const leftTravel = current == null ? 0 : routeMatrixValue(matrix, current, left);
      const rightTravel = current == null ? 0 : routeMatrixValue(matrix, current, right);
      return leftHasWindow - rightHasWindow ||
        (leftLatest ?? 24 * 60) - (rightLatest ?? 24 * 60) ||
        leftTravel - rightTravel ||
        left - right;
    })[0];
    order.push(next);
    remaining.delete(next);
    current = next;
  }
  return order;
}

function buildRouteStackVariants(stops, indices, matrix, startIndex) {
  const base = Array.from(new Set(indices)).sort((left, right) => left - right);
  if (!base.length) return [];

  const nearest = routeStackNearestOrder(base, matrix, startIndex, stops);
  const deadline = routeStackDeadlineOrder(base, matrix, startIndex, stops);
  const directional = base.slice().sort(
    (left, right) =>
      Number(stops[left]?.lng || 0) - Number(stops[right]?.lng || 0) || left - right,
  );
  const variants = [base, nearest, deadline, directional, nearest.slice().reverse()];
  const seen = new Set();
  return variants.filter((variant) => {
    const key = variant.join(">");
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function nextRouteStackItem(stack, visited, pointer = 0) {
  let position = pointer;
  while (position < stack.length && visited.has(stack[position])) position += 1;
  return position < stack.length
    ? { index: stack[position], position }
    : { index: null, position };
}

function routeStackViolationCount(order, stack) {
  if (!Array.isArray(stack) || stack.length < 2) return 0;
  const position = new Map(order.map((index, orderPosition) => [index, orderPosition]));
  let violations = 0;
  for (let index = 1; index < stack.length; index += 1) {
    const previous = position.get(stack[index - 1]);
    const current = position.get(stack[index]);
    if (previous != null && current != null && previous > current) violations += 1;
  }
  return violations;
}

function routeStackExceptionAllowed(stops, preferredIndex, alternativeIndex) {
  const preferred = stops[preferredIndex];
  const alternative = stops[alternativeIndex];
  const preferredLatest = routeClockMinutes(preferred?.latestStart);
  const alternativeLatest = routeClockMinutes(alternative?.latestStart);
  const alternativeWindow = text(alternative?.timeWindowType).toUpperCase();
  const preferredWindow = text(preferred?.timeWindowType).toUpperCase();

  if (
    alternativeLatest != null &&
    (preferredLatest == null || alternativeLatest < preferredLatest) &&
    alternativeWindow !== "NONE"
  ) {
    return true;
  }
  return preferredWindow === "NONE" && alternativeWindow === "HARD";
}

function buildStackRouteCandidates(stops, relationModel, matrix, planningContext) {
  const pickupIndices = Array.from(relationModel.byPickup.keys()).sort((left, right) => left - right);
  const deliveryIndices = Array.from(relationModel.byDelivery.keys()).sort((left, right) => left - right);
  const anchor = planningContext.originIndex ?? null;
  const pickupStacks = pickupIndices.length
    ? buildRouteStackVariants(stops, pickupIndices, matrix, anchor)
    : [[]];
  const deliveryStacks = deliveryIndices.length
    ? buildRouteStackVariants(stops, deliveryIndices, matrix, anchor)
    : [[]];
  const serviceSet = new Set(planningContext.serviceIndices);
  const expectedLength = serviceSet.size + (planningContext.originIndex == null ? 0 : 1);
  const candidates = new Map();
  const maxCandidates = 24000;

  const merge = (pickupStack, deliveryStack, state) => {
    if (candidates.size >= maxCandidates) return;
    if (state.order.length === expectedLength) {
      const key = state.order.join(">");
      const previous = candidates.get(key);
      const next = {
        order: state.order,
        pickupStack,
        deliveryStack,
        stackExceptionCount: state.stackExceptionCount,
      };
      if (!previous || next.stackExceptionCount < previous.stackExceptionCount) {
        candidates.set(key, next);
      }
      return;
    }

    const nextPickup = nextRouteStackItem(pickupStack, state.visited, state.pickupPosition);
    const nextDelivery = nextRouteStackItem(deliveryStack, state.visited, state.deliveryPosition);
    const choices = [];
    const choiceKeys = new Set();
    const addChoice = (index, status, pickupPosition, deliveryPosition, exception = 0) => {
      if (index == null || !status || choiceKeys.has(index)) return;
      choiceKeys.add(index);
      choices.push({ index, status, pickupPosition, deliveryPosition, exception });
    };

    const nextPickupStatus = nextPickup.index == null
      ? null
      : routeCandidateStatus(
        nextPickup.index,
        state.visited,
        state.onboard,
        relationModel,
      );
    const nextDeliveryStatus = nextDelivery.index == null
      ? null
      : routeCandidateStatus(
        nextDelivery.index,
        state.visited,
        state.onboard,
        relationModel,
      );
    if (nextDeliveryStatus) {
        const pickupPosition = nextDelivery.index === nextPickup.index
          ? nextPickup.position + 1
          : state.pickupPosition;
        addChoice(
          nextDelivery.index,
          nextDeliveryStatus,
          pickupPosition,
          nextDelivery.position + 1,
        );
    }

    if (nextPickupStatus) {
        const deliveryPosition = nextPickup.index === nextDelivery.index
          ? nextDelivery.position + 1
          : state.deliveryPosition;
        addChoice(
          nextPickup.index,
          nextPickupStatus,
          nextPickup.position + 1,
          deliveryPosition,
        );
    }

    if (nextDelivery.index != null) {
      deliveryStack.slice(nextDelivery.position + 1).forEach((index, offset) => {
        if (choices.length >= 4 || state.visited.has(index)) return;
        const status = routeCandidateStatus(index, state.visited, state.onboard, relationModel);
        const canSkipPreferredDelivery = !nextDeliveryStatus ||
          routeStackExceptionAllowed(stops, nextDelivery.index, index);
        if (!status || !canSkipPreferredDelivery) return;
        addChoice(
          index,
          status,
          index === nextPickup.index ? nextPickup.position + 1 : state.pickupPosition,
          state.deliveryPosition,
          offset + 1,
        );
      });
    }

    if (nextPickup.index != null) {
      pickupStack.slice(nextPickup.position + 1).forEach((index, offset) => {
        if (choices.length >= 5 || state.visited.has(index)) return;
        const status = routeCandidateStatus(index, state.visited, state.onboard, relationModel);
        const canSkipPreferredPickup = !nextPickupStatus ||
          routeStackExceptionAllowed(stops, nextPickup.index, index);
        if (!status || !canSkipPreferredPickup) return;
        addChoice(
          index,
          status,
          state.pickupPosition,
          index === nextDelivery.index ? nextDelivery.position + 1 : state.deliveryPosition,
          offset + 1,
        );
      });
    }

    planningContext.serviceIndices.forEach((index) => {
      if (
        choices.length >= 5 ||
        state.visited.has(index) ||
        relationModel.byPickup.has(index) ||
        relationModel.byDelivery.has(index)
      ) return;
      const status = routeCandidateStatus(index, state.visited, state.onboard, relationModel);
      addChoice(index, status, state.pickupPosition, state.deliveryPosition);
    });

    choices.forEach((choice) => {
      const visited = new Set(state.visited);
      visited.add(choice.index);
      const onboard = new Map(state.onboard);
      const remainingDeliveries = new Map(state.remainingDeliveries);
      applyRouteCargoState(
        choice.status,
        onboard,
        new Set(),
        remainingDeliveries,
      );
      merge(pickupStack, deliveryStack, {
        order: [...state.order, choice.index],
        visited,
        onboard,
        remainingDeliveries,
        pickupPosition: choice.pickupPosition,
        deliveryPosition: choice.deliveryPosition,
        stackExceptionCount: state.stackExceptionCount + choice.exception,
      });
    });
  };

  pickupStacks.forEach((pickupStack) => {
    deliveryStacks.forEach((deliveryStack) => {
      const visited = planningContext.originIndex == null
        ? new Set()
        : new Set([planningContext.originIndex]);
      merge(pickupStack, deliveryStack, {
        order: planningContext.originIndex == null ? [] : [planningContext.originIndex],
        visited,
        onboard: new Map(),
        remainingDeliveries: new Map(
          relationModel.deliveryCountByCargoKey || [],
        ),
        pickupPosition: 0,
        deliveryPosition: 0,
        stackExceptionCount: 0,
      });
    });
  });

  return {
    pickupStackCount: pickupStacks.length,
    deliveryStackCount: deliveryStacks.length,
    candidates: Array.from(candidates.values()),
    truncated: candidates.size >= maxCandidates,
  };
}

function selectStackDiverseCandidates(candidates, limit = 10) {
  const ranked = candidates.slice().sort((left, right) =>
    compareRouteScore(left.evaluation, right.evaluation),
  );
  const selected = [];
  const families = new Set();

  ranked.forEach((candidate) => {
    if (selected.length >= limit) return;
    const family = `${(candidate.pickupStack || []).join(">")}|${(
      candidate.deliveryStack || []
    ).join(">")}`;
    if (families.has(family)) return;
    families.add(family);
    selected.push(candidate);
  });

  if (selected.length < limit) {
    ranked.forEach((candidate) => {
      if (selected.length >= limit || selected.includes(candidate)) return;
      selected.push(candidate);
    });
  }
  return selected.slice(0, limit);
}

function matrixCandidateToCard(candidate, route, evaluation, planningContext) {
  const serviceStops = (route?.stops || []).filter(
    (stop) => !(planningContext.originIndex != null && text(stop?.id).toUpperCase() === "SRT"),
  );
  const complete = routeOrderIsComplete(candidate.order, planningContext);
  const cargoFeasible =
    complete &&
    evaluation.firstStopViolationCount === 0 &&
    evaluation.cargoFlowViolationCount === 0 &&
    evaluation.relationViolationCount === 0 &&
    evaluation.onboardCargoAtEnd.length === 0;
  return {
    id: candidate.id,
    label: candidate.label,
    source: candidate.source,
    route,
    pickupStack: candidate.pickupStack || [],
    deliveryStack: candidate.deliveryStack || [],
    stackKey: `${(candidate.pickupStack || []).join(">")}|${(
      candidate.deliveryStack || []
    ).join(">")}`,
    serviceStops,
    serviceStopCount: serviceStops.length,
    evaluation,
    eligible: cargoFeasible,
    canApply: cargoFeasible,
    cargoFeasible,
    isRecommended: false,
    order: candidate.order,
  };
}

function getDriverColor(index) {
  return [
    "#3B82F6",
    "#F97316",
    "#8B5CF6",
    "#10B981",
    "#EF4444",
    "#06B6D4",
    "#F59E0B",
    "#6366F1",
  ][index % 8];
}

function vehiclePhoto(asset) {
  const photos = Array.isArray(asset?.photos) ? asset.photos : [];
  return text(
    asset?.photoUrl ||
      asset?.photoUrls?.[0] ||
      photos.find((item) => item?.preview || item?.url)?.preview ||
      photos.find((item) => item?.url)?.url,
  );
}

function assetPrimaryLabel(asset) {
  return (
    text(asset?.plateNumber) ||
    text(asset?.displayName) ||
    text(asset?.assetId || asset?.id) ||
    "未命名資產"
  );
}

function vehiclePlate(asset) {
  return assetPrimaryLabel(asset);
}

function scheduleDateTimeLabel(value) {
  const date = firestoreDate(value);
  if (!date) return "—";
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const FLEET_ASSET_KIND_CONFIG = [
  { key: "vehicle", label: "車輛", icon: "car", field: "vehicleId", multiple: false },
  { key: "trailer", label: "托架", icon: "trailer", field: "trailerIds", multiple: true },
  { key: "container", label: "容器", icon: "container", field: "containerIds", multiple: true },
  { key: "equipment", label: "設備", icon: "box", field: "equipmentIds", multiple: true },
];

const FLEET_ASSET_TYPE_LABELS = {
  GENERAL_TRUCK: "一般貨車",
  OIL_TANKER: "油罐車",
  CHEMICAL_TANKER: "化學槽車",
  SEPTIC_TANKER: "水肥車",
  WATER_TANKER: "水車",
  SPRINKLER_TRUCK: "灑水車",
  REFRIGERATED_TRUCK: "冷藏車",
  DUMP_TRUCK: "傾卸車",
  CONCRETE_MIXER: "混凝土車",
  TRACTOR: "曳引車",
  INTEGRATED_VEHICLE: "一體式車輛",
  OIL_TANK_TRAILER: "油槽拖架",
  CHEMICAL_TANK_TRAILER: "化學槽拖架",
  REFRIGERATED_TRAILER: "冷藏拖架",
  FLATBED_TRAILER: "平板拖架",
  SEMI_TRAILER: "半拖車",
  FUEL_TANK: "油桶／油槽",
  CHEMICAL_TANK: "化學槽",
  WATER_TANK: "水槽",
  WASTE_TANK: "污水槽",
  CARGO_CONTAINER: "貨櫃",
  PALLET: "棧板",
  CAGE_CART: "籠車",
  PUMP_EQUIPMENT: "抽水設備",
  GENERATOR: "發電機",
  IBC_CONTAINER: "IBC 容器",
  OTHER: "其他",
};

function fleetAssetKindLabel(kind) {
  return FLEET_ASSET_KIND_CONFIG.find((item) => item.key === text(kind).toLowerCase())?.label || "資產";
}

function fleetAssetTypeLabel(asset) {
  const type = text(asset?.assetType).toUpperCase();
  if (type === "OTHER") return text(asset?.assetTypeOther) || "其他";
  return FLEET_ASSET_TYPE_LABELS[type] || text(asset?.assetTypeOther) || type || fleetAssetKindLabel(asset?.assetKind);
}

function vehicleTitle(asset) {
  return [
    fleetAssetTypeLabel(asset),
    text(asset?.make || asset?.brand),
    text(asset?.model),
  ]
    .filter(Boolean)
    .join(" · ");
}

function fleetAssetStatusLabel(status) {
  return {
    PARKED: "停放",
    IN_USE: "使用中",
    MAINTENANCE: "維修中",
    RETIRED: "已報廢",
  }[text(status).toUpperCase()] || "可排程檢查";
}

function fleetAssetUnavailable(asset) {
  return ["MAINTENANCE", "RETIRED"].includes(text(asset?.status).toUpperCase());
}

function normalizeFleetAssetSummary(asset) {
  const assetId = text(asset?.assetId || asset?.id);
  const assetKind = text(asset?.assetKind).toLowerCase();
  if (!assetId || !FLEET_ASSET_KIND_CONFIG.some((item) => item.key === assetKind)) return null;
  return {
    ...(asset || {}),
    id: assetId,
    assetId,
    assetKind,
    assetType: text(asset?.assetType).toUpperCase(),
    assetTypeOther: text(asset?.assetTypeOther),
    displayName: text(asset?.displayName),
    plateNumber: text(asset?.plateNumber),
    make: text(asset?.make || asset?.brand),
    model: text(asset?.model),
    status: text(asset?.status).toUpperCase() || "PARKED",
    permittedDriverUids: Array.isArray(asset?.permittedDriverUids)
      ? asset.permittedDriverUids.map(text).filter(Boolean)
      : [],
  };
}

function normalizeFleetAssetSummaryItems(items) {
  return (Array.isArray(items) ? items : [])
    .map(normalizeFleetAssetSummary)
    .filter(Boolean);
}

function uniqueIds(values) {
  const list = Array.isArray(values) ? values : values ? [values] : [];
  return Array.from(new Set(list.map(text).filter(Boolean)));
}

function assignmentIds(assignment, field, legacyField = "") {
  const value = Array.isArray(assignment?.[field])
    ? assignment[field]
    : legacyField && assignment?.[legacyField]
      ? [assignment[legacyField]]
      : [];
  return uniqueIds(value);
}

function assignmentTrailerIds(assignment) {
  return assignmentIds(assignment, "trailerIds", "trailerId");
}

function assignmentContainerIds(assignment) {
  return assignmentIds(assignment, "containerIds");
}

function assignmentEquipmentIds(assignment) {
  return assignmentIds(assignment, "equipmentIds");
}

function assignmentAssetIds(assignment) {
  return uniqueIds([
    assignment?.vehicleId,
    ...assignmentTrailerIds(assignment),
    ...assignmentContainerIds(assignment),
    ...assignmentEquipmentIds(assignment),
  ]);
}

function vehicleBindingAssetIds(binding) {
  return {
    trailerIds: uniqueIds(binding?.trailerIds),
    containerIds: uniqueIds(binding?.containerIds),
  };
}

function isTractorAsset(asset) {
  const type = text(asset?.assetType || asset?.vehicleType).toUpperCase().replace(/[\s_-]/g, "");
  return ["TRACTOR", "曳引車", "拖車頭"].includes(type);
}

function fleetAssetSnapshot(asset) {
  return {
    assetId: text(asset?.assetId || asset?.id),
    assetKind: text(asset?.assetKind).toLowerCase(),
    assetType: text(asset?.assetType).toUpperCase(),
    assetTypeOther: text(asset?.assetTypeOther) || null,
    displayName: text(asset?.displayName) || null,
    plateNumber: text(asset?.plateNumber) || null,
    make: text(asset?.make || asset?.brand) || null,
    model: text(asset?.model) || null,
    status: text(asset?.status).toUpperCase() || null,
  };
}

function assetMatchesSearch(asset, query) {
  const normalizedQuery = text(query).normalize("NFKC").toLocaleLowerCase().trim();
  if (!normalizedQuery) return true;
  return [
    asset?.assetId,
    asset?.displayName,
    asset?.plateNumber,
    asset?.assetTypeOther,
    asset?.make,
    asset?.brand,
    asset?.model,
    fleetAssetKindLabel(asset?.assetKind),
    fleetAssetTypeLabel(asset),
  ]
    .map((value) => text(value).normalize("NFKC").toLocaleLowerCase())
    .some((value) => value.includes(normalizedQuery));
}

function Icon({ name, size = 18 }) {
  const paths = {
    back: "m14 6-6 6 6 6",
    next: "m10 6 6 6-6 6",
    plus: "M12 5v14M5 12h14",
    minus: "M5 12h14",
    close: "m6 6 12 12M18 6 6 18",
    edit: "M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16v4ZM13.5 6.5l4 4",
    check: "m5 12 4 4L19 6",
    chevronDown: "m6 9 6 6 6-6",
    chevronUp: "m18 15-6-6-6 6",
    search: "m16 16 5 5M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0",
    pin: "M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0ZM15 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0",
    eye: "M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Zm10 3a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z",
    photo: "M3 3h18v18H3zM3 16l5-5 4 4 3-3 6 6M9 7h.01",
    expand: "M8 3H3v5M16 3h5v5M3 16v5h5M21 16v5h-5",
    target: "M12 2v4m0 12v4M2 12h4m12 0h4M19 12a7 7 0 1 1-14 0 7 7 0 0 1 14 0",
    clock: "M12 7v5l3 2M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0",
    truck:
      "M2 5h12v12H2zM14 9h4l4 4v4h-8M8 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0M20 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0",
    car: "M5 17h14M6 17l1-6 2-3h6l2 3 1 6M7 13h10M8 17v2M16 17v2",
    trailer:
      "M3 7h13v9H3zM16 11h3l2 2v3h-5M8 18a2 2 0 1 1-4 0M20 18a2 2 0 1 1-4 0",
    user: "M20 21a8 8 0 0 0-16 0M12 13a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z",
    box: "M4 7 12 3l8 4-8 4-8-4Zm0 0v10l8 4 8-4V7M12 11v10",
    container: "M3 5h18v14H3zM7 5v14M17 5v14M3 10h4m10 0h4",
    phone:
      "M6 3h3l2 5-2 2a14 14 0 0 0 5 5l2-2 5 2v3c0 1-1 2-2 2C11 20 4 13 4 5c0-1 1-2 2-2Z",
    more: "M5 12h.01M12 12h.01M19 12h.01",
    spark: "M12 3 13.9 9.1 20 11l-6.1 1.9L12 19l-1.9-6.1L4 11l6.1-1.9L12 3ZM19 17v4m2-2h-4",
    map: "M9 18 3 21V6l6-3 6 3 6-3v15l-6 3-6-3Zm0-15v15m6-12v15",
    upload: "M12 16V4m0 0L7 9m5-5 5 5M5 20h14",
    history: "M3 12a9 9 0 1 0 3-6.7M3 4v5h5M12 7v5l3 2",
    alert: "M12 3 2.8 20h18.4L12 3Zm0 6v5m0 3h.01",
    file: "M6 3h9l3 3v15H6zM15 3v4h4M9 12h6M9 16h6",
    scale: "M12 3v18M5 7h14M5 7l-3 6h6L5 7Zm14 0-3 6h6l-3-6ZM8 21h8",
    currency: "M12 2v20M17 6.5C16 5.5 14.6 5 12.8 5 10.6 5 9 6.1 9 7.8c0 4.2 8 2.1 8 6.4 0 1.8-1.7 3.1-4.2 3.1-2 0-3.6-.6-4.8-1.8",
  };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[name] || paths.pin} />
    </svg>
  );
}

function DocumentProofEditor({ documents = [], onAdd, onChange, onRemove, onFile }) {
  return (
    <div className="cfj-document-proof-editor">
      <div className="cfj-document-proof-intro">
        <div>
          <strong>文件、圖片都放這裡</strong>
          <span>例如通行證、證明文件、現場照片或 PDF；不需要填文件編號。</span>
        </div>
        <button type="button" onClick={onAdd}>
          <Icon name="plus" size={13} />
          新增文件
        </button>
      </div>
      {documents.length ? (
        <div className="cfj-document-proof-list">
          {documents.map((document, index) => (
            <div className="cfj-document-proof-card" key={document.id || index}>
              <div className="cfj-document-proof-card-head">
                <span>文件 {index + 1}</span>
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  aria-label={`刪除文件 ${index + 1}`}
                >
                  <Icon name="close" size={13} />
                </button>
              </div>
              <textarea
                rows={2}
                value={document.documentName || ""}
                onChange={(event) => onChange(index, "documentName", event.target.value)}
                placeholder="請輸入文件名稱，例如：南區港口進場通行證"
              />
              <div className="cfj-document-proof-file-row">
                <label className="cfj-document-proof-upload">
                  <Icon name="upload" size={15} />
                  <span>{document.fileName || document.assetId ? "更換附件" : "加入圖片／PDF"}</span>
                  <input
                    type="file"
                    accept="image/*,.pdf,application/pdf"
                    onChange={(event) => onFile(index, event)}
                  />
                </label>
                <div className="cfj-document-proof-file-meta">
                  {document.previewUrl && document.fileType?.startsWith("image/") ? (
                    <img src={document.previewUrl} alt={document.documentName || "文件預覽"} />
                  ) : (
                    <Icon name="file" size={18} />
                  )}
                  <span>
                    {document.fileName || (document.assetId || document.storagePath ? "已有附件" : "尚未選擇附件")}
                    {document.fileSize ? `・${Math.ceil(document.fileSize / 1024)} KB` : ""}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <button type="button" className="cfj-document-proof-empty" onClick={onAdd}>
          <Icon name="file" size={22} />
          <strong>尚未新增文件證明</strong>
          <span>點這裡新增內容，再附上圖片或 PDF</span>
        </button>
      )}
    </div>
  );
}

function CargoSenderEditor({ sender = {}, onChange, onPick }) {
  const value = normalizeParty(sender, "SENDER");
  return (
    <section className="cfj-cargo-sender-editor">
      <div className="cfj-cargo-sender-head">
        <div>
          <strong>寄件人</strong>
          <small>這位寄件人只綁定目前這一件貨物</small>
        </div>
        <button type="button" className="cfj-common-picker-button" onClick={onPick}>
          <Icon name="user" size={14} />
          選擇常用聯絡人
        </button>
      </div>
      <div className="cfj-cargo-sender-grid">
        <input placeholder="寄件人姓名／公司（選填）" value={value.name || value.company || ""} onChange={(event) => onChange("sender.name", event.target.value)} />
        <input placeholder="寄件人公司（選填）" value={value.company || ""} onChange={(event) => onChange("sender.company", event.target.value)} />
        <input placeholder="寄件人電話（選填）" value={value.phone || ""} onChange={(event) => onChange("sender.phone", event.target.value)} />
        <input type="email" placeholder="寄件人 Email（選填）" value={value.email || ""} onChange={(event) => onChange("sender.email", event.target.value)} />
        <input className="is-wide" placeholder="寄件地址（選填）" value={value.addressText || ""} onChange={(event) => onChange("sender.addressText", event.target.value)} />
      </div>
    </section>
  );
}

function DeliveryDetailDrawer({
  editor,
  draft,
  category,
  recipient,
  sourceSummary,
  documents,
  sections,
  onClose,
  onChange,
  onAddDocument,
  onChangeDocument,
  onRemoveDocument,
  onFileDocument,
  onToggle,
  onPickSender,
  onSave,
}) {
  return (
    <div
      className="cfj-cargo-detail-overlay cfj-delivery-detail-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cfj-delivery-detail-title"
      onClick={onClose}
    >
      <aside
        className="cfj-cargo-detail-drawer"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="cfj-cargo-detail-head">
          <div className="cfj-cargo-detail-heading">
            <div className="cfj-cargo-detail-title-row">
              <Icon name="truck" size={22} />
              <h2 id="cfj-delivery-detail-title">編輯配送分配</h2>
            </div>
          </div>
          <button
            type="button"
            className="cfj-cargo-detail-close"
            onClick={onClose}
            aria-label="關閉配送分配詳細資料"
          >
            <Icon name="close" size={18} />
          </button>
        </header>
        <div className="cfj-cargo-detail-summary">
          <div className="cfj-delivery-detail-summary-icon">
            <Icon name="box" size={20} />
          </div>
          <div>
            <strong>{text(draft?.name) || "尚未命名配送內容"}</strong>
            <small>
              {text(draft?.quantity) || "—"} {text(draft?.unit) || "件"}
            </small>
          </div>
        </div>
        {normalizeCargoOperation(draft?.operationType, "DELIVERY") === "DELIVERY" && sourceSummary ? (
          <div className="cfj-cargo-source-summary">
            <div>
              <span>來源貨物</span>
              <strong>{sourceSummary.name}</strong>
            </div>
            <b>取貨 {sourceSummary.pickupCount} 次</b>
            <b>送貨 {sourceSummary.deliveryCount} 次</b>
          </div>
        ) : null}
        {normalizeCargoOperation(draft?.operationType, "DELIVERY") === "DELIVERY" ? (
          <CargoSenderEditor
            sender={draft?.sender}
            onChange={onChange}
            onPick={onPickSender}
          />
        ) : null}
        <div className="cfj-cargo-detail-scroll">
          {sections.map((section) => {
            const expanded = editor?.expandedSections?.[section.id] !== false;
            return (
              <section
                className={`cfj-cargo-detail-section${expanded ? " is-expanded" : ""}`}
                key={section.id}
              >
                <button
                  type="button"
                  className="cfj-cargo-detail-section-head"
                  aria-expanded={expanded}
                  onClick={() => onToggle(section.id)}
                >
                  <span>
                    <Icon
                      name={
                        section.id === "payment"
                          ? "currency"
                          : section.id === "documents"
                            ? "file"
                            : section.id === "execution"
                              ? "check"
                              : section.id === "unloading"
                                ? "truck"
                                : "box"
                      }
                      size={15}
                    />
                    {section.title}
                  </span>
                  <Icon name={expanded ? "minus" : "plus"} size={14} />
                </button>
                {expanded ? (
                  section.id === "documents" ? (
                    <DocumentProofEditor
                      documents={documents}
                      onAdd={onAddDocument}
                      onChange={onChangeDocument}
                      onRemove={onRemoveDocument}
                      onFile={onFileDocument}
                    />
                  ) : (
                  <div className="cfj-cargo-detail-fields">
                    {section.fields.map((field) => {
                      const value = readCargoDetailPath(draft, field.path);
                      const inputValue = value === null || value === undefined ? "" : value;
                      return (
                        <label
                          className={`cfj-cargo-detail-field${field.wide ? " is-wide" : ""}`}
                          key={field.key}
                        >
                          <span>{field.label}{field.suffix ? `（${field.suffix}）` : ""}</span>
                          {field.type === "textarea" ? (
                            <textarea
                              rows={3}
                              value={inputValue}
                              placeholder={`請輸入${field.label}`}
                              onChange={(event) => onChange(field.path, event.target.value)}
                            />
                          ) : (
                            <div className="cfj-cargo-detail-input-wrap">
                              <input
                                type={field.type || "text"}
                                value={inputValue}
                                placeholder={`請輸入${field.label}`}
                                onChange={(event) => onChange(field.path, event.target.value)}
                              />
                              {field.suffix ? <small>{field.suffix}</small> : null}
                            </div>
                          )}
                        </label>
                      );
                    })}
                  </div>
                  )
                ) : null}
              </section>
            );
          })}
        </div>
        <footer className="cfj-cargo-detail-footer">
          <button
            type="button"
            className="cfj-cargo-secondary-button"
            onClick={onClose}
          >
            取消
          </button>
          <button
            type="button"
            className="cfj-cargo-primary-button"
            onClick={onSave}
          >
            <Icon name="check" size={15} />
            儲存配送分配
          </button>
        </footer>
      </aside>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="cfj-field">
      <span>
        {label}
        {hint && <small>{hint}</small>}
      </span>
      {children}
    </label>
  );
}

function ImportPreviewModal({
  fileName,
  stations,
  errors,
  loading,
  startTime,
  onChangeStation,
  onChangeRecipient,
  onChangeCargo,
  onAddRecipient,
  onRemoveRecipient,
  onAddCargo,
  onRemoveCargo,
  onCancel,
  onConfirm,
  onChangeStartTime,
}) {
  return (
    <div className="cfj-import-overlay" role="dialog" aria-modal="true">
      <div className="cfj-import-modal" onClick={(event) => event.stopPropagation()}>
        <div className="cfj-import-head">
          <div>
            <h2>確認配送站、收件人與貨物</h2>
            <p>{fileName || "匯入檔案"}・即可在此編輯。</p>
          </div>
          <div className="cfj-import-head-actions">
            <label className="cfj-import-start-time">
              <span>開始時間</span>
              <input
                type="time"
                required
                value={startTime}
                onChange={(event) => onChangeStartTime(event.target.value)}
              />
            </label>
            <button type="button" className="cfj-binding-close" onClick={onCancel} aria-label="關閉">×</button>
          </div>
        </div>
        <div className="cfj-import-summary">
          <span><b>{stations.length}</b> 個站點</span>
          <span><b>{stations.reduce((sum, station) => sum + station.recipients.length, 0)}</b> 位收件人</span>
          <span><b>{stations.reduce((sum, station) => sum + station.recipients.reduce((count, recipient) => count + recipientContentCount(recipient), 0), 0)}</b> 筆貨物事件</span>
        </div>
        <div className="cfj-import-list">
          {stations.map((station, stationIndex) => (
            <section className="cfj-import-station" key={`${station.stationIndex}-${stationIndex}`}>
              <div className="cfj-import-station-head">
                <div className="cfj-import-station-number">{station.stationIndex}</div>
                <div className="cfj-import-station-title">
                  <strong>站點 {station.stationIndex}</strong>
                  <span>{station.recipients.length} 位收件人・{station.recipients.reduce((sum, recipient) => sum + recipientContentCount(recipient), 0)} 筆貨物</span>
                </div>
              </div>
              <div className="cfj-import-station-fields">
                <input value={station.address} onChange={(event) => onChangeStation(stationIndex, "address", event.target.value)} placeholder="地址" />
                <select value={station.timeWindowType} onChange={(event) => onChangeStation(stationIndex, "timeWindowType", event.target.value)}>
                  <option value="NONE">不限時間</option>
                  <option value="SOFT">軟性時間</option>
                  <option value="HARD">硬性時間</option>
                </select>
                <input value={station.earliestStart} onChange={(event) => onChangeStation(stationIndex, "earliestStart", event.target.value)} placeholder="最早開始" />
                <input value={station.latestStart} onChange={(event) => onChangeStation(stationIndex, "latestStart", event.target.value)} placeholder="最晚開始" />
                <input type="number" min="0" value={station.serviceMinutes} onChange={(event) => onChangeStation(stationIndex, "serviceMinutes", event.target.value)} placeholder="作業分鐘" />
              </div>
              <div className="cfj-import-recipients">
                {station.recipients.map((recipient, recipientIndex) => (
                  <div className="cfj-import-recipient" key={`${recipient.id}-${recipientIndex}`}>
                    <div className="cfj-import-recipient-head">
                      <span>收件人 {recipientIndex + 1}</span>
                      {station.recipients.length > 1 ? <button type="button" onClick={() => onRemoveRecipient(stationIndex, recipientIndex)}>刪除收件人</button> : null}
                    </div>
                    <div className="cfj-import-recipient-fields">
                      <input value={recipient.id} onChange={(event) => onChangeRecipient(stationIndex, recipientIndex, "id", event.target.value)} placeholder="收件人編號" />
                      <input value={recipient.name} onChange={(event) => onChangeRecipient(stationIndex, recipientIndex, "name", event.target.value)} placeholder="收件人姓名" />
                      <input value={recipient.company} onChange={(event) => onChangeRecipient(stationIndex, recipientIndex, "company", event.target.value)} placeholder="公司" />
                      <input value={recipient.phone} onChange={(event) => onChangeRecipient(stationIndex, recipientIndex, "phone", event.target.value)} placeholder="電話" />
                      <input type="email" value={recipient.email} onChange={(event) => onChangeRecipient(stationIndex, recipientIndex, "email", event.target.value)} placeholder="Email" />
                    </div>
                    <div className="cfj-import-cargo-head"><b>貨物</b><button type="button" onClick={() => onAddCargo(stationIndex, recipientIndex)}>＋ 新增貨物</button></div>
                    <div className="cfj-import-cargo-list">
                      {recipientContents(recipient).map((item, cargoIndex) => (
                        <div className="cfj-import-cargo-row" key={`${item.id}-${cargoIndex}`}>
                          <input value={item.name} onChange={(event) => onChangeCargo(stationIndex, recipientIndex, cargoIndex, "name", event.target.value)} placeholder="貨物名稱" />
                          <input type="number" min="1" value={item.quantity} onChange={(event) => onChangeCargo(stationIndex, recipientIndex, cargoIndex, "quantity", event.target.value)} placeholder="數量" />
                          <input value={item.unit} onChange={(event) => onChangeCargo(stationIndex, recipientIndex, cargoIndex, "unit", event.target.value)} placeholder="單位" />
                          <select value={item.operationType} onChange={(event) => onChangeCargo(stationIndex, recipientIndex, cargoIndex, "operationType", event.target.value)}>
                            <option value="PICKUP">取貨</option>
                            <option value="DELIVERY">送貨</option>
                          </select>
                          <input value={item.note} onChange={(event) => onChangeCargo(stationIndex, recipientIndex, cargoIndex, "note", event.target.value)} placeholder="備註（選填）" />
                          <button type="button" className="cfj-import-remove" onClick={() => onRemoveCargo(stationIndex, recipientIndex, cargoIndex)} aria-label="刪除貨物">×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" className="cfj-import-add-recipient" onClick={() => onAddRecipient(stationIndex)}>＋ 新增收件人</button>
            </section>
          ))}
        </div>
        {errors.length ? (
          <div className="cfj-import-errors">
            <strong>請先修正以下資料</strong>
            <ul>{errors.slice(0, 12).map((error, index) => <li key={`${error}-${index}`}>{error}</li>)}</ul>
            {errors.length > 12 ? <small>另有 {errors.length - 12} 項檢查結果。</small> : null}
          </div>
        ) : <div className="cfj-import-ready"><Icon name="check" size={15} />資料格式已通過基本檢查；按確認後會開始定位地址。</div>}
        <div className="cfj-import-footer">
          <button type="button" className="cfj-cargo-secondary-button" onClick={onCancel}>取消</button>
          <button type="button" className="cfj-cargo-primary-button" disabled={loading || errors.length > 0} onClick={onConfirm}>{loading ? "匯入定位中…" : "確認匯入配送路線"}</button>
        </div>
      </div>
    </div>
  );
}

function Section({
  n,
  title,
  detail,
  actions,
  summary,
  expanded = true,
  onToggle,
  children,
}) {
  const collapsible = typeof onToggle === "function";

  return (
    <section className={`cfj-card${expanded ? " is-expanded" : " is-collapsed"}`}>
      <header
        className={`cfj-section-head${collapsible ? " is-collapsible" : ""}`}
        onClick={collapsible ? onToggle : undefined}
        onKeyDown={
          collapsible
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onToggle();
                }
              }
            : undefined
        }
        role={collapsible ? "button" : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? expanded : undefined}
      >
        {n ? <span className="cfj-section-n">{n}</span> : null}
        <div className="cfj-section-head-copy">
          <h2>{title}</h2>
          <p>{detail}</p>
          {!expanded && summary ? (
            <div className="cfj-section-summary">{summary}</div>
          ) : null}
        </div>
        {actions ? (
          <div
            className="cfj-section-head-actions"
            onClick={(event) => event.stopPropagation()}
          >
            {actions}
          </div>
        ) : null}
        {collapsible ? (
          <span className="cfj-section-chevron" aria-hidden="true">
            {expanded ? "⌃" : "⌄"}
          </span>
        ) : null}
      </header>
      {expanded ? children : null}
    </section>
  );
}

function VehicleCard({ item, kind, selected, disabled, onClick }) {
  const none = kind === "none";
  const photo = none ? "" : vehiclePhoto(item);
  return (
    <button
      type="button"
      className={`cfj-vehicle-card ${selected ? "is-selected" : ""} ${disabled ? "is-disabled" : ""}`}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="cfj-vehicle-image">
        {none ? (
          <Icon name="close" size={18} />
        ) : photo ? (
          <img src={photo} alt={vehiclePlate(item)} />
        ) : (
          <Icon name={kind === "trailer" ? "trailer" : "car"} size={20} />
        )}
      </span>
      <span className="cfj-vehicle-info">
        <b>
          {none
            ? "不使用"
            : vehicleTitle(item) || (kind === "trailer" ? "拖車" : "車輛")}
        </b>
        <small>{none ? "—" : vehiclePlate(item)}</small>
      </span>
      {selected && (
        <span className="cfj-vehicle-selected">
          <Icon name="check" size={11} />
        </span>
      )}
    </button>
  );
}

function makeStopIcon(stop, active) {
  const isStart = isStartStop(stop);
  const label = isStart
    ? "起"
    : isEndStop(stop)
      ? "終"
      : String(Number(stop?.order || 0));
  return L.divIcon({
    className: "cfj-leaflet-icon-shell",
    html: `<div class="cfj-map-marker ${isStart ? "is-start" : isEndStop(stop) ? "is-end" : "is-waypoint"} ${active ? "is-active" : ""}">${label}</div>`,
    iconSize: [42, 42],
    iconAnchor: [21, 21],
  });
}

function stopCoordinateKey(stop) {
  if (!coordsOK(stop)) return "";
  return `${Number(stop.lat).toFixed(5)},${Number(stop.lng).toFixed(5)}`;
}

function makeStopGroupIcon(group, activeId, simpleEndpoints = false) {
  const active = group.some((stop) => stop.id === activeId);
  const labels = group
    .map((stop) =>
      isStartStop(stop)
        ? "起"
        : simpleEndpoints && isEndStop(stop)
          ? "終"
          : String(Number(stop?.order || 0)),
    )
    .join(" · ");
  return L.divIcon({
    className: "cfj-leaflet-icon-shell cfj-leaflet-group-icon-shell",
    html: `<div class="cfj-map-marker cfj-map-marker-group ${active ? "is-active" : ""}">${labels}</div>`,
    iconSize: [Math.max(52, labels.length * 10 + 22), 42],
    iconAnchor: [Math.max(26, (labels.length * 10 + 22) / 2), 21],
  });
}

function MapEvents({ onPick, blocked = false }) {
  useMapEvents({
    click: (event) => {
      const point = {
        lat: event.latlng.lat,
        lng: event.latlng.lng,
      };

      if (!blocked) onPick?.(point);
    },
  });
  return null;
}


function MapViewport({ stops, routePath, fitVersion }) {
  const map = useMap();
  useEffect(() => {
    const points = routePath?.length
      ? routePath
      : stops
          .filter(coordsOK)
          .map((stop) => [Number(stop.lat), Number(stop.lng)]);

    if (!points.length) return;
    if (points.length === 1) {
      map.setView(points[0], 16, { animate: true });
      return;
    }
    map.fitBounds(points, {
      paddingTopLeft: [55, 110],
      paddingBottomRight: [55, 85],
      maxZoom: 16,
      animate: true,
    });
  }, [map, fitVersion]);
  return null;
}

function MapPanToPoint({ point }) {
  const map = useMap();
  useEffect(() => {
    if (!coordsOK(point)) {
      return;
    }

    map.panTo([Number(point.lat), Number(point.lng)], {
      animate: true,
      duration: 0.35,
    });
  }, [map, point?.lat, point?.lng, point?.version]);
  return null;
}

function MapSizeObserver() {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    let frameId = 0;
    const refreshSize = () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        frameId = 0;
        map.invalidateSize({ pan: false, debounceMoveend: true });
      });
    };
    const ResizeObserverClass = window.ResizeObserver;
    const observer =
      ResizeObserverClass
        ? new ResizeObserverClass(refreshSize)
        : null;
    if (observer) {
      observer.observe(container);
    } else {
      window.addEventListener("resize", refreshSize);
    }
    refreshSize();
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", refreshSize);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, [map]);
  return null;
}

function MapInstanceRef({ mapRef }) {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
    return () => {
      if (mapRef.current === map) mapRef.current = null;
    };
  }, [map, mapRef]);
  return null;
}

function MapZoomControls() {
  const map = useMap();
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      L.DomEvent.disableClickPropagation(ref.current);
      L.DomEvent.disableScrollPropagation(ref.current);
    }
  }, []);
  return (
    <div ref={ref} className="cfj-custom-zoom">
      <button type="button" aria-label="放大" onClick={() => map.zoomIn()}>
        <Icon name="plus" size={18} />
      </button>
      <span />
      <button type="button" aria-label="縮小" onClick={() => map.zoomOut()}>
        <Icon name="minus" size={18} />
      </button>
    </div>
  );
}

function MapStopSwitcher({
  stops,
  activeId,
  locationBusy,
  onSelectStop,
  onAddStop,
  simpleEndpoints = false,
}) {
  const ref = useRef(null);
  const dragRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    startScroll: 0,
  });
  const suppressClickRef = useRef(false);

  useEffect(() => {
    if (ref.current) {
      L.DomEvent.disableScrollPropagation(ref.current);
    }
  }, []);

  const handlePointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const element = ref.current;
    if (!element) return;
    dragRef.current = {
      active: true,
      moved: false,
      startX: event.clientX,
      startScroll: element.scrollLeft,
    };
    element.classList.add("is-dragging");
    element.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    const element = ref.current;
    if (!drag.active || !element) return;
    const delta = event.clientX - drag.startX;
    if (Math.abs(delta) > 4) drag.moved = true;
    if (drag.moved) {
      event.preventDefault();
      element.scrollLeft = drag.startScroll - delta;
    }
  };

  const handlePointerUp = (event) => {
    const element = ref.current;
    const moved = dragRef.current.moved;
    dragRef.current.active = false;
    dragRef.current.moved = false;
    element?.classList.remove("is-dragging");
    element?.releasePointerCapture?.(event.pointerId);
    if (moved) {
      suppressClickRef.current = true;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    }
  };

  return (
    <div
      ref={ref}
      className="cfj-map-stop-switch"
      onClick={(event) => event.stopPropagation()}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {stops.map((stop) => (
        <button
          key={stop.id}
          type="button"
          disabled={locationBusy}
          className={`cfj-map-stop-button ${activeId === stop.id ? "is-active" : ""}`}
          onClick={(event) => {
            if (suppressClickRef.current) {
              event.preventDefault();
              event.stopPropagation();
              suppressClickRef.current = false;
              return;
            }
            onSelectStop(stop.id);
          }}
        >
          <span
            className={`cfj-map-stop-dot ${isStartStop(stop) ? "is-start" : "is-end"}`}
          >
            {isStartStop(stop)
              ? simpleEndpoints
                ? "起"
                : ""
              : simpleEndpoints && isEndStop(stop)
                ? "終"
                : stop.order}
          </span>
        </button>
      ))}
      {onAddStop ? (
        <button
          type="button"
          className="cfj-map-stop-add"
          aria-label="新增配送站"
          onPointerDown={(event) => {
            event.stopPropagation();
          }}
          onPointerUp={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onAddStop();
          }}
        >
          <Icon name="plus" size={17} />
        </button>
      ) : null}
    </div>
  );
}

function RouteMap({
  stops,
  activeId,
  route,
  fitVersion,
  mapFocus,
  onPick,
  onSelectStop,
  onError,
  locationBusy = false,
  onAddStop,
  onOptimize,
  optimizeDisabled = false,
  optimizing = false,
  optimizeLabel = "最佳化站點",
  simpleEndpoints = false,
  showOptimizeButton = true,
}) {
  const mapRef = useRef(null);
  const current =
    stops.find((stop) => stop.id === activeId && coordsOK(stop)) ||
    stops.find(coordsOK);
  const center = current
    ? [Number(current.lat), Number(current.lng)]
    : [22.6273, 120.3014];
  const routeSummaryVisible =
    !route?.loading &&
    Number(route?.km) > 0 &&
    Number(route?.min) > 0 &&
    Array.isArray(route?.path) &&
    route.path.length > 0;
  const markerGroups = useMemo(() => {
    const groups = new Map();
    stops.filter(coordsOK).forEach((stop) => {
      const key = stopCoordinateKey(stop);
      const group = groups.get(key) || [];
      group.push(stop);
      groups.set(key, group);
    });
    return Array.from(groups.values());
  }, [stops]);
  return (
    <div className="cfj-map-wrap">
      {showOptimizeButton ? (
        <button
          type="button"
          className="cfj-map-optimize-button"
          disabled={optimizeDisabled || optimizing}
          onClick={onOptimize}
        >
          <Icon name="spark" size={13} />
          <span>{optimizing ? "最佳化中…" : optimizeLabel}</span>
        </button>
      ) : null}
      {routeSummaryVisible ? (
        <div
          className="cfj-map-route-summary"
          role="status"
          aria-label="目前路線預計時間與距離"
        >
          <span className="cfj-map-route-summary-metric">
            <small>預計時間</small>
            <strong>{formatRouteDurationMinutes(Number(route.min) * 1.2)}</strong>
          </span>
          <i aria-hidden="true" />
          <span className="cfj-map-route-summary-metric">
            <small>距離</small>
            <strong>{Number(route.km).toFixed(1)} km</strong>
          </span>
        </div>
      ) : null}
      <MapContainer
        center={center}
        zoom={11}
        zoomControl={false}
        scrollWheelZoom
        className="cfj-map"
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="© OpenStreetMap contributors"
        />
        <MapEvents onPick={onPick} blocked={locationBusy} />
        <MapInstanceRef mapRef={mapRef} />
        <MapViewport
          stops={stops}
          routePath={route.path}
          fitVersion={fitVersion}
        />
        <MapSizeObserver />
        <MapPanToPoint point={mapFocus} />
        <MapZoomControls />
        {markerGroups.map((group) => {
          const stop = group[0];
          const isGrouped = group.length > 1;
          return (
            <Marker
              key={`map-group-${stopCoordinateKey(stop)}`}
              position={[Number(stop.lat), Number(stop.lng)]}
              icon={
                isGrouped
                  ? makeStopGroupIcon(group, activeId, simpleEndpoints)
                  : makeStopIcon(stop, activeId === stop.id)
              }
              draggable={!isGrouped}
              eventHandlers={
                isGrouped
                  ? undefined
                  : {
                      click: () => onSelectStop(stop.id),
                      dragstart: () => onSelectStop(stop.id),
                      dragend: (event) => {
                        const point = event.target.getLatLng();
                        onPick({ lat: point.lat, lng: point.lng, stopId: stop.id });
                      },
                    }
              }
            >
              {isGrouped ? (
                <Popup className="cfj-coincident-stop-popup" closeButton>
                  <div className="cfj-coincident-stop-popup-head">
                    <strong>同一配送地址</strong>
                    <span>{text(stop.text) || "已定位地址"}</span>
                  </div>
                  <div className="cfj-coincident-stop-list">
                    {group.map((groupStop) => (
                      <button
                        key={groupStop.id}
                        type="button"
                        className={`cfj-coincident-stop-item ${activeId === groupStop.id ? "is-active" : ""}`}
                        onClick={() => onSelectStop(groupStop.id)}
                      >
                        <span className="cfj-coincident-stop-order">
                          {simpleEndpoints
                            ? isStartStop(groupStop)
                              ? "起"
                              : "終"
                            : groupStop.order}
                        </span>
                        <span className="cfj-coincident-stop-copy">
                          <strong>
                            {simpleEndpoints
                              ? isStartStop(groupStop)
                                ? "起點"
                                : "終點"
                              : `配送站 ${groupStop.order}`}
                          </strong>
                          <small>
                            {simpleEndpoints
                              ? isStartStop(groupStop)
                                ? "工作起點"
                                : "工作終點"
                              : groupStop.operationType === "PICKUP"
                                ? "取貨／寄件人"
                                : groupStop.operationType === "DELIVERY"
                                  ? "送貨／收件人"
                                  : "取貨與送貨"}
                          </small>
                        </span>
                        <span className="cfj-coincident-stop-action">查看</span>
                      </button>
                    ))}
                  </div>
                </Popup>
              ) : stop.verified && text(stop.text) ? (
                <Tooltip
                  permanent
                  direction="top"
                  offset={[0, -25]}
                  opacity={1}
                  className="cfj-stop-tooltip"
                >
                  <div className="cfj-stop-tooltip-content">
                    <span
                      className={`cfj-stop-tooltip-kind ${isStartStop(stop) ? "is-start" : isEndStop(stop) ? "is-end" : "is-waypoint"}`}
                    >
                      {isStartStop(stop)
                        ? "起"
                        : simpleEndpoints && isEndStop(stop)
                          ? "終"
                          : stop.order}
                    </span>
                    <span className="cfj-stop-tooltip-address">{stop.text}</span>
                  </div>
                </Tooltip>
              ) : null}
            </Marker>
          );
        })}
        {!!route.path?.length && (
          <>
            <Polyline
              positions={route.path}
              pathOptions={{
                color: "#fff",
                weight: 10,
                opacity: 0.9,
                lineCap: "round",
                lineJoin: "round",
              }}
            />
            <Polyline
              positions={route.path}
              pathOptions={{
                color: "#F18432",
                weight: 5,
                opacity: 1,
                lineCap: "round",
                lineJoin: "round",
              }}
            />
          </>
        )}
      </MapContainer>
      <MapStopSwitcher
        stops={stops}
        activeId={activeId}
        locationBusy={locationBusy}
        onSelectStop={onSelectStop}
        onAddStop={onAddStop}
        simpleEndpoints={simpleEndpoints}
      />
      {locationBusy && (
        <div className="cfj-map-lock" role="status" aria-live="polite">
          <span className="cfj-map-lock-spinner" />
          <span>正在取得地址…</span>
        </div>
      )}
      <div className="cfj-map-tools">
        <button
          type="button"
          title="回到我的位置"
          aria-label="回到我的位置"
          onClick={() => {
            if (!navigator.geolocation) {
              onError("此瀏覽器不支援定位功能。");
              return;
            }
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const map = mapRef.current;
                if (!map) {
                  onError("地圖尚未準備完成，請稍後再試。");
                  return;
                }
                onError("");
                map.invalidateSize({ pan: false });
                map.flyTo(
                  [position.coords.latitude, position.coords.longitude],
                  Math.max(map.getZoom(), 16),
                  { animate: true, duration: 0.65 },
                );
              },
              (error) => {
                onError(
                  error?.code === 1
                    ? "定位權限未開啟，請允許瀏覽器使用目前位置。"
                    : error?.code === 3
                      ? "取得目前位置逾時，請稍後再試。"
                      : "無法取得目前位置，請確認裝置定位已開啟。",
                );
              },
              { enableHighAccuracy: true, maximumAge: 5000, timeout: 12000 },
            );
          }}
        >
          <Icon name="target" />
        </button>
        <button
          type="button"
          title="置中完整路線"
          aria-label="置中完整路線"
          onClick={() => {
            const map = mapRef.current;
            if (!map) {
              onError("地圖尚未準備完成，請稍後再試。");
              return;
            }
            const points = route.path?.length
              ? route.path
              : stops
                  .filter(coordsOK)
                  .map((stop) => [Number(stop.lat), Number(stop.lng)]);
            if (!points.length) {
              onError("目前沒有已定位的站點可供置中。");
              return;
            }
            onError("");
            map.invalidateSize({ pan: false });
            if (points.length === 1) {
              map.setView(points[0], 16, { animate: true });
              return;
            }
            map.fitBounds(points, {
              paddingTopLeft: [55, 110],
              paddingBottomRight: [55, 85],
              maxZoom: 16,
              animate: true,
            });
          }}
        >
          <Icon name="expand" />
        </button>
      </div>
    </div>
  );
}


function routeCargoNumberMap(stops = []) {
  const keys = new Set();
  stops.forEach((stop) => {
    (stop?.recipients || []).forEach((recipient) => {
      recipientContents(recipient).forEach((item) => {
        const key = routeCargoKey(item);
        if (key) keys.add(key);
      });
    });
  });
  const numbers = new Map();
  Array.from(keys).sort().forEach((key, index) => numbers.set(key, String(index + 1)));
  return numbers;
}

function routeStopCargoEvents(stop, cargoNumbers) {
  const pickup = new Set();
  const delivery = new Set();
  (stop?.recipients || []).forEach((recipient) => {
    const record = recipientCargoRecordSummary(recipient, stop?.operationType);
    const number = cargoNumbers.get(routeCargoKey(record));
    if (!number) return;
    if (recipientHasOperation(recipient, "PICKUP", stop?.operationType)) {
      pickup.add(number);
    }
    if (recipientHasOperation(recipient, "DELIVERY", stop?.operationType)) {
      delivery.add(number);
    }
  });
  return { pickup: Array.from(pickup), delivery: Array.from(delivery) };
}

function RouteStationSequence({ candidate }) {
  const stops = candidate?.route?.stops || [];
  const cargoNumbers = routeCargoNumberMap(stops);
  return (
    <div className="cfj-route-candidate-sequence">
      <div className="cfj-route-candidate-sequence-head">
        <strong>配送站流程</strong>
        <small>左右滑動查看各站取貨與送貨貨物</small>
      </div>
      <div className="cfj-route-candidate-sequence-track">
        {stops.map((stop, index) => {
          const events = routeStopCargoEvents(stop, cargoNumbers);
          const arrivalMinutes = candidate?.evaluation?.rows?.[index]?.arrivalMinutes;
          const stationNumber = isStartStop(stop) ? "起" : stop.order;
          return (
            <React.Fragment key={`${candidate?.id || "route"}-${stop.id}-${index}`}>
              {index > 0 ? <i aria-hidden="true">›</i> : null}
              <div className="cfj-route-station-card">
                <span className="cfj-route-station-number">{stationNumber}</span>
                <strong>{stop.text || `站點 ${index + 1}`}</strong>
                <span className="cfj-route-station-eta">
                  預計 {formatRouteClockMinutes(arrivalMinutes)}
                </span>
                <span className="cfj-route-station-cargo">
                  {events.pickup.length ? <em className="is-pickup">取貨 {events.pickup.join("、")}</em> : null}
                  {events.delivery.length ? <em className="is-delivery">送貨 {events.delivery.join("、")}</em> : null}
                  {!events.pickup.length && !events.delivery.length ? <em className="is-empty">無貨物事件</em> : null}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function RouteCandidateMap({ candidate }) {
  const route = candidate?.route;
  const stops = route?.stops || [];
  const current = stops.find(coordsOK) || null;
  const center = current
    ? [Number(current.lat), Number(current.lng)]
    : [22.6273, 120.3014];
  return (
    <div className="cfj-route-candidate-map">
      <MapContainer
        key={candidate?.id || "route-candidate-map"}
        center={center}
        zoom={11}
        zoomControl={false}
        scrollWheelZoom
        className="cfj-map"
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="© OpenStreetMap contributors"
        />
        <MapViewport
          stops={stops}
          routePath={route?.path || []}
          fitVersion={candidate?.id || "route-candidate-map"}
        />
        <MapZoomControls />
        {stops.filter(coordsOK).map((stop) => (
          <Marker
            key={`${candidate?.id || "candidate"}-${stop.id}-${stop.order}`}
            position={[Number(stop.lat), Number(stop.lng)]}
            icon={makeStopIcon(stop, false)}
          >
            <Tooltip
              permanent
              direction="top"
              offset={[0, -25]}
              opacity={1}
              className="cfj-stop-tooltip"
            >
              <div className="cfj-stop-tooltip-content">
                <span
                  className={`cfj-stop-tooltip-kind ${isStartStop(stop) ? "is-start" : isEndStop(stop) ? "is-end" : "is-waypoint"}`}
                >
                  {isStartStop(stop) ? "起" : stop.order}
                </span>
                <span className="cfj-stop-tooltip-address">
                  {stop.text || "未命名站點"}
                </span>
              </div>
            </Tooltip>
          </Marker>
        ))}
        {!!route?.path?.length && (
          <>
            <Polyline
              positions={route.path}
              pathOptions={{
                color: "#fff",
                weight: 10,
                opacity: 0.92,
                lineCap: "round",
                lineJoin: "round",
              }}
            />
            <Polyline
              positions={route.path}
              pathOptions={{
                color: "#F18432",
                weight: 5,
                opacity: 1,
                lineCap: "round",
                lineJoin: "round",
              }}
            />
          </>
        )}
      </MapContainer>
      <div className="cfj-route-candidate-map-label">
        <Icon name="map" size={14} />
        點選左側路線卡即可切換地圖
      </div>
    </div>
  );
}

function RouteCandidatesModal({
  candidates,
  selectedId,
  loading,
  error,
  onSelect,
  onCancel,
  onApply,
}) {
  const selected = candidates.find((candidate) => candidate.id === selectedId) || candidates[0] || null;
  return (
    <div
      className="cfj-route-candidates-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="選擇配送路線"
      onClick={onCancel}
    >
      <div
        className="cfj-route-candidates-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="cfj-route-candidates-head">
          <div>
            <small>ROUTE OPTIONS</small>
            <h2>選擇配送路線</h2>
            <p>
              {loading
                ? "正在產生候選路線與地圖…"
                : `已產生 ${candidates.length} 組候選，請先查看順序再套用。`}
            </p>
          </div>
          <button
            type="button"
            className="cfj-binding-close"
            onClick={onCancel}
            aria-label="關閉"
          >
            ×
          </button>
        </header>
        {error ? <div className="cfj-route-candidates-error">{error}</div> : null}
        {loading ? (
          <div className="cfj-route-candidates-loading" role="status" aria-live="polite">
            <span className="cfj-route-candidates-spinner" />
            <strong>正在計算候選路線</strong>
            <span>會逐組確認道路距離、時間與取送順序，請稍候。</span>
          </div>
        ) : (
          <div className="cfj-route-candidates-body">
            <div className="cfj-route-candidate-list">
              {candidates.map((candidate) => (
                <button
                  type="button"
                  key={candidate.id}
                  className={`cfj-route-candidate-card ${candidate.id === selected?.id ? "is-selected" : ""} ${candidate.canApply ? "" : "is-invalid"}`}
                  onClick={() => onSelect(candidate.id)}
                >
                  <span className="cfj-route-candidate-copy">
                    <span className="cfj-route-candidate-title-row">
                      <strong>{candidate.label}</strong>
                      {candidate.isRecommended ? <em>{candidate.canApply ? "建議" : "最接近"}</em> : null}
                    </span>
                    <span className="cfj-route-candidate-metrics">
                      <span className="cfj-route-candidate-metric">
                        <small>路線</small>
                        <b>{Number(candidate.route.km || 0).toFixed(1)} km</b>
                      </span>
                      <span className="cfj-route-candidate-metric">
                        <small>時間</small>
                        <b>{formatRouteDurationMinutes(Number(candidate.route.min || 0) * 1.2)}</b>
                      </span>
                      <span
                        className={`cfj-route-candidate-metric cfj-route-candidate-metric-late ${
                          candidate.evaluation.hardLateCount + candidate.evaluation.softLateCount
                            ? "is-late"
                            : "is-on-time"
                        }`}
                      >
                        <small>遲到</small>
                        <b>
                          {candidate.evaluation.hardLateCount + candidate.evaluation.softLateCount
                            ? `${candidate.evaluation.hardLateCount + candidate.evaluation.softLateCount} 站 · ${Math.round(
                                candidate.evaluation.hardLateMinutes + candidate.evaluation.softLateMinutes,
                              )} 分鐘`
                            : "準時"}
                        </b>
                      </span>
                    </span>
                  </span>
                  <span className="cfj-route-candidate-radio" aria-hidden="true">
                    {candidate.id === selected?.id ? <Icon name="check" size={13} /> : null}
                  </span>
                </button>
              ))}
              {!candidates.length ? (
                <div className="cfj-route-candidates-empty">目前沒有可顯示的候選路線。</div>
              ) : null}
            </div>
            <div className="cfj-route-candidate-preview">
              {selected ? (
                <>
                  <div className="cfj-route-candidate-preview-head">
                    <div>
                      <small>目前預覽</small>
                      <strong>{selected.label}</strong>
                    </div>
                    
                  </div>
                  <RouteStationSequence candidate={selected} />
                  <RouteCandidateMap candidate={selected} />
                </>
              ) : (
                <div className="cfj-route-candidates-empty">選擇左側候選路線查看地圖。</div>
              )}
            </div>
          </div>
        )}
        <footer className="cfj-route-candidates-footer">
          <span>
            {selected
              ? selected.canApply
                ? `已選擇：${selected.label}`
                : selected.evaluation?.hardLateCount
                  ? "目前有硬性時間窗衝突，請調整開始時間或時間窗"
                  : selected.evaluation?.precedenceIssues?.length
                    ? "目前路線有取送資料衝突，不能套用"
                    : "目前路線尚未完成最終確認，不能套用"
              : "尚未選擇路線"}
          </span>
          <div>
            <button type="button" className="cfj-cargo-secondary-button" onClick={onCancel}>
              取消
            </button>
            <button
              type="button"
              className="cfj-cargo-primary-button"
              disabled={loading || !selected || !selected.canApply}
              onClick={onApply}
            >
              套用此路線
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default function CreateInternalFleetJob({
  user: propUser,
  account: propAccount,
  orgId: propOrgId = "",
  profileLoading = false,
  embedded = false,
  initialApplication = null,
  copyMode = false,
  initialCargoPlan = null,
  headerCollapsed = false,
  onBack,
  onCreated,
}) {
  const [authUser, setAuthUser] = useState(
    propUser || auth.currentUser || null,
  );
  const [footerReady, setFooterReady] = useState(false);
  const [authAccount, setAuthAccount] = useState(propAccount || null);
  const user = propUser || authUser;
  const account = propAccount || authAccount;
  // 新版資料結構：公司歸屬只使用 drivers/{uid}.orgId。
  const orgId = text(propOrgId);
  const editMode = !copyMode && Boolean(
    initialApplication?.applicationId || initialApplication?.id,
  );
  const savedVehicleAssignment = initialApplication?.vehicleAssignment || {};
  const savedAssetSnapshots = Array.isArray(initialApplication?.assetSnapshots)
    ? initialApplication.assetSnapshots
    : Array.isArray(savedVehicleAssignment.assetSnapshots)
      ? savedVehicleAssignment.assetSnapshots
      : [];
  const originalVehicleSnapshot = savedAssetSnapshots.find(
    (item) => text(item?.assetKind).toLowerCase() === "vehicle",
  );
  const originalVehicleId = text(
    savedVehicleAssignment.vehicleId ||
      savedVehicleAssignment.tractorId ||
      initialApplication?.vehicle?.id ||
      initialApplication?.vehicle?.assetId ||
      originalVehicleSnapshot?.assetId ||
      originalVehicleSnapshot?.id,
  );
  const originalAssignedAssetIds = new Set(
    uniqueIds([
      originalVehicleId,
      ...(Array.isArray(savedVehicleAssignment.assetIds)
        ? savedVehicleAssignment.assetIds
        : []),
      ...(Array.isArray(savedVehicleAssignment.trailerIds)
        ? savedVehicleAssignment.trailerIds
        : []),
      ...(Array.isArray(savedVehicleAssignment.containerIds)
        ? savedVehicleAssignment.containerIds
        : []),
      ...(Array.isArray(savedVehicleAssignment.equipmentIds)
        ? savedVehicleAssignment.equipmentIds
        : []),
      ...(Array.isArray(initialApplication?.vehicleSchedule?.assetIds)
        ? initialApplication.vehicleSchedule.assetIds
        : []),
      ...savedAssetSnapshots.map((item) => item?.assetId || item?.id),
      ...(Array.isArray(initialApplication?.trailerIds)
        ? initialApplication.trailerIds
        : []),
      ...(Array.isArray(initialApplication?.trailers)
        ? initialApplication.trailers.map((item) => item?.id || item?.assetId)
        : []),
      text(initialApplication?.trailer?.id || initialApplication?.trailer?.assetId),
    ]),
  );
  const startedStates = [
    "IN_PROGRESS",
    "ONGOING",
    "RUNNING",
    "ACTIVE",
    "APPROACHING",
    "ARRIVED",
    "PAUSED",
    "COMPLETED",
    "DONE",
    "CLOSED",
  ];
  const hasExecutionStarted = Boolean(
    initialApplication?.execution?.startedAt ||
      initialApplication?.execution?.completedAt ||
      startedStates.includes(
        text(initialApplication?.execution?.status).toUpperCase(),
      ) ||
      startedStates.includes(text(initialApplication?.status).toUpperCase()) ||
      (Array.isArray(initialApplication?.stops) &&
        initialApplication.stops.some(
          (stop) =>
            stop?.progress?.startedAt ||
            stop?.progress?.arrivedAt ||
            stop?.progress?.completedAt ||
            startedStates.includes(
              text(stop?.progress?.status).toUpperCase(),
            ),
        )),
  );
  const canEditVehicleSchedule = !editMode || !hasExecutionStarted;

  const [applicationName, setApplicationName] = useState("");
  const [workType, setWorkType] = useState(DEFAULT_CARGO_CATEGORY_ID);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [categoryChangeRequest, setCategoryChangeRequest] = useState(null);
  const [schedule, setSchedule] = useState(() => {
    const now = new Date();
    const later = new Date(now.getTime() + 60 * 60 * 1000);
    return {
      date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
      startTime: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
      hasRange: false,
      endTime: `${pad(later.getHours())}:${pad(later.getMinutes())}`,
    };
  });
  // 配送工作不再使用獨立取貨站；保留此內部旗標是為了相容既有路線最佳化資料。
  const [startPointEnabled, setStartPointEnabled] = useState(true);
  const [stops, setStops] = useState(() =>
    normalizeStops([
      createStop("start", [], "SRT"),
      createStop("end", [{ id: "SRT" }]),
    ]),
  );
  const [activeId, setActiveId] = useState("SRT");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [locationBusy, setLocationBusy] = useState(false);
  const [route, setRoute] = useState({
    path: [],
    km: null,
    min: null,
    key: "",
    loading: false,
    error: "",
  });
  const [routeEtaDurations, setRouteEtaDurations] = useState({});
  const [fitVersion, setFitVersion] = useState(0);
  const [mapCollapsed, setMapCollapsed] = useState(false);
  const [mapWidthPercent, setMapWidthPercent] = useState(30);
  const [expandedSections, setExpandedSections] = useState({
    work: true,
    route: false,
    drivers: false,
  });
  const [mapFocus, setMapFocus] = useState(null);
  const [expandedStopKeys, setExpandedStopKeys] = useState({});
  const [moveMenuStopId, setMoveMenuStopId] = useState("");
  const [expandedRecipientKeys, setExpandedRecipientKeys] = useState({});
  const [expandedCargoKeys, setExpandedCargoKeys] = useState({});
  const [assetPhotoPreviewUrls, setAssetPhotoPreviewUrls] = useState({});
  const [fleetData, setFleetData] = useState({
    members: [],
    assets: [],
    vehicles: [],
    trailers: [],
    containers: [],
    equipment: [],
  });
  const [vehicleBindings, setVehicleBindings] = useState({});
  const [commonContacts, setCommonContacts] = useState([]);
  const [commonAddresses, setCommonAddresses] = useState([]);
  const [commonPicker, setCommonPicker] = useState(null);
  const [commonSearch, setCommonSearch] = useState("");
  const [commonLoading, setCommonLoading] = useState(false);
  const [commonError, setCommonError] = useState("");
  const commonPageRef = useRef({ contacts: 0, addresses: 0 });
  const commonHasMore = useRef({ contacts: true, addresses: true });
  const commonLoadingRef = useRef(false);
  const [commonVisibleCount, setCommonVisibleCount] = useState({
    contacts: 120,
    addresses: 120,
  });
  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState("");
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [driverSearch, setDriverSearch] = useState("");
  const [assetPickerUid, setAssetPickerUid] = useState("");
  const [assetPickerTab, setAssetPickerTab] = useState("vehicle");
  const [assetPickerSearch, setAssetPickerSearch] = useState("");
  const [assetPickerTypeFilter, setAssetPickerTypeFilter] = useState("all");
  const [assetPickerStatusFilter, setAssetPickerStatusFilter] = useState("all");
  const [assetPickerDraft, setAssetPickerDraft] = useState(null);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [routePreview, setRoutePreview] = useState(null);
  const [routePreviewLoading, setRoutePreviewLoading] = useState(false);
  const [routePreviewError, setRoutePreviewError] = useState("");
  const [optimizationRecord, setOptimizationRecord] = useState(null);
  const routePreviewAbort = useRef(null);
  const optimizationInputKeyRef = useRef("");
  const preserveOptimizationRef = useRef(false);
  const skipNextRouteCalculationRef = useRef(false);
  const routeApiCacheRef = useRef(new Map());
  const [mapError, setMapError] = useState("");
  const [scheduleReview, setScheduleReview] = useState(null);
  const [emailTestState, setEmailTestState] = useState({
    status: "",
    message: "",
  });
  const [cargoDetailEditor, setCargoDetailEditor] = useState(null);
  const [deliveryDetailEditor, setDeliveryDetailEditor] = useState(null);
  const [cargoSourcePicker, setCargoSourcePicker] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importFileName, setImportFileName] = useState("");
  const [importErrors, setImportErrors] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const importFileRef = useRef(null);
  const [emailTemplateEditor, setEmailTemplateEditor] = useState(null);
  const mounted = useRef(true);
  const activeRef = useRef("SRT");
  const stopElementRefs = useRef({});
  const pendingStopFocusRef = useRef("");
  const locationVersion = useRef(0);
  const routeVersion = useRef(0);
  const searchTimer = useRef(null);
  const submitLock = useRef(false);
  const scheduleReviewConfirmed = useRef(false);
  const photoUrls = useRef(new Set());
  const hydratedEditId = useRef("");
  const editBaselineRef = useRef("");

  useEffect(() => {
    let active = true;
    const assetIds = Array.from(
      new Set(
        (stops || []).flatMap((stop) =>
          (stop.recipients || []).flatMap((recipient) =>
            recipientContents(recipient).flatMap((content) =>
              (content.photos || [])
                .filter((photo) => photo?.assetId && !photo.preview && !photo.url)
                .map((photo) => text(photo.assetId)),
            ),
          ),
        ),
      ),
    );
    Promise.all(
      assetIds.map(async (assetId) => {
        try {
          const url = await getDownloadURL(
            storageRef(getStorage(), `assets/${assetId}/preview`),
          );
          return [assetId, url];
        } catch (previewError) {
          console.warn("[CreateInternalFleetJob] photo preview:", assetId, previewError?.message);
          return [assetId, ""];
        }
      }),
    ).then((entries) => {
      if (active) {
        setAssetPhotoPreviewUrls((current) => ({
          ...current,
          ...Object.fromEntries(entries.filter(([, url]) => url)),
        }));
      }
    });
    return () => { active = false; };
  }, [stops]);

  useEffect(() => {
    const stopId = pendingStopFocusRef.current;
    if (!stopId) return undefined;
    const frame = requestAnimationFrame(() => {
      const element = stopElementRefs.current[stopId];
      if (!element) return;
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      pendingStopFocusRef.current = "";
    });
    return () => cancelAnimationFrame(frame);
  }, [stops]);

  useEffect(() => {
    const applicationId = text(
      initialApplication?.applicationId || initialApplication?.id,
    );
    const hydrationKey = `${copyMode ? "copy" : "edit"}:${applicationId}`;
    if (
      !initialApplication ||
      !applicationId ||
      hydratedEditId.current === hydrationKey
    )
      return;

    const sourceStops = sortStopsBySavedOrder(
      Array.isArray(initialApplication.stops)
        ? initialApplication.stops
        : [],
    )
      .slice(0, 2)
      .map((stop, index) => ({
        ...stop,
        id: index === 0 ? "SRT" : "END",
        order: index + 1,
        operationType: index === 0 ? "PICKUP" : "DELIVERY",
      }));
    const hydrationStops = copyMode
      ? resetCopiedJobExecutionData(sourceStops)
      : sourceStops;
    const restoredStops = normalizeStops(
      hydrationStops.map((stop) => ({
        ...stop,
        text: text(
          stop.text ||
            [stop.city, stop.suburb, stop.road, stop.houseNumber]
              .map(text)
              .filter(Boolean)
              .join(""),
        ),
        verified: stop.verified === true || coordsOK(stop),
        recipients: normalizeRecipients(stop),
        photos: [
          ...(!copyMode && Array.isArray(stop.proof?.photos) ? stop.proof.photos : []),
          ...(Array.isArray(stop.photos) ? stop.photos : []),
        ].map((photo) =>
          typeof photo === "string"
            ? { url: photo, preview: photo }
            : { ...photo, preview: photo.preview || photo.url },
        ),
      })),
    );
    const restoredSchedule = {
      date:
        inputDateValue(initialApplication.schedule?.startAt) ||
        text(initialApplication.dateKey),
      startTime: inputTimeValue(
        initialApplication.schedule?.startAt ||
          initialApplication.schedule?.startTime ||
          initialApplication.startTime,
      ),
      hasRange: initialApplication.schedule?.hasRange === true,
      endTime: inputTimeValue(
        initialApplication.schedule?.endAt ||
          initialApplication.schedule?.endTime ||
          initialApplication.endTime,
      ),
    };
    const driverUid = text(initialApplication.driver?.uid);
    const restoredSelectedMemberIds = !copyMode && driverUid ? [driverUid] : [];
    const savedAssignment = initialApplication.vehicleAssignment || {};
    const savedAssetSnapshots = Array.isArray(initialApplication.assetSnapshots) &&
      initialApplication.assetSnapshots.length
      ? initialApplication.assetSnapshots
      : Array.isArray(savedAssignment.assetSnapshots)
        ? savedAssignment.assetSnapshots
        : [];
    const savedVehicleSnapshot = savedAssetSnapshots.find(
      (item) => text(item?.assetKind).toLowerCase() === "vehicle",
    );
    const savedVehicleSnapshotId = savedVehicleSnapshot?.assetId || savedVehicleSnapshot?.id;
    const restoredAssignments = !copyMode && driverUid
      ? {
          [driverUid]: {
            vehicleId: text(
              savedAssignment.vehicleId ||
                savedAssignment.tractorId ||
                initialApplication.vehicle?.id ||
                initialApplication.vehicle?.assetId ||
                savedVehicleSnapshotId,
            ),
            trailerIds: uniqueIds([
              ...(Array.isArray(savedAssignment.trailerIds) ? savedAssignment.trailerIds : []),
              ...(Array.isArray(initialApplication.trailerIds) ? initialApplication.trailerIds : []),
              ...(Array.isArray(initialApplication.trailers) ? initialApplication.trailers.map((item) => item?.id || item?.assetId) : []),
              text(savedAssignment.trailerId),
              text(initialApplication.trailer?.id || initialApplication.trailer?.assetId),
              ...savedAssetSnapshots.filter((item) => text(item?.assetKind).toLowerCase() === "trailer").map((item) => item?.assetId || item?.id),
            ]),
            containerIds: uniqueIds([
              ...(Array.isArray(savedAssignment.containerIds) ? savedAssignment.containerIds : []),
              ...savedAssetSnapshots.filter((item) => text(item?.assetKind).toLowerCase() === "container").map((item) => item?.assetId || item?.id),
            ]),
            equipmentIds: uniqueIds([
              ...(Array.isArray(savedAssignment.equipmentIds) ? savedAssignment.equipmentIds : []),
              ...savedAssetSnapshots.filter((item) => text(item?.assetKind).toLowerCase() === "equipment").map((item) => item?.assetId || item?.id),
            ]),
          },
        }
      : {};

    if (restoredStops.length) {
      setStops(restoredStops);
      setStartPointEnabled(true);
      setActiveId(restoredStops[0].id);
      activeRef.current = restoredStops[0].id;
    }

    setApplicationName(copyMode ? "" : text(initialApplication.applicationName));
    setWorkType(
      text(initialApplication.workType).toUpperCase() === "INSPECTION"
        ? "INSPECTION"
        : categoryIdForWorkDomain(initialApplication.workDomain || "NONE"),
    );
    setSchedule(restoredSchedule);
    setSelectedMemberIds(restoredSelectedMemberIds);
    setAssignments(restoredAssignments);
    editBaselineRef.current = editableSnapshot({
      applicationName: text(initialApplication.applicationName),
      workType:
        text(initialApplication.workType).toUpperCase() === "INSPECTION"
          ? "INSPECTION"
          : categoryIdForWorkDomain(initialApplication.workDomain || "NONE"),
      schedule: restoredSchedule,
      startPointEnabled: true,
      stops: restoredStops,
      selectedMemberIds: restoredSelectedMemberIds,
      assignments: restoredAssignments,
    });
    hydratedEditId.current = hydrationKey;
  }, [initialApplication, copyMode]);

  useEffect(() => {
    if (editMode || initialCargoPlan?.mode !== "single") return;
    const sourceStops = Array.isArray(initialCargoPlan.stops) ? initialCargoPlan.stops : [];
    if (sourceStops.length !== 2) return;
    const nextStops = normalizeStops(sourceStops);
    if (nextStops.length !== 2 || !isStartStop(nextStops[0]) || !isEndStop(nextStops[1])) return;
    setStops(nextStops);
    setStartPointEnabled(true);
    setActiveId(nextStops[0].id);
    activeRef.current = nextStops[0].id;
    pendingStopFocusRef.current = nextStops[0].id;
    setRoute({ path: [], km: null, min: null, key: "", loading: false, error: "" });
    setRouteEtaDurations({});
    setOptimizationRecord(null);
    setError("");
    setMapError("");
    setFitVersion((value) => value + 1);
  }, [editMode, initialCargoPlan]);

  useEffect(() => {
    let alive = true;
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!alive) return;
      setAuthUser(firebaseUser || null);
      if (!firebaseUser?.uid) return setAuthAccount(null);
      try {
        const snapshot = await getDoc(doc(db, "drivers", firebaseUser.uid));
        if (alive)
          setAuthAccount(
            snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null,
          );
      } catch (authError) {
        console.error("[CreateInternalFleetJob] account:", authError);
        if (alive) setAuthAccount(null);
      }
    });
    return () => {
      alive = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      routePreviewAbort.current?.abort();
      if (searchTimer.current) clearTimeout(searchTimer.current);
      photoUrls.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    setFleetData({ members: [], assets: [], vehicles: [], trailers: [], containers: [], equipment: [] });
    setVehicleBindings({});
    if (!editMode) setAssignments({});
    setAssetPickerUid("");
    setAssetPickerDraft(null);
    setDataError("");
    if (!orgId) {
      setLoading(profileLoading);
      if (profileLoading) return undefined;
      setDataError("目前登入帳號沒有可用的 orgId，無法載入所屬資產摘要。");
      return undefined;
    }
    let alive = true;
    let driversReady = false;
    let assetsReady = false;
    let bindingsReady = false;
    setLoading(true);
    const finish = () => {
      if (alive && driversReady && assetsReady && bindingsReady) setLoading(false);
    };
    const unsubDrivers = onSnapshot(
      collection(db, "drivers"),
      (snapshot) => {
        if (!alive) return;
        const members = snapshot.docs
          .map((item, index) => ({ uid: item.id, ...item.data(), color: getDriverColor(index) }))
          .filter((item) => text(item.orgId) === orgId)
          .sort((a, b) => text(a.displayName || a.name).localeCompare(text(b.displayName || b.name), "zh-Hant"));
        setFleetData((previous) => ({ ...previous, members }));
        driversReady = true;
        finish();
      },
      (firestoreError) => {
        console.error("[CreateInternalFleetJob] drivers:", firestoreError);
        driversReady = true;
        if (alive) setDataError("無法讀取成員資料，請確認 Firestore 權限。");
        finish();
      },
    );
    const unsubBindings = onSnapshot(
      collection(db, "orgs", orgId, "vehicleBindings"),
      (snapshot) => {
        if (!alive) return;
        const nextBindings = {};
        snapshot.docs.forEach((item) => {
          const data = item.data() || {};
          nextBindings[item.id] = {
            vehicleId: item.id,
            trailerIds: uniqueIds(data.trailerIds),
            containerIds: uniqueIds(data.containerIds),
          };
        });
        setVehicleBindings(nextBindings);
        bindingsReady = true;
        finish();
      },
      (firestoreError) => {
        console.error("[CreateInternalFleetJob] vehicle bindings:", firestoreError);
        bindingsReady = true;
        if (alive) {
          setVehicleBindings({});
          setDataError("無法讀取新版車輛綁定資料，請確認 Firestore 權限後重試。");
        }
        finish();
      },
    );
    const loadAssetSummary = async () => {
      try {
        const directory = await readVehicleDirectory(db, orgId);
        if (!alive) return;
        const assets = normalizeFleetAssetSummaryItems(directory?.items || []);
        setFleetData((previous) => ({
          ...previous,
          assets,
          vehicles: assets.filter((item) => item.assetKind === "vehicle"),
          trailers: assets.filter((item) => item.assetKind === "trailer"),
          containers: assets.filter((item) => item.assetKind === "container"),
          equipment: assets.filter((item) => item.assetKind === "equipment"),
        }));
      } catch (firestoreError) {
        console.error("[CreateInternalFleetJob] asset summary:", firestoreError);
        if (alive) setDataError("無法讀取新版資產摘要，請確認資產目錄與 Firestore 權限。");
      } finally {
        assetsReady = true;
        finish();
      }
    };
    loadAssetSummary();
    return () => {
      alive = false;
      unsubDrivers();
      unsubBindings();
    };
  }, [editMode, orgId, profileLoading]);

  useEffect(() => {
    setCommonContacts([]);
    setCommonAddresses([]);
    setCommonPicker(null);
    setCommonSearch("");
    setCommonError("");
    commonPageRef.current = { contacts: 0, addresses: 0 };
    commonHasMore.current = { contacts: true, addresses: true };
    setCommonVisibleCount({ contacts: 120, addresses: 120 });
    commonLoadingRef.current = false;
  }, [orgId]);

  const loadCommonPage = async (key, requestedPage = null) => {
    if (!orgId || commonLoadingRef.current || !commonHasMore.current[key])
      return;
    const pageNo = requestedPage || commonPageRef.current[key] + 1;
    if (pageNo <= commonPageRef.current[key]) return;
    commonLoadingRef.current = true;
    setCommonLoading(true);
    setCommonError("");
    try {
      const config = directoryConfig(key);
      const snapshot = await getDoc(
        doc(db, "orgs", orgId, config.collection, directoryPageId(pageNo)),
      );
      const page = hydrateDirectoryPage(snapshot, pageNo);
      if (!page.exists) {
        commonHasMore.current = { ...commonHasMore.current, [key]: false };
        return;
      }
      const setter =
        key === "contacts" ? setCommonContacts : setCommonAddresses;
      setter((current) => sortDirectoryItems([...current, ...page.items]));
      commonPageRef.current = { ...commonPageRef.current, [key]: pageNo };
      commonHasMore.current = {
        ...commonHasMore.current,
        [key]: page.items.length >= config.pageCapacity,
      };
      setCommonVisibleCount((current) => ({
        ...current,
        [key]: Math.max(current[key], 120),
      }));
    } catch (loadError) {
      console.error("[CreateInternalFleetJob] common data:", loadError);
      setCommonError("常用資料讀取失敗，請確認目前帳號的組織權限。");
    } finally {
      commonLoadingRef.current = false;
      setCommonLoading(false);
    }
  };

  const openCommonPicker = async (
    type,
    stopId,
    recipientId = "",
    target = "recipient",
    contentId = "",
  ) => {
    const key = type === "contact" ? "contacts" : "addresses";
    if (!orgId) {
      if (profileLoading) return;
      setError("目前登入帳號尚未取得 orgId，無法讀取常用資料。");
      return;
    }
    setCommonPicker({ type, stopId, recipientId, target, contentId });
    setCommonSearch("");
    setCommonError("");
    if (commonPageRef.current[key] === 0) await loadCommonPage(key, 1);
  };

  const loadMoreCommonItems = (key) => {
    const items = key === "contacts" ? commonContacts : commonAddresses;
    if (commonVisibleCount[key] < items.length) {
      setCommonVisibleCount((current) => ({
        ...current,
        [key]: current[key] + 120,
      }));
      return;
    }
    if (commonHasMore.current[key]) loadCommonPage(key);
  };

  const commonKey = commonPicker?.type === "contact" ? "contacts" : "addresses";
  const commonItems =
    commonPicker?.type === "contact" ? commonContacts : commonAddresses;
  const visibleCommonItems = commonItems.slice(
    0,
    commonVisibleCount[commonKey],
  );
  const filteredCommonItems = useMemo(() => {
    const query = text(commonSearch).toLowerCase();
    if (!query) return visibleCommonItems;
    return visibleCommonItems.filter((item) =>
      [
        item.name,
        item.displayName,
        item.company,
        item.phone,
        item.email,
        item.address,
        item.addressText,
      ]
        .map(text)
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [commonSearch, visibleCommonItems]);

  const routeKey = useMemo(
    () =>
      JSON.stringify([
        startPointEnabled,
        stops.map((stop) => [stop.id, stop.verified, stop.lat, stop.lng]),
      ]),
    [startPointEnabled, stops],
  );

  const optimizationInputKey = useMemo(
    () => routePlanningInputKey(stops, schedule, startPointEnabled),
    [stops, schedule, startPointEnabled],
  );

  useEffect(() => {
    if (!optimizationInputKeyRef.current) {
      optimizationInputKeyRef.current = optimizationInputKey;
      return;
    }
    if (optimizationInputKeyRef.current === optimizationInputKey) return;
    const appliedRouteUpdate = preserveOptimizationRef.current;
    preserveOptimizationRef.current = false;
    optimizationInputKeyRef.current = optimizationInputKey;
    if (appliedRouteUpdate) {
      setOptimizationRecord((previous) =>
        previous ? { ...previous, inputKey: optimizationInputKey } : previous,
      );
      return;
    }
    routePreviewAbort.current?.abort();
    setOptimizationRecord(null);
    setRoutePreview(null);
    setRoutePreviewError("路線資料已變更，請重新最佳化路線。");
  }, [optimizationInputKey]);

  const activeStop = stops.find((stop) => stop.id === activeId) || stops[0];
  const isInspectionMode = workType === "INSPECTION";
  const cargoCategoryId = isInspectionMode
    ? DEFAULT_CARGO_CATEGORY_ID
    : normalizeCargoCategory(workType);
  const deliveryStops = hasExplicitStartStop(stops) ? stops.slice(1) : stops;
  const stopsRequiringRecipients = stops.filter(
    (stop) => !isStartStop(stop) || (stop.recipients || []).length > 0,
  );
  const visibleStops = startPointEnabled
    ? stops
    : stops.filter((stop) => !isStartStop(stop));
  const targetStops = startPointEnabled ? stops : deliveryStops;
  const routeReady =
    targetStops.length >= 2 &&
    targetStops.every((stop) => stop.verified && coordsOK(stop));
  const routeValid =
    route.key === routeKey &&
    !route.loading &&
    Number(route.km) > 0 &&
    Number(route.min) > 0 &&
    routeReady;

  useEffect(() => {
    if (editMode || isInspectionMode || initialCargoPlan?.mode !== "single" || !orgId)
      return undefined;
    const sourceStops = Array.isArray(initialCargoPlan.stops)
      ? initialCargoPlan.stops
      : [];
    const nextStops = normalizeStops(sourceStops);
    if (
      nextStops.length !== 2 ||
      !isStartStop(nextStops[0]) ||
      !isEndStop(nextStops[1])
    )
      return undefined;

    const stopAddress = (stop) =>
      text(
        stop?.text ||
          stop?.addressText ||
          stop?.address ||
          [stop?.city, stop?.suburb, stop?.road, stop?.houseNumber]
            .map(text)
            .filter(Boolean)
            .join(""),
      );
    const unresolvedStops = nextStops.filter(
      (stop) => !coordsOK(stop) && normalizeAddressMatchKey(stopAddress(stop)),
    );
    if (!unresolvedStops.length) return undefined;

    const controller = new AbortController();
    let active = true;
    const resolveCargoPlanAddresses = async () => {
      let commonItems = [];
      try {
        commonItems = await loadCommonAddressDirectoryItems(orgId);
      } catch (loadError) {
        console.warn(
          "[CreateInternalFleetJob] common addresses for cargo plan:",
          loadError,
        );
      }
      if (!active || controller.signal.aborted) return;

      const commonByKey = new Map();
      commonItems.forEach((item) => {
        const key = normalizeAddressMatchKey(commonAddressText(item));
        if (key && !commonByKey.has(key)) commonByKey.set(key, item);
      });
      const uniqueQueries = new Map();
      unresolvedStops.forEach((stop) => {
        const address = stopAddress(stop);
        const key = normalizeAddressMatchKey(address);
        if (key && !uniqueQueries.has(key)) uniqueQueries.set(key, { key, address });
      });

      const results = await Promise.all(
        Array.from(uniqueQueries.values()).map(async ({ key, address }) => {
          const saved = commonByKey.get(key);
          const savedCoords = commonAddressCoords(saved);
          if (saved && savedCoords) {
            return {
              key,
              location: {
                text: address,
                ...savedCoords,
                postalCode:
                  text(saved.postalCode) ||
                  splitTaiwanPostalPrefix(address).postalCode,
                city: text(saved.city),
                suburb: text(saved.suburb),
                road: text(saved.road),
                houseNumber: text(saved.houseNumber),
                verified: true,
              },
            };
          }
          try {
            const hereResult = await requestHereGeocode(
              address,
              orgId,
              controller.signal,
            );
            if (!coordsOK(hereResult)) return { key, location: null };
            const parsed = splitTaiwanPostalPrefix(
              text(hereResult.address) || address,
            );
            return {
              key,
              location: {
                text: address,
                lat: Number(hereResult.lat),
                lng: Number(hereResult.lng),
                postalCode:
                  text(hereResult.postalCode) ||
                  parsed.postalCode ||
                  splitTaiwanPostalPrefix(address).postalCode,
                city: text(hereResult.city),
                suburb: text(hereResult.suburb),
                road: text(hereResult.road),
                houseNumber: text(hereResult.houseNumber),
                verified: true,
              },
            };
          } catch (locateError) {
            if (locateError?.name === "AbortError") throw locateError;
            console.warn(
              "[CreateInternalFleetJob] HERE cargo address:",
              locateError,
            );
            return { key, location: null };
          }
        }),
      );
      if (!active || controller.signal.aborted) return;

      const locationsByKey = new Map(
        results
          .filter((item) => item.location)
          .map((item) => [item.key, item.location]),
      );
      const updatesByStopId = new Map();
      unresolvedStops.forEach((stop) => {
        const key = normalizeAddressMatchKey(stopAddress(stop));
        const location = locationsByKey.get(key);
        if (location) updatesByStopId.set(stop.id, { key, location });
      });

      if (updatesByStopId.size) {
        setStops((current) =>
          normalizeStops(
            current.map((stop) => {
              const update = updatesByStopId.get(stop.id);
              if (
                !update ||
                coordsOK(stop) ||
                normalizeAddressMatchKey(stopAddress(stop)) !== update.key
              )
                return stop;
              return { ...stop, ...update.location };
            }),
          ),
        );
        setFitVersion((value) => value + 1);
      }

      const unresolvedCount = unresolvedStops.filter(
        (stop) => !locationsByKey.has(normalizeAddressMatchKey(stopAddress(stop))),
      ).length;
      setMapError(
        unresolvedCount
          ? `已帶入地址，但有 ${unresolvedCount} 個站點無法定位；請確認地址或在地圖上標記。`
          : "",
      );
    };

    resolveCargoPlanAddresses().catch((locateError) => {
      if (!active || locateError?.name === "AbortError") return;
      console.error("[CreateInternalFleetJob] cargo address lookup:", locateError);
      setMapError("帶入的地址暫時無法定位，請稍後重試或在地圖上標記。");
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [editMode, isInspectionMode, initialCargoPlan, orgId]);

  const selectedIds = selectedMemberIds;
  const assetById = useMemo(
    () => Object.fromEntries(fleetData.assets.map((asset) => [asset.id, asset])),
    [fleetData.assets],
  );
  const bindingOwnerByAssetId = useMemo(() => {
    const ownerByAssetId = {};
    Object.entries(vehicleBindings).forEach(([vehicleId, binding]) => {
      const ids = vehicleBindingAssetIds(binding);
      [...ids.trailerIds, ...ids.containerIds].forEach((assetId) => {
        if (!ownerByAssetId[assetId]) ownerByAssetId[assetId] = vehicleId;
      });
    });
    return ownerByAssetId;
  }, [vehicleBindings]);
  const getBoundAssetsForVehicle = (vehicleId) => {
    if (!vehicleId) return { trailerIds: [], containerIds: [] };
    const bound = vehicleBindingAssetIds(vehicleBindings[vehicleId]);
    if (editMode && text(vehicleId) === originalVehicleId) {
      return {
        trailerIds: bound.trailerIds.filter((assetId) =>
          originalAssignedAssetIds.has(assetId),
        ),
        containerIds: bound.containerIds.filter((assetId) =>
          originalAssignedAssetIds.has(assetId),
        ),
      };
    }
    return bound;
  };
  const getEffectiveAssignmentAssetIds = (assignment) => {
    const bound = getBoundAssetsForVehicle(text(assignment?.vehicleId));
    return uniqueIds([
      ...assignmentAssetIds(assignment),
      ...bound.trailerIds,
      ...bound.containerIds,
    ]);
  };
  const getEffectiveAssignmentAssets = (assignment) =>
    getEffectiveAssignmentAssetIds(assignment)
      .map((assetId) => assetById[assetId])
      .filter(Boolean);
  const visibleMembers = useMemo(() => {
    const queryText = text(driverSearch).toLowerCase();
    if (!queryText) return fleetData.members;
    return fleetData.members.filter((member) =>
      `${member.displayName || member.name || ""} ${member.phone || ""}`
        .toLowerCase()
        .includes(queryText),
    );
  }, [fleetData.members, driverSearch]);
  const editCurrentSnapshot = useMemo(
    () =>
      editableSnapshot({
        applicationName,
        workType,
        schedule,
        startPointEnabled,
        stops,
        selectedMemberIds,
        assignments,
      }),
    [
      applicationName,
      workType,
      schedule,
      startPointEnabled,
      stops,
      selectedMemberIds,
      assignments,
    ],
  );
  const editDirty =
    editMode &&
    Boolean(editBaselineRef.current) &&
    editCurrentSnapshot !== editBaselineRef.current;

  const applyCategoryChange = (nextWorkType) => {
    if (nextWorkType === "INSPECTION") {
      setStops((previous) =>
        normalizeStops(
          previous.slice(0, 2).map((stop, index) => ({
            ...stop,
            id: index === 0 ? "SRT" : "END",
            order: index + 1,
            recipients: [],
          })),
        ),
      );
    } else {
      setStops((previous) =>
        normalizeStops(
          previous.map((stop) => ({
            ...stop,
            recipients: (stop.recipients || []).map((recipient) => ({
              ...recipient,
              domainDetails: undefined,
              typeDetails: undefined,
            })),
          })),
        ),
      );
    }
    setWorkType(nextWorkType);
    setCategoryChangeRequest(null);
    setCategoryPickerOpen(false);
  };

  const requestCategoryChange = (nextWorkType) => {
    if (nextWorkType === workType) return;
    setCategoryChangeRequest({ nextWorkType });
  };

  const canUseAsset = (asset, uid) => {
    const permitted = Array.isArray(asset?.permittedDriverUids)
      ? asset.permittedDriverUids.map(text).filter(Boolean)
      : [];
    return permitted.length === 0 || permitted.includes(uid);
  };

  const exitEditor = () => {
    if (editDirty && !window.confirm("目前有尚未儲存的修改，確定要退出嗎？"))
      return;
    onBack?.();
  };

  const focusStop = (id) => {
    if (locationBusy || !stops.some((stop) => stop.id === id)) return;
    activeRef.current = id;
    setActiveId(id);
    setSuggestions([]);
    setMapError("");
  };

  const updateAddress = (id, value) => {
    if (locationBusy) return;
    const parsedAddress = splitTaiwanPostalPrefix(value);
    locationVersion.current += 1;
    activeRef.current = id;
    setActiveId(id);
    setMapError("");
    setStops((previous) =>
      previous.map((stop) =>
        stop.id === id
          ? {
              ...stop,
              text: parsedAddress.address,
              lat: null,
              lng: null,
              city: "",
              suburb: "",
              road: "",
              houseNumber: "",
              postalCode: parsedAddress.postalCode,
              verified: false,
              distanceFromPreviousKm: isStartStop(stop) ? 0 : null,
              durationFromPreviousMinutes: isStartStop(stop) ? 0 : null,
            }
          : stop,
      ),
    );
  };

  const applyLocation = async (id, lat, lng, addressData = null) => {
    if (!coordsOK({ lat, lng })) return;
    const version = ++locationVersion.current;

    const controller = new AbortController();
    setLocationBusy(true);
    setMapError("");
    setStops((previous) =>
      previous.map((stop) =>
        stop.id === id
          ? {
              ...stop,
              lat: null,
              lng: null,
              text: "正在取得地址…",
              city: "",
              suburb: "",
              road: "",
              houseNumber: "",
              postalCode: "",
              verified: false,
            }
          : stop,
      ),
    );
    try {
      let value;
      if (addressData) {
        value = addressData;
      } else {
        const fallback = await reverseGeocode(lat, lng, controller.signal);
        value = await reverseGeocodeWithHere(
          lat,
          lng,
          orgId,
          controller.signal,
          fallback,
        );
      }
      if (
        !mounted.current ||
        version !== locationVersion.current ||
        !value?.text
      )
        throw new Error("找不到地址");

      setStops((previous) =>
        normalizeStops(
          previous.map((stop) =>
            stop.id === id
              ? {
                  ...stop,
                  ...value,
                  lat: Number(value.lat),
                  lng: Number(value.lng),
                  verified: true,
                }
              : stop,
          ),
        ),
      );
      setActiveId(id);
      activeRef.current = id;
      setSuggestions([]);
      setMapFocus({
        lat: Number(value.lat),
        lng: Number(value.lng),
        version,
      });
    } catch (locationError) {
      if (locationError?.name !== "AbortError") {
        if (!addressData && coordsOK({ lat, lng })) {
          const fallbackText = `地圖選取位置 ${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
          setStops((previous) =>
            previous.map((stop) =>
              stop.id === id
                ? {
                    ...stop,
                    lat: Number(lat),
                    lng: Number(lng),
                    text: fallbackText,
                    city: "",
                    suburb: "",
                    road: "",
                    houseNumber: "",
                    verified: true,
                  }
                : stop,
            ),
          );
          setMapFocus({
            lat: Number(lat),
            lng: Number(lng),
            version,
          });
          setMapError("地圖上查無此點，已保留你點選的附近位置。");
        } else {
          setStops((previous) =>
            previous.map((stop) =>
              stop.id === id
                ? {
                    ...stop,
                    lat: null,
                    lng: null,
                    text: stop.text === "正在取得地址…" ? "" : stop.text,
                    city: "",
                    suburb: "",
                    road: "",
                    houseNumber: "",
                    verified: false,
                  }
                : stop,
            ),
          );
          setMapError("找不到這個地址，請在地圖上標記鄰近位置後再試。");
        }
      }
    } finally {
      if (mounted.current && version === locationVersion.current)
        setLocationBusy(false);
    }
  };

  const locate = async (
    id = activeRef.current,
    input = null,
    suggestion = null,
  ) => {
    if (locationBusy) {
      return;
    }
    const inputAddress = splitTaiwanPostalPrefix(input);
    const queryText = inputAddress.address;
    if (!suggestion && !queryText) return;

    setLocationBusy(true);
    setSuggestions([]);
    setStops((previous) =>
      previous.map((stop) =>
        stop.id === id
          ? {
              ...stop,
              lat: null,
              lng: null,
              text: "正在取得地址…",
              city: "",
              suburb: "",
              road: "",
              houseNumber: "",
              postalCode: inputAddress.postalCode,
              verified: false,
            }
          : stop,
      ),
    );
    try {
      if (suggestion?.normalized) {
        const enriched = await enrichLocationWithHere(
          suggestion.normalized,
          suggestion.display || suggestion.normalized.text,
          orgId,
          undefined,
        );
        if (inputAddress.postalCode) {
          enriched.postalCode = inputAddress.postalCode;
        }
        return await applyLocation(id, enriched.lat, enriched.lng, enriched);
      }
      const results = await searchAddress(queryText, undefined, 1);

      if (!results[0]?.normalized) {
        const hereResult = await requestHereGeocode(
          queryText,
          orgId,
          undefined,
        );
        if (!hereResult || !coordsOK(hereResult)) {
          throw new Error("找不到地址");
        }
        await applyLocation(id, hereResult.lat, hereResult.lng, {
          text: splitTaiwanPostalPrefix(hereResult.address || queryText)
            .address,
          lat: Number(hereResult.lat),
          lng: Number(hereResult.lng),
          city: "",
          suburb: "",
          road: "",
          houseNumber: "",
          postalCode: inputAddress.postalCode || text(hereResult.postalCode),
        });
      } else {
        const enriched = await enrichLocationWithHere(
          results[0].normalized,
          queryText,
          orgId,
          undefined,
        );
        if (inputAddress.postalCode) {
          enriched.postalCode = inputAddress.postalCode;
        }
        await applyLocation(id, enriched.lat, enriched.lng, enriched);
      }
    } catch (locateError) {
      setStops((previous) =>
        previous.map((stop) =>
          stop.id === id
            ? {
                ...stop,
                lat: null,
                lng: null,
                text: stop.text === "正在取得地址…" ? "" : stop.text,
                city: "",
                suburb: "",
                road: "",
                houseNumber: "",
                postalCode: "",
                verified: false,
              }
            : stop,
        ),
      );
      setLocationBusy(false);
      setMapError("找不到這個地址，請在地圖上標記鄰近位置後再試。");
    }
  };

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    setSuggestions([]);
    if (!activeStop || activeStop.verified || text(activeStop.text).length < 3)
      return undefined;
    const controller = new AbortController();
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const result = await searchAddress(
          activeStop.text,
          controller.signal,
          5,
        );
        if (!controller.signal.aborted && activeRef.current === activeStop.id)
          setSuggestions(result);
      } catch (searchError) {
        if (searchError?.name !== "AbortError")
          setMapError("地址搜尋暫時無法使用，可直接點選地圖。");
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, 650);
    return () => {
      controller.abort();
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [activeStop?.id, activeStop?.text, activeStop?.verified]);

  const handleMapPick = ({ lat, lng, stopId }) => {
    const target = stopId || activeRef.current || "SRT";

    if (locationBusy) return;
    focusStop(target);
    applyLocation(target, lat, lng);
  };

  async function calculateRoute(customStops = null) {
    const allStops = normalizeStops(customStops || stops);
    const routeStops = startPointEnabled
      ? allStops
      : hasExplicitStartStop(allStops)
        ? allStops.slice(1)
        : allStops;
    if (routeStops.length < 2 || routeStops.some((stop) => !coordsOK(stop))) {
      routeVersion.current += 1;
      setRouteEtaDurations({});
      setRoute({
        path: [],
        km: null,
        min: null,
        key: routeKey,
        loading: false,
        error: "",
      });
      setStops((previous) =>
        normalizeStops(previous).map((stop, index) => ({
          ...stop,
          distanceFromPreviousKm: index === 0 ? 0 : null,
          durationFromPreviousMinutes: index === 0 ? 0 : null,
        })),
      );
      return;
    }
    const requestVersion = ++routeVersion.current;
    const controller = new AbortController();
    setRouteEtaDurations({});
    setRoute((previous) => ({ ...previous, loading: true, error: "" }));
    try {
      const result = await fetchRoute(routeStops, controller.signal);
      if (!mounted.current || requestVersion !== routeVersion.current) return;
      setRoute({
        path: result.path,
        km: result.km,
        min: result.min,
        key: routeKey,
        loading: false,
        error: "",
      });
      setRouteEtaDurations(routeEtaDurationsFromStops(result.stops));
      if (startPointEnabled) {
        setStops((current) => {
          const byId = new Map(result.stops.map((stop) => [stop.id, stop]));
          const routed = current.map((stop, index) => ({
            ...stop,
            ...(byId.get(stop.id) || {}),
            order: index + 1,
          }));
          return normalizeStops(preserveDriverExecutionData(routed, current));
        });
      } else {
        const byId = new Map(result.stops.map((stop) => [stop.id, stop]));
        setStops((current) =>
          normalizeStops(
            current.map((stop, index) =>
              index === 0
                ? {
                    ...stop,
                    distanceFromPreviousKm: 0,
                    durationFromPreviousMinutes: 0,
                  }
                : {
                    ...stop,
                    ...(byId.get(stop.id) || {}),
                    distanceFromPreviousKm:
                      index === 1
                        ? 0
                        : (byId.get(stop.id)?.distanceFromPreviousKm ?? null),
                    durationFromPreviousMinutes:
                      index === 1
                        ? 0
                        : (byId.get(stop.id)?.durationFromPreviousMinutes ??
                          null),
                  },
            ),
          ),
        );
      }
      setFitVersion((value) => value + 1);
    } catch (routeError) {
      if (requestVersion === routeVersion.current) {
        setRouteEtaDurations({});
        setRoute({
          path: [],
          km: null,
          min: null,
          key: routeKey,
          loading: false,
          error: "無法計算行車路線，請確認所有站點已定位。",
        });
      }
    }
  }

  useEffect(() => {
    if (skipNextRouteCalculationRef.current) {
      skipNextRouteCalculationRef.current = false;
      return undefined;
    }
    setRouteEtaDurations({});
    const timer = setTimeout(() => calculateRoute(), 250);
    return () => clearTimeout(timer);
  }, [routeKey]);

  const updateImportPreview = (updater) => {
    setImportPreview((previous) => {
      if (!previous) return previous;
      const next = typeof updater === "function" ? updater(previous) : updater;
      setImportErrors(validateImportPreview(next));
      return next;
    });
  };
  const updateImportStation = (stationIndex, field, value) =>
    updateImportPreview((previous) =>
      previous.map((station, index) =>
        index === stationIndex ? { ...station, [field]: value } : station,
      ),
    );
  const updateImportRecipient = (stationIndex, recipientIndex, field, value) =>
    updateImportPreview((previous) =>
      previous.map((station, index) =>
        index !== stationIndex
          ? station
          : {
              ...station,
              recipients: station.recipients.map((recipient, currentIndex) =>
                currentIndex === recipientIndex
                  ? { ...recipient, [field]: value }
                  : recipient,
              ),
            },
      ),
    );
  const updateImportCargo = (
    stationIndex,
    recipientIndex,
    cargoIndex,
    field,
    value,
  ) =>
    updateImportPreview((previous) =>
      previous.map((station, stationCursor) =>
        stationCursor !== stationIndex
          ? station
          : {
              ...station,
              recipients: station.recipients.map((recipient, recipientCursor) =>
                recipientCursor !== recipientIndex
                  ? recipient
                  : {
                      ...recipient,
                      contents: recipientContents(recipient).map((item, itemCursor) =>
                        itemCursor === cargoIndex
                          ? { ...item, [field]: value }
                          : item,
                      ),
                    },
              ),
            },
      ),
    );
  const addImportRecipient = (stationIndex) =>
    updateImportPreview((previous) =>
      previous.map((station, index) =>
        index !== stationIndex
          ? station
              : {
                  ...station,
                  recipients: [
                    {
                      id: `收件人-${makeRandomId().slice(0, 6)}`,
                      name: "",
                  company: "",
                  phone: "",
                      email: "",
                      contents: [],
                    },
                    ...station.recipients,
                  ],
            },
      ),
    );
  const removeImportRecipient = (stationIndex, recipientIndex) =>
    updateImportPreview((previous) =>
      previous.map((station, index) =>
        index !== stationIndex
          ? station
          : {
              ...station,
              recipients: station.recipients.filter(
                (_, currentIndex) => currentIndex !== recipientIndex,
              ),
            },
      ),
    );
  const addImportCargo = (stationIndex, recipientIndex) => {
    updateImportPreview(function (previous) {
      return previous.map(function (station, stationCursor) {
        if (stationCursor !== stationIndex) return station;
        return {
          ...station,
          recipients: station.recipients.map(function (
            recipient,
            recipientCursor,
          ) {
            if (recipientCursor !== recipientIndex) return recipient;
            return {
              ...recipient,
              contents: [
                {
                  id: `import-${makeRandomId().slice(0, 8)}`,
                  operationType: "DELIVERY",
                  name: "",
                  quantity: "",
                  unit: "件",
                  note: "",
                },
                ...recipientContents(recipient),
              ],
            };
          }),
        };
      });
    });
  };
  const removeImportCargo = (stationIndex, recipientIndex, cargoIndex) =>
    updateImportPreview((previous) =>
      previous.map((station, stationCursor) =>
        stationCursor !== stationIndex
          ? station
          : {
              ...station,
              recipients: station.recipients.map((recipient, recipientCursor) =>
                recipientCursor !== recipientIndex
                  ? recipient
                  : {
                      ...recipient,
                      contents: recipientContents(recipient).filter(
                        (_, currentIndex) => currentIndex !== cargoIndex,
                      ),
                    },
              ),
            },
      ),
    );
  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const emptyPreview = createEmptyImportPreview();
    setImportFileName(file.name);
    setImportPreview(emptyPreview);
    setImportErrors(["正在讀取匯入檔案…"]);
    setImportLoading(true);
    setError("");
    try {
      const extension = file.name.toLowerCase().split(".").pop();
      const source = extension === "csv" ? await file.text() : await file.arrayBuffer();
      const parsedRows = parseFleetImportRows(importRowsFromFile(file, source));
      const draft = buildImportPreview(parsedRows);
      setImportPreview(draft);
      setImportErrors(validateImportPreview(draft));
    } catch (importError) {
      console.error("[CreateInternalFleetJob] import:", importError);
      setImportErrors([importError?.message || "檔案解析失敗，請確認欄位格式。"]);
      setError("");
    } finally {
      setImportLoading(false);
    }
  };
  const applyImportPreview = async () => {
    if (!importPreview) return;
    if (importErrors.length) return;
    if (importPreview.length !== 1) {
      setImportErrors([
        "匯入失敗：單點工作只能匯入一個送貨站地址。請刪除多餘站點後再確認。",
      ]);
      setProgress("");
      setImportLoading(false);
      return;
    }
    const importedStops = importPreview.map((station, stationIndex) => {
      const stopId = "END";
      const stop = {
        ...createStop("end", [], stopId),
        id: stopId,
        order: stationIndex + 1,
        operationType: "DELIVERY",
        text: text(station.address),
        postalCode: splitTaiwanPostalPrefix(station.address).postalCode,
        verified: false,
        lat: null,
        lng: null,
        timeWindowType: station.timeWindowType,
        earliestStart: text(station.earliestStart),
        latestStart: text(station.latestStart),
        serviceMinutes: Number(station.serviceMinutes) || 0,
        recipients: station.recipients.map((recipient) => {
          const recipientId = text(recipient.id) || makeRandomId();
          const operationType = normalizeCargoOperation(
            recipient.operationType || recipientContents(recipient)[0]?.operationType,
            "DELIVERY",
          );
          return {
            id: recipientId,
            recipientId,
            cargoNumber: text(recipient.cargoNumber),
            operationType,
            name: text(recipient.name),
            company: text(recipient.company),
            phone: text(recipient.phone),
            email: text(recipient.email),
            notificationSettings: normalizeRecipientNotificationSettings(),
            contents: recipientContents(recipient).map((item) => ({
              ...createCargoItem(operationType),
              id: makeRandomId(),
              recipientId,
              operationType,
              name: text(item.name),
              quantity: item.quantity,
              unit: text(item.unit) || "件",
              note: text(item.note),
              status: "",
            })),
          };
        }),
        photos: [],
      };
      return stop;
    });
    if (importedStops.length !== 1) {
      setImportErrors([
        "匯入失敗：單點工作只能匯入一個送貨站地址。請刪除多餘站點後再確認。",
      ]);
      setProgress("");
      setImportLoading(false);
      return;
    }
    const importedEnd = {
      ...importedStops[0],
      id: "END",
      order: 2,
      operationType: "DELIVERY",
    };
    const nextStops = [createStop("start", [], "SRT"), importedEnd];
    const normalizedStops = normalizeStops(nextStops);
    setStops(normalizedStops);
    setStartPointEnabled(true);
    setActiveId(normalizedStops.find((stop) => stop.id !== "SRT")?.id || "SRT");
    activeRef.current = normalizedStops.find((stop) => stop.id !== "SRT")?.id || "SRT";
    setRoute({ path: [], km: null, min: null, key: "", loading: false, error: "" });
    setFitVersion((value) => value + 1);
    setImportPreview(null);
    setImportFileName("");
    setImportErrors([]);
    setError("");
    setImportLoading(true);
    const stopsToLocate = normalizedStops.filter((stop) => text(stop.text));
    for (let index = 0; index < stopsToLocate.length; index += 1) {
      const stop = stopsToLocate[index];
      setProgress(`正在定位匯入配送站 ${index + 1}/${stopsToLocate.length}…`);
      await locate(stop.id, stop.text);
    }
    setImportLoading(false);
    setProgress("");
  };

  const addStop = () => {
    if (stops.length >= 2) return;
    setStops((previous) => {
      const nextStop = createStop("end", previous);
      const insertAt = previous.length;
      const next = normalizeStops([
        ...previous.slice(0, insertAt),
        nextStop,
        ...previous.slice(insertAt),
      ]);
      pendingStopFocusRef.current = nextStop.id;
      setActiveId(nextStop.id);
      activeRef.current = nextStop.id;
      setExpandedStopKeys((current) => ({
        ...current,
        [nextStop.id]: true,
      }));
      setRoute((value) => ({
        ...value,
        path: [],
        km: null,
        min: null,
        key: "",
      }));
      return next;
    });
  };

  const moveStopToOrder = (stopId, targetOrder) => {
    const currentStops = normalizeStops(stops);
    const movingIndex = currentStops.findIndex((stop) => stop.id === stopId);
    const targetIndex = Number(targetOrder);
    if (
      movingIndex <= 0 ||
      currentStops[movingIndex]?.id === "END" ||
      !Number.isInteger(targetIndex) ||
      targetIndex < 1 ||
      targetIndex >= currentStops.length ||
      targetIndex === movingIndex
    ) {
      setMoveMenuStopId("");
      return;
    }

    const movingStop = currentStops[movingIndex];
    const remainingStops = currentStops.filter((stop) => stop.id !== stopId);
    const next = normalizeStops([
      ...remainingStops.slice(0, targetIndex),
      movingStop,
      ...remainingStops.slice(targetIndex),
    ]);
    setStops(next);
    setMoveMenuStopId("");
    pendingStopFocusRef.current = stopId;
    setActiveId(stopId);
    activeRef.current = stopId;
    setExpandedStopKeys((current) => ({
      ...current,
      [stopId]: true,
    }));
    setRoute((value) => ({
      ...value,
      path: [],
      km: null,
      min: null,
      key: "",
    }));
    if (next.every((stop) => coordsOK(stop)))
      setTimeout(() => calculateRoute(next), 0);
  };

  const swapSinglePointEndpoints = () => {
    if (
      locationBusy ||
      !startPointEnabled ||
      isInspectionMode ||
      stops.length !== 2 ||
      (editMode && hasExecutionStarted)
    ) {
      return;
    }
    const currentStops = normalizeStops(stops);
    const startIndex = currentStops.findIndex(isStartStop);
    const endIndex = currentStops.findIndex(isEndStop);
    if (startIndex < 0 || endIndex < 0) return;

    const swappedStops = currentStops.slice();
    [swappedStops[startIndex], swappedStops[endIndex]] = [
      swappedStops[endIndex],
      swappedStops[startIndex],
    ];
    const next = normalizeStops(
      swappedStops.map((stop, index) => ({
        ...stop,
        distanceFromPreviousKm: index === 0 ? 0 : null,
        durationFromPreviousMinutes: index === 0 ? 0 : null,
      })),
    );

    routeVersion.current += 1;
    locationVersion.current += 1;
    routePreviewAbort.current?.abort();
    pendingStopFocusRef.current = "";
    setStops(next);
    const nextActiveId = activeId === "SRT" ? "END" : "SRT";
    setActiveId(nextActiveId);
    activeRef.current = nextActiveId;
    setExpandedStopKeys((current) => ({ ...current, SRT: true, END: true }));
    setSuggestions([]);
    setMapError("");
    setRouteEtaDurations({});
    setRoutePreview(null);
    setRoutePreviewError("");
    setOptimizationRecord(null);
    setRoute({ path: [], km: null, min: null, key: "", loading: false, error: "" });
  };

  const removeStop = (id) => {
    if (!id || id === "SRT" || stops.length <= 2) return;
    const next = normalizeStops(stops.filter((stop) => stop.id !== id));
    setStops(next);
    setExpandedStopKeys((current) => {
      const nextExpanded = { ...current };
      delete nextExpanded[id];
      return nextExpanded;
    });
    const nextActive = next.some((stop) => stop.id === activeId)
      ? activeId
      : next[next.length - 1]?.id || "SRT";
    setActiveId(nextActive);
    activeRef.current = nextActive;
    setRoute((value) => ({ ...value, path: [], km: null, min: null, key: "" }));
    if (next.every((stop) => coordsOK(stop)))
      setTimeout(() => calculateRoute(next), 0);
  };

  const updateStop = (id, updater) =>
    setStops((previous) =>
      normalizeStops(
        previous.map((stop) => (stop.id === id ? updater(stop) : stop)),
      ),
    );
  const updateRecipient = (stopId, recipientId, updater) =>
    updateStop(stopId, (stop) => ({
      ...stop,
      recipients: (stop.recipients || []).map((recipient) =>
        recipient.id === recipientId ? updater(recipient) : recipient,
      ),
    }));
  const updateContact = (stopId, recipientId, field, value) =>
    updateRecipient(stopId, recipientId, (recipient) => ({
      ...recipient,
      [field]: value,
    }));
  const updateRecipientNotification = (
    stopId,
    recipientId,
    field,
    value,
  ) =>
    updateRecipient(stopId, recipientId, (recipient) => ({
      ...recipient,
      notificationSettings: {
        ...normalizeRecipientNotificationSettings(
          recipient.notificationSettings,
        ),
        [field]: value,
      },
    }));
  const recipientEmailTemplateValues = (stop, recipient) => ({
    workName: text(applicationName) || "尚未填寫工作名稱",
    workId:
      text(initialApplication?.applicationId || initialApplication?.id) ||
      "建立後產生",
    startAt: schedule.date
      ? `${dateText(schedule.date)}${text(schedule.startTime) ? ` ${text(schedule.startTime)}${schedule.hasRange && text(schedule.endTime) ? `–${text(schedule.endTime)}` : ""}` : ""}`
      : "尚未設定日期",
    stopAddress: text(stop?.text) || "尚未填寫配送站地址",
    cargoSummary: recipientCargoRecordSummary(recipient) || "尚未填寫貨物",
    recipientName: text(recipient?.name) || "尚未填寫",
    recipientCompany: text(recipient?.company) || "未填寫",
    recipientPhone: text(recipient?.phone) || "未填寫",
    recipientEmail: text(recipient?.email) || "未填寫",
    trackingUrl: "建立工作後產生",
  });
  const openEmailTemplateEditor = (stopId, recipientId) => {
    const stop = stops.find((item) => item.id === stopId);
    const recipient = stop?.recipients?.find((item) => item.id === recipientId);
    const settings = normalizeRecipientNotificationSettings(
      recipient?.notificationSettings,
    );
    const templateValues = recipientEmailTemplateValues(stop, recipient);
    setEmailTemplateEditor({
      stopId,
      recipientId,
      previewStopId: stopId,
      recipientName: text(recipient?.name) || "目前收件人",
      templateValues,
      draft: renderRecipientEmailTemplate(
        settings.emailTemplate,
        templateValues,
      ),
    });
  };
  const addRecipient = (stopId, requestedOperation = "") => {
    const stop = stops.find((item) => item.id === stopId);
    const operationType = normalizeCargoOperation(
      requestedOperation,
      defaultCargoOperation(deriveStopOperation(stop)),
    );
    const recipient = createRecipient(operationType);
    updateStop(stopId, (current) => ({
      ...current,
      recipients: [recipient, ...(current.recipients || [])],
    }));
    setExpandedRecipientKeys((current) => ({
      ...current,
      [`${stopId}:${recipient.id}`]: true,
    }));
    return recipient;
  };
  const removeRecipient = (stopId, recipientId) =>
    updateStop(stopId, (stop) => {
      const nextRecipients = (stop.recipients || []).filter(
        (recipient) => recipient.id !== recipientId,
      );
      return {
        ...stop,
        recipients: nextRecipients,
      };
    });
  const updateCargo = (stopId, recipientId, contentId, field, value) =>
    updateRecipient(stopId, recipientId, (recipient) => ({
      ...recipient,
      contents: recipientContents(recipient).map((content) =>
        text(content.id || content.contentId) === text(contentId)
          ? { ...content, [field]: value }
          : content,
      ),
    }));
  const updateCargoOperation = (stopId, recipientId, contentId) =>
    updateRecipient(stopId, recipientId, (recipient) => {
      const operationType = normalizeCargoOperation(
        recipient?.operationType,
        "DELIVERY",
      );
      return {
        ...recipient,
        operationType,
        contents: recipientContents(recipient).map((content) => ({
          ...content,
          operationType,
        })),
      };
    });
  const addCargo = (stopId, recipientId) => {
    const recipient = stops
      .find((stop) => stop.id === stopId)
      ?.recipients?.find((item) => item.id === recipientId);
    const content = {
      ...createCargoItem(
        normalizeCargoOperation(recipient?.operationType, "DELIVERY"),
      ),
      recipientId,
    };
    updateRecipient(stopId, recipientId, (current) => ({
      ...current,
      contents: [content, ...recipientContents(current)],
    }));
    setExpandedRecipientKeys((current) => ({
      ...current,
      [`${stopId}:${recipientId}`]: true,
    }));
    setExpandedCargoKeys((current) => ({
      ...current,
      [`${stopId}:${recipientId}:${content.id}`]: true,
    }));
  };
  const openCargoSourcePicker = (stopId, recipientId) => {
    setCargoSourcePicker({ stopId, recipientId, selectedKeys: [] });
  };
  const toggleCargoSource = (sourceKey) => {
    setCargoSourcePicker((current) => {
      if (!current) return current;
      const selectedKeys = new Set(current.selectedKeys || []);
      if (selectedKeys.has(sourceKey)) selectedKeys.delete(sourceKey);
      else selectedKeys.add(sourceKey);
      return { ...current, selectedKeys: Array.from(selectedKeys) };
    });
  };
  const addCargoFromSelectedSources = () => {
    if (!cargoSourcePicker) return;
    const selectedKeys = new Set(cargoSourcePicker.selectedKeys || []);
    const selectedSources = cargoSourceOptions.filter((source) =>
      selectedKeys.has(source.key),
    );
    if (!selectedSources.length) return;
    const { stopId, recipientId } = cargoSourcePicker;
    const firstRecipient = selectedSources[0].recipient;
    const sourceCargoRefs = selectedSources.map((source) => ({
      jobId: text(initialApplication?.applicationId || initialApplication?.id),
      dateKey: text(schedule.date),
      stopId: text(source.stop?.id),
      recipientId: text(source.recipient?.id || source.recipient?.recipientId),
      cargoNumber: text(source.recipient?.cargoNumber),
    }));
    const clonedContents = selectedSources.flatMap((source) =>
      recipientContents(source.recipient).map((content) => {
        const clonedContent = cloneCargoContentForNewStop(content, recipientId);
        return {
          ...clonedContent,
          sender: normalizeParty(
            {
              ...(content.sender || {}),
              name:
                text(source.recipient?.name) || text(content.sender?.name),
              company:
                text(source.recipient?.company) ||
                text(content.sender?.company),
              phone:
                text(source.recipient?.phone) || text(content.sender?.phone),
              email:
                text(source.recipient?.email) || text(content.sender?.email),
              addressText:
                text(source.stop?.text) || text(content.sender?.addressText),
              senderId: text(
                content.sender?.senderId ||
                  source.recipient?.id ||
                  source.recipient?.recipientId,
              ),
            },
            "SENDER",
          ),
        };
      }),
    );
    const isEmptyDefaultCargo = (content) => {
      const hasMeaningfulValue = (value, key = "") => {
        if (["id", "contentId", "recipientId"].includes(key)) return false;
        if (value === null || value === undefined) return false;
        if (Array.isArray(value)) {
          return value.some((item) => hasMeaningfulValue(item));
        }
        if (typeof value === "object") {
          return Object.entries(value).some(([childKey, childValue]) =>
            hasMeaningfulValue(childValue, childKey),
          );
        }
        if (typeof value === "boolean") return value;
        if (typeof value === "number") return true;

        const normalized = text(value);
        if (!normalized) return false;
        if (key === "unit" && normalized === "件") return false;
        if (
          key === "operationType" &&
          ["PICKUP", "DELIVERY", "PICKUP_DELIVERY"].includes(
            normalized.toUpperCase(),
          )
        ) {
          return false;
        }
        if (key === "receivingStatus" && normalized === "待配送") return false;
        return true;
      };

      return !hasMeaningfulValue(content);
    };

    updateRecipient(stopId, recipientId, (recipient) => {
      const currentContents = recipientContents(recipient);
      const replaceEmptyDefault =
        clonedContents.length > 0 &&
        currentContents.length === 1 &&
        isEmptyDefaultCargo(currentContents[0]);

      return {
        ...recipient,
        operationType: "DELIVERY",
        cargoNumber: text(firstRecipient.cargoNumber) || text(recipient.cargoNumber),
        sourceCargoRef: sourceCargoRefs[0],
        sourceCargoRefs,
        contents: replaceEmptyDefault
          ? clonedContents
          : [...clonedContents, ...currentContents],
      };
    });
    setCargoSourcePicker(null);
    setExpandedRecipientKeys((current) => ({
      ...current,
      [`${stopId}:${recipientId}`]: true,
    }));
  };
  const addCargoPhotos = (stopId, recipientId, contentId, event) => {
    const currentContent = stops
      .find((stop) => stop.id === stopId)
      ?.recipients?.find((recipient) => recipient.id === recipientId)
      ?.contents?.find(
        (content) => text(content.id || content.contentId) === text(contentId),
      );
    const currentCount = currentContent?.photos?.length || 0;
    const selectedFiles = Array.from(event.target.files || []);
    const acceptedFiles = selectedFiles.filter(
      (file) => file.type.startsWith("image/") && file.size <= 10 * 1024 * 1024,
    );
    const files = acceptedFiles.slice(0, Math.max(0, 5 - currentCount));
    event.target.value = "";
    if (selectedFiles.length && !acceptedFiles.length) {
      setError("照片需為圖片格式且每張不超過 10 MB。");
      return;
    }
    if (acceptedFiles.length > files.length) {
      setError("每筆貨物最多可放 5 張照片。");
    }
    if (!files.length) return;
    setError("");
    const newPhotos = files.map((file) => {
      const preview = URL.createObjectURL(file);
      photoUrls.current.add(preview);
      return { file, preview };
    });
    updateRecipient(stopId, recipientId, (recipient) => ({
      ...recipient,
      contents: recipientContents(recipient).map((content) =>
        text(content.id || content.contentId) === text(contentId)
          ? { ...content, photos: [...(content.photos || []), ...newPhotos].slice(0, 5) }
          : content,
      ),
    }));
  };
  const removeCargoPhoto = (stopId, recipientId, contentId, photo) => {
    if (photo?.preview) {
      URL.revokeObjectURL(photo.preview);
      photoUrls.current.delete(photo.preview);
    }
    updateRecipient(stopId, recipientId, (recipient) => ({
      ...recipient,
      contents: recipientContents(recipient).map((content) =>
        text(content.id || content.contentId) === text(contentId)
          ? { ...content, photos: (content.photos || []).filter((candidate) => candidate !== photo) }
          : content,
      ),
    }));
  };
  const removeCargo = (stopId, recipientId, contentId) =>
    updateRecipient(stopId, recipientId, (recipient) => {
      const nextContents = recipientContents(recipient).filter(
        (content) => text(content.id || content.contentId) !== text(contentId),
      );
      return {
        ...recipient,
        contents: nextContents.length
          ? nextContents
          : [{ ...createCargoItem(recipient.operationType), recipientId }],
      };
    });
  const updateCargoItem = (stopId, recipientId, itemId, updater) =>
    updateRecipient(stopId, recipientId, (recipient) => {
      const draft = cargoRecordDraft(recipient, recipient?.operationType);
      const nextDraft = updater(draft);
      return applyCargoRecordDraft(
        recipient,
        { ...nextDraft, id: text(recipient.id || recipient.recipientId) },
        recipient?.operationType,
        itemId,
      );
    });
  const openCargoDetailEditor = (stopId, recipientId, item) => {
    setDeliveryDetailEditor(null);
    setCargoDetailEditor({
      stopId,
      recipientId,
      itemId: item.id,
      draft: cloneCargoDetailDraft(item, recipientId),
      expandedSections: {
        basic: true,
        weight: false,
        documents: false,
        payment: false,
      },
    });
  };
  const closeCargoDetailEditor = () => setCargoDetailEditor(null);
  const updateCargoDetailDraft = (path, value) => {
    if (path === "operationType") return;
    setCargoDetailEditor((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        draft: writeCargoDetailPath(previous.draft, path, value),
      };
    });
  };
  const updateCargoDetailDocuments = (updater) => {
    setCargoDetailEditor((previous) => {
      if (!previous) return previous;
      const current = normalizeDocumentProofs(
        previous.draft?.accessRequirements?.documents,
        true,
        true,
      );
      const next = typeof updater === "function" ? updater(current) : updater;
      return {
        ...previous,
        draft: {
          ...writeCargoDetailPath(
            previous.draft,
            "accessRequirements.documents",
            next,
          ),
          documents: next,
        },
      };
    });
  };
  const addCargoDetailDocument = () =>
    updateCargoDetailDocuments((documents) => [
      { id: makeRandomId(), documentName: "", fileName: "", fileType: "" },
      ...documents,
    ]);
  const changeCargoDetailDocument = (index, field, value) =>
    updateCargoDetailDocuments((documents) =>
      documents.map((document, documentIndex) =>
        documentIndex === index ? { ...document, [field]: value } : document,
      ),
    );
  const removeCargoDetailDocument = (index) =>
    updateCargoDetailDocuments((documents) =>
      documents.filter((_, documentIndex) => documentIndex !== index),
    );
  const selectCargoDetailDocumentFile = (index, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const previewUrl = file.type.startsWith("image/")
      ? URL.createObjectURL(file)
      : "";
    updateCargoDetailDocuments((documents) =>
      documents.map((document, documentIndex) =>
        documentIndex === index
          ? {
              ...document,
              file,
              fileName: file.name,
              fileType: file.type,
              contentType: file.type,
              fileSize: file.size,
              previewUrl,
            }
          : document,
      ),
    );
  };
  const toggleCargoDetailSection = (sectionId) => {
    setCargoDetailEditor((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        expandedSections: {
          ...(previous.expandedSections || {}),
          [sectionId]: !previous.expandedSections?.[sectionId],
        },
      };
    });
  };
  const saveCargoDetailEditor = () => {
    if (!cargoDetailEditor) return;
    const { stopId, recipientId, itemId, draft } = cargoDetailEditor;
    updateCargoItem(stopId, recipientId, itemId, () => ({
      ...draft,
      id: recipientId,
      recipientId,
      operationType: normalizeCargoOperation(
        draft.operationType,
        "PICKUP",
      ),
      sourceCargoRef:
        normalizeCargoOperation(draft.operationType, "PICKUP") === "PICKUP"
          ? undefined
          : draft.sourceCargoRef,
    }));
    setCargoDetailEditor(null);
    setError("");
  };
  const openDeliveryDetailEditor = (stopId, recipientId, item) => {
    setCargoDetailEditor(null);
    setDeliveryDetailEditor({
      stopId,
      recipientId,
      itemId: item.id,
      draft: cloneCargoDetailDraft(item, recipientId),
      expandedSections: {
        deliveryBasics: true,
        packaging: false,
        documents: false,
        payment: false,
        execution: false,
        category: false,
      },
    });
  };
  const closeDeliveryDetailEditor = () => setDeliveryDetailEditor(null);
  const updateDeliveryDetailDraft = (path, value) => {
    setDeliveryDetailEditor((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        draft: writeCargoDetailPath(previous.draft, path, value),
      };
    });
  };
  const updateDeliveryDetailDocuments = (updater) => {
    setDeliveryDetailEditor((previous) => {
      if (!previous) return previous;
      const current = normalizeDocumentProofs(
        previous.draft?.accessRequirements?.documents,
        true,
        true,
      );
      const next = typeof updater === "function" ? updater(current) : updater;
      return {
        ...previous,
        draft: {
          ...writeCargoDetailPath(
            previous.draft,
            "accessRequirements.documents",
            next,
          ),
          documents: next,
        },
      };
    });
  };
  const addDeliveryDetailDocument = () =>
    updateDeliveryDetailDocuments((documents) => [
      { id: makeRandomId(), documentName: "", fileName: "", fileType: "" },
      ...documents,
    ]);
  const changeDeliveryDetailDocument = (index, field, value) =>
    updateDeliveryDetailDocuments((documents) =>
      documents.map((document, documentIndex) =>
        documentIndex === index ? { ...document, [field]: value } : document,
      ),
    );
  const removeDeliveryDetailDocument = (index) =>
    updateDeliveryDetailDocuments((documents) =>
      documents.filter((_, documentIndex) => documentIndex !== index),
    );
  const selectDeliveryDetailDocumentFile = (index, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const previewUrl = file.type.startsWith("image/")
      ? URL.createObjectURL(file)
      : "";
    updateDeliveryDetailDocuments((documents) =>
      documents.map((document, documentIndex) =>
        documentIndex === index
          ? {
              ...document,
              file,
              fileName: file.name,
              fileType: file.type,
              contentType: file.type,
              fileSize: file.size,
              previewUrl,
            }
          : document,
      ),
    );
  };
  const toggleDeliveryDetailSection = (sectionId) => {
    setDeliveryDetailEditor((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        expandedSections: {
          ...(previous.expandedSections || {}),
          [sectionId]: !previous.expandedSections?.[sectionId],
        },
      };
    });
  };
  const saveDeliveryDetailEditor = () => {
    if (!deliveryDetailEditor) return;
    const { stopId, recipientId, itemId, draft } = deliveryDetailEditor;
    updateCargoItem(stopId, recipientId, itemId, () => ({
      ...draft,
      id: recipientId,
      recipientId,
      operationType: "DELIVERY",
    }));
    setDeliveryDetailEditor(null);
    setError("");
  };
  const cargoDetailEditorDraft = cargoDetailEditor?.draft || null;
  const cargoDetailEditorCategory = cargoDetailEditorDraft
    ? cargoCategoryMeta(cargoCategoryId)
    : null;
  const cargoDetailEditorSections = cargoDetailEditorDraft
    ? cargoDetailSectionsForCategory(cargoCategoryId)
    : [];
  const deliveryDetailEditorDraft = deliveryDetailEditor?.draft || null;
  const deliveryDetailEditorCategory = deliveryDetailEditorDraft
    ? cargoCategoryMeta(cargoCategoryId)
    : null;
  const deliveryDetailEditorSections = deliveryDetailEditorDraft
    ? deliveryDetailSectionsForCategory(cargoCategoryId)
    : [];
  const deliveryDetailEditorRecipient = deliveryDetailEditor
    ? stops
        .flatMap((stop) =>
          (stop.recipients || []).map((recipient) => ({ stop, recipient })),
        )
        .find(
          ({ stop, recipient }) =>
            stop.id === deliveryDetailEditor.stopId &&
            recipient.id === deliveryDetailEditor.recipientId,
        )?.recipient || null
    : null;
  const cargoSourceSummaries = useMemo(
    () => cargoSourceSummary(stops),
    [stops],
  );
  const cargoSourceByKey = useMemo(
    () => new Map(cargoSourceSummaries.map((source) => [source.key, source])),
    [cargoSourceSummaries],
  );
  const cargoDetailSourceSummary = cargoDetailEditorDraft
    ? cargoSourceByKey.get(cargoSourceKey(cargoDetailEditorDraft))
    : null;
  const deliveryDetailSourceSummary = deliveryDetailEditorDraft
    ? cargoSourceByKey.get(cargoSourceKey(deliveryDetailEditorDraft))
    : null;
  const cargoSourceOptions = useMemo(() => {
    const seen = new Set();
    const targetStopIndex = cargoSourcePicker
      ? stops.findIndex((stop) => stop.id === cargoSourcePicker.stopId)
      : Number.POSITIVE_INFINITY;
    return stops.flatMap((stop, stopIndex) =>
      (stop?.recipients || [])
        .filter((recipient) => {
          return (
            stopIndex < targetStopIndex &&
            recipientHasOperation(recipient, "PICKUP", stop?.operationType)
          );
        })
        .flatMap((recipient) => {
          const record = recipientCargoRecordSummary(recipient, stop?.operationType);
          const key = cargoSourceKey(record);
          if (!key || seen.has(key)) return [];
          seen.add(key);
          return [
            {
              key,
              recipient,
              record,
              stop,
              stopId: text(stop?.id),
              stopIndex,
              recipientId: text(recipient?.id),
              summary: cargoSourceByKey.get(key),
            },
          ];
        }),
    );
  }, [cargoSourceByKey, cargoSourcePicker, stops]);
  const applyContact = (
    stopId,
    recipientId,
    contact,
    target = "recipient",
    contentId = "",
  ) => {
    const contactData = {
      name: text(contact.name || contact.displayName),
      phone: text(contact.phone),
      company: text(
        contact.company || contact.companyName || contact.organization,
      ),
      email: text(contact.email),
    };
    if (target === "sender") {
      if (
        !contentId ||
        text(deliveryDetailEditor?.stopId) !== text(stopId) ||
        text(deliveryDetailEditor?.recipientId) !== text(recipientId) ||
        text(deliveryDetailEditor?.itemId) !== text(contentId)
      ) return;
      updateDeliveryDetailDraft(
        "sender",
        normalizeParty(
          {
            ...(deliveryDetailEditor?.draft?.sender || {}),
            ...contactData,
            addressText: text(
              contact.addressText ||
                contact.address ||
                contact.fullAddress ||
                contact.formattedAddress,
            ),
            senderId: text(contact.id || contact.uid || contact.contactId),
          },
          "SENDER",
        ),
      );
      return;
    }
    updateRecipient(stopId, recipientId, (recipient) => ({
      ...recipient,
      ...contactData,
    }));
    const address = text(
      contact.addressText ||
        contact.address ||
        contact.fullAddress ||
        contact.formattedAddress,
    );
    const currentStop = stops.find((stop) => stop.id === stopId);
    if (!address || text(currentStop?.text)) return;

    const hasSavedPoint = coordsOK(contact);
    if (hasSavedPoint) {
      setTimeout(
        () =>
          applyLocation(stopId, contact.lat, contact.lng, {
            text: address,
            postalCode: text(contact.postalCode),
            lat: Number(contact.lat),
            lng: Number(contact.lng),
          }),
        0,
      );
    } else {
      setTimeout(() => locate(stopId, address), 0);
    }
  };

  const applyCommonAddress = (stopId, item) => {
    const address = text(
      item?.addressText ||
        item?.address ||
        item?.fullAddress ||
        item?.formattedAddress,
    );
    if (!address) return;
    updateAddress(stopId, address);
    const lat = Number(item?.lat);
    const lng = Number(item?.lng);
    if (coordsOK({ lat, lng })) {
      setTimeout(
        () =>
          applyLocation(stopId, lat, lng, {
            text: address,
            postalCode: text(item?.postalCode),
            lat,
            lng,
          }),
        0,
      );
    } else {
      setTimeout(() => locate(stopId, address), 0);
    }
    setCommonPicker(null);
  };

  const applyCommonContact = (
    stopId,
    recipientId,
    item,
    target = "recipient",
    contentId = "",
  ) => {
    applyContact(stopId, recipientId, item, target, contentId);
    setCommonPicker(null);
  };

  const toggleMember = (uid) => {
    if (editMode) return;
    if (selectedMemberIds[0] === uid) {
      setSelectedMemberIds([]);
      setAssignments({});
      return;
    }
    setSelectedMemberIds([uid]);
    setAssignments((previous) => ({
      [uid]: previous[uid] || {
        vehicleId: "",
        trailerIds: [],
        containerIds: [],
        equipmentIds: [],
      },
    }));
  };

  const openAssetPicker = (uid) => {
    if (!uid || submitting) return;
    const assignment = assignments[uid] || {};
    setAssetPickerDraft({
      vehicleId: text(assignment.vehicleId),
      trailerIds: assignmentTrailerIds(assignment),
      containerIds: assignmentContainerIds(assignment),
      equipmentIds: assignmentEquipmentIds(assignment),
    });
    setAssetPickerUid(uid);
    setAssetPickerTab("vehicle");
    setAssetPickerSearch("");
    setAssetPickerTypeFilter("all");
    setAssetPickerStatusFilter("all");
  };

  const closeAssetPicker = () => {
    setAssetPickerUid("");
    setAssetPickerDraft(null);
  };

  const updateAssetPickerVehicle = (assetId) => {
    if (!assetPickerUid || !assetPickerDraft || !canEditVehicleSchedule) return;
    setAssetPickerDraft((previous) => {
      if (!previous) return previous;
      const nextVehicleId = text(previous.vehicleId) === text(assetId) ? "" : text(assetId);
      const bound = vehicleBindingAssetIds(vehicleBindings[nextVehicleId]);
      const retainedOriginalBindings =
        editMode && nextVehicleId === originalVehicleId
          ? {
              trailerIds: bound.trailerIds.filter((id) =>
                originalAssignedAssetIds.has(id),
              ),
              containerIds: bound.containerIds.filter((id) =>
                originalAssignedAssetIds.has(id),
              ),
            }
          : { trailerIds: [], containerIds: [] };
      const keepManuallySelected = (ids) => uniqueIds(ids).filter((id) =>
        !bindingOwnerByAssetId[id] &&
        !bound.trailerIds.includes(id) &&
        !bound.containerIds.includes(id)
      );
      return {
        ...previous,
        vehicleId: nextVehicleId,
        trailerIds: uniqueIds([
          ...keepManuallySelected(previous.trailerIds),
          ...retainedOriginalBindings.trailerIds,
        ]),
        containerIds: uniqueIds([
          ...keepManuallySelected(previous.containerIds),
          ...retainedOriginalBindings.containerIds,
        ]),
      };
    });
  };

  const toggleAssetPickerItem = (uid, kind, assetId) => {
    if (
      !assetPickerDraft ||
      uid !== assetPickerUid ||
      !assetId ||
      !canEditVehicleSchedule ||
      submitting
    ) return;
    if (kind === "vehicle") {
      updateAssetPickerVehicle(assetId);
      return;
    }
    const ownerVehicleId = bindingOwnerByAssetId[assetId];
    const selectedVehicleId = text(assetPickerDraft.vehicleId);
    const bound = vehicleBindingAssetIds(vehicleBindings[selectedVehicleId]);
    const field = FLEET_ASSET_KIND_CONFIG.find((item) => item.key === kind)?.field;
    if (!field || field === "vehicleId") return;
    const isSelected = assignmentIds(assetPickerDraft, field).includes(assetId);
    if (ownerVehicleId && ownerVehicleId !== selectedVehicleId && !isSelected) return;
    if (
      !isSelected &&
      ((kind === "trailer" && bound.trailerIds.includes(assetId)) ||
        (kind === "container" && bound.containerIds.includes(assetId)))
    ) return;
    setAssetPickerDraft((previous) => {
      if (!previous) return previous;
      const currentIds = assignmentIds(previous, field);
      const nextIds = currentIds.includes(assetId)
        ? currentIds.filter((id) => id !== assetId)
        : [...currentIds, assetId];
      return { ...previous, [field]: nextIds };
    });
  };

  const confirmAssetPicker = () => {
    if (!assetPickerUid || !assetPickerDraft) return;
    if (canEditVehicleSchedule) {
      setAssignments((previous) => ({
        ...previous,
        [assetPickerUid]: {
          vehicleId: text(assetPickerDraft.vehicleId),
          trailerIds: uniqueIds(assetPickerDraft.trailerIds),
          containerIds: uniqueIds(assetPickerDraft.containerIds),
          equipmentIds: uniqueIds(assetPickerDraft.equipmentIds),
        },
      }));
    }
    closeAssetPicker();
  };

  const isAssetAssignedToAnotherDriver = (assetId, uid) =>
    Object.entries(assignments).some(
      ([assignedUid, assignment]) =>
        assignedUid !== uid && getEffectiveAssignmentAssetIds(assignment).includes(assetId),
    );

  async function loadScheduleReview(
    dateKey,
    startAt,
    { excludeApplicationId = "", excludeScheduleId = "" } = {},
  ) {
    const uid = selectedIds[0];
    const member = fleetData.members.find((item) => item.uid === uid);
    const assignment = assignments[uid] || {};
    const assetIds = getEffectiveAssignmentAssetIds(assignment);
    const assets = assetIds.map((assetId) => assetById[assetId]).filter(Boolean);
    if (!assetIds.length) return null;

    const snapshot = await getDocs(
      collection(db, "orgs", orgId, "vehicleSchedules", dateKey, "items"),
    );
    const selectedAssetIds = new Set(assetIds.map(text));
    const schedules = snapshot.docs
      .map((item) => ({ scheduleId: item.id, ...item.data() }))
      .filter((item) => {
        if (
          (excludeApplicationId &&
            text(item.applicationId) === text(excludeApplicationId)) ||
          (excludeScheduleId &&
            text(item.scheduleId) === text(excludeScheduleId))
        ) {
          return false;
        }
        return (Array.isArray(item.assetIds) ? item.assetIds : []).some(
          (assetId) => selectedAssetIds.has(text(assetId)),
        );
      })
      .sort((left, right) => {
        const leftTime = firestoreDate(left.startAt)?.getTime() || 0;
        const rightTime = firestoreDate(right.startAt)?.getTime() || 0;
        return leftTime - rightTime;
      });

    return {
      dateKey,
      startAt,
      hasRange: schedule.hasRange === true,
      endAt: schedule.hasRange
        ? dateTime(schedule.date, schedule.endTime)
        : null,
      selectedScheduleRows: [
        {
          uid,
          driverName: text(member?.displayName || member?.name) || "未命名成員",
          assets,
          assetIds,
        },
      ],
      schedules,
    };
  }

  async function confirmScheduleReview() {
    if (!scheduleReview) return;
    scheduleReviewConfirmed.current = true;
    setScheduleReview(null);
    await submit({ preventDefault() {} });
  }

  const closeRoutePreview = () => {
    routePreviewAbort.current?.abort();
    routePreviewAbort.current = null;
    setRoutePreview(null);
    setRoutePreviewLoading(false);
    setOptimizing(false);
    setProgress("");
    setRoutePreviewError("");
  };

  const applyRouteCandidate = () => {
    const candidate = routePreview?.candidates?.find(
      (item) => item.id === routePreview.selectedId,
    );
    if (!candidate?.eligible) return;
    const candidateStops = normalizeStops(candidate.route.stops);
    const originStop = hasExplicitStartStop(stops)
      ? stops.find(
          (stop) =>
            text(stop?.id).toUpperCase() === "SRT" ||
            isStartStop(stop),
        )
      : null;
    const nextStops = startPointEnabled
      ? candidateStops
      : originStop
        ? normalizeStops([originStop, ...candidateStops])
        : candidateStops;
    const nextRouteKey = JSON.stringify([
      startPointEnabled,
      nextStops.map((stop) => [stop.id, stop.verified, stop.lat, stop.lng]),
    ]);
    preserveOptimizationRef.current = true;
    skipNextRouteCalculationRef.current = true;
    setStops(nextStops);
    setActiveId(nextStops[0]?.id || "SRT");
    activeRef.current = nextStops[0]?.id || "SRT";
    setRoute({
      path: candidate.route.path,
      km: candidate.route.km,
      min: candidate.route.min,
      key: nextRouteKey,
      loading: false,
      error: "",
    });
    setRouteEtaDurations(routeEtaDurationsFromStops(candidate.route.stops));
    setOptimizationRecord((previous) =>
      previous
        ? {
            ...previous,
            selectedCandidateId: candidate.id,
            appliedCandidateId: candidate.id,
          }
        : previous,
    );
    setFitVersion((value) => value + 1);
    setError("");
    closeRoutePreview();
  };

  const optimizeRoute = async (forceRecalculate = false) => {
    if (
      !forceRecalculate &&
      optimizationRecord?.inputKey === optimizationInputKey &&
      optimizationRecord.candidates?.length
    ) {
      setRoutePreview({
        candidates: optimizationRecord.candidates,
        selectedId:
          optimizationRecord.appliedCandidateId ||
          optimizationRecord.selectedCandidateId ||
          optimizationRecord.candidates[0]?.id ||
          "",
      });
      setRoutePreviewError("");
      return;
    }

    const optimizeStops = normalizeStops(
      startPointEnabled
        ? stops
        : hasExplicitStartStop(stops)
          ? stops.filter((stop) => text(stop?.id).toUpperCase() !== "SRT")
          : stops,
    );
    if (optimizeStops.length < 2) return setError("至少需要 2 個配送站點才能最佳化路線。");
    if (optimizeStops.some((stop) => !coordsOK(stop))) return setError("請先完成所有配送站定位。");
    if (forceRecalculate) routeApiCacheRef.current.clear();

    setOptimizing(true);
    setRoutePreviewLoading(true);
    setRoutePreviewError("");
    setOptimizationRecord(null);
    setRoutePreview({ candidates: [], selectedId: "" });
    setError("");
    routePreviewAbort.current?.abort();
    const controller = new AbortController();
    routePreviewAbort.current = controller;

    try {
      setProgress("正在建立貨物取送關係與 OSRM Matrix…");
      const planningContext = getRoutePlanningContext(optimizeStops);
      const relationModel = buildRouteRelationModel(optimizeStops);
      if (relationModel.errors.length) throw new Error(relationModel.errors[0]);
      if (relationModel.cycles.length) {
        throw new Error("取送關係形成循環，單一路線無法完成；請調整其中一批貨物的取貨站或送貨站。");
      }
      const matrix = await fetchRouteMatrix(optimizeStops, controller.signal);
      setProgress("正在依取貨與送貨順序建立候選路線…");
      const generated = buildStackRouteCandidates(
        optimizeStops,
        relationModel,
        matrix,
        planningContext,
      );
      if (!generated.candidates.length) {
        throw new Error("沒有產生完整且合法的取送候選路線。");
      }

      const matrixCandidates = [];
      for (let index = 0; index < generated.candidates.length; index += 1) {
        if (controller.signal.aborted) throw new DOMException("路線最佳化已取消", "AbortError");
        const generatedCandidate = generated.candidates[index];
        const order = generatedCandidate.order;
        const evaluation = evaluateRouteOrder(
          order,
          optimizeStops,
          relationModel,
          matrix,
          schedule.startTime,
          planningContext,
        );
        if (evaluation.cargoFeasible && routeOrderIsComplete(order, planningContext)) {
          const stackViolationCount =
            generatedCandidate.stackExceptionCount +
            routeStackViolationCount(order, generatedCandidate.pickupStack) +
            routeStackViolationCount(order, generatedCandidate.deliveryStack);
          evaluation.stackViolationCount = stackViolationCount;
          evaluation.score = [
            evaluation.firstStopViolationCount,
            evaluation.cargoFlowViolationCount,
            evaluation.hardLateCount,
            evaluation.hardLateMinutes,
            evaluation.relationViolationCount,
            evaluation.softLateCount,
            evaluation.softLateMinutes,
            stackViolationCount,
            evaluation.totalMinutes,
            evaluation.travelMinutes,
            evaluation.totalDistanceKm,
          ];
          matrixCandidates.push({
            id: `route-candidate-${index + 1}`,
            label: `路線 ${matrixCandidates.length + 1}`,
            source: "取送順序最佳化",
            order,
            pickupStack: generatedCandidate.pickupStack,
            deliveryStack: generatedCandidate.deliveryStack,
            stackKey: `${generatedCandidate.pickupStack.join(">")}|${generatedCandidate.deliveryStack.join(">")}`,
            evaluation,
          });
        }
        if (index % 300 === 0) {
          setProgress(`Matrix 評估 ${index + 1}/${generated.candidates.length} 組候選…`);
          await nextRouteTick();
        }
      }
      if (!matrixCandidates.length) throw new Error("沒有產生完整且合法的候選路線。");
      const topMatrixCandidates = selectStackDiverseCandidates(matrixCandidates, 10);

      setProgress(`正在對 Matrix 前 ${topMatrixCandidates.length} 組候選進行 Route API 最終確認…`);
      const confirmedCandidates = [];
      for (let index = 0; index < topMatrixCandidates.length; index += 1) {
        if (controller.signal.aborted) throw new DOMException("路線最佳化已取消", "AbortError");
        const candidate = topMatrixCandidates[index];
        const cacheKey = `${optimizationInputKey}:${candidate.order.join(",")}`;
        let finalRoute = routeApiCacheRef.current.get(cacheKey);
        try {
          if (!finalRoute) {
            finalRoute = await fetchRoute(
              candidate.order.map((stopIndex) => optimizeStops[stopIndex]),
              controller.signal,
            );
            routeApiCacheRef.current.set(cacheKey, finalRoute);
          }
          const finalEvaluation = evaluateRouteOrder(
            candidate.order,
            optimizeStops,
            relationModel,
            matrix,
            schedule.startTime,
            planningContext,
            finalRoute.stops,
            finalRoute.legs,
          );
          const finalStackViolationCount =
            candidate.evaluation.stackViolationCount ||
            routeStackViolationCount(candidate.order, candidate.pickupStack) +
            routeStackViolationCount(candidate.order, candidate.deliveryStack);
          finalEvaluation.stackViolationCount = finalStackViolationCount;
          finalEvaluation.score = [
            finalEvaluation.firstStopViolationCount,
            finalEvaluation.cargoFlowViolationCount,
            finalEvaluation.hardLateCount,
            finalEvaluation.hardLateMinutes,
            finalEvaluation.relationViolationCount,
            finalEvaluation.softLateCount,
            finalEvaluation.softLateMinutes,
            finalStackViolationCount,
            finalEvaluation.totalMinutes,
            finalEvaluation.travelMinutes,
            finalEvaluation.totalDistanceKm,
          ];
          confirmedCandidates.push({
            ...matrixCandidateToCard(candidate, finalRoute, finalEvaluation, planningContext),
            pickupStack: candidate.pickupStack,
            deliveryStack: candidate.deliveryStack,
          });
        } catch (reason) {
          if (controller.signal.aborted) throw reason;
          console.warn("[CreateInspectionFleetJob] Route API candidate:", reason);
        }
        setProgress(`Route API 最終確認 ${index + 1}/${topMatrixCandidates.length} 組…`);
      }

      const cards = confirmedCandidates
        .sort((left, right) => compareRouteScore(left.evaluation, right.evaluation))
        .map((candidate, index) => ({
          ...candidate,
          label: `路線 ${index + 1}`,
          id: `route-candidate-${index + 1}`,
          isRecommended: index === 0,
        }));
      if (!cards.length) throw new Error("Route API 確認後沒有完整的取送合法路線。");

      const selectedCandidateId = cards[0]?.id || "";
      setOptimizationRecord({
        version: "ordered-stack-merge-v1",
        inputKey: optimizationInputKey,
        createdAt: Date.now(),
        pickupStackCount: generated.pickupStackCount,
        deliveryStackCount: generated.deliveryStackCount,
        candidatePoolCount: generated.candidates.length,
        candidatePoolTruncated: generated.truncated,
        matrixCandidateCount: matrixCandidates.length,
        confirmedCandidateCount: cards.length,
        candidates: cards,
        selectedCandidateId,
        appliedCandidateId: null,
      });
      setRoutePreview({ candidates: cards, selectedId: selectedCandidateId });
      if (cards.length < 10) {
        setRoutePreviewError(
          `前 10 組候選已完成 Route API 確認，目前有 ${cards.length} 組通過最終驗證。`,
        );
      }
    } catch (optimizationError) {
      if (!controller.signal.aborted) {
        setRoutePreviewError(`路線最佳化失敗：${optimizationError?.message || "請稍後再試"}`);
        setError("");
      }
    } finally {
      if (routePreviewAbort.current === controller) {
        routePreviewAbort.current = null;
        setRoutePreviewLoading(false);
        setOptimizing(false);
        setProgress("");
      }
    }
  };
  async function makeControlledImage(file, maxDimension, quality = 0.82) {
    if (!file || !text(file.type).toLowerCase().startsWith("image/")) return file;
    try {
      const bitmap = await window.createImageBitmap(file);
      const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
      bitmap.close();
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
      return blob ? new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" }) : file;
    } catch (imageError) {
      console.warn("[CreateInspectionFleetJob] image processing skipped:", imageError);
      return file;
    }
  }

  async function uploadAsset(file, { source }) {
    if (!file) return null;
    const assetId = makeAssetUuid();
    const originalFile = await makeControlledImage(file, 2560, 0.86);
    const previewFile = await makeControlledImage(originalFile, 640, 0.78);
    const originalPath = `assets/${assetId}/original`;
    const previewPath = `assets/${assetId}/preview`;
    const contentType = text(originalFile.type || file.type) || "application/octet-stream";
    const storage = getStorage();
    await uploadBytes(storageRef(storage, originalPath), originalFile, { contentType });
    await uploadBytes(storageRef(storage, previewPath), previewFile, { contentType: text(previewFile.type) || contentType });
    return {
      assetId,
      originalName: text(file.name) || "未命名檔案",
      contentType,
      source,
      previewPath,
      originalPath,
      originalSizeBytes: Number(originalFile.size) || 0,
      previewSizeBytes: Number(previewFile.size) || 0,
      processingStatus: "completed",
      createdAt: serverTimestamp(),
    };
  }

  async function uploadCargoPhotos(applicationId, stop, recipient, item, rowIndex, assetRecords) {
    const uploaded = [];
    const photos = (item.photos || []).slice(0, 5);
    for (const photo of photos) {
      if (photo?.assetId && !photo.file) {
        uploaded.push({
          assetId: text(photo.assetId),
          fileName: text(photo.fileName || photo.name || photo.originalName),
          contentType: text(photo.contentType || photo.type) || "image/jpeg",
        });
        continue;
      }
      if (!photo?.file) continue;
      setProgress(`上傳第 ${rowIndex} 站的貨物照片…`);
      const asset = await uploadAsset(photo.file, {
        source: "initial_photo",
        ownerOrgId: orgId,
        applicationId,
        stopId: text(stop.id).toUpperCase(),
        recipientId: canonicalRecipientCode(recipient.id || recipient.recipientId),
        contentId: canonicalCargoCode(item.id || item.contentId),
      });
      assetRecords.push(asset);
      uploaded.push({
        assetId: asset.assetId,
        fileName: asset.originalName,
        contentType: text(photo.file.type) || "image/jpeg",
      });
    }
    return uploaded;
  }

  async function uploadCargoDocuments(applicationId, stop, recipient, item, rowIndex, assetRecords) {
    const documents = item.documents || [];
    const uploaded = [];
    for (const document of documents) {
      if (document?.assetId && !document.file) {
        uploaded.push({
          assetId: text(document.assetId),
          documentName: text(document.documentName || document.content),
          contentType: text(document.contentType || document.fileType) || "application/octet-stream",
        });
        continue;
      }
      if (!document?.file) continue;
      setProgress(`上傳第 ${rowIndex} 站的文件證明…`);
      const asset = await uploadAsset(document.file, {
        source: "document",
        ownerOrgId: orgId,
        applicationId,
        stopId: text(stop.id).toUpperCase(),
        recipientId: canonicalRecipientCode(recipient.id || recipient.recipientId),
        contentId: canonicalCargoCode(item.id || item.contentId),
      });
      assetRecords.push(asset);
      uploaded.push({
        assetId: asset.assetId,
        documentName: text(document.documentName || document.content),
        contentType: text(document.file?.type) || "application/octet-stream",
      });
    }
    return uploaded;
  }

  async function submit(event) {
    event.preventDefault();
    if (submitLock.current || success) return;
    if (editMode && !editDirty) {
      onBack?.();
      return;
    }
    setError("");
    if (
      editMode &&
      Number(initialApplication?.schemaVersion) !== 5
    ) {
      setError("這份工作不是目前固定資料結構，無法編輯。請重新建立工作。");
      return;
    }
    if (!orgId) return setError(profileLoading ? "正在載入組織資料，請稍候再試。" : "目前登入帳號沒有可用的 orgId，無法建立工作。");
    if (loading || dataError) return setError("成員、資產摘要或綁定資料尚未載入完成，請重新整理後再試。");
    if (!text(applicationName)) return setError("請輸入工作名稱。");
    if (!selectedIds.length) return setError("請選擇一位成員。");
    if (!schedule.date || !schedule.startTime)
      return setError("請選擇配送日期與開始時間。");
    if (
      schedule.hasRange &&
      (!schedule.endTime ||
        dateTime(schedule.date, schedule.endTime) <=
          dateTime(schedule.date, schedule.startTime))
    )
      return setError("最晚開始時間必須晚於最早開始時間。");
    if (!isInspectionMode && (!routeReady || route.loading || !routeValid))
      return setError("請先完成所有站點定位並等待物流路線計算完成。");

    const incompleteLocation = (startPointEnabled ? stops : deliveryStops).find(
      (stop) => !stop.verified || !text(stop.text),
    );
    if (incompleteLocation && !isInspectionMode)
      return setError(
        `第 ${incompleteLocation.order || "起點"} 站地址尚未完成。`,
      );
    const invalidStop = stopsRequiringRecipients.find((stop) => {
      return (
        !(stop.recipients || []).length ||
        (stop.recipients || []).some((recipient) => {
          const enteredItems = recipientContents(recipient).filter(
            (item) => text(item.name) || text(item.quantity) || text(item.note),
          );
          return (
            !text(recipient.name) ||
            !text(recipient.email) ||
            !enteredItems.length ||
            enteredItems.some(
              (item) =>
                !text(item.name) ||
                !(Number(item.quantity) > 0) ||
                !text(item.unit),
            )
          );
        })
      );
    });
    if (invalidStop && !isInspectionMode)
      return setError(
        `第 ${invalidStop.order} 站資料未完成：請確認每位收件人的姓名、Email 與至少一筆完整內容物。`,
      );
    if (!isInspectionMode && (!(Number(route.km) > 0) || !(Number(route.min) > 0)))
      return setError("尚未取得完整物流路線距離與時間。");

    const hasUnmigratedMedia = (stops || []).some((stop) =>
      (stop.recipients || []).some((recipient) =>
        recipientContents(recipient).some((content) =>
          [
            ...(content.photos || []),
            ...normalizeDocumentProofs(
              content.documents || content.accessRequirements?.documents,
              true,
              true,
            ),
          ].some((media) => !media?.file && !media?.assetId && Boolean(media?.url || media?.path || media?.storagePath)),
        ),
      ),
    );
    if (editMode && hasUnmigratedMedia) {
      return setError("此工作含尚未遷移的舊媒體資料，為避免遺失，請先完成媒體資產遷移後再編輯儲存。");
    }

    const uid = selectedIds[0];
    const member = fleetData.members.find((item) => item.uid === uid);
    const assignment = assignments[uid] || {};
    const selectedAssetIds = getEffectiveAssignmentAssetIds(assignment);
    const missingAssetIds = selectedAssetIds.filter((assetId) => !assetById[assetId]);
    if (missingAssetIds.length) {
      return setError("資產摘要中找不到已選或車輛綁定的資產，請重新整理資產摘要與綁定資料後再試。");
    }
    const selectedAssetData = selectedAssetIds.map((assetId) => assetById[assetId]);
    const vehicleData = assignment.vehicleId ? assetById[text(assignment.vehicleId)] || null : null;
    if (assignment.vehicleId && vehicleData?.assetKind !== "vehicle") {
      return setError("所選主車輛不屬於新版車輛資產，請重新選擇。");
    }
    const trailerDataList = selectedAssetData.filter((item) => item.assetKind === "trailer");
    const containerDataList = selectedAssetData.filter((item) => item.assetKind === "container");
    const equipmentDataList = selectedAssetData.filter((item) => item.assetKind === "equipment");
    const trailerIds = trailerDataList.map((item) => item.id);
    const containerIds = containerDataList.map((item) => item.id);
    const equipmentIds = equipmentDataList.map((item) => item.id);
    const trailerData = trailerDataList[0] || null;
    const assetSnapshots = selectedAssetData.map(fleetAssetSnapshot);
    const unauthorizedAsset = selectedAssetData.find(
      (item) =>
        !canUseAsset(item, uid) &&
        (!editMode || !originalAssignedAssetIds.has(item.id)),
    );
    if (unauthorizedAsset) {
      return setError(`${fleetAssetKindLabel(unauthorizedAsset.assetKind)}「${assetPrimaryLabel(unauthorizedAsset)}」目前維修中、已報廢或未授權此成員使用，不能指派。`);
    }
    const boundElsewhere = selectedAssetData.find((item) => {
      if (!["trailer", "container"].includes(item.assetKind)) return false;
      const ownerVehicleId = bindingOwnerByAssetId[item.id];
      return (
        ownerVehicleId &&
        ownerVehicleId !== text(vehicleData?.id) &&
        (!editMode || !originalAssignedAssetIds.has(item.id))
      );
    });
    if (boundElsewhere) {
      return setError(`${fleetAssetKindLabel(boundElsewhere.assetKind)}「${assetPrimaryLabel(boundElsewhere)}」已綁定其他車輛，請先到車隊資產調整綁定。`);
    }

    const applicationId = editMode
      ? text(initialApplication?.applicationId || initialApplication?.id)
      : makeRandomId();
    const vehicleScheduleId = editMode
      ? text(initialApplication?.vehicleSchedule?.scheduleId) || makeRandomId()
      : makeRandomId();
    const dispatchBatchId = editMode
      ? text(initialApplication?.dispatchBatchId) || makeRandomId()
      : makeRandomId();
    const startAt = dateTime(schedule.date, schedule.startTime);
    const endAt = schedule.hasRange
      ? dateTime(schedule.date, schedule.endTime)
      : startAt;
    const originalDateKey =
      inputDateValue(initialApplication?.schedule?.startAt) ||
      text(initialApplication?.dateKey);
    if (editMode && schedule.date !== originalDateKey) {
      return setError("跨日期編輯尚未開放，請維持原配送日期。日期搬移需要同步更新工作、成員與資產排程資料。");
    }
    const originalScheduleAssetIds = uniqueIds(
      Array.isArray(initialApplication?.vehicleSchedule?.assetIds) &&
        initialApplication.vehicleSchedule.assetIds.length
        ? initialApplication.vehicleSchedule.assetIds
        : Array.isArray(initialApplication?.vehicleAssignment?.assetIds) &&
            initialApplication.vehicleAssignment.assetIds.length
          ? initialApplication.vehicleAssignment.assetIds
          : [...originalAssignedAssetIds],
    );
    const sortedAssetIds = [...selectedAssetIds].sort();
    const sortedOriginalAssetIds = [...originalScheduleAssetIds].sort();
    const assignmentChanged =
      editMode &&
      JSON.stringify(sortedAssetIds) !== JSON.stringify(sortedOriginalAssetIds);
    const originalStartTime = inputTimeValue(
      initialApplication?.schedule?.startAt ||
        initialApplication?.schedule?.startTime ||
        initialApplication?.startTime,
    );
    const originalEndTime = inputTimeValue(
      initialApplication?.schedule?.endAt ||
        initialApplication?.schedule?.endTime ||
        initialApplication?.endTime,
    );
    const scheduleTimeChanged =
      editMode &&
      (schedule.startTime !== originalStartTime ||
        (schedule.hasRange === true) !==
          (initialApplication?.schedule?.hasRange === true) ||
        (schedule.hasRange && schedule.endTime !== originalEndTime));
    if (
      editMode &&
      hasExecutionStarted &&
      (assignmentChanged || scheduleTimeChanged)
    ) {
      return setError("工作已開始，不能更改車輛、資產或排程時間；成員與已產生的執行紀錄會保持不變。");
    }
    const stopsForSave = isInspectionMode
      ? normalizeStops(
          stops
            .filter((stop) => text(stop.text) || coordsOK(stop))
            .slice(0, 2)
            .map((stop, index) => ({
              ...stop,
              id: index === 0 ? "SRT" : "END",
              order: index + 1,
              operationType: index === 0 ? "PICKUP" : "DELIVERY",
              recipients: [],
            })),
        )
      : normalizeStops(
          (startPointEnabled
            ? stops
            : hasExplicitStartStop(stops)
              ? stops.filter((stop) => text(stop?.id).toUpperCase() !== "SRT")
              : stops)
            .slice(0, 2)
            .map((stop, index) => ({
              ...stop,
              id: index === 0 ? "SRT" : "END",
              order: index + 1,
              operationType: index === 0 ? "PICKUP" : "DELIVERY",
            })),
        );

    if (!isInspectionMode) {
      let pendingMediaCount = 0;
      stopsForSave.forEach((stop) =>
        (stop.recipients || []).forEach((recipient) =>
          recipientContents(recipient).forEach((content) => {
            pendingMediaCount += (content.photos || []).filter(
              (media) => media?.file,
            ).length;
            pendingMediaCount += normalizeDocumentProofs(
              content?.documents || content?.accessRequirements?.documents,
              true,
              true,
            ).filter((media) => media?.file).length;
          }),
        ),
      );
      const estimatedDocumentBytes =
        serializedFirestoreSizeBytes(
          compactRecord({
            applicationId,
            orgId,
            dateKey: schedule.date,
            applicationName: text(applicationName).slice(0, 80),
            workType: "INTERNAL_DELIVERY",
            workMode: "DELIVERY",
            workDomain: normalizeWorkDomain(workType),
            driver: {
              uid,
              name: text(member?.displayName || member?.name),
              phone: text(member?.phone),
            },
            vehicle: vehicleData,
            trailer: trailerData,
            trailers: trailerDataList,
            trailerIds,
            schedule: {
              startAt,
              hasRange: schedule.hasRange,
              distanceKm: Number(Number(route.km).toFixed(2)),
              durationMinutes: Math.max(1, Math.round(Number(route.min))),
              ...(schedule.hasRange ? { endAt } : {}),
            },
            stops: stopsForSave,
            execution: editMode ? initialApplication?.execution : undefined,
            cancelRequest: editMode ? initialApplication?.cancelRequest : null,
            vehicleSchedule: editMode ? initialApplication?.vehicleSchedule : undefined,
          }),
        ) +
        pendingMediaCount * 1024 +
        24 * 1024;
      if (estimatedDocumentBytes > FIRESTORE_APPLICATION_SAFE_BYTES) {
        setError(
          `工作資料估算約 ${Math.ceil(estimatedDocumentBytes / 1024)} KB，超過 Firestore 安全上限（${Math.floor(FIRESTORE_APPLICATION_SAFE_BYTES / 1024)} KB）。請減少貨物照片／文件或過長內容後再儲存；尚未上傳任何媒體。`,
        );
        return;
      }
    }

    submitLock.current = true;
    setSubmitting(true);

    const shouldReviewSchedule =
      !scheduleReviewConfirmed.current &&
      (!editMode || assignmentChanged || scheduleTimeChanged);
    if (shouldReviewSchedule) {
      setProgress("正在讀取目前資產排程…");
      try {
        const review = await loadScheduleReview(schedule.date, startAt, {
          excludeApplicationId: editMode ? applicationId : "",
          excludeScheduleId: editMode
            ? initialApplication?.vehicleSchedule?.scheduleId
            : "",
        });
        if (review) {
          setScheduleReview(review);
          submitLock.current = false;
          setSubmitting(false);
          setProgress("");
          return;
        }
      } catch (scheduleError) {
        submitLock.current = false;
        setSubmitting(false);
        setProgress("");
        setError(
          `目前資產排程讀取失敗：${scheduleError?.message || "請稍後再試"}`,
        );
        return;
      }
    }

    try {
      const uploadedByCargo = {};
      const uploadedByDocuments = {};
      const uploadedAssetRecords = [];
      for (let stopIndex = 0; stopIndex < stopsForSave.length; stopIndex += 1) {
        const stop = stopsForSave[stopIndex];
        if (!isInspectionMode) {
          for (const recipient of stop.recipients || []) {
            const recipientId = canonicalRecipientCode(
              recipient.id || recipient.recipientId,
            );
            for (const content of recipientContents(recipient)) {
              const contentId = canonicalCargoCode(content.id || content.contentId);
              const cargoKey = cargoMediaKey(stop.id, recipientId, contentId);
              uploadedByCargo[cargoKey] = await uploadCargoPhotos(
                applicationId,
                stop,
                recipient,
                { ...content, id: contentId, contentId },
                stop.order || stopIndex,
                uploadedAssetRecords,
              );
              const normalizedDocuments = normalizeDocumentProofs(
                content.documents || content.accessRequirements?.documents,
                true,
                true,
              );
              uploadedByDocuments[cargoKey] = await uploadCargoDocuments(
                applicationId,
                stop,
                recipient,
                {
                  ...content,
                  id: contentId,
                  contentId,
                  documents: normalizedDocuments,
                },
                stop.order || stopIndex,
                uploadedAssetRecords,
              );
            }
          }
        }
      }
      setProgress(editMode ? "正在儲存單點工作…" : "正在建立單點工作…");
      const originalStopsById = new Map(
        (copyMode ? [] : initialApplication?.stops || []).map((item) => [
          text(item?.id).toUpperCase(),
          item,
        ]),
      );
      const payload = {
        schemaVersion: 5,
        cargoModelVersion: "recipient-cargo-number-v1",
        workDomainModelVersion: "single-domain-v1",
        applicationId,
        orgId,
        dateKey: schedule.date,
        type: "internal",
        workType: isInspectionMode ? "INSPECTION" : "INTERNAL_DELIVERY",
        workMode: isInspectionMode ? "ROUTE_RECORDING" : "DELIVERY",
        applicationName: text(applicationName).slice(0, 80),
        workDomain: isInspectionMode ? "NONE" : normalizeWorkDomain(workType),
        status: editMode
          ? text(initialApplication?.status).toUpperCase() || "ASSIGNED"
          : "ASSIGNED",
        dispatchBatchId,
        createdByUid: editMode
          ? text(initialApplication?.createdByUid) || text(user?.uid)
          : text(user?.uid),
        createdByName: editMode
          ? text(initialApplication?.createdByName) ||
            text(account?.displayName || user?.displayName) ||
            "管理員"
          : text(account?.displayName || user?.displayName) || "管理員",
        createdAt: editMode
          ? initialApplication?.createdAt || serverTimestamp()
          : serverTimestamp(),
        updatedByUid: text(user?.uid),
        updatedAt: serverTimestamp(),
        driver: {
          uid: uid || (editMode ? text(initialApplication?.driver?.uid) : ""),
          name: text(
            member?.displayName ||
              member?.name ||
              (editMode ? initialApplication?.driver?.name : ""),
          ),
          phone: text(
            member?.phone || (editMode ? initialApplication?.driver?.phone : ""),
          ),
        },
        vehicle: vehicleData
          ? {
              id: vehicleData.id,
              assetId: vehicleData.id,
              assetKind: vehicleData.assetKind,
              assetType: vehicleData.assetType,
              assetTypeOther: text(vehicleData.assetTypeOther) || null,
              displayName: text(vehicleData.displayName) || null,
              plateNumber: text(vehicleData.plateNumber),
              vehicleType: fleetAssetTypeLabel(vehicleData),
              brand: text(vehicleData.make || vehicleData.brand),
              make: text(vehicleData.make || vehicleData.brand),
              model: text(vehicleData.model),
              tonnage: vehicleData.tonnage ?? vehicleData.capacity?.maxGrossWeightKg ?? null,
            }
          : null,
        trailer: trailerData
          ? {
              id: trailerData.id,
              assetId: trailerData.id,
              assetKind: trailerData.assetKind,
              assetType: trailerData.assetType,
              assetTypeOther: text(trailerData.assetTypeOther) || null,
              displayName: text(trailerData.displayName) || null,
              plateNumber: text(trailerData.plateNumber),
              vehicleType: fleetAssetTypeLabel(trailerData),
            }
          : null,
        trailers: trailerDataList.map((item) => ({
          id: item.id,
          assetId: item.id,
          assetKind: item.assetKind,
          assetType: item.assetType,
          assetTypeOther: text(item.assetTypeOther) || null,
          displayName: text(item.displayName) || null,
          plateNumber: text(item.plateNumber),
          vehicleType: fleetAssetTypeLabel(item),
        })),
        trailerIds,
        assetSnapshots,
        vehicleAssignment: {
          ...(editMode ? initialApplication?.vehicleAssignment || {} : {}),
          driverId: uid,
          vehicleId: vehicleData?.id || null,
          tractorId: isTractorAsset(vehicleData) ? vehicleData.id : null,
          trailerId: null,
          containerId: null,
          equipmentId: null,
          trailerIds,
          containerIds,
          equipmentIds,
          assetIds: selectedAssetIds,
          assetSnapshots,
        },
        vehicleSchedule: {
          ...(editMode ? initialApplication?.vehicleSchedule || {} : {}),
          scheduleId: vehicleScheduleId,
          trailerId: null,
          containerId: null,
          equipmentId: null,
          workDateKey: schedule.date,
          startAt,
          applicationId,
          jobTitle: text(applicationName).slice(0, 20),
          driverId: uid,
          driverNameSnapshot: text(member?.displayName || member?.name),
          vehicleMode: vehicleData
            ? isTractorAsset(vehicleData)
              ? "TRACTOR_WITH_TRAILERS"
              : "INTEGRATED_VEHICLE"
            : "ASSET_ONLY",
          vehicleId: vehicleData?.id || null,
          tractorId: isTractorAsset(vehicleData) ? vehicleData.id : null,
          trailerIds,
          containerIds,
          equipmentIds,
          assetIds: selectedAssetIds,
          assetSnapshots,
          vehicleSnapshot: {
            vehiclePlateNumber: text(vehicleData?.plateNumber),
            vehicleType: vehicleData ? fleetAssetTypeLabel(vehicleData) : null,
            assetKind: vehicleData?.assetKind || null,
            assetType: vehicleData?.assetType || null,
            displayName: text(vehicleData?.displayName) || null,
            trailerPlateNumbers: trailerDataList
              .map((item) => assetPrimaryLabel(item))
              .filter(Boolean),
            containerNames: containerDataList
              .map((item) => assetPrimaryLabel(item))
              .filter(Boolean),
            equipmentNames: equipmentDataList
              .map((item) => assetPrimaryLabel(item))
              .filter(Boolean),
          },
          status: "PLANNED",
          createdByUid: text(user?.uid),
          createdAt: editMode
            ? initialApplication?.vehicleSchedule?.createdAt || serverTimestamp()
            : serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        schedule: {
          startAt,
          hasRange: isInspectionMode ? false : schedule.hasRange,
          ...(isInspectionMode
            ? {}
            : {
                distanceKm: Number(Number(route.km).toFixed(2)),
                durationMinutes: Math.max(1, Math.round(Number(route.min))),
              }),
          ...(!isInspectionMode && schedule.hasRange ? { endAt } : {}),
        },
        routePlanning:
          !isInspectionMode && optimizationRecord?.appliedCandidateId
            ? {
                algorithmVersion: optimizationRecord.version,
                selectedCandidateId: optimizationRecord.appliedCandidateId,
                inputKey: optimizationRecord.inputKey,
                candidateCount: optimizationRecord.candidates?.length || 0,
                appliedOrder: stopsForSave.map((stop) => text(stop.id).toUpperCase()),
              }
            : !isInspectionMode && editMode
              ? initialApplication?.routePlanning || null
              : null,
        stops: stopsForSave.map((stop, index) => {
          const isStart = text(stop.id).toUpperCase() === "SRT";
          const stopId = isStart ? "SRT" : text(stop.id).toUpperCase();
          const originalStop = originalStopsById.get(stopId);
          const originalRecipients = originalStop
            ? normalizeRecipients(originalStop)
            : [];
          const recipients = (stop.recipients || []).map((recipient) => {
            const recipientId = text(recipient.id || recipient.recipientId) || makeRandomId();
            const recipientEmail = normalizeRecipientEmail(recipient.email);
            const recipientProfileId = stableRecipientProfileId(orgId, recipientEmail);
            const originalRecipient = originalRecipients.find(
              (candidate) =>
                text(candidate.id || candidate.recipientId) === recipientId,
            );
            const recipientForSave = {
              ...recipient,
              recipientId,
              recipientProfileId,
              email: recipientEmail,
              accessRequirements: (() => {
                const requirements = { ...(recipient.accessRequirements || {}) };
                delete requirements.documents;
                return requirements;
              })(),
              contents: recipientContents(recipient).map((content) => {
                const contentId = canonicalCargoCode(content.id || content.contentId);
                const cargoKey = cargoMediaKey(stop.id, recipientId, contentId);
                return {
                  ...content,
                  id: contentId,
                  contentId,
                  photos: uploadedByCargo[cargoKey] || [],
                  documents: Array.isArray(uploadedByDocuments[cargoKey])
                    ? uploadedByDocuments[cargoKey]
                    : normalizeDocumentProofs(
                        content.documents || content.accessRequirements?.documents,
                      ),
                  accessRequirements: (() => {
                    const requirements = { ...(content.accessRequirements || {}) };
                    delete requirements.documents;
                    return requirements;
                  })(),
                };
              }),
            };
            return formalRecipientRecord({
              recipient: recipientForSave,
              applicationId,
              stopId,
              operationType: normalizeCargoOperation(
                recipient.operationType || stop.operationType,
              ),
              workDomain: isInspectionMode ? "NONE" : normalizeWorkDomain(workType),
              originalRecipient,
            });
          });
          const stopRecord = {
            id: stopId,
            order: Number(stop.order) || index + 1,
            operationType: deriveStopOperation(
              recipients,
              isStart ? "PICKUP" : "DELIVERY",
            ),
            timeWindowType: text(stop.timeWindowType) || "NONE",
            serviceMinutes: Number(stop.serviceMinutes) || 0,
            text: text(stop.text),
            lat: Number(stop.lat),
            lng: Number(stop.lng),
            city: text(stop.city),
            suburb: text(stop.suburb),
            road: text(stop.road),
            postalCode: text(stop.postalCode),
            verified: stop.verified === true,
            distanceFromPreviousKm:
              index === 0
                ? 0
                : Number(Number(stop.distanceFromPreviousKm || 0).toFixed(2)),
            durationFromPreviousMinutes:
              index === 0
                ? 0
                : Math.max(
                    0,
                    Math.round(Number(stop.durationFromPreviousMinutes || 0)),
                  ),
            recipients,
            proof: isStart
              ? undefined
              : compactRecord({
                  qrScannedAt: originalStop?.proof?.qrScannedAt,
                  qrScannedByUid: originalStop?.proof?.qrScannedByUid,
                  signature: originalStop?.proof?.signature,
                  signedAt: originalStop?.proof?.signedAt,
                  signedByUid: originalStop?.proof?.signedByUid,
                }),
          };
          if (text(stop.earliestStart) || text(stop.latestStart)) {
            stopRecord.timeWindow = compactRecord({
              type: text(stop.timeWindowType) || "NONE",
              earliestStart: text(stop.earliestStart),
              latestStart: text(stop.latestStart),
            });
          } else {
            stopRecord.timeWindow = { type: "NONE" };
          }
          if (text(stop.houseNumber)) stopRecord.houseNumber = text(stop.houseNumber);
          stopRecord.summary = {
            recipientCount: recipients.length,
            contentCount: recipients.reduce(
              (sum, recipient) => sum + recipient.contents.length,
              0,
            ),
            pickupCount: recipients.filter((recipient) =>
              recipientHasOperation(recipient, "PICKUP", stop.operationType),
            ).length,
            deliveryCount: recipients.filter((recipient) =>
              recipientHasOperation(recipient, "DELIVERY", stop.operationType),
            ).length,
          };
          return compactRecord(stopRecord);
        }),
        execution: {
          status: editMode
            ? text(initialApplication?.execution?.status) || "NOT_STARTED"
            : "NOT_STARTED",
          ...(editMode && initialApplication?.execution?.startedAt !== undefined
            ? { startedAt: initialApplication.execution.startedAt }
            : {}),
          ...(editMode && initialApplication?.execution?.pausedAt !== undefined
            ? { pausedAt: initialApplication.execution.pausedAt }
            : {}),
          ...(editMode && initialApplication?.execution?.pauseReason !== undefined
            ? { pauseReason: initialApplication.execution.pauseReason }
            : {}),
          ...(editMode && initialApplication?.execution?.completedAt !== undefined
            ? { completedAt: initialApplication.execution.completedAt }
            : {}),
          ...(editMode && initialApplication?.execution?.currentStopId !== undefined
            ? { currentStopId: initialApplication.execution.currentStopId }
            : {}),
          ...(editMode && initialApplication?.execution?.startLocation !== undefined
            ? { startLocation: initialApplication.execution.startLocation }
            : {}),
          ...(isInspectionMode
            ? {
                routeTrack: editMode && Array.isArray(initialApplication?.execution?.routeTrack)
                  ? initialApplication.execution.routeTrack
                  : [],
                records: editMode && Array.isArray(initialApplication?.execution?.records)
                  ? initialApplication.execution.records
                  : [],
              }
            : {}),
        },
        cancelRequest:
          editMode && initialApplication?.cancelRequest
            ? initialApplication.cancelRequest
          : {
                active: false,
                status: "none",
              },
        routeLocked: isInspectionMode
          ? false
          : editMode
            ? initialApplication?.routeLocked !== false
            : true,
      };
      payload.driver = compactRecord(payload.driver);
      payload.vehicleAssignment = compactRecord(payload.vehicleAssignment);
      if (payload.vehicle) payload.vehicle = compactRecord(payload.vehicle);
      if (payload.trailer) payload.trailer = compactRecord(payload.trailer);
      if (!payload.vehicle) delete payload.vehicle;
      if (!payload.trailer) delete payload.trailer;
      if (!payload.trailers?.length) delete payload.trailers;
      if (!payload.trailerIds?.length) delete payload.trailerIds;
      if (!payload.assetSnapshots?.length) delete payload.assetSnapshots;
      if (!payload.routePlanning) delete payload.routePlanning;
      if (!editMode) delete payload.cancelRequest;
      const applicationRef = doc(
        db,
        "orgs",
        orgId,
        "applications",
        schedule.date,
        "jobs",
        applicationId,
      );
      const oldCargoReportDay = cargoReportDayFromStartAt(
        initialApplication?.schedule?.startAt,
        initialApplication?.dateKey,
      );
      const nextCargoReportDay = cargoReportDayFromStartAt(
        payload.schedule?.startAt,
        schedule.date,
      );
      const allReportStops = [
        ...(Array.isArray(payload.stops) ? payload.stops : []),
      ];
      const nextCargoReports = cargoReportItems(applicationId, allReportStops);
      const oldCargoReports = cargoReportItems(
        applicationId,
        Array.isArray(initialApplication?.stops)
          ? initialApplication.stops
          : [],
      );
      const oldCargoIds = new Set(oldCargoReports.map((item) => item.id));
      const nextCargoIds = new Set(nextCargoReports.map((item) => item.id));
      const cargoDayRef = (day) =>
        doc(db, "orgs", orgId, "reportDays", day, "types", "cargo");
      const cargoMetaRef = (day) => cargoDayRef(day);
      const cargoChunkRef = (day, chunkId) =>
        doc(cargoDayRef(day), "chunks", chunkId);
      const cargoReportDays = [...new Set(
        [oldCargoReportDay, nextCargoReportDay].filter(Boolean),
      )];
      const oldAttendanceReportDay = cargoReportDayFromStartAt(
        initialApplication?.schedule?.startAt,
        initialApplication?.dateKey,
      );
      const nextAttendanceReportDay = cargoReportDayFromStartAt(
        payload.schedule?.startAt,
        schedule.date,
      );
      const attendanceDayRef = (day) =>
        doc(db, "orgs", orgId, "reportDays", day, "types", "attendance");
      const attendanceMetaRef = (day) => attendanceDayRef(day);
      const attendanceChunkRef = (day, chunkId) =>
        doc(attendanceDayRef(day), "chunks", chunkId);
      const attendanceReportDays = [...new Set(
        [oldAttendanceReportDay, nextAttendanceReportDay].filter(Boolean),
      )];
      const nextAttendanceReport = buildAttendanceReportItem(
        applicationId,
        nextAttendanceReportDay,
        payload,
        "single",
      );
      const vehicleScheduleRef = doc(
        db,
        "orgs",
        orgId,
        "vehicleSchedules",
        schedule.date,
        "items",
        vehicleScheduleId,
      );
      const dayRef = doc(db, "orgs", orgId, "applications", schedule.date);
      const driverScheduleRef = doc(
        db,
        "drivers",
        uid,
        "dailyLogs",
        schedule.date,
        "schedule",
        "main",
      );
      await runTransaction(db, async (transaction) => {
        const applicationSnapshot = await transaction.get(applicationRef);
        const latestApplicationData = applicationSnapshot.exists() ? applicationSnapshot.data() || {} : {};
        const latestExecutionStarted = Boolean(
          latestApplicationData.execution?.startedAt ||
            latestApplicationData.execution?.completedAt ||
            startedStates.includes(
              text(latestApplicationData.execution?.status).toUpperCase(),
            ) ||
            startedStates.includes(
              text(latestApplicationData.status).toUpperCase(),
            ) ||
            (Array.isArray(latestApplicationData.stops) &&
              latestApplicationData.stops.some(
                (stop) =>
                  stop?.progress?.startedAt ||
                  stop?.progress?.arrivedAt ||
                  stop?.progress?.completedAt ||
                  startedStates.includes(
                    text(stop?.progress?.status).toUpperCase(),
                  ),
              )),
        );
        if (
          editMode &&
          latestExecutionStarted &&
          (assignmentChanged || scheduleTimeChanged)
        ) {
          const startedError = new Error("工作已開始，不能更改車輛、資產或排程時間。");
          startedError.code = "WORK_ALREADY_STARTED";
          throw startedError;
        }
        if (latestApplicationData.status !== undefined) payload.status = latestApplicationData.status;
        if (latestApplicationData.execution !== undefined) payload.execution = latestApplicationData.execution;
        if (latestApplicationData.cancelRequest !== undefined) payload.cancelRequest = latestApplicationData.cancelRequest;
        payload.stops = preserveDriverExecutionData(
          payload.stops,
          Array.isArray(latestApplicationData.stops) ? latestApplicationData.stops : [],
        );
        const scheduleSnapshot = await transaction.get(driverScheduleRef);
        const oldData = scheduleSnapshot.exists()
          ? scheduleSnapshot.data() || {}
          : {};
        const oldIds = Array.isArray(oldData.orderedApplicationIds)
          ? oldData.orderedApplicationIds.map(text).filter(Boolean)
          : [];
        const nextIds = oldIds.includes(applicationId)
          ? oldIds
          : [...oldIds, applicationId];
        const cargoStates = new Map();
        for (const day of cargoReportDays) {
          const metaRef = cargoMetaRef(day);
          const metaSnapshot = await transaction.get(metaRef);
          const meta = metaSnapshot.exists() ? metaSnapshot.data() || {} : {};
          const chunkIds = Array.isArray(meta.chunkIds)
            ? meta.chunkIds.map(text).filter(Boolean)
            : [];
          const items = [];
          for (const chunkId of chunkIds) {
            const chunkSnapshot = await transaction.get(cargoChunkRef(day, chunkId));
            const chunkItems = chunkSnapshot.exists()
              ? chunkSnapshot.data()?.items || {}
              : {};
            Object.entries(chunkItems).forEach(([id, data]) =>
              items.push({ id, data }),
            );
          }
          cargoStates.set(day, { metaRef, chunkIds, items });
        }
        const attendanceStates = new Map();
        for (const day of attendanceReportDays) {
          const metaRef = attendanceMetaRef(day);
          const metaSnapshot = await transaction.get(metaRef);
          const meta = metaSnapshot.exists() ? metaSnapshot.data() || {} : {};
          const chunkIds = Array.isArray(meta.chunkIds)
            ? meta.chunkIds.map(text).filter(Boolean)
            : [];
          const items = [];
          for (const chunkId of chunkIds) {
            const chunkSnapshot = await transaction.get(attendanceChunkRef(day, chunkId));
            const chunkItems = chunkSnapshot.exists()
              ? chunkSnapshot.data()?.items || {}
              : {};
            Object.entries(chunkItems).forEach(([id, data]) =>
              items.push({ id, data }),
            );
          }
          attendanceStates.set(day, { metaRef, chunkIds, items });
        }
        const existingNextItems = cargoStates.get(nextCargoReportDay)?.items || [];
        const mergedItems = existingNextItems.filter(
          (item) => text(item.data?.applicationId) !== text(applicationId),
        );
        mergedItems.push(...nextCargoReports);
        const nextChunks = buildCargoReportChunks(mergedItems);
        for (const [day, state] of cargoStates) {
          state.chunkIds.forEach((chunkId) =>
            transaction.delete(cargoChunkRef(day, chunkId)),
          );
          if (day === nextCargoReportDay) {
            const chunkIds = nextChunks.map((_, index) =>
              `chunk_${String(index + 1).padStart(6, "0")}`,
            );
            nextChunks.forEach((items, index) => {
              const chunkId = chunkIds[index];
              transaction.set(cargoChunkRef(day, chunkId), {
                chunkId,
                sequence: index + 1,
                itemCount: Object.keys(items).length,
                estimatedBytes: new TextEncoder().encode(JSON.stringify(items)).length,
                items,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              });
            });
            transaction.set(state.metaRef, {
              reportType: "cargo",
              chunkCount: chunkIds.length,
              chunkIds,
              totalItemCount: mergedItems.length,
              activeChunkId: chunkIds[chunkIds.length - 1] || null,
              targetBytes: CARGO_REPORT_CHUNK_TARGET_BYTES,
              updatedAt: serverTimestamp(),
            }, { merge: true });
          } else {
            const remainingItems = state.items.filter(
              (item) => text(item.data?.applicationId) !== text(applicationId),
            );
            const chunks = buildCargoReportChunks(remainingItems);
            const chunkIds = chunks.map((_, index) =>
              `chunk_${String(index + 1).padStart(6, "0")}`,
            );
            chunks.forEach((items, index) => {
              const chunkId = chunkIds[index];
              transaction.set(cargoChunkRef(day, chunkId), {
                chunkId,
                sequence: index + 1,
                itemCount: Object.keys(items).length,
                estimatedBytes: new TextEncoder().encode(JSON.stringify(items)).length,
                items,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              });
            });
            transaction.set(state.metaRef, {
              reportType: "cargo",
              chunkCount: chunkIds.length,
              chunkIds,
              totalItemCount: remainingItems.length,
              activeChunkId: chunkIds[chunkIds.length - 1] || null,
              targetBytes: CARGO_REPORT_CHUNK_TARGET_BYTES,
              updatedAt: serverTimestamp(),
            }, { merge: true });
          }
        }
        for (const [day, state] of attendanceStates) {
          state.chunkIds.forEach((chunkId) =>
            transaction.delete(attendanceChunkRef(day, chunkId)),
          );
          const remainingItems = state.items.filter(
            (item) => text(item.data?.applicationId) !== text(applicationId),
          );
          const existingAttendance = [...attendanceStates.values()]
            .flatMap((entry) => entry.items)
            .find(
              (item) =>
                text(item.data?.applicationId) === text(applicationId),
            );
          const nextAttendanceItem = nextAttendanceReport
            ? {
                ...nextAttendanceReport,
                data: {
                  ...nextAttendanceReport.data,
                  ...(existingAttendance?.data
                    ? {
                        attendanceStatus: text(
                          existingAttendance.data.attendanceStatus,
                        ),
                        workStatus: text(existingAttendance.data.workStatus),
                        startedAt: existingAttendance.data.startedAt ?? null,
                        endedAt: existingAttendance.data.endedAt ?? null,
                        createdAt: existingAttendance.data.createdAt ?? null,
                      }
                    : {}),
                  updatedAt: serverTimestamp(),
                },
              }
            : null;
          const nextItems = day === nextAttendanceReportDay && nextAttendanceItem
            ? [...remainingItems, nextAttendanceItem]
            : remainingItems;
          const chunks = buildAttendanceReportChunks(nextItems);
          const chunkIds = chunks.map((_, index) =>
            `chunk_${String(index + 1).padStart(6, "0")}`,
          );
          chunks.forEach((items, index) => {
            const chunkId = chunkIds[index];
            transaction.set(attendanceChunkRef(day, chunkId), {
              chunkId,
              sequence: index + 1,
              itemCount: Object.keys(items).length,
              estimatedBytes: new TextEncoder().encode(JSON.stringify(items)).length,
              items,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
          });
          transaction.set(state.metaRef, {
            reportType: "attendance",
            chunkCount: chunkIds.length,
            chunkIds,
            totalItemCount: nextItems.length,
            activeChunkId: chunkIds[chunkIds.length - 1] || null,
            targetBytes: ATTENDANCE_REPORT_CHUNK_TARGET_BYTES,
            updatedAt: serverTimestamp(),
          }, { merge: true });
        }
        transaction.set(applicationRef, payload);
        uploadedAssetRecords.forEach((asset) => {
          transaction.set(doc(db, "mediaAssets", asset.assetId), asset);
        });
        transaction.set(vehicleScheduleRef, payload.vehicleSchedule, {
          merge: true,
        });
        transaction.set(
          dayRef,
          {
            orgId,
            dateKey: schedule.date,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
        transaction.set(
          driverScheduleRef,
          {
            dateKey: schedule.date,
            driverUid: uid,
            driverName: text(member?.displayName || member?.name),
            orderedApplicationIds: nextIds,
            jobCount: nextIds.length,
            published: oldData.published === true,
            locked: oldData.locked === true,
            orderSource: "admin",
            updatedByUid: text(user?.uid),
            updatedAt: serverTimestamp(),
            createdAt: oldData.createdAt || serverTimestamp(),
          },
          { merge: true },
        );
      });
      let emailQueueWarning = "";
      if (!editMode) {
        try {
          const groupsByKey = new Map();
          const emailNotifications = [];
          payload.stops.forEach((stop) => {
            (stop.recipients || []).forEach((recipient) => {
              const email = normalizeRecipientEmail(recipient.email);
              if (!email) return;
              const profileId = stableRecipientProfileId(orgId, email);
              const groupKey = `email:${email}`;
              let group = groupsByKey.get(groupKey);
              if (!group) {
                const settings = normalizeRecipientNotificationSettings(
                  recipient.notificationSettings,
                );
                group = {
                  groupKey,
                  profileId,
                  email,
                  emailEnabled: false,
                  emailTemplate: settings.emailTemplate,
                  recipientName: text(recipient.name),
                  recipientCompany: text(recipient.company),
                  recipientPhone: text(recipient.phone),
                  recipientEmail: email,
                  stopRecords: [],
                  items: [],
                };
                groupsByKey.set(groupKey, group);
              }
              const settings = normalizeRecipientNotificationSettings(
                recipient.notificationSettings,
              );
              group.emailEnabled = group.emailEnabled ||
                (settings.emailEnabled && Boolean(email));
              if (!text(group.emailTemplate) ||
                  group.emailTemplate === DEFAULT_RECIPIENT_EMAIL_TEMPLATE) {
                group.emailTemplate = settings.emailTemplate;
              }
              if (!text(group.recipientName)) group.recipientName = text(recipient.name);
              if (!text(group.recipientCompany)) group.recipientCompany = text(recipient.company);
              if (!text(group.recipientPhone)) group.recipientPhone = text(recipient.phone);
              const items = recipientContents(recipient).map((item) => ({
                ...item,
                stopId: text(stop.id),
                order: Number(stop.order || 0),
              }));
              group.items.push(...items);
              const stopRecord = {
                stopId: text(stop.id),
                order: Number(stop.order || 0),
                address: stopAddressText(stop, recipient),
                cargoCount: items.length,
                cargoQuantity: items.reduce(
                  (sum, item) => sum + cargoQuantity(item),
                  0,
                ),
                status: aggregateCargoStatus(cargoStatusCounts(items)),
              };
              group.stopRecords.push(stopRecord);
              if (settings.emailEnabled && email) {
                emailNotifications.push({
                  groupKey,
                  email,
                  emailTemplate: settings.emailTemplate,
                  recipientName: text(recipient.name),
                  recipientCompany: text(recipient.company),
                  recipientPhone: text(recipient.phone),
                  stopRecord,
                  items,
                });
              }
            });
          });

          const notificationGroups = Array.from(groupsByKey.values()).filter(
            (group) => group.emailEnabled,
          );
          const provisionalExpiresAt = new Date(
            endAt.getTime() + 3 * 24 * 60 * 60 * 1000,
          );
          for (const group of notificationGroups) {
            if (!group.email || !group.profileId) {
              throw new Error("收件人 Email 無效，無法建立配送追蹤資料。");
            }
            group.scopeKey = `recipient:${group.profileId}`;
            console.info("[CreateInternalFleetJob] recipient profile resolved", {
              applicationId,
              orgId,
              profileId: group.profileId,
              scopeKey: group.scopeKey,
              source: "normalized_email",
            });
          }
          const profileIdsByEmail = new Map(
            notificationGroups.map((group) => [group.email, group.profileId]),
          );
          payload.stops = payload.stops.map((stop) => ({
            ...stop,
            recipients: (stop.recipients || []).map((recipient) => {
              const profileId = profileIdsByEmail.get(
                normalizeRecipientEmail(recipient.email),
              );
              return profileId
                ? { ...recipient, recipientProfileId: profileId }
                : recipient;
            }),
          }));
          await setDoc(applicationRef, { stops: payload.stops }, { merge: true });
          for (const group of notificationGroups) {
            const scopeKey = `recipient:${group.profileId}`;
            const profileRef = doc(
              db,
              "orgs",
              orgId,
              "recipientProfiles",
              group.profileId,
            );
            const deliveryRef = doc(
              db,
              "orgs",
              orgId,
              "recipientProfiles",
              group.profileId,
              "deliveries",
              applicationId,
            );
            group.scopeKey = scopeKey;
            group.trackingUrl = "";
            group.linkId = "";
            group.deliverySaved = false;

            // 先建立收件人 Profile；否則 API 建立追蹤連結時會找不到收件人。
            await setDoc(
              profileRef,
              {
                profileId: group.profileId,
                displayName: group.recipientName,
                company: group.recipientCompany,
                phone: group.recipientPhone,
                email: group.email,
                emailNormalized: group.email,
                emailEnabled: group.emailEnabled,
                updatedAt: serverTimestamp(),
              },
              { merge: true },
            );

            try {
              const tracking = await requestCreateTrackingLink({
                orgId,
                dateKey: schedule.date,
                applicationId,
                scopeKey,
                recipientEmail: group.email,
                recipientProfileId: group.profileId,
                expiresAt: provisionalExpiresAt,
              });
              const canonicalTrackingUrl = normalizeTrackingUrl(tracking.trackingUrl);
              const linkId = text(tracking.linkId || tracking.link?.linkId);
              if (!isCanonicalTrackingUrl(canonicalTrackingUrl) || !/^[A-Za-z0-9_-]{1,128}$/.test(linkId)) {
                throw new Error("追蹤服務未回傳有效連結識別碼；為避免寄出無法使用的網址，已停止寄送。");
              }
              group.trackingUrl = canonicalTrackingUrl;
              console.info("[CreateInternalFleetJob] tracking URL normalization", {
                apiUrl: trackingUrlDiagnostics(tracking.trackingUrl),
                emailUrl: trackingUrlDiagnostics(group.trackingUrl),
              });
              group.linkId = linkId;
            } catch (trackingError) {
              group.trackingError = trackingError;
              emailQueueWarning =
                "工作已建立，但部分追蹤連結未能建立；相關收件人 Email 未寄出。";
              console.error(
                "[CreateInternalFleetJob] tracking link:",
                trackingError,
              );
            }

            try {
              const summary = recipientGroupSummary(group);
              await runTransaction(db, async (transaction) => {
              const profileSnapshot = await transaction.get(profileRef);
              const deliverySnapshot = await transaction.get(deliveryRef);
              const current = profileSnapshot.exists
                ? profileSnapshot.data() || {}
                : {};
              const alreadyCounted = deliverySnapshot.exists;
              const previousStatusCounts = current.statusCounts || {};
              const addCount = (key) =>
                Number(previousStatusCounts[key] || 0) +
                (alreadyCounted ? 0 : Number(summary.statusCounts[key] || 0));
              transaction.set(
                profileRef,
                {
                  profileId: group.profileId,
                  displayName: group.recipientName,
                  company: group.recipientCompany,
                  phone: group.recipientPhone,
                  email: group.email,
                  emailNormalized: group.email,
                  emailEnabled: group.emailEnabled,
                  totalJobs:
                    Number(current.totalJobs || 0) + (alreadyCounted ? 0 : 1),
                  totalCargoItems:
                    Number(current.totalCargoItems || 0) +
                    (alreadyCounted ? 0 : summary.cargoCount),
                  totalCargoQuantity:
                    Number(current.totalCargoQuantity || 0) +
                    (alreadyCounted ? 0 : summary.cargoQuantity),
                  statusCounts: {
                    PENDING_PICKUP: addCount("PENDING_PICKUP"),
                    PICKED_UP: addCount("PICKED_UP"),
                    IN_TRANSIT: addCount("IN_TRANSIT"),
                    DELIVERED: addCount("DELIVERED"),
                    EXCEPTION: addCount("EXCEPTION"),
                  },
                  firstDeliveryAt: current.firstDeliveryAt || startAt,
                  lastDeliveryAt: startAt,
                  updatedAt: serverTimestamp(),
                },
                { merge: true },
              );
              transaction.set(
                deliveryRef,
                {
                  applicationId,
                  dateKey: schedule.date,
                  applicationName: text(payload.applicationName),
                  recipientProfileId: group.profileId,
                  email: group.email,
                  status: summary.status,
                  stopCount: summary.stopCount,
                  cargoCount: summary.cargoCount,
                  cargoQuantity: summary.cargoQuantity,
                  operationType: summary.operationType,
                  cargoItems: summary.cargoItems,
                  statusCounts: summary.statusCounts,
                  stops: summary.stops,
                  startAt,
                  scheduledEndAt: endAt,
                  access: {
                    status: group.trackingUrl && group.linkId ? "active" : "link_error",
                    scopeKey,
                    linkId: group.linkId || "",
                    trackingUrl: group.trackingUrl || "",
                    emailEnabled: group.emailEnabled,
                    expiresAt: provisionalExpiresAt,
                    expiresAfter: "completedAt_plus_3_days",
                  },
                  updatedAt: serverTimestamp(),
                },
                { merge: true },
              );
              });
              group.deliverySaved = true;
            } catch (profileError) {
              emailQueueWarning =
                "工作已建立，但收件人配送資料尚未同步；相關收件人 Email 未寄出。";
              console.error(
                "[CreateInternalFleetJob] recipient summary:",
                profileError,
              );
            }
          }

          const emailArrivalWindowsByStopId = buildEmailArrivalWindowsByStopId(
            payload.stops,
            startAt,
            routeValid,
          );
          const emailJobs = emailNotifications
            .filter((notification) => {
              const group = groupsByKey.get(notification.groupKey);
              const ready = Boolean(
                group?.deliverySaved && Boolean(text(group?.linkId)) && isCanonicalTrackingUrl(group?.trackingUrl),
              );
              if (group && !ready) {
                emailQueueWarning =
                  "工作已建立，但部分追蹤連結或配送資料未完成；相關收件人 Email 未寄出。";
              }
              return ready;
            })
            .map((notification) => {
              const group = groupsByKey.get(notification.groupKey);
              const baseSummary = recipientGroupSummary({
                items: notification.items,
                stopRecords: [notification.stopRecord],
              });
              const summary = {
                ...baseSummary,
                stops: baseSummary.stops.map((stop) => ({
                  ...stop,
                  arrivalWindow: emailArrivalWindowsByStopId[text(stop.stopId).toUpperCase()] || "",
                })),
              };
              const deliveryUrl = normalizeTrackingUrl(group.trackingUrl);
              console.info("[CreateInternalFleetJob] tracking URL before email enqueue", {
                applicationId,
                linkId: text(group.linkId),
                usedHomepageFallback: !text(group.trackingUrl),
                trackingUrl: trackingUrlDiagnostics(deliveryUrl),
              });
              const emailItems = notification.items.filter(
                hasRecipientEmailCargoItem,
              );
              const body = renderRecipientEmailTemplate(
                notification.emailTemplate,
                {
                  workName: text(payload.applicationName),
                  workId: applicationId,
                  startAt: dateText(schedule.date),
                  arrivalWindow: recipientEmailArrivalSummary(summary.stops),
                  stopAddress: text(notification.stopRecord.address) || "尚未提供地址",
                  cargoSummary: emailItems
                    .map(
                      (item) =>
                        `${text(item.name) || "未命名貨物"} ${text(item.quantity)} ${text(item.unit) || "件"}`.trim(),
                    )
                    .join("、"),
                  recipientName: notification.recipientName,
                  recipientCompany: notification.recipientCompany,
                  recipientPhone: notification.recipientPhone,
                  recipientEmail: notification.email,
                  trackingUrl: deliveryUrl,
                },
              );
              const html = renderRecipientDeliveryEmailHtml({
                recipientName: notification.recipientName,
                trackingUrl: deliveryUrl,
                summary,
                items: emailItems,
                customBody:
                  text(notification.emailTemplate).trim() ===
                  DEFAULT_RECIPIENT_EMAIL_TEMPLATE.trim()
                    ? ""
                    : body,
              });
              return addDoc(collection(db, "mail"), {
                to: notification.email,
                message: {
                  subject: `Tallarge｜${text(payload.applicationName)} 已建立`,
                  text: body,
                  html,
                },
                metadata: {
                  type: "logistics_delivery_digest",
                  orgId,
                  applicationId,
                  dateKey: schedule.date,
                  recipientProfileId: group.profileId,
                  scopeKey: group.scopeKey || `recipient:${group.profileId}`,
                  linkId: group.linkId || "",
                  trackingUrl: group.trackingUrl || "",
                  expiresAt: provisionalExpiresAt.toISOString(),
                  stopCount: summary.stopCount,
                  cargoQuantity: summary.cargoQuantity,
                },
              });
            });
          if (emailJobs.length) {
            setProgress(`正在排入 ${emailJobs.length} 封站點聯絡人通知…`);
            await Promise.all(emailJobs);
          }
        } catch (emailError) {
          console.error("[CreateInternalFleetJob] recipient access:", emailError);
          emailQueueWarning =
            "工作已建立，但收件人配送摘要或 Email 通知尚未完成，請確認權限設定。";
        }
      }
      setSuccess(true);
      setProgress("");
      setError(emailQueueWarning);
      onCreated?.({ applicationId, dateKey: schedule.date, editMode, application: payload });
      } catch (submitError) {
        console.error("[CreateInternalFleetJob] submit:", submitError);
        setError(
          submitError?.code === "aborted"
            ? "目前貨物資料正在同步，請稍候幾秒後再試。"
            : submitError?.code === "WORK_ALREADY_STARTED"
              ? "工作已開始，不能更改車輛、資產或排程時間。"
              : `建立失敗：${submitError?.message || "請稍後再試"}`,
        );
      } finally {
      submitLock.current = false;
      scheduleReviewConfirmed.current = false;
      if (mounted.current) {
        setSubmitting(false);
        setProgress("");
      }
    }
  }

  const emailPreviewValues = parseRecipientEmailDraft(
    emailTemplateEditor?.draft,
    emailTemplateEditor?.templateValues || {},
  );
  const emailPreviewEmailValue = text(emailPreviewValues.recipientEmail);
  const emailPreviewEmail =
    emailPreviewEmailValue === "未填寫"
      ? ""
      : normalizeRecipientEmail(emailPreviewEmailValue);
  const emailPreviewRecipientId = text(emailTemplateEditor?.recipientId);
  const emailPreviewDraftStopId = text(emailTemplateEditor?.stopId);
  const emailPreviewStationRecords = stops
    .filter((stop) => text(stop.id) === emailPreviewDraftStopId)
    .map((stop, index) => {
      const recipients = (stop.recipients || []).filter(
        (item) =>
          text(item.id || item.recipientId) === emailPreviewRecipientId,
      );
      return {
        id: stop.id,
        order: Number(stop.order || index + 1),
        address: stopAddressText(stop, recipients[0]),
        recipients,
        contents: recipients.flatMap((item) => recipientContents(item)),
      };
    })
    .filter((stop) => stop.recipients.length > 0);
  const emailPreviewAddresses = emailPreviewStationRecords
    .map((stop) => ({
      id: stop.id,
      address:
        stop.id === emailPreviewDraftStopId
          ? text(emailPreviewValues.stopAddress) || stop.address
          : stop.address,
    }))
    .filter((item) => text(item.address));
  const emailPreviewCargoItems = emailPreviewStationRecords
    .flatMap((record) => record.contents || [])
    .filter(hasRecipientEmailCargoItem);

  const sendTestEmail = async () => {
    if (emailTestState.status === "sending") return;
    if (!emailPreviewEmail) {
      setEmailTestState({
        status: "error",
        message: "目前收件人沒有可用的 Email。",
      });
      return;
    }
    setEmailTestState({ status: "sending", message: "正在排入測試信…" });
    const testStartAt =
      dateTime(schedule.date, schedule.startTime) || new Date();
    const testArrivalWindowsByStopId = buildEmailArrivalWindowsByStopId(
      stops,
      testStartAt,
      routeValid,
    );
    const testSummary = {
      stopCount: emailPreviewAddresses.length,
      cargoCount: emailPreviewCargoItems.length,
      stops: emailPreviewAddresses.map((item, index) => ({
        order: index + 1,
        address: item.address,
        arrivalWindow: testArrivalWindowsByStopId[text(item.id).toUpperCase()] || "",
      })),
    };
    const testItems = emailPreviewCargoItems.map((item) => ({
      name: text(item.name) || "未命名貨物",
      quantity: item.quantity,
      unit: text(item.unit) || "件",
    }));
    const testValues = {
      workName: text(applicationName) || "配送工作測試",
      workId: "測試信",
      startAt: dateText(schedule.date) || dateText(testStartAt),
      arrivalWindow: recipientEmailArrivalSummary(testSummary.stops),
      stopAddress:
        testSummary.stops
          .map((stop) => text(stop.address))
          .filter(Boolean)
          .join("\n") || "尚未提供地址",
      cargoSummary: testItems
        .map((item) => `${item.name} ${item.quantity} ${item.unit}`.trim())
        .join("、"),
      recipientName: emailPreviewValues.recipientName,
      recipientCompany: emailPreviewValues.recipientCompany,
      recipientPhone: emailPreviewValues.recipientPhone,
      recipientEmail: emailPreviewEmail,
      trackingUrl: "https://tallarge.com",
    };
    const testBody = renderRecipientEmailTemplate(
      DEFAULT_RECIPIENT_EMAIL_TEMPLATE,
      testValues,
    );
    const testHtml = renderRecipientDeliveryEmailHtml({
      recipientName: testValues.recipientName,
      trackingUrl: testValues.trackingUrl,
      summary: testSummary,
      items: testItems,
    });
    try {
      await addDoc(collection(db, "mail"), {
        to: emailPreviewEmail,
        message: {
          subject: `Tallarge｜測試信｜${testValues.workName}`,
          text: testBody,
          html: testHtml,
        },
        metadata: {
          type: "logistics_delivery_test",
          orgId,
          recipientEmail: emailPreviewEmail,
          createdByUid: text(user?.uid),
        },
      });
      setEmailTestState({
        status: "success",
        message: "測試信已寫入 mail 集合，等待 Trigger Email 寄送。",
      });
    } catch (testError) {
      console.error("[CreateInternalFleetJob] test email:", testError);
      setEmailTestState({
        status: "error",
        message: `測試信排入失敗：${testError?.message || "請確認 mail 權限"}`,
      });
    }
  };

  const routeStopsForSummary = visibleStops;
  const routeRecipientsForSummary = routeStopsForSummary.reduce(
    (total, stop) => total + (stop.recipients || []).length,
    0,
  );
  const routeCargoForSummary = routeStopsForSummary.reduce(
    (total, stop) =>
      total +
      (stop.recipients || []).reduce(
        (recipientTotal, recipient) =>
          recipientTotal + recipientContents(recipient).length,
        0,
      ),
    0,
  );
  const routePickupForSummary = routeStopsForSummary.reduce(
    (total, stop) =>
      total +
      (stop.recipients || []).reduce(
        (recipientTotal, recipient) =>
          recipientTotal +
          recipientContents(recipient).filter(
            (item) => item.operationType === "PICKUP",
          ).length,
        0,
      ),
    0,
  );
  const routeDeliveryForSummary = routeStopsForSummary.reduce(
    (total, stop) =>
      total +
      (stop.recipients || []).reduce(
        (recipientTotal, recipient) =>
          recipientTotal +
          recipientContents(recipient).filter(
            (item) => item.operationType === "DELIVERY",
          ).length,
        0,
      ),
    0,
  );
  const selectedDriverNamesForSummary = selectedIds
    .map((uid) => fleetData.members.find((member) => member.uid === uid))
    .filter(Boolean)
    .map((member) => member.displayName || member.name || "未命名成員");
  const selectedAssetLabelsForSummary = selectedIds.flatMap((uid) =>
    getEffectiveAssignmentAssets(assignments[uid] || {}).map(
      (asset) => `${fleetAssetKindLabel(asset.assetKind)}：${assetPrimaryLabel(asset)}`,
    ),
  );
  const toggleSection = (key) =>
    setExpandedSections((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  const beginMapResize = (event) => {
    if (mapCollapsed || event.button !== 0) return;
    event.preventDefault();
    const layout = event.currentTarget.parentElement;
    if (!layout) return;
    const updateWidth = (clientX) => {
      const rect = layout.getBoundingClientRect();
      if (!rect.width) return;
      const nextPercent = ((rect.right - clientX) / rect.width) * 100;
      setMapWidthPercent(Math.min(64, Math.max(20, nextPercent)));
    };
    const handlePointerMove = (moveEvent) => updateWidth(moveEvent.clientX);
    const handlePointerUp = () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp, { once: true });
    updateWidth(event.clientX);
  };

  return (
        <div
          className={`cfj-root ${embedded ? "cfj-embedded" : ""}${
            headerCollapsed ? " is-header-collapsed" : ""
          }`}
        >
      <style>{`${styles}${SINGLE_POINT_FIXED_ROUTE_CSS}${INSPECTION_ASSET_PICKER_CSS}`}</style>
      <form onSubmit={submit}>
        <header className="cfj-header">
          <div>
            <button
              type="button"
              className="cfj-page-back-title"
              onClick={editMode ? exitEditor : onBack}
              disabled={submitting}
            >
              <Icon name="back" size={17} />
              <span>{editMode ? "編輯單點工作" : "建立單點工作"}</span>
            </button>
          </div>
          <button
            type="button"
            className={`cfj-header-map-toggle${mapCollapsed ? " is-collapsed" : ""}`}
            aria-expanded={!mapCollapsed}
            aria-label={mapCollapsed ? "顯示地圖" : "收起地圖"}
            onClick={() => setMapCollapsed((value) => !value)}
          >
            <span aria-hidden="true">{mapCollapsed ? "‹" : "›"}</span>
            {mapCollapsed ? "顯示地圖" : "收起地圖"}
          </button>
        </header>

        {dataError && <div className="cfj-notice cfj-error">{dataError}</div>}
        {success && (
          <div className="cfj-notice cfj-success">
            <Icon name="check" />
            <div>
              <b>{editMode ? "單點工作已更新" : "單點工作已建立"}</b>
              <p>
                {editMode
                  ? "已保留原本工作與成員指派。"
                  : "已加入成員當日排程。"}
              </p>
            </div>
            <button type="button" onClick={() => onBack?.()}>
              完成
            </button>
          </div>
        )}

        <fieldset disabled={submitting || success}>
          <div
            className={`cfj-layout${mapCollapsed ? " is-map-collapsed" : ""}`}
            style={{ "--cfj-map-width": `${mapWidthPercent}%` }}
          >
            <div className="cfj-left-column">
              <div
                className="cfj-form-scroll"
                onScroll={(event) => {
                  const target = event.currentTarget;
                  setFooterReady(
                    target.scrollTop + target.clientHeight >=
                      target.scrollHeight - 12,
                  );
                }}
              >
                <div className="cfj-form-column">
              <Section
                n="01"
                title="工作與時間"
                detail={isInspectionMode ? "先設定勤務名稱與時間，再選填起點、終點地址。" : "先設定工作名稱、工作種類與配送時間，再安排配送路線。"}
                summary={
                  <div className="cfj-summary-items">
                    <span>{schedule.date || "尚未設定日期"}</span>
                    <span>{applicationName || "尚未填寫工作名稱"}</span>
                    <span>{isInspectionMode ? "巡檢模式" : cargoCategoryMeta(cargoCategoryId).label}</span>
                  </div>
                }
                expanded={expandedSections.work}
                onToggle={() => toggleSection("work")}
              >
                <Field label="工作名稱" hint={`${applicationName.length}/20`}>
                  <input
                    className="cfj-required-field"
                    required
                    maxLength={20}
                    value={applicationName}
                    onChange={(event) => setApplicationName(event.target.value)}
                    placeholder="例如：高雄台南物流配送"
                  />
                </Field>
                <div className="cfj-category-field">
                  <div className="cfj-category-field-head">
                    <div>
                      <b>工作種類</b>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="cfj-category-picker-trigger"
                    onClick={() => setCategoryPickerOpen(true)}
                    aria-haspopup="dialog"
                    aria-expanded={categoryPickerOpen}
                  >
                    {isInspectionMode ? (
                      <img
                        className="cfj-category-picker-icon"
                        src={inspectionCategoryIcon}
                        alt=""
                        loading="lazy"
                      />
                    ) : (
                      <img
                        className="cfj-category-picker-icon"
                        src={cargoCategoryMeta(cargoCategoryId).icon}
                        alt=""
                        loading="lazy"
                      />
                    )}
                    <span className="cfj-category-picker-copy">
                      <b>{isInspectionMode ? "巡檢模式" : cargoCategoryMeta(cargoCategoryId).label}</b>
                      <small>{isInspectionMode ? "記錄實際路線、影像與勤務紀錄" : cargoCategoryMeta(cargoCategoryId).subtitle}</small>
                    </span>
                    <span className="cfj-category-picker-action">
                      更換種類
                      <Icon name="next" size={14} />
                    </span>
                  </button>
                </div>
                <div className="cfj-schedule-grid">
                  <Field label={editMode ? (isInspectionMode ? "勤務日期（固定）" : "配送日期（固定）") : (isInspectionMode ? "勤務日期" : "配送日期")}>
                    <input
                      className="cfj-required-field"
                      type="date"
                      required
                      value={schedule.date}
                      disabled={editMode}
                      onChange={(event) =>
                        setSchedule((previous) => ({
                          ...previous,
                          date: event.target.value,
                        }))
                      }
                    />
                  </Field>
                  <Field label="最早開始時間">
                    <input
                      className="cfj-required-field"
                      type="time"
                      required
                      value={schedule.startTime}
                      disabled={editMode && hasExecutionStarted}
                      onChange={(event) =>
                        setSchedule((previous) => ({
                          ...previous,
                          startTime: event.target.value,
                        }))
                      }
                    />
                  </Field>
                </div>
                <label className="cfj-start-toggle">
                  <input
                    type="checkbox"
                    checked={schedule.hasRange}
                    disabled={editMode && hasExecutionStarted}
                    onChange={(event) =>
                      setSchedule((previous) => ({
                        ...previous,
                        hasRange: event.target.checked,
                      }))
                    }
                  />
                  <span>設定最晚開始時間</span>
                  <small>選填時間區間</small>
                </label>
                {schedule.hasRange && (
                  <Field label="最晚開始時間">
                    <input
                      type="time"
                      min={schedule.startTime}
                      value={schedule.endTime}
                      disabled={editMode && hasExecutionStarted}
                      onChange={(event) =>
                        setSchedule((previous) => ({
                          ...previous,
                          endTime: event.target.value,
                        }))
                      }
                    />
                  </Field>
                )}
              </Section>

              <Section
                n="02"
                title={isInspectionMode ? "勤務路線" : "配送路線"}
                detail={isInspectionMode ? "起點與終點可選填；成員開始勤務後，系統會記錄實際 GPS 路線。" : "設定固定兩個站點：起點取貨、終點送貨，可輸入地址或直接在右側地圖點選。"}
                summary={
                  isInspectionMode ? (
                    <div className="cfj-route-summary-pill"><span>起終點地址選填</span></div>
                  ) : (
                  <div className="cfj-route-summary-pill">
                    <span>{routeStopsForSummary.length} 站</span>
                    <span>{routeRecipientsForSummary} 位收件人</span>
                    <span>{routeCargoForSummary} 件貨物</span>
                    <span>取 : {routePickupForSummary}</span>
                    <span>送 : {routeDeliveryForSummary}</span>
                  </div>
                  )
                }
                expanded={expandedSections.route}
                onToggle={() => toggleSection("route")}
                actions={!isInspectionMode ? (
                  <div className="cfj-route-head-actions">
                    {startPointEnabled && stops.length === 2 ? (
                      <button
                        type="button"
                        aria-label="交換起點與終點的位置"
                        title="交換起點與終點的位置"
                        disabled={
                          locationBusy || (editMode && hasExecutionStarted)
                        }
                        style={{
                          appearance: "none",
                          border: 0,
                          background: "transparent",
                          boxShadow: "none",
                          padding: 0,
                          width: 44,
                          height: 44,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          lineHeight: 0,
                        }}
                        onClick={swapSinglePointEndpoints}
                      >
                        <img
                          src={swapStopsIcon}
                          alt=""
                          style={{
                            width: 36,
                            height: 36,
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="cfj-route-optimize-button"
                      disabled={
                        optimizing ||
                        routePreviewLoading ||
                        stops.length < 3
                      }
                      onClick={() => optimizeRoute(false)}
                    >
                      <Icon name="spark" size={14} />
                      <span>
                        {optimizing
                          ? "產生路線中…"
                          : optimizationRecord?.inputKey === optimizationInputKey
                            ? "查看最佳化紀錄"
                            : "最佳化站點順序"}
                      </span>
                    </button>
                    {optimizationRecord?.inputKey === optimizationInputKey ? (
                      <button
                        type="button"
                        className="cfj-route-recalculate-button"
                        disabled={optimizing || routePreviewLoading || !routeReady}
                        onClick={() => optimizeRoute(true)}
                      >
                        重新計算
                      </button>
                    ) : null}
                    <label className="cfj-route-import-button">
                      <Icon name="upload" size={14} />
                      <span>{importLoading ? "匯入處理中…" : "匯入資料"}</span>
                      <input
                        ref={importFileRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        hidden
                        disabled={importLoading}
                        onChange={handleImportFile}
                      />
                    </label>
                  </div>
                ) : null}
              >
                {isInspectionMode ? (
                  <div className="cfj-inspection-addresses">
                    {stops.slice(0, 2).map((stop, index) => (
                      <div className="cfj-inspection-address-row" key={stop.id}>
                        <label>
                          <span>{index === 0 ? "起點地址" : "終點地址"}（選填）</span>
                          <div className="cfj-inspection-address-input-row">
                            <input
                              value={stop.text || ""}
                              placeholder={index === 0 ? "例如：公司基地" : "例如：返回基地"}
                              onFocus={() => focusStop(stop.id)}
                              onChange={(event) => updateAddress(stop.id, event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  event.preventDefault();
                                  locate(stop.id, event.currentTarget.value);
                                }
                              }}
                            />
                            <button
                              type="button"
                              className="cfj-inspection-common-address"
                              onClick={() => openCommonPicker("address", stop.id)}
                            >
                              <Icon name="map" size={13} />
                              選擇常用地址
                            </button>
                          </div>
                        </label>
                        {stop.verified && coordsOK(stop) ? <small>已定位</small> : <small>輸入地址後按 Enter 定位</small>}
                      </div>
                    ))}
                    <p className="cfj-inspection-hint">巡檢開始與結束位置會由成員 APP 實際記錄；此處地址僅作為選填參考。</p>
                  </div>
                ) : (
                <>
                <div className="cfj-stops">
                  {visibleStops.map((stop) => {
                    const stopOpen =
                      expandedStopKeys[stop.id] ??
                      (isStartStop(stop) || Number(stop.order) === 1);
                    const stationOperations = Array.from(
                      new Set(
                        (stop.recipients || [])
                          .flatMap((recipient) => {
                            const operation = recipientOperationType(
                              recipient,
                              stop.operationType,
                            );
                            return operation === "PICKUP_DELIVERY"
                              ? ["PICKUP", "DELIVERY"]
                              : [operation];
                          })
                          .filter(
                            (operation) =>
                              operation === "PICKUP" || operation === "DELIVERY",
                          ),
                      ),
                    );
                    if (!stationOperations.length) {
                      const fallbackOperation = text(stop.operationType).toUpperCase();
                      if (fallbackOperation === "PICKUP_DELIVERY") {
                        stationOperations.push("PICKUP", "DELIVERY");
                      } else {
                        stationOperations.push(
                          normalizeCargoOperation(
                            fallbackOperation,
                            isStartStop(stop) ? "PICKUP" : "DELIVERY",
                          ),
                        );
                      }
                    }
                    const stopRecipients = Array.isArray(stop.recipients)
                      ? stop.recipients
                      : [];
                    const stopCargoCount = stopRecipients.reduce(
                      (count, recipient) => count + recipientContents(recipient).length,
                      0,
                    );
                    const etaOffset = routeEtaDurations[text(stop.id)];
                    const etaStartMinutes = routeClockMinutes(schedule.startTime);
                    const estimatedArrival =
                      routeValid &&
                      etaStartMinutes != null &&
                      Number.isFinite(Number(etaOffset))
                        ? formatRouteClockMinutes(etaStartMinutes + Number(etaOffset))
                        : "";
                    return (
                    <div
                      key={stop.id}
                      ref={(element) => {
                        if (element) stopElementRefs.current[stop.id] = element;
                      }}
                      className={`cfj-stop ${activeId === stop.id ? "is-active" : ""} ${stopOpen ? "" : "is-collapsed"}`}
                      onClick={() => {
                        activeRef.current = stop.id;
                        setActiveId(stop.id);
                      }}
                    >
                      <span
                        className={`cfj-stop-number ${isEndStop(stop) ? "is-end" : (!isStartStop(stop) && !isEndStop(stop)) ? "is-waypoint" : ""}`}
                      >
                        {isStartStop(stop)
                          ? "起"
                          : isEndStop(stop)
                            ? "終"
                            : stop.order}
                      </span>
                      <div className="cfj-stop-body">
                        <div className="cfj-stop-topline">
                          <div className="cfj-stop-title">
                            <strong>
                              {isStartStop(stop)
                                ? "起點"
                                : isEndStop(stop)
                                  ? "終點"
                                  : `第 ${stop.order} 站`}
                            </strong>
                            {activeId === stop.id && (
                              <span className="cfj-selecting">地圖設定中</span>
                            )}
                          </div>
                          <div className="cfj-stop-tools">
                            {!isStartStop(stop) && (
                              <>
                                {deliveryStops.length > 1 && (
                                  <div className="cfj-stop-move-wrap">
                                    <button
                                      type="button"
                                      className="cfj-stop-move-button"
                                      aria-label={`調整送貨站 順序`}
                                      aria-expanded={moveMenuStopId === stop.id}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setMoveMenuStopId((current) =>
                                          current === stop.id ? "" : stop.id,
                                        );
                                      }}
                                    >
                                      <Icon name="more" size={14} />
                                      移動
                                    </button>
                                    {moveMenuStopId === stop.id && (
                                      <div
                                        className="cfj-stop-move-menu"
                                        onClick={(event) => event.stopPropagation()}
                                      >
                                        <span>移動到第幾站</span>
                                        <div className="cfj-stop-move-options">
                                          {deliveryStops.map((targetStop) =>
                                            targetStop.id === stop.id ? null : (
                                              <button
                                                key={targetStop.id}
                                                type="button"
                                                onClick={() =>
                                                  moveStopToOrder(
                                                    stop.id,
                                                    targetStop.order,
                                                  )
                                                }
                                              >
                                                {targetStop.order}
                                              </button>
                                            ),
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                <button
                                  type="button"
                                  className="cfj-stop-delete"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    removeStop(stop.id);
                                  }}
                                >
                                  <Icon name="close" size={14} />
                                  刪除此站點
                                </button>
                              </>
                            )}
                            <button
                              type="button"
                              className="cfj-stop-toggle"
                              aria-label={stopOpen ? "收合配送站" : "展開配送站"}
                              aria-expanded={stopOpen}
                              onClick={(event) => {
                                event.stopPropagation();
                                setExpandedStopKeys((current) => ({
                                  ...current,
                                  [stop.id]: !stopOpen,
                                }));
                              }}
                            >
                              <Icon
                                name={stopOpen ? "back" : "next"}
                                size={16}
                              />
                            </button>
                          </div>
                        </div>
                        <div className="cfj-stop-collapsed-summary">
                          <span
                            className="cfj-stop-collapsed-address"
                            title={text(stop.text) || "尚未設定地址"}
                          >
                            {text(stop.text) || "尚未設定地址"}
                          </span>
                          <span className="cfj-stop-collapsed-counts">
                            <span>{stopRecipients.length} 位聯絡人</span>
                            <i aria-hidden="true">·</i>
                            <span>{stopCargoCount} 件貨物</span>
                          </span>
                        </div>
                        <div className="cfj-stop-address-label">
                          <span>
                            站點地址 <b>*</b>
                          </span>
                          {!isInspectionMode && estimatedArrival ? (
                            <small
                              className="cfj-stop-eta"
                              title="僅供管理端檢視，不會傳送給聯絡人"
                            >
                              預計抵達 {estimatedArrival}
                            </small>
                          ) : null}
                        </div>
                        <div
                          className={`cfj-address-row cfj-required-field${
                            stop.verified && text(stop.text) ? " is-filled" : ""
                          }`}
                        >
                          <span className="cfj-address-leading">
                            <Icon name="pin" size={15} />
                          </span>
                          <input
                            value={stop.text}
                            disabled={locationBusy}
                            onFocus={() => focusStop(stop.id)}
                            onChange={(event) =>
                              updateAddress(stop.id, event.target.value)
                            }
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                activeRef.current = stop.id;
                                setActiveId(stop.id);
                                locate(stop.id, event.currentTarget.value);
                              }
                            }}
                            placeholder={
                              isStartStop(stop)
                                ? "請輸入取貨站地址"
                                : "請輸入配送站地址"
                            }
                            autoComplete="off"
                          />
                          {text(stop.text) && (
                            <button
                              type="button"
                              className="cfj-address-clear"
                              aria-label="清除站點地址"
                              onClick={(event) => {
                                event.stopPropagation();
                                updateAddress(stop.id, "");
                              }}
                            >
                              <Icon name="close" size={13} />
                            </button>
                          )}
                        </div>
                        <div className="cfj-common-actions">
                          <button
                            type="button"
                            onClick={() => openCommonPicker("address", stop.id)}
                          >
                            選擇常用地址
                          </button>
                        </div>
                        {activeId === stop.id &&
                          (searching || locationBusy) && (
                            <small className="cfj-muted">
                              {locationBusy ? "正在取得地址…" : "搜尋地址中…"}
                            </small>
                          )}
                        {activeId === stop.id && suggestions.length > 0 && (
                          <div className="cfj-suggestions">
                            {suggestions.map((suggestion) => (
                              <button
                                key={suggestion.id}
                                type="button"
                                disabled={locationBusy}
                                onClick={() =>
                                  locate(stop.id, null, suggestion)
                                }
                              >
                                <Icon name="pin" size={15} />
                                <span>
                                  {suggestion.normalized.text}
                                  <small>{suggestion.display}</small>
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                        {(
                          <div className="cfj-stop-details cfj-hierarchy-panel">
                            {stationOperations.map((groupOperation) => {
                              const groupRecipients = (stop.recipients || []).filter((recipient) =>
                                recipientHasOperation(recipient, groupOperation, stop.operationType),
                              );
                              const groupLabel = groupOperation === "PICKUP" ? "寄件人" : "收件人";
                              return (
                                <div className="cfj-contact-role-group" key={`${stop.id}:${groupOperation}`}>
                                  <div className="cfj-contact-role-head">
                                    <div className="cfj-recipient-title">
                                      <b>{groupLabel} <em>({groupRecipients.length})</em></b>
                                    </div>
                                    <div className="cfj-hierarchy-head-actions">
                                      <button
                                        type="button"
                                        className="cfj-compact-action is-primary"
                                        onClick={() => addRecipient(stop.id, groupOperation)}
                                      >
                                        <Icon name="plus" size={14} />
                                        新增
                                      </button>
                                    </div>
                                  </div>
                                  <div className="cfj-contact-role-list">
                                    {groupRecipients.map(
                              (recipient, recipientIndex) => {
                                const contents = recipientContents(recipient);
                                const recipientKey = `${stop.id}:${recipient.id}`;
                                const recipientNameInputId = `recipient-name-${stop.id}-${recipient.id}`;
                                const recipientOpen = Object.prototype.hasOwnProperty.call(
                                  expandedRecipientKeys,
                                  recipientKey,
                                )
                                  ? Boolean(expandedRecipientKeys[recipientKey])
                                  : groupRecipients.length <= 1;
                                const recipientOperation = recipientOperationType(
                                  recipient,
                                  stop.operationType,
                                );
                                const contactLabel =
                                  groupOperation === "PICKUP" ? "寄件人" : "收件人";
                                return (
                                  <div
                                    className={`cfj-recipient-card cfj-contact-card ${
                                      recipientOpen ? "is-open" : ""
                                    }`}
                                    key={recipient.id}
                                  >
                                    <div className="cfj-contact-record-head">
                                      <button
                                        type="button"
                                        className="cfj-contact-record-toggle"
                                        onClick={() =>
                                          setExpandedRecipientKeys((current) => ({
                                            ...current,
                                            [recipientKey]: !recipientOpen,
                                          }))
                                        }
                                        aria-expanded={recipientOpen}
                                      >
                                        <strong>
                                          {recipient.name || `${contactLabel} ${recipientIndex + 1}`}
                                        </strong>
                                        <span className="cfj-contact-cargo-count">· {contents.length} 項貨物</span>
                                        <Icon
                                          name={recipientOpen ? "chevronUp" : "chevronDown"}
                                          size={15}
                                        />
                                      </button>
                                      {(stop.recipients || []).length > 1 ? (
                                        <button
                                          type="button"
                                          className="cfj-contact-remove"
                                          onClick={() => removeRecipient(stop.id, recipient.id)}
                                        >
                                          移除
                                        </button>
                                      ) : null}
                                    </div>
                                    {recipientOpen ? (
                                      <div className="cfj-contact-record-body">
                                        <div className="cfj-contact-fields">
                                          <div className="cfj-contact-field">
                                            <div className="cfj-contact-name-label">
                                              <label htmlFor={recipientNameInputId}>{contactLabel}姓名 <b>*</b></label>
                                              <button
                                                type="button"
                                                className="cfj-contact-common-button"
                                                onClick={() => openCommonPicker("contact", stop.id, recipient.id, "recipient")}
                                              >
                                                <Icon name="user" size={12} />
                                                常用聯絡人
                                              </button>
                                            </div>
                                            <input
                                              id={recipientNameInputId}
                                              className="cfj-required-field"
                                              required
                                              aria-required="true"
                                              value={recipient.name || ""}
                                              onChange={(event) =>
                                                updateContact(
                                                  stop.id,
                                                  recipient.id,
                                                  "name",
                                                  event.target.value,
                                                )
                                              }
                                            />
                                          </div>
                                          <label className="cfj-contact-field">
                                            <span>Email <b>*</b></span>
                                            <input
                                              className="cfj-required-field"
                                              type="email"
                                              required
                                              aria-required="true"
                                              value={recipient.email || ""}
                                              onChange={(event) =>
                                                updateContact(
                                                  stop.id,
                                                  recipient.id,
                                                  "email",
                                                  event.target.value,
                                                )
                                              }
                                            />
                                          </label>
                                          <label className="cfj-contact-field">
                                            <span>公司／門市（選填）</span>
                                            <input
                                              value={recipient.company || ""}
                                              onChange={(event) =>
                                                updateContact(
                                                  stop.id,
                                                  recipient.id,
                                                  "company",
                                                  event.target.value,
                                                )
                                              }
                                            />
                                          </label>
                                          <label className="cfj-contact-field">
                                            <span>聯絡電話（選填）</span>
                                            <input
                                              value={recipient.phone || ""}
                                              onChange={(event) =>
                                                updateContact(
                                                  stop.id,
                                                  recipient.id,
                                                  "phone",
                                                  event.target.value,
                                                )
                                              }
                                            />
                                          </label>
                                          <label className="cfj-contact-field">
                                            <span>備註（選填）</span>
                                            <textarea
                                              rows={2}
                                              className="cfj-contact-note"
                                              value={recipient.note || ""}
                                              onChange={(event) =>
                                                updateContact(
                                                  stop.id,
                                                  recipient.id,
                                                  "note",
                                                  event.target.value,
                                                )
                                              }
                                              placeholder="例如：聯絡時段或其他注意事項"
                                            />
                                          </label>
                                        </div>
                                        <div className="cfj-contact-email-row">
                                          <label className="cfj-notification-option cfj-inline-notification">
                                            <input
                                              type="checkbox"
                                              checked={
                                                recipient.notificationSettings
                                                  ?.emailEnabled !== false
                                              }
                                              onChange={(event) =>
                                                updateRecipientNotification(
                                                  stop.id,
                                                  recipient.id,
                                                  "emailEnabled",
                                                  event.target.checked,
                                                )
                                              }
                                            />
                                            <span className="cfj-email-badge">@</span>
                                            <span>寄送 Email 通知</span>
                                          </label>
                                          <button
                                            type="button"
                                            className="cfj-contact-email-preview"
                                            onClick={() =>
                                              openEmailTemplateEditor(
                                                stop.id,
                                                recipient.id,
                                              )
                                            }
                                          >
                                            <Icon name="eye" size={14} />
                                            預覽
                                          </button>
                                        </div>

                                          <div className="cfj-progressive-section cfj-cargo-section cfj-contact-owned-cargo">
                                            <div className="cfj-progressive-section-head">
                                              <span className="cfj-cargo-list-title">
                                              <span className="cfj-tree-node is-cargo">
                                                <Icon name="container" size={14} />
                                              </span>
                                              <span>
                                                    <strong>此聯絡人的貨物</strong>
                                                    <small>{contents.length} 筆內容物</small>
                                              </span>
                                            </span>
                                            <div className="cfj-inline-actions">
                                              {contents.some(
                                                (content) =>
                                                  normalizeCargoOperation(
                                                    content.operationType,
                                                    recipientOperation,
                                                  ) === "DELIVERY",
                                              ) ? (
                                                <button
                                                  type="button"
                                                  className="cfj-common-picker-button"
                                                  onClick={() =>
                                                    openCargoSourcePicker(
                                                      stop.id,
                                                      recipient.id,
                                                    )
                                                  }
                                                >
                                                  <Icon name="container" size={13} />
                                                  選取既有寄件貨物
                                                </button>
                                              ) : null}
                                              <button
                                                type="button"
                                                className="cfj-compact-action is-primary"
                                                onClick={() => addCargo(stop.id, recipient.id)}
                                              >
                                                <Icon name="plus" size={13} />
                                                新增貨物
                                              </button>
                                            </div>
                                          </div>
                                          <div className="cfj-cargo-list">
                                            {contents.map((content, contentIndex) => {
                                              const contentId = text(
                                                content.id || content.contentId,
                                              );
                                              const cargoKey = `${recipientKey}:${contentId}`;
                                              const cargoOpen = Boolean(
                                                expandedCargoKeys[cargoKey],
                                              );
                                              const contentOperation =
                                                normalizeCargoOperation(
                                                  content.operationType,
                                                  recipientOperation,
                                                );
                                              return (
                                                <div
                                                  className={`cfj-cargo-item ${
                                                    cargoOpen ? "is-open" : ""
                                                  }`}
                                                  key={contentId || contentIndex}
                                                >
                                                  <div className="cfj-tree-row cfj-cargo-summary">
                                                    <button
                                                      type="button"
                                                      className="cfj-cargo-summary-trigger"
                                                      onClick={() =>
                                                        setExpandedCargoKeys((current) => ({
                                                          ...current,
                                                          [cargoKey]: !current[cargoKey],
                                                        }))
                                                      }
                                                      aria-expanded={cargoOpen}
                                                    >
                                                      <span className="cfj-tree-label">
                                                        <strong>
                                                          {text(content.name) ||
                                                            `貨物 ${contentIndex + 1}`}
                                                          <small className="cfj-cargo-quantity">
                                                            {content.quantity || "—"} {content.unit || "件"}
                                                          </small>
                                                        </strong>
                                                        {text(content.note) ? (
                                                          <small className="cfj-cargo-note-preview">
                                                            備註：{text(content.note)}
                                                          </small>
                                                        ) : null}
                                                      </span>
                                                      <span
                                                        className="cfj-cargo-operation-select cfj-compact-operation cfj-operation-fixed"
                                                        aria-label="貨物作業方向"
                                                      >
                                                        {CARGO_OPERATION_LABELS[recipientOperation] ||
                                                          CARGO_OPERATION_LABELS[contentOperation]}
                                                      </span>
                                                      <span className="cfj-cargo-chevron" aria-hidden="true">
                                                        <Icon
                                                          name={cargoOpen ? "chevronUp" : "chevronDown"}
                                                          size={14}
                                                        />
                                                      </span>
                                                    </button>
                                                  </div>
                                                  {cargoOpen ? (
                                                      <div className="cfj-cargo-inline-detail">
                                                      <div className="cfj-cargo-inline-grid">
                                                        <input
                                                          className={`cfj-required-field${
                                                            text(content.name) ? " is-filled" : ""
                                                          }`}
                                                          required
                                                          placeholder="貨物名稱"
                                                          value={content.name || ""}
                                                          onChange={(event) =>
                                                            updateCargo(
                                                              stop.id,
                                                              recipient.id,
                                                              contentId,
                                                              "name",
                                                              event.target.value,
                                                            )
                                                          }
                                                        />
                                                        <input
                                                          className={`cfj-required-field${
                                                            text(content.quantity) ? " is-filled" : ""
                                                          }`}
                                                          type="number"
                                                          required
                                                          min="1"
                                                          placeholder="數量"
                                                          value={content.quantity ?? ""}
                                                          onChange={(event) =>
                                                            updateCargo(
                                                              stop.id,
                                                              recipient.id,
                                                              contentId,
                                                              "quantity",
                                                              event.target.value,
                                                            )
                                                          }
                                                        />
                                                        <input
                                                          className={`cfj-required-field${
                                                            text(content.unit) ? " is-filled" : ""
                                                          }`}
                                                          required
                                                          placeholder="單位"
                                                          value={content.unit || ""}
                                                          onChange={(event) =>
                                                            updateCargo(
                                                              stop.id,
                                                              recipient.id,
                                                              contentId,
                                                              "unit",
                                                              event.target.value,
                                                            )
                                                          }
                                                        />
                                                        <input
                                                          className="is-wide"
                                                          placeholder="標註"
                                                          value={content.note || ""}
                                                          onChange={(event) =>
                                                            updateCargo(
                                                              stop.id,
                                                              recipient.id,
                                                              contentId,
                                                              "note",
                                                              event.target.value,
                                                            )
                                                          }
                                                        />
                                                      </div>
                                                      <div className="cfj-cargo-inline-actions">
                                                        <button
                                                          type="button"
                                                          className="cfj-cargo-detail-button"
                                                          onClick={() => {
                                                            const detailItem = {
                                                              ...recipient,
                                                              ...content,
                                                              contents: [content],
                                                            };
                                                            if (contentOperation === "DELIVERY") {
                                                              openDeliveryDetailEditor(
                                                                stop.id,
                                                                recipient.id,
                                                                detailItem,
                                                              );
                                                            } else {
                                                              openCargoDetailEditor(
                                                                stop.id,
                                                                recipient.id,
                                                                detailItem,
                                                              );
                                                            }
                                                          }}
                                                        >
                                                          <Icon name="edit" size={13} />
                                                          編輯詳細資料
                                                        </button>
                                                        <button
                                                          type="button"
                                                          className="cfj-link-action is-danger"
                                                          disabled={contents.length <= 1}
                                                          onClick={() =>
                                                            removeCargo(
                                                              stop.id,
                                                              recipient.id,
                                                              contentId,
                                                            )
                                                          }
                                                        >
                                                          移除貨物
                                                        </button>
                                                      </div>
                                                    </div>
                                                  ) : null}
                                                  <div className="cfj-cargo-photos-panel">
                                                    <div className="cfj-cargo-photos-heading">
                                                      <span>照片</span>
                                                      <small>最多 5 張</small>
                                                    </div>
                                                    <div className="cfj-cargo-photos-grid">
                                                      {(content.photos || []).map((photo, photoIndex) => {
                                                        const source = photo?.preview || photo?.url || photo?.path || assetPhotoPreviewUrls[photo?.assetId];
                                                        if (!source) return null;
                                                        return (
                                                          <div
                                                            className="cfj-cargo-photo-thumb"
                                                            key={photo.assetId || photo.storagePath || photo.url || `${contentId}-${photoIndex}`}
                                                          >
                                                            <img src={source} alt={`貨物照片 ${photoIndex + 1}`} />
                                                            <button
                                                              type="button"
                                                              aria-label="移除貨物照片"
                                                              onClick={() => removeCargoPhoto(stop.id, recipient.id, contentId, photo)}
                                                            >
                                                              <Icon name="close" size={11} />
                                                            </button>
                                                          </div>
                                                        );
                                                      })}
                                                      {(content.photos || []).length < 5 && (
                                                        <label className="cfj-cargo-photo-add cfj-cargo-photo-add-large">
                                                          <Icon name="upload" size={13} />
                                                          <span>＋照片</span>
                                                          <input
                                                            type="file"
                                                            accept="image/*"
                                                            multiple
                                                            hidden
                                                            onChange={(event) => addCargoPhotos(stop.id, recipient.id, contentId, event)}
                                                          />
                                                        </label>
                                                      )}
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>


                                        </div>
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              },
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
                {!!route.error && (
                  <div className="cfj-inline-error">{route.error}</div>
                )}
                </>)}
              </Section>

              <Section
                n="03"
                title="成員與資產"
                detail={
                  editMode
                    ? hasExecutionStarted
                      ? "工作已開始，車輛與排程時間固定；成員不可更換。"
                      : "成員固定不可更換；車輛與資產可調整，時間可編輯；日期目前固定。"
                    : "選擇一位成員及新版車隊資產；已綁定資產會自動帶入。"
                }
                summary={
                  <div className="cfj-summary-items">
                    <span>
                      {selectedDriverNamesForSummary.length
                        ? selectedDriverNamesForSummary.join("、")
                        : "尚未選擇成員"}
                    </span>
                    <span>
                      {selectedAssetLabelsForSummary.length
                        ? selectedAssetLabelsForSummary.join("、")
                        : "尚未選擇資產"}
                    </span>
                  </div>
                }
                expanded={expandedSections.drivers}
                onToggle={() => toggleSection("drivers")}
              >
                <div>
                  {editMode && (
                    <div className="cfj-edit-lock-note">
                      {hasExecutionStarted
                        ? "工作已開始，為保護現場執行紀錄，車輛／資產與排程時間不可更換；成員維持原指派。"
                        : "成員維持原指派；可調整車輛、資產及時間。配送日期目前固定，跨日搬移尚未開放。"}
                    </div>
                  )}
                  <div className="cfj-driver-toolbar">
                    <div className="cfj-driver-search">
                      <Icon name="search" size={16} />
                      <input
                        value={driverSearch}
                        onChange={(event) =>
                          setDriverSearch(event.target.value)
                        }
                        placeholder="搜尋成員姓名或電話"
                        disabled={editMode}
                      />
                    </div>
                    <span>{selectedIds.length} 位已選擇</span>
                  </div>
                  {loading ? (
                    <div className="cfj-empty">正在讀取成員、資產摘要與綁定…</div>
                  ) : !visibleMembers.length ? (
                    <div className="cfj-empty">沒有可顯示的成員</div>
                  ) : (
                    <div className="cfj-member-scroll">
                      {visibleMembers.map((member) => {
                        const selected = selectedIds.includes(member.uid);
                        return (
                          <button
                            key={member.uid}
                            type="button"
                            className={`cfj-member-chip ${selected ? "is-active" : ""}`}
                            disabled={editMode}
                            onClick={() => toggleMember(member.uid)}
                          >
                            <span
                              className="cfj-member-avatar"
                              style={{
                                backgroundColor: selected
                                  ? member.color
                                  : "#EEF1F3",
                                color: selected ? "#fff" : "#64748B",
                              }}
                            >
                              {text(
                                member.displayName || member.name || "司",
                              ).slice(0, 1)}
                            </span>
                            <span>
                              {member.displayName ||
                                member.name ||
                                "未命名成員"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {selectedIds.length ? (
                    selectedIds.map((uid) => {
                      const member = fleetData.members.find(
                        (item) => item.uid === uid,
                      );
                      const assignment = assignments[uid] || {};
                      return (
                        <div className="cfj-assignment-card" key={uid}>
                          <div className="cfj-assignment-head">
                            <span
                              className="cfj-assignment-dot"
                              style={{ backgroundColor: member?.color || "#64748B" }}
                            />
                            <div>
                              <b>{member?.displayName || member?.name || "未命名成員"}</b>
                              <small>選擇這位成員執行巡檢時使用的資產</small>
                            </div>
                            <button
                              type="button"
                              className="cfj-assignment-remove"
                              disabled={editMode}
                              onClick={() => toggleMember(uid)}
                            >
                              <Icon name="close" size={14} />
                            </button>
                          </div>
                          {(() => {
                            const selectedAssets = getEffectiveAssignmentAssets(assignment);
                            const summary = selectedAssets
                              .map((asset) => `${fleetAssetKindLabel(asset.assetKind)} ${assetPrimaryLabel(asset)}`)
                              .join("、");
                            return (
                              <div className="cfj-asset-picker-trigger">
                                <div className="cfj-asset-picker-trigger-copy">
                                  <span className="cfj-asset-picker-trigger-icon">
                                    <Icon name="truck" size={17} />
                                  </span>
                                  <span>
                                    <strong>{selectedAssets.length ? `已選 ${selectedAssets.length} 項資產` : "尚未選擇資產"}</strong>
                                    <small>{summary || "車輛、托架、容器與設備"}</small>
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  className="cfj-asset-picker-open"
                                  onClick={() => openAssetPicker(uid)}
                                  disabled={submitting || loading}
                                >
                                  <Icon name="plus" size={14} />
                                  {editMode
                                    ? canEditVehicleSchedule
                                      ? "調整資產"
                                      : "檢視資產"
                                    : "選擇資產"}
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })
                  ) : (
                    <div className="cfj-driver-empty">
                      <Icon name="user" size={25} />
                      <b>尚未選擇成員</b>
                      <p>點選上方成員即可開始指派車輛。</p>
                    </div>
                  )}
                </div>
              </Section>
                </div>
              <footer className={`cfj-footer${footerReady ? " is-ready" : ""}`}>
                <div>
                  <b>
                    {selectedIds.length
                      ? editMode
                        ? editDirty
                          ? "即將更新 1 筆單點工作"
                          : "尚未修改工作內容"
                        : "即將建立 1 筆單點工作"
                      : "這趟配送準備好了嗎？"}
                  </b>
                  <small>
                    {progress ||
                      (editMode
                        ? editDirty
                          ? "成員人選固定，儲存後返回工作詳情"
                          : "沒有修改，退出即可，不會讀寫 Firestore"
                        : "確認完成後即可建立")}
                  </small>
                </div>
                <button
                  type={editMode && !editDirty ? "button" : "submit"}
                  onClick={editMode && !editDirty ? exitEditor : undefined}
                  className="cfj-submit"
                  disabled={submitting || success || loading || !!dataError}
                >
                  {submitting ? (
                    <span className="cfj-spinner" />
                  ) : (
                    <Icon name={editMode && !editDirty ? "back" : "plus"} />
                  )}
                  {submitting
                    ? editMode
                      ? "儲存中…"
                      : "建立中…"
                    : success
                      ? editMode
                        ? "已更新工作"
                        : "已建立工作"
                      : editMode && !editDirty
                        ? "退出編輯"
                        : editMode
                          ? "儲存變更"
                          : "建立並指派"}
                </button>
              </footer>
              </div>
            </div>
            <div
              className="cfj-layout-gap cfj-layout-resize-handle"
              role="separator"
              aria-label="調整地圖寬度"
              aria-orientation="vertical"
              aria-valuemin={20}
              aria-valuemax={64}
              aria-valuenow={Math.round(mapWidthPercent)}
              tabIndex={0}
              onPointerDown={beginMapResize}
              onKeyDown={(event) => {
                if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
                event.preventDefault();
                setMapWidthPercent((current) =>
                  Math.min(
                    64,
                    Math.max(20, current + (event.key === "ArrowLeft" ? 2 : -2)),
                  ),
                );
              }}
            >
              <span aria-hidden="true" />
            </div>
            <aside className="cfj-map-column">
              <div>
                <RouteMap
                  stops={visibleStops}
                  activeId={activeId}
                  route={route}
                  fitVersion={fitVersion}
                  mapFocus={mapFocus}
                  onPick={handleMapPick}
                  onSelectStop={focusStop}
                  onError={setMapError}
                  locationBusy={locationBusy}
                  onAddStop={isInspectionMode ? addStop : undefined}
                  simpleEndpoints={!isInspectionMode}
                  showOptimizeButton={isInspectionMode}
                  onOptimize={() => optimizeRoute(false)}
                  optimizeDisabled={stops.length < 3}
                  optimizing={optimizing || routePreviewLoading}
                  optimizeLabel={
                    optimizationRecord?.inputKey === optimizationInputKey
                      ? "查看最佳化"
                      : "最佳化站點"
                  }
                />
                {mapError && (
                  <div className="cfj-map-error">
                    {mapError}
                    <button type="button" onClick={() => setMapError("")}>
                      關閉
                    </button>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </fieldset>

        {categoryPickerOpen ? (
          <div
            className="cfj-category-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cfj-category-modal-title"
            onClick={() => setCategoryPickerOpen(false)}
          >
            <div
              className="cfj-category-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="cfj-category-modal-head">
                <div>
                  <h2 id="cfj-category-modal-title">選擇工作種類</h2>
                  <p>請選擇配送工作種類，工作表單會依類型顯示對應欄位。</p>
                </div>
                <button
                  type="button"
                  className="cfj-category-modal-close"
                  onClick={() => setCategoryPickerOpen(false)}
                  aria-label="關閉工作種類選擇"
                >
                  ×
                </button>
              </div>
              <div className="cfj-category-modal-body">
                <div className="cfj-category-modal-grid">
                  {CARGO_CATEGORY_OPTIONS.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      className={`cfj-category-option${
                        workType === category.id ? " is-active" : ""
                      }`}
                      aria-pressed={workType === category.id}
                      onClick={() => requestCategoryChange(category.id)}
                    >
                      <img
                        className="cfj-category-icon"
                        src={category.icon}
                        alt=""
                        loading="lazy"
                      />
                      <span className="cfj-category-copy">
                        <b>{category.label}</b>
                        <small>{category.subtitle}</small>
                      </span>
                      {workType === category.id ? (
                        <Icon name="check" size={14} />
                      ) : null}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {categoryChangeRequest ? (
          <div
            className="cfj-category-confirm-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cfj-category-confirm-title"
            onClick={() => setCategoryChangeRequest(null)}
          >
            <div
              className="cfj-category-confirm-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="cfj-category-confirm-icon">
                <Icon name="info" size={22} />
              </div>
              <h2 id="cfj-category-confirm-title">確定要變更工作種類嗎？</h2>
              <p>
                變更工作種類後，目前貨物的詳細資料將會被清除，包含
                <strong className="cfj-category-confirm-danger-text">
                  重量、尺寸、體積、包裝方式及工作種類專屬欄位
                </strong>
                。
              </p>
              <p>
                貨物名稱、數量、單位與備註會保留。
                <strong className="cfj-category-confirm-danger-text">
                  此操作無法復原。
                </strong>
              </p>
              <div className="cfj-category-confirm-actions">
                <button
                  type="button"
                  className="cfj-cargo-secondary-button"
                  onClick={() => setCategoryChangeRequest(null)}
                >
                  取消
                </button>
                <button
                  type="button"
                  className="cfj-category-confirm-danger"
                  onClick={() =>
                    applyCategoryChange(categoryChangeRequest.nextWorkType)
                  }
                >
                  繼續變更
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {cargoSourcePicker ? (
          <div
            className="cfj-cargo-source-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cfj-cargo-source-title"
            onClick={() => setCargoSourcePicker(null)}
          >
            <div
              className="cfj-cargo-source-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <header className="cfj-cargo-source-head">
                <div>
                  <h2 id="cfj-cargo-source-title">選取既有寄件貨物</h2>
                  <p>選擇前面取貨站建立的寄件貨物，會自動帶入寄件人與貨物資料。</p>
                </div>
                <button
                  type="button"
                  className="cfj-cargo-detail-close"
                  onClick={() => setCargoSourcePicker(null)}
                  aria-label="關閉貨櫃來源選擇"
                >
                  <Icon name="close" size={18} />
                </button>
              </header>
              <div className="cfj-cargo-source-list">
                {cargoSourceOptions.length ? (
                  cargoSourceOptions.map((source) => {
                    const selected = (cargoSourcePicker.selectedKeys || []).includes(source.key);
                    return (
                      <button
                        type="button"
                        className={`cfj-cargo-source-option${selected ? " is-selected" : ""}`}
                        key={source.key}
                        aria-pressed={selected}
                        onClick={() => toggleCargoSource(source.key)}
                      >
                        <span className="cfj-cargo-source-checkbox" aria-hidden="true">
                          {selected ? <Icon name="check" size={14} /> : null}
                        </span>
                        <span className="cfj-cargo-source-option-icon">
                          <Icon name="container" size={20} />
                        </span>
                        <span className="cfj-cargo-source-option-copy">
                          <strong>{text(source.record?.name) || "未命名貨物"}</strong>
                          <small>
                            {source.record?.quantity || "—"} {text(source.record?.unit) || "件"}・
                            取貨站：{source.summary?.pickupList?.map((item) => item.index).join("、") || "—"}
                          </small>
                          <em>
                            已建立取貨 {source.summary?.pickupCount || 0} 次・送貨 {source.summary?.deliveryCount || 0} 次
                          </em>
                        </span>
                        <Icon name={selected ? "check" : "next"} size={17} />
                      </button>
                    );
                  })
                ) : (
                  <div className="cfj-cargo-source-empty">
                    <Icon name="container" size={28} />
                    <strong>目前還沒有可引用的貨物</strong>
                    <span>請先在前面的取貨站建立一筆可配送的貨物。</span>
                  </div>
                )}
              </div>
              <footer className="cfj-cargo-source-footer">
                <span>可複選多筆寄件貨物，確認後會一次加入本站配送清單。</span>
                <button
                  type="button"
                  className="cfj-cargo-primary-button"
                  disabled={!(cargoSourcePicker.selectedKeys || []).length}
                  onClick={addCargoFromSelectedSources}
                >
                  <Icon name="check" size={14} />
                  加入已選貨物（{(cargoSourcePicker.selectedKeys || []).length}）
                </button>
              </footer>
            </div>
          </div>
        ) : null}

        {cargoDetailEditorDraft ? (
          <div
            className="cfj-cargo-detail-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cfj-cargo-detail-title"
            onClick={closeCargoDetailEditor}
          >
            <aside
              className="cfj-cargo-detail-drawer"
              onClick={(event) => event.stopPropagation()}
            >
              <header className="cfj-cargo-detail-head">
                <div className="cfj-cargo-detail-heading">
                  <div className="cfj-cargo-detail-title-row">
                    <Icon name="box" size={22} />
                    <h2 id="cfj-cargo-detail-title">貨物詳細資料</h2>
                  </div>
                  <p>只調整目前這一件貨物，儲存後才會回寫到貨物資料。</p>
                </div>
                <button
                  type="button"
                  className="cfj-cargo-detail-close"
                  onClick={closeCargoDetailEditor}
                  aria-label="關閉貨物詳細資料"
                >
                  <Icon name="close" size={18} />
                </button>
              </header>
              <div className="cfj-cargo-detail-summary">
                <img
                  src={cargoDetailEditorCategory?.icon}
                  alt=""
                  loading="lazy"
                />
                <div>
                  <strong>
                    {text(cargoDetailEditorDraft.name) || "尚未命名貨物"}
                  </strong>
                  <small>
                    {text(cargoDetailEditorDraft.quantity) || "—"} {text(cargoDetailEditorDraft.unit) || "件"}
                  </small>
                </div>
                <span>{cargoDetailEditorCategory?.code}｜{cargoDetailEditorCategory?.label}</span>
                <b>{CARGO_OPERATION_LABELS[normalizeCargoOperation(cargoDetailEditorDraft.operationType)]}</b>
                <em>{CARGO_STATUS_LABELS[optionalCargoStatus(cargoDetailEditorDraft.status)] || "待設定"}</em>
              </div>
              {cargoDetailSourceSummary ? (
                <div className="cfj-cargo-source-summary">
                  <div>
                    <span>貨櫃來源</span>
                    <strong>{cargoDetailSourceSummary.name}</strong>
                  </div>
                  <b>取貨 {cargoDetailSourceSummary.pickupCount} 次</b>
                  <b>送貨 {cargoDetailSourceSummary.deliveryCount} 次</b>
                </div>
              ) : null}
              
              <div className="cfj-cargo-detail-scroll">
                {cargoDetailEditorSections.map((section) => {
                  const expanded = cargoDetailEditor.expandedSections?.[section.id] !== false;
                  return (
                    <section
                      className={`cfj-cargo-detail-section${expanded ? " is-expanded" : ""}`}
                      key={section.id}
                    >
                      <button
                        type="button"
                        className="cfj-cargo-detail-section-head"
                        aria-expanded={expanded}
                        onClick={() => toggleCargoDetailSection(section.id)}
                      >
                        <span>
                          <Icon
                            name={
                              section.id === "payment"
                                ? "currency"
                                : section.id === "documents"
                                  ? "file"
                                  : section.id === "weight"
                                    ? "scale"
                                    : "box"
                            }
                            size={15}
                          />
                          {section.title}
                        </span>
                        <Icon name={expanded ? "minus" : "plus"} size={14} />
                      </button>
                      {expanded ? (
                        section.id === "documents" ? (
                          <DocumentProofEditor
                            documents={cargoDetailEditorDraft.accessRequirements?.documents}
                            onAdd={addCargoDetailDocument}
                            onChange={changeCargoDetailDocument}
                            onRemove={removeCargoDetailDocument}
                            onFile={selectCargoDetailDocumentFile}
                          />
                        ) : (
                        <div className="cfj-cargo-detail-fields">
                          {section.fields.map((field) => {
                            const value = readCargoDetailPath(
                              cargoDetailEditorDraft,
                              field.path,
                            );
                            const inputValue = value === null || value === undefined ? "" : value;
                            return (
                              <label
                                className={`cfj-cargo-detail-field${field.wide ? " is-wide" : ""}`}
                                key={field.key}
                              >
                                <span>{field.label}{field.suffix ? `（${field.suffix}）` : ""}</span>
                                {field.path === "operationType" ? (
                                  <div className="cfj-cargo-detail-readonly-operation">
                                    {CARGO_OPERATION_LABELS[normalizeCargoOperation(inputValue)]}
                                  </div>
                                ) : field.type === "textarea" ? (
                                  <textarea
                                    rows={3}
                                    value={inputValue}
                                    placeholder={`請輸入${field.label}`}
                                    onChange={(event) =>
                                      updateCargoDetailDraft(field.path, event.target.value)
                                    }
                                  />
                                ) : field.type === "select" ? (
                                  <select
                                    value={inputValue}
                                    onChange={(event) =>
                                      updateCargoDetailDraft(field.path, event.target.value)
                                    }
                                  >
                                    {field.options.map(([optionValue, optionLabel]) => (
                                      <option key={optionValue} value={optionValue}>
                                        {optionLabel}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <div className="cfj-cargo-detail-input-wrap">
                                    <input
                                      type={field.type || "text"}
                                      value={inputValue}
                                      placeholder={`請輸入${field.label}`}
                                      onChange={(event) =>
                                        updateCargoDetailDraft(field.path, event.target.value)
                                      }
                                    />
                                    {field.suffix ? <small>{field.suffix}</small> : null}
                                  </div>
                                )}
                              </label>
                            );
                          })}
                        </div>
                        )
                      ) : null}
                    </section>
                  );
                })}
              </div>
              <footer className="cfj-cargo-detail-footer">
                <button
                  type="button"
                  className="cfj-cargo-secondary-button"
                  onClick={closeCargoDetailEditor}
                >
                  取消
                </button>
                <button
                  type="button"
                  className="cfj-cargo-primary-button"
                  onClick={saveCargoDetailEditor}
                >
                  <Icon name="check" size={15} />
                  儲存貨物資料
                </button>
              </footer>
            </aside>
          </div>
        ) : null}
        {deliveryDetailEditorDraft ? (
          <DeliveryDetailDrawer
            editor={deliveryDetailEditor}
            draft={deliveryDetailEditorDraft}
            category={deliveryDetailEditorCategory}
            recipient={deliveryDetailEditorRecipient}
            sourceSummary={deliveryDetailSourceSummary}
            documents={deliveryDetailEditorDraft.accessRequirements?.documents}
            sections={deliveryDetailEditorSections}
            onClose={closeDeliveryDetailEditor}
            onChange={updateDeliveryDetailDraft}
            onAddDocument={addDeliveryDetailDocument}
            onChangeDocument={changeDeliveryDetailDocument}
            onRemoveDocument={removeDeliveryDetailDocument}
            onFileDocument={selectDeliveryDetailDocumentFile}
            onToggle={toggleDeliveryDetailSection}
            onPickSender={() =>
              openCommonPicker(
                "contact",
                deliveryDetailEditor.stopId,
                deliveryDetailEditor.recipientId,
                "sender",
                deliveryDetailEditor.itemId,
              )
            }
            onSave={saveDeliveryDetailEditor}
          />
        ) : null}


        {!!error && <div className="cfj-notice cfj-error">{error}</div>}
        {routePreview ? (
          <RouteCandidatesModal
            candidates={routePreview.candidates || []}
            selectedId={routePreview.selectedId}
            loading={routePreviewLoading}
            error={routePreviewError}
            onSelect={(selectedId) => {
              setRoutePreview((previous) =>
                previous ? { ...previous, selectedId } : previous,
              );
              setOptimizationRecord((previous) =>
                previous
                  ? { ...previous, selectedCandidateId: selectedId }
                  : previous,
              );
            }}
            onCancel={closeRoutePreview}
            onApply={applyRouteCandidate}
          />
        ) : null}
        {importPreview ? (
          <ImportPreviewModal
            fileName={importFileName}
            stations={importPreview}
            errors={importErrors}
            loading={importLoading}
            startTime={schedule.startTime}
            onChangeStation={updateImportStation}
            onChangeRecipient={updateImportRecipient}
            onChangeCargo={updateImportCargo}
            onAddRecipient={addImportRecipient}
            onRemoveRecipient={removeImportRecipient}
            onAddCargo={addImportCargo}
            onRemoveCargo={removeImportCargo}
            onChangeStartTime={(startTime) =>
              setSchedule((previous) => ({ ...previous, startTime }))
            }
            onCancel={() => {
              if (!importLoading) {
                setImportPreview(null);
                setImportFileName("");
                setImportErrors([]);
              }
            }}
            onConfirm={applyImportPreview}
          />
        ) : null}
        {assetPickerUid && assetPickerDraft ? (() => {
          const member = fleetData.members.find((item) => item.uid === assetPickerUid);
          const selectedAssetIds = getEffectiveAssignmentAssetIds(assetPickerDraft);
          const selectedAssets = selectedAssetIds.map((assetId) => assetById[assetId]).filter(Boolean);
          const boundAssets = getBoundAssetsForVehicle(text(assetPickerDraft.vehicleId));
          const activeKind = FLEET_ASSET_KIND_CONFIG.find((item) => item.key === assetPickerTab) || FLEET_ASSET_KIND_CONFIG[0];
          const tabAssets = fleetData.assets.filter((item) => item.assetKind === activeKind.key);
          const typeOptions = [...new Set(tabAssets.map((item) => text(item.assetType)).filter(Boolean))].sort();
          const visibleAssets = tabAssets.filter((item) => {
            if (!assetMatchesSearch(item, assetPickerSearch)) return false;
            if (assetPickerTypeFilter !== "all" && item.assetType !== assetPickerTypeFilter) return false;
            if (assetPickerStatusFilter !== "all" && item.status !== assetPickerStatusFilter) return false;
            return true;
          });
          const isAutomaticallyBound = (asset) =>
            (asset.assetKind === "trailer" && boundAssets.trailerIds.includes(asset.id)) ||
            (asset.assetKind === "container" && boundAssets.containerIds.includes(asset.id));
          const statusText = (status) => ({
            PARKED: "停放中",
            IN_USE: "使用中",
            MAINTENANCE: "維修中",
            RETIRED: "已報廢",
          }[text(status).toUpperCase()] || "狀態未知");
          return (
            <div
              className="cfj-asset-picker-overlay"
              role="dialog"
              aria-modal="true"
              aria-label="選擇出車資產"
              onClick={closeAssetPicker}
            >
              <div className="cfj-asset-picker-modal" onClick={(event) => event.stopPropagation()}>
                <header className="cfj-asset-picker-header">
                  <div>
                    <h2>選擇出車資產</h2>
                    <p>{member?.displayName || member?.name || "成員"}・選擇本次工作使用的資產</p>
                  </div>
                  <button type="button" className="cfj-asset-picker-close" onClick={closeAssetPicker} aria-label="關閉">×</button>
                </header>
                <nav className="cfj-asset-picker-tabs" aria-label="資產類型">
                  {FLEET_ASSET_KIND_CONFIG.map((kind) => {
                    const count = fleetData.assets.filter((item) => item.assetKind === kind.key).length;
                    const selectedCount = selectedAssets.filter((item) => item.assetKind === kind.key).length;
                    return (
                      <button
                        key={kind.key}
                        type="button"
                        className={`cfj-asset-picker-tab${assetPickerTab === kind.key ? " is-active" : ""}`}
                        aria-pressed={assetPickerTab === kind.key}
                        onClick={() => {
                          setAssetPickerTab(kind.key);
                          setAssetPickerTypeFilter("all");
                          setAssetPickerStatusFilter("all");
                        }}
                      >
                        <Icon name={kind.icon} size={16} />
                        <span>{kind.label}</span>
                        <small>{selectedCount ? `${selectedCount} 已選` : count}</small>
                      </button>
                    );
                  })}
                </nav>
                <div className="cfj-asset-picker-body">
                  <section className="cfj-asset-picker-browser" aria-label={`${activeKind.label}資產清單`}>
                    <div className="cfj-asset-picker-filters">
                      <label className="cfj-asset-picker-search">
                        <Icon name="search" size={15} />
                        <input
                          type="search"
                          value={assetPickerSearch}
                          onChange={(event) => setAssetPickerSearch(event.target.value)}
                          placeholder="搜尋車牌、名稱或型號"
                          aria-label="搜尋資產"
                        />
                      </label>
                      <label className="cfj-asset-picker-select">
                        <span>種類</span>
                        <select value={assetPickerTypeFilter} onChange={(event) => setAssetPickerTypeFilter(event.target.value)}>
                          <option value="all">全部種類</option>
                          {typeOptions.map((type) => <option value={type} key={type}>{fleetAssetTypeLabel({ assetKind: activeKind.key, assetType: type })}</option>)}
                        </select>
                      </label>
                      <label className="cfj-asset-picker-select">
                        <span>狀態</span>
                        <select value={assetPickerStatusFilter} onChange={(event) => setAssetPickerStatusFilter(event.target.value)}>
                          <option value="all">全部狀態</option>
                          <option value="PARKED">停放中</option>
                          <option value="IN_USE">使用中</option>
                          <option value="MAINTENANCE">維修中</option>
                          <option value="RETIRED">已報廢</option>
                        </select>
                      </label>
                    </div>
                    <div className="cfj-asset-picker-list-head">
                      <span>選擇</span><span>照片</span><span>車牌／名稱</span><span>種類／型號</span><span>狀態</span>
                    </div>
                    <div className="cfj-asset-picker-list">
                      {visibleAssets.map((asset) => {
                        const selected = selectedAssetIds.includes(asset.id);
                        const automaticallyBound = isAutomaticallyBound(asset);
                        const ownerVehicleId = bindingOwnerByAssetId[asset.id];
                        const boundToAnotherVehicle = Boolean(ownerVehicleId && ownerVehicleId !== text(assetPickerDraft.vehicleId));
                        const usedByAnotherDriver = isAssetAssignedToAnotherDriver(asset.id, assetPickerUid);
                        const unavailable = fleetAssetUnavailable(asset);
                        const permittedDriverUids = Array.isArray(asset.permittedDriverUids)
                          ? asset.permittedDriverUids.map(text).filter(Boolean)
                          : [];
                        const permitted = !permittedDriverUids.length || permittedDriverUids.includes(assetPickerUid);
                        const blocked =
                          automaticallyBound ||
                          (boundToAnotherVehicle && !selected) ||
                          (usedByAnotherDriver && !selected) ||
                          !canEditVehicleSchedule ||
                          submitting ||
                          (!selected && (unavailable || !permitted));
                        const detail = automaticallyBound
                          ? "隨車帶入"
                          : boundToAnotherVehicle
                            ? `已綁定 ${assetPrimaryLabel(assetById[ownerVehicleId])}`
                            : usedByAnotherDriver
                              ? "此工作已指派給其他成員"
                              : unavailable
                                ? statusText(asset.status)
                                : !permitted
                                  ? "此成員未獲使用權限"
                                  : statusText(asset.status);
                        return (
                          <button
                            key={asset.id}
                            type="button"
                            className={`cfj-asset-picker-row${selected ? " is-selected" : ""}${blocked ? " is-blocked" : ""}`}
                            aria-pressed={selected}
                            disabled={blocked}
                            onClick={() => toggleAssetPickerItem(assetPickerUid, activeKind.key, asset.id)}
                          >
                            <span className={`cfj-asset-picker-choice${activeKind.multiple ? " is-check" : " is-radio"}`}>{selected ? <Icon name="check" size={13} /> : null}</span>
                            <span className="cfj-asset-picker-image">
                              {vehiclePhoto(asset) ? <img src={vehiclePhoto(asset)} alt="" /> : <Icon name={activeKind.icon} size={20} />}
                            </span>
                            <span className="cfj-asset-picker-identity">
                              <strong>{assetPrimaryLabel(asset)}</strong>
                              <small>{asset.displayName && asset.plateNumber ? asset.displayName : asset.id}</small>
                            </span>
                            <span className="cfj-asset-picker-type">
                              <strong>{fleetAssetTypeLabel(asset)}</strong>
                              <small>{[asset.make || asset.brand, asset.model].map(text).filter(Boolean).join(" ") || fleetAssetKindLabel(asset.assetKind)}</small>
                            </span>
                            <span className={`cfj-asset-picker-status is-${text(asset.status).toLowerCase()}`}>
                              {detail}
                            </span>
                          </button>
                        );
                      })}
                      {!visibleAssets.length ? (
                        <div className="cfj-asset-picker-empty">
                          {tabAssets.length ? "沒有符合條件的資產" : `目前沒有${activeKind.label}資產`}
                        </div>
                      ) : null}
                    </div>
                  </section>
                  <aside className="cfj-asset-picker-selection" aria-label="本次出車配置">
                    <div className="cfj-asset-picker-selection-head">
                      <strong>本次出車配置</strong>
                      <span>{selectedAssets.length} 項</span>
                    </div>
                    <div className="cfj-asset-picker-selected-list">
                      {selectedAssets.map((asset) => {
                        const automaticallyBound = isAutomaticallyBound(asset);
                        const lockedToOther = Boolean(bindingOwnerByAssetId[asset.id] && bindingOwnerByAssetId[asset.id] !== text(assetPickerDraft.vehicleId));
                        return (
                          <div className="cfj-asset-picker-selected-item" key={asset.id}>
                            <span className="cfj-asset-picker-selected-image">
                              {vehiclePhoto(asset) ? <img src={vehiclePhoto(asset)} alt="" /> : <Icon name={FLEET_ASSET_KIND_CONFIG.find((item) => item.key === asset.assetKind)?.icon || "box"} size={16} />}
                            </span>
                            <span className="cfj-asset-picker-selected-copy">
                              <small>{fleetAssetKindLabel(asset.assetKind)}{automaticallyBound ? "・隨車帶入" : ""}</small>
                              <strong>{assetPrimaryLabel(asset)}</strong>
                              <em>{fleetAssetTypeLabel(asset)}</em>
                            </span>
                            {!automaticallyBound && canEditVehicleSchedule ? (
                              <button
                                type="button"
                                className="cfj-asset-picker-remove"
                                aria-label={`移除${assetPrimaryLabel(asset)}`}
                                onClick={() => toggleAssetPickerItem(assetPickerUid, asset.assetKind, asset.id)}
                              >×</button>
                            ) : lockedToOther ? <span className="cfj-asset-picker-locked">已綁定</span> : null}
                          </div>
                        );
                      })}
                      {!selectedAssets.length ? <div className="cfj-asset-picker-selection-empty">尚未選擇資產</div> : null}
                    </div>
                  </aside>
                </div>
                <footer className="cfj-asset-picker-footer">
                  <small>車輛單選；托架、容器與設備可複選</small>
                  <div>
                    <button type="button" className="cfj-asset-picker-cancel" onClick={closeAssetPicker}>取消</button>
                    <button type="button" className="cfj-asset-picker-confirm" onClick={confirmAssetPicker} disabled={submitting}>
                      {editMode
                        ? canEditVehicleSchedule
                          ? `確認調整${selectedAssets.length ? `（${selectedAssets.length}）` : ""}`
                          : "完成查看"
                        : `確認選擇${selectedAssets.length ? `（${selectedAssets.length}）` : ""}`}
                    </button>
                  </div>
                </footer>
              </div>
            </div>
          );
        })() : null}
        {emailTemplateEditor ? (
          <div
            className="cfj-email-template-overlay"
            role="dialog"
            aria-modal="true"
            onClick={() => setEmailTemplateEditor(null)}
          >
            <div
              className="cfj-email-template-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="cfj-email-template-head">
                <div>
                  <h2>查看 Email 預覽</h2>
                </div>
                <button
                  type="button"
                  className="cfj-cargo-detail-close"
                  onClick={() => setEmailTemplateEditor(null)}
                  aria-label="關閉"
                >
                  <Icon name="close" size={18} />
                </button>
              </div>
              <div className="cfj-email-template-body">
                <div className="cfj-email-preview">
                  <div className="cfj-email-preview-brand">
                    <img src={TallargeLogoFull} alt="Tallarge" />
                  </div>
                  <div className="cfj-email-preview-hero">
                    <small>您好，{emailPreviewValues.recipientName || "收件人"}</small>
                    <strong>
                      {emailPreviewValues.workName &&
                      !emailPreviewValues.workName.startsWith("尚未")
                        ? `${emailPreviewValues.workName} 已建立`
                        : "您的配送工作已建立"}
                    </strong>
                    <p>
                      我們已為您安排本次配送，您可以查看地址與貨物清單。
                    </p>
                  </div>
                  <div className="cfj-email-preview-content">
                    <h3>本次配送摘要</h3>
                    <div className="cfj-email-preview-summary">
                      <div>
                        <small>配送日期與時間</small>
                        <b>{emailPreviewValues.startAt || "尚未設定"}</b>
                      </div>
                      <div>
                        <small>貨物</small>
                        <b>
                          {emailPreviewCargoItems.length
                            ? `${emailPreviewCargoItems.length} 項`
                            : "尚未填寫"}
                        </b>
                      </div>
                    </div>
                    <div className="cfj-email-preview-panel">
                      <h4>地址</h4>
                      {emailPreviewAddresses.length ? (
                        <div className="cfj-email-preview-addresses">
                          {emailPreviewAddresses.map((item, index) => (
                            <div
                              className="cfj-email-preview-address"
                              key={item.id || index}
                            >
                              <strong>地址：</strong>
                              <span>{item.address}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="cfj-email-preview-empty">尚未填寫地址</p>
                      )}
                    </div>
                    <div className="cfj-email-preview-panel">
                      <h4>貨物清單</h4>
                      {emailPreviewCargoItems.length ? (
                        emailPreviewCargoItems.map((item, index) => (
                          <div className="cfj-email-preview-cargo" key={item.id || index}>
                            <span>{item.name || "未命名貨物"}</span>
                            <b>
                              {item.quantity || 0} {item.unit || "件"}
                            </b>
                          </div>
                        ))
                      ) : (
                        <p className="cfj-email-preview-empty">尚未填寫貨物</p>
                      )}
                    </div>
                  </div>
                  <div className="cfj-email-preview-button">查看全部配送資訊　→</div>
                  <div className="cfj-email-preview-footer">此信由 Tallarge 發送。</div>
                </div>
              </div>
              <div className="cfj-email-template-footer">
                <div className="cfj-email-test-actions">
                  <button
                    type="button"
                    className="cfj-email-test-button"
                    onClick={sendTestEmail}
                    disabled={emailTestState.status === "sending"}
                  >
                    {emailTestState.status === "sending"
                      ? "排入中…"
                      : "發送測試 Email"}
                  </button>
                  {emailTestState.message ? (
                    <span className={`cfj-email-test-message is-${emailTestState.status}`}>
                      {emailTestState.message}
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="cfj-cargo-secondary-button"
                  onClick={() => setEmailTemplateEditor(null)}
                >
                  取消
                </button>
              </div>
            </div>
          </div>
        ) : null}
        {commonPicker ? (
          <div
            className="cfj-common-overlay"
            role="dialog"
            aria-modal="true"
            onClick={() => setCommonPicker(null)}
          >
            <div
              className="cfj-common-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="cfj-common-modal-head">
                <div>
                  <h2>
                    {commonPicker.type === "contact"
                      ? "選擇常用聯絡人"
                      : "選擇常用地址"}
                  </h2>
                </div>
                <button
                  type="button"
                  className="cfj-common-close"
                  onClick={() => setCommonPicker(null)}
                  aria-label="關閉"
                >
                  ×
                </button>
              </div>
              <label className="cfj-common-search">
                <Icon name="search" size={15} />
                <input
                  value={commonSearch}
                  onChange={(event) => setCommonSearch(event.target.value)}
                  placeholder={
                    commonPicker.type === "contact"
                      ? "搜尋姓名、公司、電話或 Email"
                      : "搜尋地址"
                  }
                  autoFocus
                />
              </label>
              {commonLoading ? (
                <div className="cfj-common-empty">讀取常用資料中…</div>
              ) : commonError ? (
                <div className="cfj-common-empty is-error">{commonError}</div>
              ) : !filteredCommonItems.length ? (
                <div className="cfj-common-empty">
                  目前沒有符合條件的常用資料。
                </div>
              ) : (
                <div
                  className="cfj-common-list"
                  onScroll={(event) => {
                    const target = event.currentTarget;
                    if (
                      target.scrollTop + target.clientHeight >=
                      target.scrollHeight - 80
                    ) {
                      loadMoreCommonItems(commonKey);
                    }
                  }}
                >
                  {filteredCommonItems.map((item) => (
                    <button
                      type="button"
                      className="cfj-common-item"
                      key={item.id}
                      onClick={() =>
                        commonPicker.type === "contact"
                          ? applyCommonContact(
                              commonPicker.stopId,
                              commonPicker.recipientId,
                              item,
                              commonPicker.target,
                              commonPicker.contentId,
                            )
                          : applyCommonAddress(commonPicker.stopId, item)
                      }
                    >
                      <span className="cfj-common-item-main">
                        <strong>
                          {commonPicker.type === "contact"
                            ? text(item.name || item.displayName) ||
                              "未命名聯絡人"
                            : text(item.address) || "未命名地址"}
                        </strong>
                        {commonPicker.type === "contact" ? (
                          <small>
                            {[
                              item.company,
                              item.phone,
                              item.email,
                              item.addressText || item.address,
                            ]
                              .map(text)
                              .filter(Boolean)
                              .join("　·　")}
                          </small>
                        ) : (
                          <small>
                          </small>
                        )}
                      </span>
                      <span
                        className={`cfj-common-status ${item.geocodeStatus === "verified" && coordsOK(item) ? "is-ready" : ""}`}
                      >
                        {item.geocodeStatus === "verified" && coordsOK(item)
                          ? "已定位"
                          : "選用"}
                      </span>
                    </button>
                  ))}
                  {commonHasMore.current[commonKey] ||
                  commonVisibleCount[commonKey] < commonItems.length ? (
                    <button
                      type="button"
                      className="cfj-common-load-more"
                      onClick={() => loadMoreCommonItems(commonKey)}
                    >
                      {commonLoading ? "載入中…" : "載入更多"}
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        ) : null}
        {scheduleReview ? (
          <div
            className="cfj-schedule-review"
            role="dialog"
            aria-modal="true"
            aria-label="確認資產排程"
            onClick={() => setScheduleReview(null)}
          >
            <div
              className="cfj-schedule-review-card"
              onClick={(event) => event.stopPropagation()}
            >
              <header className="cfj-schedule-review-head">
                <div>
                  <h2>
                    {editMode ? "確認車輛與排程更新" : "確認出車資產排程"}
                  </h2>
                  <p>
                    {scheduleReview.schedules.length
                      ? "以下列出同日使用相同資產的其他工作，請確認後再繼續。"
                      : "目前沒有其他工作使用這些資產，確認後即可繼續。"}
                  </p>
                </div>
                <button
                  type="button"
                  className="cfj-schedule-review-close"
                  aria-label="關閉排程確認"
                  onClick={() => setScheduleReview(null)}
                >
                  ×
                </button>
              </header>
              <section className="cfj-schedule-review-selected">
                <strong>本次工作配置</strong>
                {(scheduleReview.selectedScheduleRows || []).map((row, index) => (
                  <div
                    className="cfj-schedule-review-row"
                    key={row.uid || index}
                  >
                    <div>
                      <small>日期／時間</small>
                      <strong>
                        {scheduleReview.dateKey} {inputTimeValue(scheduleReview.startAt)}
                        {scheduleReview.hasRange && scheduleReview.endAt
                          ? `–${inputTimeValue(scheduleReview.endAt)}`
                          : ""}
                      </strong>
                    </div>
                    <div>
                      <small>固定成員</small>
                      <strong>{row.driverName || "未指定成員"}</strong>
                    </div>
                    <div className="cfj-review-vehicle">
                      <small>車輛與資產</small>
                      <strong>
                        {(row.assets || [])
                          .map(
                            (asset) =>
                              `${fleetAssetKindLabel(asset.assetKind)} ${assetPrimaryLabel(asset)}`,
                          )
                          .join("、") || "未選擇資產"}
                      </strong>
                    </div>
                  </div>
                ))}
              </section>
              <section className="cfj-schedule-review-list">
                <div className="cfj-schedule-review-list-head">
                  同日使用相同資產的其他排程（{scheduleReview.schedules.length}）
                </div>
                {scheduleReview.schedules.length ? (
                  scheduleReview.schedules.map((item, index) => {
                    const snapshotAssets = Array.isArray(item.assetSnapshots)
                      ? item.assetSnapshots
                      : [];
                    const scheduledAssetLabels = (Array.isArray(item.assetIds)
                      ? item.assetIds
                      : []
                    )
                      .map((assetId) => {
                        const asset =
                          assetById[text(assetId)] ||
                          snapshotAssets.find(
                            (snapshot) =>
                              text(snapshot.assetId || snapshot.id) ===
                              text(assetId),
                          );
                        return asset
                          ? `${fleetAssetKindLabel(asset.assetKind)} ${assetPrimaryLabel(asset)}`
                          : text(assetId);
                      })
                      .filter(Boolean)
                      .join("、");
                    return (
                      <div
                        className="cfj-schedule-review-existing"
                        key={item.scheduleId || item.applicationId || index}
                      >
                        <div>
                          <strong>
                            {text(
                              item.jobTitle ||
                                item.applicationName ||
                                item.applicationId,
                            ) || "未命名工作"}
                          </strong>
                          <span>{inputTimeValue(item.startAt) || "時間未提供"}</span>
                        </div>
                        <div>
                          <span>
                            成員：{text(item.driverNameSnapshot) || "未指定成員"}
                          </span>
                        </div>
                        <div>
                          <span>
                            資產：{scheduledAssetLabels || "未提供資產資訊"}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="cfj-schedule-review-empty">
                    尚無其他工作使用這些資產。
                  </div>
                )}
              </section>
              <div className="cfj-schedule-review-actions">
                <button
                  type="button"
                  className="cfj-schedule-review-cancel"
                  onClick={() => setScheduleReview(null)}
                >
                  返回編輯
                </button>
                <button
                  type="button"
                  className="cfj-schedule-review-confirm"
                  onClick={confirmScheduleReview}
                >
                  {editMode ? "確認更新工作" : "確認建立並指派"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </form>
    </div>
  );
}

const styles = `
.cfj-root{
  --accent:#ff8522;
  --ink:#19232d;
  --muted:#7c858f;
  --line:#e9ecef;
  color:var(--ink);
  background:transparent;
  padding:0 0 90px;
  min-width:0;
  height:auto;
  overflow:visible;
  font-family:Inter,"Noto Sans TC",-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
  container-type:inline-size;
}.cfj-root *{box-sizing:border-box}.cfj-root button,.cfj-root input,.cfj-root textarea,.cfj-root select{font:inherit}.cfj-root button{cursor:pointer;transition:background .16s,border-color .16s,box-shadow .16s,transform .16s,color .16s}.cfj-root button:disabled{cursor:not-allowed}.cfj-root h1,.cfj-root h2,.cfj-root p{margin:0}.cfj-root form{width:100%;max-width:1480px;min-width:0;margin:auto}.cfj-header{display:flex;justify-content:space-between;align-items:center;gap:16px;margin-bottom:28px}.cfj-breadcrumb{display:flex;gap:12px;align-items:center;color:#929aa3;font-size:12px;margin-bottom:18px}.cfj-breadcrumb button{display:flex;align-items:center;gap:5px;border:0;background:none;color:#6b7681;padding:0}.cfj-header h1{display:flex;align-items:center;gap:14px;font-size:30px;font-weight:750;letter-spacing:-1px}.cfj-header p{margin-top:9px;color:var(--muted);font-size:13px}.cfj-tag{padding:6px 10px;border-radius:6px;background:#fff0e2;color:#cd6819;font-size:11px;font-weight:600}.cfj-header-meta{display:flex;align-items:center;gap:8px;color:#69747c;font-size:12px}.cfj-dot{width:7px;height:7px;border-radius:50%;background:var(--accent)}fieldset{border:0;padding:0;margin:0;min-width:0}.cfj-layout{display:grid;width:100%;grid-template-columns:minmax(0,49%) minmax(0,2%) minmax(0,49%);align-items:start;position:relative;isolation:isolate;min-width:0}.cfj-form-column{grid-column:1;display:grid;gap:16px;min-width:0;width:100%;position:relative;z-index:4}.cfj-layout-gap{grid-column:2;min-width:0}.cfj-map-column{grid-column:3;position:sticky;top:24px;min-width:0;width:100%;z-index:1}.cfj-card,.cfj-map-panel{background:#fff;border:1px solid var(--line);border-radius:16px;padding:24px;min-width:0;max-width:100%}.cfj-section-head-copy{min-width:0;flex:1}.cfj-section-head-actions{margin-left:auto;display:flex;align-items:flex-start;flex:0 0 auto}.cfj-route-import-button{display:inline-flex;align-items:center;gap:6px;border:1px solid #d7e7f1;border-radius:9px;padding:9px 12px;background:#f5fbff;color:#3b6f8e;font-size:11px;font-weight:750;cursor:pointer;white-space:nowrap}.cfj-route-import-button:hover{border-color:#9cc4da;background:#eaf6fc;color:#245d7c}.cfj-import-overlay{position:fixed;inset:0;z-index:1200;display:flex;align-items:center;justify-content:center;padding:24px;background:rgba(17,42,61,.42);backdrop-filter:blur(7px)}.cfj-import-modal{width:min(1180px,100%);max-height:min(900px,calc(100vh - 40px));display:flex;flex-direction:column;overflow:hidden;border:1px solid rgba(255,255,255,.9);border-radius:18px;background:#fff;box-shadow:0 30px 90px rgba(17,42,61,.3)}.cfj-import-head{display:flex;align-items:flex-start;justify-content:space-between;gap:18px;padding:23px 26px 16px;border-bottom:1px solid #edf2f5}.cfj-import-head-actions{display:flex;align-items:center;gap:12px;flex:none}.cfj-import-start-time{display:flex;align-items:center;gap:8px;border:1px solid #dfe9ef;border-radius:9px;padding:6px 9px;background:#f8fbfd;color:#5b7588;font-size:10px;font-weight:750}.cfj-import-start-time input{border:0;outline:0;background:transparent;color:#234862;font-size:12px;font-weight:800}.cfj-import-head small{color:#d7771e;font-size:10px;font-weight:800;letter-spacing:.08em}.cfj-import-head h2{margin-top:5px;color:#183d5b;font-size:21px;letter-spacing:-.03em}.cfj-import-head p{margin-top:6px;color:#8999a6;font-size:11px}.cfj-import-summary{display:flex;gap:22px;padding:12px 26px;border-bottom:1px solid #edf2f5;background:#f7fbfd;color:#708495;font-size:11px}.cfj-import-summary b{margin-right:4px;color:#1f4e6f;font-size:15px}.cfj-import-list{overflow:auto;padding:16px 26px}.cfj-import-station{margin-bottom:14px;padding:14px;border:1px solid #e2ecf2;border-radius:13px;background:#fbfdff}.cfj-import-station-head{display:flex;align-items:center;gap:10px}.cfj-import-station-number{width:28px;height:28px;display:grid;place-items:center;border-radius:50%;background:#2bb879;color:#fff;font-size:11px;font-weight:800}.cfj-import-station-title{display:flex;flex-direction:column;gap:3px;min-width:0;flex:1}.cfj-import-station-title strong{color:#234862;font-size:13px}.cfj-import-station-title span{color:#8b9aa6;font-size:10px}.cfj-import-station-head select,.cfj-import-station-fields select,.cfj-import-cargo-row select{border:1px solid #dce7ed;border-radius:7px;padding:8px 9px;background:#fff;color:#37566b;font-size:11px}.cfj-import-station-fields{display:grid;grid-template-columns:2fr 120px 110px 110px 100px;gap:7px;margin:12px 0}.cfj-import-station-fields input,.cfj-import-recipient-fields input,.cfj-import-cargo-row input{min-width:0;border:1px solid #dfe9ef;border-radius:7px;padding:8px 9px;background:#fff;color:#314e62;font-size:11px}.cfj-import-recipients{display:grid;gap:9px}.cfj-import-recipient{padding:11px;border:1px solid #e5edf2;border-radius:10px;background:#fff}.cfj-import-recipient-head{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;color:#536f82;font-size:11px;font-weight:750}.cfj-import-recipient-head button,.cfj-import-cargo-head button{border:0;background:transparent;color:#b96832;font-size:10px;cursor:pointer}.cfj-import-recipient-fields{display:grid;grid-template-columns:125px 1fr 1fr 140px 1.3fr;gap:7px}.cfj-import-cargo-head{display:flex;justify-content:space-between;align-items:center;margin:11px 0 7px;color:#536f82;font-size:10px}.cfj-import-cargo-list{display:grid;gap:6px}.cfj-import-cargo-row{display:grid;grid-template-columns:1.3fr 75px 65px 82px 1fr 26px;gap:6px;align-items:center}.cfj-import-remove{width:26px;height:30px;border:0;border-radius:7px;background:#fff0ef;color:#c85b53;font-size:16px}.cfj-import-add-recipient{margin-top:11px;border:1px dashed #d7a46f;border-radius:8px;padding:7px 10px;background:#fffaf4;color:#bd6d2f;font-size:10px;font-weight:750}.cfj-import-errors{margin:0 26px 12px;padding:11px 13px;border:1px solid #f1c8ba;border-radius:10px;background:#fff6f2;color:#9b5a4c;font-size:11px}.cfj-import-errors ul{margin:6px 0 0;padding-left:18px;line-height:1.6}.cfj-import-errors small{display:block;margin-top:4px;color:#b78073}.cfj-import-ready{display:flex;align-items:center;gap:7px;margin:0 26px 12px;padding:11px 13px;border:1px solid #cfe7d9;border-radius:10px;background:#f2fbf5;color:#39805c;font-size:11px}.cfj-import-footer{display:flex;justify-content:flex-end;gap:8px;padding:14px 26px 18px;border-top:1px solid #edf2f5;background:#fff}.cfj-import-footer button{min-height:36px;border-radius:8px;padding:0 14px;font-size:11px}.cfj-import-footer .cfj-cargo-primary-button:disabled{opacity:.45;cursor:not-allowed}
.cfj-section-head{display:flex;gap:12px;align-items:flex-start;margin-bottom:23px}.cfj-section-n{width:30px;height:30px;display:grid;place-items:center;flex:0 0 30px;border-radius:8px;background:#fff3e8;color:#d87323;font-size:11px;font-weight:750}.cfj-section-head h2{font-size:16px}.cfj-section-head p{margin-top:4px;color:var(--muted);font-size:11px;line-height:1.7}.cfj-field{display:flex;flex-direction:column;gap:9px;margin-bottom:17px;color:#34414b;font-size:12px;font-weight:600}.cfj-field:last-child{margin-bottom:0}.cfj-field>span{display:flex;justify-content:space-between}.cfj-field small,.cfj-start-toggle small{color:#969ea7;font-size:11px;font-weight:400}.cfj-field input,.cfj-field textarea,.cfj-field select{width:100%;border:1px solid #e4e8ec;border-radius:9px;background:#fff;padding:12px;color:#27343d;font-size:13px;font-weight:400;min-width:0}.cfj-field textarea{resize:vertical}.cfj-start-toggle{display:flex;align-items:center;gap:9px;margin:0 0 17px;font-size:12px}.cfj-start-toggle input{accent-color:#ed842f}.cfj-start-toggle small{margin-left:auto}.cfj-stops{display:grid;gap:16px}.cfj-stop{display:flex;gap:12px;position:relative}.cfj-stop:not(:last-child)::after{content:"";position:absolute;top:32px;bottom:-12px;left:12px;border-left:1px dashed #cbd3d9}.cfj-stop-number{width:26px;height:26px;display:grid;place-items:center;flex:0 0 26px;border:1px solid #c7ded5;border-radius:8px;background:#edf6f1;color:#368366;font-size:11px}.cfj-stop-number.is-end{background:#fff0ef;border-color:#f1cccc;color:#ce5151}.cfj-stop-number.is-waypoint{background:#fff5e9;border-color:#f3d3aa;color:#c8772e}.cfj-stop-body{flex:1;min-width:0}.cfj-stop-body>label{display:flex;align-items:center;gap:8px;margin:4px 0 10px;font-size:12px;font-weight:650}.cfj-selecting{border-radius:5px;padding:3px 6px;background:#fff2e7;color:#cb6c20;font-size:9px;font-weight:500}.cfj-address-row{display:flex;align-items:center;border:1px solid #e3e8ec;border-radius:9px;background:#fafbfc;overflow:hidden}.cfj-stop.is-active .cfj-address-row{border-color:#ffb271;background:#fffaf6;box-shadow:0 0 0 3px #fff5ec}.cfj-address-row input{flex:1;min-width:0;border:0;outline:0;padding:12px 10px;background:transparent;color:#35404b;font-size:12px}.cfj-address-row button{display:grid;place-items:center;border:0;padding:10px;background:transparent;color:#cf742a}.cfj-suggestions{margin-top:8px;border:1px solid var(--line);border-radius:9px;background:#fff;overflow:hidden;box-shadow:0 8px 22px rgba(23,35,45,.08);position:relative;z-index:5}.cfj-suggestions button{width:100%;display:flex;align-items:flex-start;gap:8px;border:0;border-bottom:1px solid #f2f4f5;padding:11px;background:#fff;text-align:left;color:#4e5c66;font-size:12px;line-height:1.55}.cfj-suggestions button:hover{background:#fff7f1}.cfj-suggestions button>span{display:flex;flex-direction:column;min-width:0}.cfj-suggestions small{margin-top:2px;color:#9aa3aa;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.cfj-muted{display:block;margin-top:7px;color:#8a949e;font-size:11px}.cfj-stop-details{margin-top:12px;padding:13px;border:1px solid #edf0f2;border-radius:12px;background:#fcfdfd}.cfj-stop-detail-head,.cfj-cargo-head{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:10px;color:#54616b;font-size:11px}.cfj-stop-detail-head select{max-width:170px;border:1px solid #e3e8ec;border-radius:7px;padding:6px;background:#fff;color:#68747d;font-size:10px}.cfj-recipient-title{display:flex;flex-direction:column;gap:3px;min-width:0}.cfj-recipient-title small{color:#929ba3;font-size:9px;font-weight:500}.cfj-recipient-card{margin:10px 0 12px;padding:11px;border:1px solid #e8edf0;border-radius:10px;background:#fff}.cfj-recipient-card:last-child{margin-bottom:0}.cfj-recipient-head{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:9px}.cfj-recipient-head>div:first-child{min-width:0}.cfj-recipient-head span{display:block;color:#98a1a8;font-size:9px}.cfj-recipient-head b{display:block;margin-top:3px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#33414b;font-size:12px}.cfj-recipient-actions{display:flex;align-items:center;gap:5px;flex:0 0 auto}.cfj-recipient-actions .cfj-icon-button{width:25px;height:28px}.cfj-recipient-card .cfj-cargo-head{margin-top:12px}.cfj-recipient-card .cfj-cargo-head span{font-weight:700}.cfj-recipient-card .cfj-cargo-head span small{margin-left:4px;color:#98a1a8;font-size:9px;font-weight:500}.cfj-contact-grid{display:grid;grid-template-columns:1.2fr 1fr 1fr 1fr;gap:7px}.cfj-contact-grid input,.cfj-cargo-row input,.cfj-delivery-note{min-width:0;width:100%;border:1px solid #e7ebee;border-radius:7px;padding:9px;background:#fff;color:#35404b;font-size:11px}.cfj-cargo-head{margin-top:14px}.cfj-cargo-head button,.cfj-route-actions button{display:inline-flex;align-items:center;gap:5px;border:1px solid #f2c69f;border-radius:7px;padding:6px 9px;background:#fff8f1;color:#c66d24;font-size:10px}.cfj-cargo-item{margin-bottom:12px;padding:10px 10px 11px;border:1px solid #edf0f2;border-radius:9px;background:#fff}.cfj-cargo-item:last-child{margin-bottom:0}.cfj-cargo-photo-head{display:flex;align-items:center;gap:8px;margin:2px 0 7px;color:#6b7780;font-size:10px;font-weight:750}.cfj-cargo-photo-head small{color:#a0a8ae;font-size:9px;font-weight:400}.cfj-cargo-photos{display:flex;flex-wrap:wrap;gap:6px}.cfj-cargo-photo,.cfj-cargo-photo-add{position:relative;width:54px;height:46px;display:flex;align-items:center;justify-content:center;overflow:hidden;border-radius:7px}.cfj-cargo-photo{background:#f4f6f7}.cfj-cargo-photo img{width:100%;height:100%;object-fit:cover}.cfj-cargo-photo button{position:absolute;top:3px;right:3px;width:16px;height:16px;display:grid;place-items:center;border:0;border-radius:50%;background:rgba(15,23,42,.76);color:#fff}.cfj-cargo-photo-add{flex-direction:column;gap:2px;border:1px dashed #d7dee3;background:#fafbfc;color:#7b8790;font-size:8px}.cfj-cargo-photo-add:hover{border-color:#f2b478;background:#fff8f1;color:#c66d24}.cfj-cargo-row{display:grid;grid-template-columns:minmax(0,0.9fr) minmax(0,1.1fr) 74px 64px 25px;gap:6px;margin-bottom:7px}.cfj-icon-button{width:25px;height:32px;display:grid;place-items:center;border:0;border-radius:7px;background:#fff0ef;color:#c85b53}.cfj-delivery-note{margin-top:4px}.cfj-photo-head{display:flex;gap:10px;margin:13px 0 8px;color:#59656e;font-size:11px;font-weight:650}.cfj-photo-head small{color:#9aa2a9;font-weight:400}.cfj-photos{display:flex;gap:8px;flex-wrap:wrap}.cfj-photo,.cfj-photo-add{width:70px;height:62px;border-radius:9px;overflow:hidden;position:relative}.cfj-photo img{width:100%;height:100%;object-fit:cover}.cfj-photo button{position:absolute;top:4px;right:4px;width:19px;height:19px;display:grid;place-items:center;border:0;border-radius:50%;background:rgba(15,23,42,.75);color:#fff}.cfj-photo-add{display:flex;flex-direction:column;justify-content:center;align-items:center;gap:4px;border:1px dashed #d4dbe0;background:#f8fafb;color:#75818a;font-size:9px}.cfj-stop-footer{display:flex;justify-content:space-between;align-items:center;gap:8px;margin-top:12px;color:#919ba2;font-size:10px}.cfj-stop-footer div{display:flex;gap:4px}.cfj-stop-footer button{border:0;border-radius:6px;padding:5px 7px;background:#f3f6f7;color:#67747d;font-size:10px}.cfj-stop-footer button:disabled{opacity:.4}.cfj-stop-footer button.is-danger{background:#fff0ef;color:#c4544b}.cfj-route-actions{display:flex;justify-content:space-between;gap:8px;margin-top:18px}.cfj-route-actions button:last-child{border-color:#d9e7f2;background:#f4f9fc;color:#4c7996}.cfj-route-summary{display:flex;gap:18px;flex-wrap:wrap;border-radius:8px;padding:12px;margin-top:16px;background:#f7f9fa;color:#626e78;font-size:11px}.cfj-route-summary span{display:flex;align-items:center;gap:7px}.cfj-inline-error,.cfj-map-error{padding:12px 16px;background:#fff4eb;color:#a7642e;font-size:11px}.cfj-schedule-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}.cfj-edit-lock-note{margin-bottom:12px;padding:10px 12px;border-radius:10px;background:#f5f3ff;color:#6651bb;font-size:11px;line-height:1.6;font-weight:700}.cfj-driver-toolbar{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:18px}.cfj-driver-toolbar>span{white-space:nowrap;color:#8b959e;font-size:11px;font-weight:600}.cfj-driver-search{flex:1;min-width:0;height:42px;display:flex;align-items:center;gap:8px;padding:0 12px;border:1px solid #e8ecef;border-radius:11px;background:#f8fafb;color:#8a949d}.cfj-driver-search input{flex:1;min-width:0;border:0;outline:0;background:transparent;color:#33404a;font-size:12px}.cfj-member-scroll{display:flex;gap:15px;overflow-x:auto;padding:5px 2px 12px;scrollbar-width:thin}.cfj-member-chip{width:70px;flex:0 0 70px;display:flex;flex-direction:column;align-items:center;border:0;padding:0;background:transparent;color:#68747e;font-size:11px}.cfj-member-avatar{width:54px;height:54px;display:grid;place-items:center;border-radius:50%;box-shadow:0 3px 11px rgba(15,23,42,.08);font-size:17px;font-weight:800}.cfj-member-chip>span:last-child{width:100%;margin-top:8px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;text-align:center}.cfj-member-chip.is-active>span:last-child{color:#17232d;font-weight:750}.cfj-assignment-card{border:1px solid #e9edf0;border-radius:15px;padding:17px;margin-top:15px;background:#fff;box-shadow:0 4px 14px rgba(15,23,42,.035);min-width:0;max-width:100%;overflow:hidden}.cfj-assignment-head{display:flex;align-items:center;gap:9px;padding-bottom:13px;border-bottom:1px solid #f0f2f4}.cfj-assignment-dot{width:10px;height:10px;flex:0 0 10px;border-radius:50%}.cfj-assignment-head>div{flex:1;min-width:0}.cfj-assignment-head b{display:block;color:#1e2933;font-size:14px}.cfj-assignment-head small{display:block;margin-top:3px;color:#929ba3;font-size:10px}.cfj-assignment-remove{width:30px;height:30px;display:grid;place-items:center;border:0;border-radius:50%;background:#f6f8f9;color:#8c969e}.cfj-vehicle-image{
  width:48px;
  height:48px;

  flex:
    0 0 48px;

  display:grid;
  place-items:center;

  border-radius:10px;

  background:#fff;

  color:#73808a;

  overflow:hidden;
}

.cfj-vehicle-image img{
  width:100%;
  height:100%;

  object-fit:cover;
}

.cfj-vehicle-info{
  flex:1;

  min-width:0;
}

.cfj-vehicle-info b{
  display:block;

  overflow:hidden;

  text-overflow:
    ellipsis;

  white-space:
    nowrap;

  color:#27343d;

  font-size:11px;
  font-weight:750;
}

.cfj-vehicle-info small{
  display:block;

  margin-top:4px;

  overflow:hidden;

  text-overflow:
    ellipsis;

  white-space:
    nowrap;

  color:#89939b;

  font-size:10px;
  font-weight:550;
}

.cfj-vehicle-selected{
  position:absolute;

  top:-6px;
  right:-6px;

  width:20px;
  height:20px;

  display:grid;
  place-items:center;

  border-radius:50%;

  background:#f97316;
  color:#fff;

  border:
    2px solid #fff;

  box-shadow:
    0 2px 6px
    rgba(
      15,
      23,
      42,
      .15
    );
}

.cfj-no-asset{
  min-width:130px;

  display:grid;
  place-items:center;

  border-radius:12px;

  padding:15px;

  background:#f8fafb;

  color:#98a1a8;

  font-size:11px;
}


/* DRIVER EMPTY */

.cfj-driver-empty{
  min-height:120px;

  margin-top:12px;

  display:flex;

  flex-direction:column;

  align-items:center;

  justify-content:center;

  border:
    1px dashed
    #e3e8ec;

  border-radius:14px;

  background:#fafbfc;

  color:#89949c;

  text-align:center;
}

.cfj-driver-empty-icon{
  width:42px;
  height:42px;

  display:grid;
  place-items:center;

  margin-bottom:9px;

  border-radius:50%;

  background:#f0f3f5;
}

.cfj-driver-empty b{
  color:#68747d;

  font-size:12px;
}

.cfj-driver-empty p{
  margin-top:5px;

  color:#9ca4aa;

  font-size:10px;
}


/* SPLIT WORKSPACE: left form scroll + left action footer + right map */
.cfj-left-column{
  grid-column:1;
  grid-row:1;
  display:flex;
  flex-direction:column;
  min-width:0;
  min-height:calc(100vh - 110px);
  position:relative;
  z-index:4;
}
.cfj-form-scroll{
  flex:1 1 auto;
  min-width:0;
  min-height:0;
  overflow-y:auto;
  overscroll-behavior:contain;
  scrollbar-width:thin;
}
.cfj-left-column>.cfj-footer{
  flex:0 0 auto;
  width:100%;
}

/* MAP */

.cfj-map-column{
  grid-column:3;
  grid-row:1;
  position:sticky;

  top:24px;

  min-width:0;
  width:100%;
  justify-self:end;
  align-self:start;
  z-index:1;
}

.cfj-map-panel{
  position:relative;
  width:100%;
  overflow:hidden;
  z-index:1;

  border:
    1px solid
    var(--line);

  border-radius:17px;

  background:#fff;

  box-shadow:
    0 8px 30px
    rgba(
      23,
      35,
      45,
      .045
    );
}

.cfj-map-panel > header{
  padding:22px 24px;
}

.cfj-eyebrow{
  color:#9ba2a9;

  font-size:9px;

  letter-spacing:2px;
}

.cfj-map-panel h2{
  margin-top:6px;

  color:#202c35;

  font-size:18px;
  font-weight:650;
}

.cfj-map-wrap{
  position:relative;

  height:
    clamp(
      420px,
      58vh,
      680px
    );

  background:#e7ebed;
  overflow:hidden;
  border-radius:18px;
}

.cfj-card{
  position:relative;
  z-index:2;
}

.leaflet-tooltip.cfj-stop-tooltip{
  max-width:none !important;
  padding:0 !important;
  border:1px solid rgba(216,222,226,.96) !important;
  border-radius:11px !important;
  background:rgba(255,255,255,.97) !important;
  color:#27343d !important;
  box-shadow:0 8px 24px rgba(20,34,44,.16) !important;
  white-space:normal !important;
  pointer-events:none;
}

.leaflet-tooltip-top.cfj-stop-tooltip::before{
  border-top-color:#fff !important;
}

.cfj-stop-tooltip-content{
  width:max-content;
  max-width:380px;
  display:flex;
  align-items:flex-start;
  gap:8px;
  padding:8px 10px;
  white-space:normal;
}

.cfj-stop-tooltip-kind{
  flex:0 0 auto;
  min-width:34px;
  height:20px;
  display:grid;
  place-items:center;
  margin-top:1px;
  border-radius:6px;
  color:#fff;
  font-size:9px;
  font-weight:800;
}

.cfj-stop-tooltip-kind.is-start{
  background:#3d8c69;
}

.cfj-stop-tooltip-kind.is-end{
  background:#df5a5a;
}

.cfj-stop-tooltip-kind.is-waypoint{
  background:#c8772e;
}

.cfj-stop-tooltip-address{
  display:block;
  max-width:320px;
  white-space:normal;
  overflow:visible;
  text-overflow:clip;
  overflow-wrap:anywhere;
  word-break:break-word;
  color:#35424b;
  font-size:10px;
  font-weight:650;
  line-height:1.5;
}

.cfj-map{
  width:100%;
  height:100%;

  z-index:1;
}

.cfj-map-route-summary{
  position:absolute;
  top:12px;
  right:12px;
  z-index:850;
  display:flex;
  align-items:center;
  gap:12px;
  max-width:calc(100% - 24px);
  padding:8px 12px;
  border:1px solid rgba(219,231,237,.96);
  border-radius:12px;
  background:rgba(255,255,255,.96);
  box-shadow:0 6px 20px rgba(35,70,96,.16);
  backdrop-filter:blur(8px);
  pointer-events:none;
}

.cfj-map-route-summary-metric{
  display:grid;
  gap:3px;
  min-width:0;
  white-space:nowrap;
}

.cfj-map-route-summary-metric small{
  color:#8797a0;
  font-size:9px;
  line-height:1.2;
}

.cfj-map-route-summary-metric strong{
  color:#315a73;
  font-size:12px;
  font-weight:900;
  line-height:1.2;
}

.cfj-map-route-summary>i{
  width:1px;
  height:27px;
  flex:0 0 1px;
  background:#e4eaed;
}

.cfj-map .leaflet-control-attribution{
  box-sizing:border-box;
  max-width:calc(100% - 16px);
  margin:0 7px 7px 0!important;
  padding:3px 7px!important;
  border:1px solid rgba(255,255,255,.82);
  border-radius:999px;
  background:rgba(255,255,255,.78);
  box-shadow:0 3px 10px rgba(15,23,42,.12);
  color:#64748b;
  font-size:9px;
  font-weight:700;
  line-height:1.25;
  white-space:nowrap;
  backdrop-filter:blur(10px);
  -webkit-backdrop-filter:blur(10px);
}

.cfj-map .leaflet-control-attribution a{
  color:#475569!important;
  font-weight:800;
  text-decoration:none;
}

.cfj-map .leaflet-control-attribution a:hover{
  color:#e9781e!important;
  text-decoration:underline;
}

@media(max-width:560px){
  .cfj-map-route-summary{
    top:9px;
    right:9px;
    gap:8px;
    padding:7px 9px;
  }

  .cfj-map-route-summary-metric small{
    font-size:8px;
  }

  .cfj-map-route-summary-metric strong{
    font-size:10px;
  }

  .cfj-map .leaflet-control-attribution{
    max-width:calc(100% - 14px);
    margin:0 7px 7px 0!important;
    padding:3px 7px!important;
    font-size:8px;
  }
}


/* MARKER */

.cfj-leaflet-icon-shell{
  border:0!important;

  background:
    transparent!important;
}

.cfj-map-marker{
  width:40px;
  height:40px;

  display:grid;
  place-items:center;

  border:
    3px solid #fff;

  border-radius:50%;

  color:#fff;

  font-size:13px;
  font-weight:800;

  box-shadow:
    0 5px 16px
    rgba(
      20,
      35,
      45,
      .26
    );

  user-select:none;

  transition:
    transform .16s,
    box-shadow .16s;
}

.cfj-map-marker.is-start{
  background:#3d8c69;
}

.cfj-map-marker.is-end{
  background:#df5a5a;
}

.cfj-map-marker.is-active{
  transform:
    scale(1.15);

  box-shadow:
    0 0 0 6px
      rgba(
        255,
        255,
        255,
        .72
      ),
    0 7px 22px
      rgba(
        20,
        35,
        45,
        .3
      );
}


/* CUSTOM ZOOM */

.cfj-custom-zoom{
  position:absolute;

  top:14px;
  left:14px;

  z-index:900;

  width:42px;

  overflow:hidden;

  display:flex;

  flex-direction:column;

  border:
    1px solid
    rgba(
      216,
      222,
      226,
      .96
    );

  border-radius:12px;

  background:
    rgba(
      255,
      255,
      255,
      .96
    );

  box-shadow:
    0 6px 22px
    rgba(
      25,
      38,
      47,
      .14
    );
}

.cfj-custom-zoom button{
  width:42px;
  height:42px;

  display:grid;
  place-items:center;

  border:0;

  background:
    transparent;

  color:#4d5b65;
}

.cfj-custom-zoom button:hover{
  background:#f4f6f7;

  color:#19252e;
}

.cfj-custom-zoom > span{
  height:1px;

  margin:
    0 9px;

  background:#e8ecef;
}


/* MAP SWITCH */

.cfj-map-style-switcher{
  position:absolute;

  top:14px;
  right:14px;

  z-index:950;
}

.cfj-map-style-main{
  min-height:42px;

  display:flex;

  align-items:center;

  gap:8px;

  border:
    1px solid
    #d9dfe3;

  border-radius:11px;

  padding:
    0 12px;

  background:
    rgba(
      255,
      255,
      255,
      .96
    );

  color:#4d5962;

  font-size:11px;
  font-weight:650;

  box-shadow:
    0 6px 22px
    rgba(
      25,
      38,
      47,
      .14
    );
}

.cfj-map-style-arrow{
  transition:
    transform .16s;
}

.cfj-map-style-main.is-open
.cfj-map-style-arrow{
  transform:
    rotate(180deg);
}

.cfj-map-style-menu{
  position:absolute;

  top:49px;
  right:0;

  width:225px;

  padding:7px;

  border:
    1px solid
    #e1e6e9;

  border-radius:13px;

  background:#fff;

  box-shadow:
    0 14px 38px
    rgba(
      20,
      34,
      44,
      .2
    );
}

.cfj-map-style-menu > button{
  width:100%;

  display:flex;

  align-items:center;

  gap:10px;

  border:0;

  border-radius:9px;

  padding:8px;

  background:
    transparent;

  color:#49545d;

  text-align:left;
}

.cfj-map-style-menu > button:hover{
  background:#f5f7f8;
}

.cfj-map-style-menu > button.is-active{
  background:#fff5ec;
}

.cfj-map-style-preview{
  width:40px;
  height:40px;

  flex:
    0 0 40px;

  display:grid;
  place-items:center;

  border-radius:8px;

  font-size:11px;
  font-weight:750;
}

.cfj-map-style-preview.is-normal{
  background:
    linear-gradient(
      135deg,
      #e7eee6,
      #dce8ef,
      #f3f0e4
    );

  color:#53646d;
}

.cfj-map-style-preview.is-hybrid{
  background:
    linear-gradient(
      135deg,
      #314839,
      #736f4c,
      #31444a
    );

  color:#fff;
}

.cfj-map-style-preview.is-terrain{
  background:
    linear-gradient(
      135deg,
      #dce9c7,
      #c6d8a6,
      #e7ddbd
    );

  color:#58684a;
}

.cfj-map-style-copy{
  flex:1;

  display:flex;

  flex-direction:column;

  gap:3px;
}

.cfj-map-style-copy b{
  font-size:11px;
}

.cfj-map-style-copy small{
  color:#939ca3;

  font-size:9px;
}

.cfj-map-style-check{
  width:22px;
  height:22px;

  display:grid;
  place-items:center;

  border-radius:50%;

  background:#fff1e6;

  color:#d97120;
}


/* MAP BOTTOM LEFT */

.cfj-map-stop-switch{
  position:absolute;
  right:112px;
  max-width:calc(100% - 126px);
  overflow-x:auto;
  overflow-y:hidden;
  padding:2px 2px 7px;
  touch-action:pan-x;
  overscroll-behavior-x:contain;
  scrollbar-width:thin;
  cursor:grab;
  user-select:none;

  left:14px;
  bottom:14px;

  z-index:850;

  display:flex;

  gap:7px;
}

.cfj-map-stop-switch.is-dragging{
  cursor:grabbing;
}

.cfj-map-stop-button{
  width:36px;
  height:36px;
  min-height:36px;
  flex:0 0 auto;
  display:flex;

  align-items:center;

  justify-content:center;
  gap:0;

  border:
    1px solid
    #dadee2;

  border-radius:50%;

  padding:0;

  background:
    rgba(
      255,
      255,
      255,
      .96
    );

  color:#58646d;

  font-size:11px;
  font-weight:800;

  box-shadow:
    0 5px 18px
    rgba(
      23,
      35,
      45,
      .13
    );
}

.cfj-map-stop-button.is-active{
  border-color:#ffae68;

  background:#fff7f0;

  color:#a95518;
}

.cfj-map-stop-add{
  width:36px;
  height:36px;
  min-width:36px;
  flex:0 0 36px;
  display:grid;
  place-items:center;
  border:1px dashed #e9a56d;
  border-radius:50%;
  background:rgba(255,250,245,.96);
  color:#d57427;
  box-shadow:0 5px 18px rgba(23,35,45,.13);
}
.cfj-map-stop-add:hover:not(:disabled){
  border-color:#dc7b2d;
  background:#fff0df;
  transform:scale(1.06);
}
.cfj-map-stop-add:disabled{opacity:.5;cursor:not-allowed}

.cfj-map-stop-dot{
  width:27px;
  height:27px;

  display:grid;
  place-items:center;

  border:
    2px solid
    rgba(
      255,
      255,
      255,
      .85
    );

  border-radius:50%;

  color:#fff;

  font-size:9px;
  font-weight:800;
}

.cfj-map-stop-dot.is-start{
  background:#3d8c69;
}

.cfj-map-stop-dot.is-end{
  background:#df5a5a;
}

.cfj-map-stop-done{
  width:18px;
  height:18px;

  display:grid;
  place-items:center;

  border-radius:50%;

  background:#e8f5ee;

  color:#43866d;
}


.cfj-map-optimize-button{
  position:absolute;
  top:58px;
  right:12px;
  z-index:850;
  display:inline-flex;
  align-items:center;
  gap:6px;
  min-height:34px;
  padding:0 11px;
  border:1px solid #efb27d;
  border-radius:999px;
  background:rgba(255,247,238,.95);
  color:#c66b20;
  box-shadow:0 5px 16px rgba(35,70,96,.14);
  font-size:10px;
  font-weight:850;
}
.cfj-map-optimize-button:hover:not(:disabled){
  border-color:#e88b45;
  background:#ffead8;
  color:#a95415;
}
.cfj-map-optimize-button:disabled{opacity:.5;cursor:not-allowed}

/* MAP BOTTOM RIGHT */

.cfj-map-tools{
  position:absolute;

  right:14px;
  bottom:54px;

  z-index:850;

  display:flex;
  flex-direction:column;

  gap:7px;
}

.cfj-map-tools button{
  width:42px;
  height:42px;

  display:grid;
  place-items:center;

  border:
    1px solid
    #dadee2;

  border-radius:11px;

  background:
    rgba(
      255,
      255,
      255,
      .96
    );

  color:#53616b;

  box-shadow:
    0 5px 18px
    rgba(
      23,
      35,
      45,
      .13
    );
}


/* TRIP */

.cfj-trip{
  display:grid;

  grid-template-columns:
    repeat(
      3,
      1fr
    );

  padding:
    24px 12px;
}

.cfj-trip > div{
  padding:
    0 12px;

  border-right:
    1px solid
    var(--line);

  text-align:center;
}

.cfj-trip > div:last-child{
  border-right:0;
}

.cfj-trip strong{
  display:block;

  margin-bottom:7px;

  color:#25313a;

  font-size:28px;
  font-weight:570;
}

.cfj-trip strong span{
  margin-left:6px;

  color:#8b959c;

  font-size:10px;
  font-weight:400;
}

.cfj-trip small{
  color:#8a949c;

  font-size:10px;
}

.cfj-map-foot{
  padding:
    0 24px
    20px;

  color:#a0a7ac;

  font-size:10px;

  line-height:1.7;
}


/* FOOTER */

.cfj-footer{
  position:sticky;

  bottom:0;

  z-index:600;

  margin-top:24px;

  display:flex;

  justify-content:
    space-between;

  align-items:center;

  gap:16px;

  border:
    1px solid
    var(--line);

  border-radius:12px;

  padding:
    16px 22px;

  background:
    rgba(
      255,
      255,
      255,
      .96
    );

  box-shadow:
    0 -5px 25px
    rgba(
      23,
      35,
      45,
      .05
    );

  backdrop-filter:
    blur(12px);
}

.cfj-footer b{
  font-size:13px;
}

.cfj-footer small{
  display:block;

  margin-top:5px;

  color:#919aa2;

  font-size:11px;
}

.cfj-submit{
  min-height:46px;

  display:flex;

  align-items:center;

  justify-content:center;

  gap:8px;

  border:0;

  border-radius:9px;

  padding:
    0 24px;

  background:#ff8522;

  color:#fff;

  font-size:13px;
  font-weight:650;

  box-shadow:
    0 5px 14px
    rgba(
      255,
      133,
      34,
      .16
    );
}

.cfj-submit:hover:not(:disabled){
  background:#ec7617;
}


/* NOTICE */

.cfj-notice{
  margin-bottom:18px;

  border-radius:10px;

  padding:
    15px 18px;

  font-size:12px;

  line-height:1.7;
}

.cfj-error{
  border:
    1px solid
    #f2d4cc;

  background:#fff2ef;

  color:#b55240;
}

.cfj-success{
  display:flex;

  align-items:center;

  gap:12px;

  border:
    1px solid
    #d3e8d9;

  background:#eff8f2;

  color:#397755;
}

.cfj-success button{
  margin-left:auto;

  border:
    1px solid
    #c9dfd0;

  border-radius:7px;

  padding:
    8px 17px;

  background:#fff;

  color:#397755;
}

.cfj-map-error,
.cfj-inline-error{
  padding:
    12px 16px;

  background:#fff4eb;

  color:#a7642e;

  font-size:11px;
}

.cfj-map-error button,
.cfj-inline-error button{
  border:0;

  background:none;

  color:#b76d2a;

  text-decoration:
    underline;
}

.cfj-empty{
  border-radius:12px;

  padding:25px;

  background:#fafbfc;

  color:#909aa2;

  text-align:center;

  font-size:12px;
}


/* LOADER */

.cfj-spinner{
  width:16px;
  height:16px;

  border:
    2px solid
    rgba(
      255,
      255,
      255,
      .4
    );

  border-top-color:#fff;

  border-radius:50%;

  animation:
    cfj-spin
    .7s
    linear
    infinite;
}

@keyframes cfj-spin{
  to{
    transform:
      rotate(360deg);
  }
}


/* RESPONSIVE */

@container(max-width:1120px){
  .cfj-layout{
    width:100%;
    max-width:none;
    grid-template-columns:
      49% 2% 49%;
  }

  .cfj-map-column{
    position:static;
  }

  .cfj-map-wrap{
    height:350px;
  }
}


@media(max-width:700px){

  .cfj-root{
    padding:0 0 90px;
  }

  .cfj-header h1{
    font-size:24px;
  }

  .cfj-header-meta{
    display:none;
  }

  .cfj-card{
    padding:18px;
  }

  .cfj-driver-toolbar{
    align-items:stretch;

    flex-direction:column;
  }

  .cfj-selected-count{
    align-self:flex-end;
  }

  .cfj-map-style-main > span{
    display:none;
  }

  .cfj-map-style-main{
    width:42px;

    justify-content:center;

    padding:0;
  }

  .cfj-map-style-arrow{
    display:none;
  }

  .cfj-map-stop-button > span:nth-child(2){
    display:none;
  }

  .cfj-footer{
    padding:12px;
  }
}

.cfj-map-lock{position:absolute;inset:0;z-index:1200;display:flex;align-items:center;justify-content:center;gap:9px;background:rgba(255,255,255,.58);backdrop-filter:blur(2px);color:#4b5963;font-size:12px;font-weight:700;cursor:wait}.cfj-map-lock-spinner{width:16px;height:16px;border:2px solid rgba(75,89,99,.24);border-top-color:#f18432;border-radius:50%;animation:cfj-map-lock-spin .7s linear infinite}@keyframes cfj-map-lock-spin{to{transform:rotate(360deg)}}



/* 車輛選擇與一般工作頁統一 */
.cfj-assignment-card{
  border:0;
  border-radius:18px;
  padding:18px;
  background:#f8fafb;
  box-shadow:none;
  min-width:0;
  max-width:100%;
  overflow:hidden;
}
.cfj-assignment-head{
  padding:0 2px 14px;
  border-bottom:1px solid #e8edf0;
}
.cfj-asset-section{
  margin-top:18px;
  padding-top:16px;
  border-top:1px solid #e8edf0;
  min-width:0;
  max-width:100%;
}
.cfj-asset-title{
  align-items:flex-start;
  margin-bottom:11px;
}
.cfj-asset-title > span{
  padding-top:7px;
  color:#34424c;
}
.cfj-asset-tools{
  display:flex;
  align-items:center;
  gap:8px;
}
.cfj-asset-search{
  width:min(250px, 100%);
  height:34px;
  display:flex;
  align-items:center;
  gap:7px;
  padding:0 10px;
  border:1px solid #e0e7eb;
  border-radius:10px;
  background:#fff;
  color:#9aa5ad;
  transition:border-color .16s, box-shadow .16s;
}
.cfj-asset-search:focus-within{
  border-color:#f1a064;
  box-shadow:0 0 0 3px rgba(241,160,100,.13);
}
.cfj-asset-search input{
  width:100%;
  min-width:0;
  border:0;
  outline:0;
  background:transparent;
  color:#33414b;
  font-size:11px;
}
.cfj-asset-search input::placeholder{color:#a2abb2}
.cfj-asset-scroll{
  display:flex;
  width:100%;
  min-width:0;
  max-width:100%;
  box-sizing:border-box;
  gap:9px;
  max-height:none;
  overflow-x:auto;
  overflow-y:hidden;
  padding:1px 2px 7px;
  scrollbar-width:thin;
  -webkit-overflow-scrolling:touch;
}
.cfj-asset-scroll > *{
  flex:0 0 178px;
}
.cfj-asset-scroll > .cfj-no-asset{
  flex-basis:100%;
}
.cfj-vehicle-card{
  width:178px;
  max-width:178px;
  flex:0 0 178px;
  min-width:0;
  min-height:68px;
  position:relative;
  display:flex;
  align-items:center;
  gap:9px;
  border:1px solid transparent;
  border-radius:14px;
  padding:8px;
  background:#fff;
  color:#47535d;
  text-align:left;
  box-shadow:0 2px 8px rgba(31,45,55,.035);
}
.cfj-vehicle-card:hover:not(:disabled){
  border-color:#f4bc91;
  background:#fffaf6;
  transform:translateY(-1px);
}
.cfj-vehicle-card.is-selected{
  border-color:#ef995b;
  background:#fff7ef;
  box-shadow:0 0 0 3px rgba(239,153,91,.12);
}
@media(max-width:700px){
  .cfj-asset-title{align-items:flex-start;flex-wrap:wrap}
  .cfj-asset-search{width:100%;max-width:none}
}

.cfj-no-asset{
  min-width:0;
  min-height:68px;
  grid-column:1/-1;
  border-radius:12px;
  padding:14px;
  background:#fff;
  color:#9aa4ab;
  text-align:center;
}

.cfj-common-actions{display:flex;align-items:center;gap:10px;margin-top:7px;min-height:24px}.cfj-common-actions button,.cfj-common-picker-button{border:0;border-radius:7px;padding:5px 8px;background:#fff5eb;color:#c66d24;font-size:10px;font-weight:750}.cfj-common-actions button:hover,.cfj-common-picker-button:hover{background:#ffead8}.cfj-common-actions span{color:#47876d;font-size:10px}.cfj-common-picker-button{white-space:nowrap}.cfj-stop-detail-head .cfj-common-picker-button{margin-left:auto}
.cfj-asset-tools{display:flex;align-items:center;justify-content:flex-end;gap:8px;min-width:0}.cfj-binding-button{display:inline-flex;align-items:center;gap:5px;min-height:34px;border:1px solid #f0b17d;border-radius:9px;padding:0 10px;background:#fff7ef;color:#c66d24;font-size:10px;font-weight:800;white-space:nowrap}.cfj-binding-button:hover:not(:disabled){background:#ffecdc;border-color:#e99a5e}.cfj-binding-button:disabled{opacity:.55}.cfj-binding-section{background:#fbfcfd;border-radius:12px;padding:12px}.cfj-binding-section .cfj-asset-title{border-bottom:0;margin-bottom:9px;padding-bottom:0}.cfj-binding-section .cfj-asset-title>small{color:#9ba4ab;font-size:10px}.cfj-trailer-checklist{display:grid;gap:6px}.cfj-trailer-check{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:9px 10px;border:1px solid #edf0f2;border-radius:9px;background:#fff;cursor:pointer}.cfj-trailer-check.is-checked{border-color:#f1c29d;background:#fffaf6}.cfj-trailer-check-copy{display:grid;gap:2px;min-width:0}.cfj-trailer-check-copy b{color:#394750;font-size:12px}.cfj-trailer-check-copy small{color:#9aa4ab;font-size:10px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.cfj-trailer-check input{width:16px;height:16px;flex:0 0 16px;accent-color:#ed8b43}.cfj-binding-empty{padding:12px;border-radius:9px;background:#f5f7f8;color:#929da5;font-size:10px;line-height:1.6}.cfj-binding-overlay,.cfj-common-overlay{position:fixed;inset:0;z-index:3000;display:grid;place-items:center;padding:24px;background:rgba(25,35,45,.28);backdrop-filter:blur(5px)}.cfj-binding-modal,.cfj-common-modal{width:min(620px,100%);max-height:min(720px,calc(100vh - 48px));overflow:hidden;border:1px solid rgba(255,255,255,.8);border-radius:22px;background:#fff;box-shadow:0 28px 80px rgba(22,35,44,.24)}.cfj-binding-modal-head,.cfj-common-modal-head{display:flex;align-items:flex-start;justify-content:space-between;gap:18px;padding:25px 26px 18px;border-bottom:1px solid #edf0f2}.cfj-binding-modal-head small,.cfj-common-modal-head small{color:#d47b32;font-size:10px;font-weight:850;letter-spacing:.08em}.cfj-binding-modal-head h2,.cfj-common-modal-head h2{margin:5px 0 0;color:#26343d;font-size:22px;letter-spacing:-.03em}.cfj-binding-modal-head p{margin:6px 0 0;color:#929ca3;font-size:11px}.cfj-binding-close,.cfj-common-close{width:34px;height:34px;display:grid;place-items:center;border:0;border-radius:50%;background:#f5f7f8;color:#7e8a92;font-size:22px}.cfj-binding-search,.cfj-common-search{display:flex;align-items:center;gap:8px;margin:16px 26px 10px;padding:0 12px;height:40px;border:1px solid #e3e8ec;border-radius:10px;background:#fafbfc;color:#9aa5ad}.cfj-binding-search:focus-within,.cfj-common-search:focus-within{border-color:#efaa73;box-shadow:0 0 0 3px rgba(239,170,115,.12)}.cfj-binding-search input,.cfj-common-search input{width:100%;min-width:0;border:0;outline:0;background:transparent;color:#34424b;font-size:12px}.cfj-binding-list,.cfj-common-list{max-height:510px;overflow:auto;padding:4px 26px 24px}.cfj-binding-row{width:100%;display:grid;grid-template-columns:22px minmax(0,1fr) auto;align-items:center;gap:12px;padding:13px 12px;border:1px solid #edf0f2;border-radius:12px;background:#fff;text-align:left}.cfj-binding-row+.cfj-binding-row{margin-top:8px}.cfj-binding-row:hover{border-color:#efb27f;background:#fffaf6}.cfj-binding-row.is-selected{border-color:#ec995b;background:#fff7ef}.cfj-binding-check{width:19px;height:19px;display:grid;place-items:center;border:1px solid #dce3e7;border-radius:6px;color:transparent}.cfj-binding-row.is-selected .cfj-binding-check{border-color:#ed8d48;background:#ed8d48;color:#fff}.cfj-binding-main{display:grid;gap:3px;min-width:0}.cfj-binding-main strong{color:#2e3b44;font-size:14px}.cfj-binding-main small{color:#9aa4ab;font-size:10px}.cfj-binding-main span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#6c7880;font-size:11px}.cfj-binding-count{color:#d47b32;font-size:10px;font-weight:800;white-space:nowrap}.cfj-binding-empty,.cfj-common-empty{margin:8px 26px 24px;padding:30px 16px;text-align:center}.cfj-binding-empty.is-error,.cfj-common-empty.is-error{color:#b85b4d;background:#fff3f0}.cfj-common-list{padding-top:0}.cfj-common-item{width:100%;display:flex;align-items:center;justify-content:space-between;gap:14px;padding:13px 10px;border:0;border-bottom:1px solid #edf0f2;background:#fff;text-align:left}.cfj-common-item:hover{background:#fff8f1}.cfj-common-item-main{display:grid;gap:5px;min-width:0}.cfj-common-item-main strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#34424c;font-size:13px}.cfj-common-item-main small{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#98a3ab;font-size:10px}.cfj-common-status{flex:none;border-radius:999px;padding:5px 8px;background:#f4f6f7;color:#9aa4ab;font-size:9px;font-weight:800}.cfj-common-status.is-ready{background:#edf8f1;color:#4b8a70}.cfj-common-load-more{width:100%;margin-top:10px;border:0;border-radius:9px;padding:10px;background:#f5f7f8;color:#6c7981;font-size:11px;font-weight:750}.cfj-common-load-more:hover{background:#fff0e3;color:#c66d24}
@media(max-width:700px){.cfj-asset-tools{width:100%;justify-content:stretch}.cfj-binding-button{flex:1;justify-content:center}.cfj-asset-tools .cfj-asset-search{flex:1;width:auto}.cfj-binding-modal,.cfj-common-modal{max-height:calc(100vh - 24px);border-radius:18px}.cfj-binding-modal-head,.cfj-common-modal-head{padding:20px 18px 15px}.cfj-binding-search,.cfj-common-search{margin-left:18px;margin-right:18px}.cfj-binding-list,.cfj-common-list{padding-left:18px;padding-right:18px}.cfj-binding-empty,.cfj-common-empty{margin-left:18px;margin-right:18px}}

/* 精簡車輛區層次，讓重點落在車牌與綁定選擇 */
.cfj-assignment-card{padding:16px 0;background:transparent}
.cfj-assignment-head{padding:4px 2px 16px;border-bottom:1px solid #e8edf0}
.cfj-asset-section{margin-top:16px;padding:15px 0 0;border-top:1px solid #edf0f2}
.cfj-asset-title{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px}
.cfj-asset-title>span{display:flex;align-items:center;gap:7px;padding-top:0;font-size:12px;font-weight:800}
.cfj-asset-tools{flex:1;justify-content:flex-end}
.cfj-asset-search{width:min(235px,100%);background:#fff}
.cfj-asset-scroll{display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:10px;overflow:visible;padding:0 1px 2px}
.cfj-asset-scroll>*{width:100%;max-width:none;min-width:0;flex:initial}
.cfj-vehicle-card{width:100%;max-width:none;min-height:70px;border:1px solid #edf0f2;border-radius:13px;box-shadow:0 3px 12px rgba(31,45,55,.04)}
.cfj-vehicle-card.is-selected{border-color:#eda061;background:#fff8f1;box-shadow:0 0 0 3px rgba(239,153,91,.1)}
.cfj-binding-section{margin-top:18px;padding:14px;border:1px solid #edf0f2;border-radius:14px;background:#f9fafb}
.cfj-binding-section .cfj-asset-title{margin-bottom:10px}
.cfj-trailer-checklist{grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}
.cfj-trailer-check{min-width:0;padding:10px 11px;border-color:#e8edf0}
.cfj-trailer-check-copy b{font-size:11px}
.cfj-cargo-photo-add,.cfj-photo-add{font-size:0;cursor:pointer;transition:background .16s,border-color .16s,color .16s,transform .16s}
.cfj-cargo-photo-add:hover,.cfj-photo-add:hover{transform:translateY(-1px);border-color:#efaa73;background:#fff8f1;color:#c66d24}
.cfj-cargo-photo-add{width:52px;height:52px}
.cfj-photo-add{width:58px;height:58px}
@media(max-width:700px){.cfj-asset-tools{width:auto;flex-wrap:wrap}.cfj-asset-search{width:min(210px,100%)}.cfj-asset-scroll{grid-template-columns:repeat(2,minmax(0,1fr))}.cfj-trailer-checklist{grid-template-columns:1fr}}

/* 車輛卡片維持單列橫向滑動 */
.cfj-asset-scroll{display:flex;gap:10px;overflow-x:auto;overflow-y:hidden;padding:0 2px 8px;scroll-snap-type:x proximity;-webkit-overflow-scrolling:touch}
.cfj-asset-scroll>*{width:178px;max-width:178px;min-width:178px;flex:0 0 178px;scroll-snap-align:start}
.cfj-vehicle-card{width:178px;max-width:178px;min-width:178px}
@media(max-width:700px){.cfj-asset-scroll{grid-template-columns:none}.cfj-asset-scroll>*{width:170px;max-width:170px;min-width:170px;flex-basis:170px}.cfj-vehicle-card{width:170px;max-width:170px;min-width:170px}}

.cfj-cargo-control-row{display:grid;grid-template-columns:minmax(0,150px) minmax(0,1fr) 30px;align-items:center;gap:8px;margin-top:8px;padding-top:8px;border-top:1px solid #f0f2f4}
.cfj-cargo-status-field{display:flex;align-items:center;gap:6px;min-width:0;color:#7d8991;font-size:9px;font-weight:750}
.cfj-cargo-status-field select{min-width:0;flex:1;border:1px solid #e5eaed;border-radius:7px;padding:6px 22px 6px 7px;background:#fff;color:#53616b;font-size:10px;outline:0}
.cfj-cargo-status-field select:focus{border-color:#f0a36a;box-shadow:0 0 0 3px rgba(240,163,106,.12)}
.cfj-cargo-action-wrap{position:relative;justify-self:end}
.cfj-cargo-action-button{width:29px;height:29px;display:grid;place-items:center;border:1px solid #e5eaed;border-radius:8px;background:#fff;color:#76838c}
.cfj-cargo-action-button:hover{border-color:#efb17c;background:#fff8f1;color:#c66d24}
.cfj-cargo-action-menu{position:absolute;right:0;top:34px;z-index:40;width:170px;display:grid;gap:2px;padding:5px;border:1px solid #e8edf0;border-radius:11px;background:#fff;box-shadow:0 14px 34px rgba(28,42,52,.16)}
.cfj-cargo-action-menu button{display:flex;align-items:center;gap:8px;width:100%;border:0;border-radius:7px;padding:8px 9px;background:#fff;color:#52606a;text-align:left;font-size:10px}
.cfj-cargo-action-menu button:hover{background:#fff5eb;color:#c66d24}
.cfj-cargo-placement-overlay{position:fixed;inset:0;z-index:3200;display:grid;place-items:center;padding:24px;background:rgba(25,35,45,.3);backdrop-filter:blur(5px)}
.cfj-cargo-placement-modal{width:min(760px,100%);max-height:min(760px,calc(100vh - 48px));overflow:auto;border:1px solid rgba(255,255,255,.85);border-radius:22px;background:#fff;box-shadow:0 28px 80px rgba(22,35,44,.25)}

.cfj-cargo-placement-head{display:flex;align-items:flex-start;justify-content:space-between;gap:18px;padding:25px 26px 18px;border-bottom:1px solid #edf0f2}
.cfj-cargo-placement-head small{color:#d47b32;font-size:10px;font-weight:850;letter-spacing:.08em}
.cfj-cargo-placement-head h2{margin:5px 0 0;color:#26343d;font-size:22px;letter-spacing:-.03em}
.cfj-cargo-placement-head p{margin:7px 0 0;color:#929ca3;font-size:11px;line-height:1.6}
.cfj-cargo-placement-body{display:grid;grid-template-columns:minmax(0,1fr) minmax(280px,1.05fr);gap:18px;padding:20px 26px}
.cfj-cargo-placement-fields{display:grid;align-content:start;gap:13px}
.cfj-cargo-placement-field{display:grid;gap:6px;color:#53616b;font-size:11px;font-weight:750}
.cfj-cargo-placement-field input,.cfj-cargo-placement-field textarea{width:100%;min-width:0;border:1px solid #e3e8ec;border-radius:9px;padding:10px;background:#fafbfc;color:#35414b;font-size:12px;outline:0;resize:vertical}
.cfj-cargo-placement-field input:focus,.cfj-cargo-placement-field textarea:focus{border-color:#efaa73;background:#fff;box-shadow:0 0 0 3px rgba(239,170,115,.12)}
.cfj-cargo-placement-field-row{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:end;gap:8px}
.cfj-cargo-use-stop-button{height:36px;display:inline-flex;align-items:center;gap:5px;border:1px solid #f1c59d;border-radius:8px;padding:0 9px;background:#fff8f1;color:#c66d24;font-size:10px;white-space:nowrap}
.cfj-cargo-use-stop-button:hover{background:#ffecdc}
.cfj-cargo-coordinates{display:flex;flex-direction:column;gap:4px;padding:10px 11px;border-radius:9px;background:#f5f8f9;color:#6e7b84;font-size:10px}
.cfj-cargo-coordinates small{color:#a0a9af;font-size:9px}
.cfj-placement-map{position:relative;min-height:300px;overflow:hidden;border:1px solid #e4eaed;border-radius:14px;background:#e7ebed}
.cfj-placement-map .cfj-map{height:100%;min-height:300px}
.cfj-placement-map-hint{position:absolute;left:12px;bottom:12px;z-index:800;display:flex;align-items:center;gap:5px;border-radius:999px;padding:7px 9px;background:rgba(255,255,255,.92);color:#65727b;font-size:10px;box-shadow:0 4px 12px rgba(25,35,45,.12)}
.cfj-placement-marker-shell{background:transparent;border:0}
.cfj-placement-marker{width:28px;height:28px;border:4px solid #fff;border-radius:50% 50% 50% 0;background:#f18432;box-shadow:0 4px 12px rgba(25,35,45,.24);transform:rotate(-45deg)}
.cfj-cargo-placement-footer{display:flex;justify-content:flex-end;gap:8px;padding:15px 26px 20px;border-top:1px solid #edf0f2}
.cfj-cargo-secondary-button,.cfj-cargo-primary-button{display:inline-flex;align-items:center;justify-content:center;gap:6px;min-height:36px;border-radius:9px;padding:0 14px;font-size:11px;font-weight:750}
.cfj-cargo-secondary-button{border:1px solid #e3e8ec;background:#fff;color:#72808a}
.cfj-cargo-primary-button{border:1px solid #f18432;background:#f18432;color:#fff;box-shadow:0 5px 13px rgba(241,132,50,.2)}
.cfj-cargo-primary-button:hover{background:#e97624}









@media(max-width:700px){.cfj-cargo-control-row{grid-template-columns:minmax(0,1fr) 30px}.cfj-cargo-action-wrap{grid-column:2;grid-row:1}.cfj-cargo-placement-body{grid-template-columns:1fr;padding:16px 18px}.cfj-cargo-placement-head{padding:20px 18px 15px}.cfj-cargo-placement-footer{padding:13px 18px 18px}.cfj-cargo-placement-modal{max-height:calc(100vh - 24px);border-radius:18px}.cfj-placement-map,.cfj-placement-map .cfj-map{min-height:230px}.cfj-cargo-history-list{padding-left:18px;padding-right:18px}.cfj-cargo-action-menu{right:-3px}}

/* Tallarge reference UI refresh: visual-only overrides */
.cfj-root{
  --accent:#f58a1f;
  --ink:#18354e;
  --muted:#73879a;
  --line:#e2ebf2;
  background:#f6faff;
  color:var(--ink);
  padding:22px 28px 92px;
  font-family:"Noto Sans TC",Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
}
.cfj-root form{max-width:1440px}
.cfj-header{margin-bottom:22px;padding:0 2px}
.cfj-page-back-title{display:inline-flex;align-items:center;gap:8px;border:0;padding:0;background:transparent;color:#163d5b;font-size:28px;font-weight:750;letter-spacing:-.04em;line-height:1.2}
.cfj-page-back-title:hover{color:#d87520;transform:translateX(-1px)}
.cfj-breadcrumb{margin-bottom:12px;color:#8a9bab;font-size:12px}
.cfj-breadcrumb button{color:#5e7487}
.cfj-header h1{color:#163d5b;font-size:28px;letter-spacing:-.04em}
.cfj-header p{margin-top:7px;color:#8093a3;font-size:12px}
.cfj-tag{border-radius:999px;padding:5px 10px;background:#fff1e3;color:#d8761c;font-size:10px}
.cfj-header-meta{border:1px solid #e2ebf2;border-radius:999px;padding:8px 12px;background:#fff;color:#71879a;font-size:11px;box-shadow:0 5px 16px rgba(35,74,105,.05)}
.cfj-header-map-toggle{
  display:inline-flex;
  align-items:center;
  gap:5px;
  min-height:34px;
  border:1px solid #e2ebf2;
  border-radius:999px;
  padding:0 12px;
  background:#fff;
  color:#4f7188;
  box-shadow:0 5px 16px rgba(35,74,105,.05);
  font-size:11px;
  font-weight:800;
  cursor:pointer;
}
.cfj-header-map-toggle:hover{border-color:#f1ad6f;background:#fff8f1;color:#b8651d}
.cfj-header-map-toggle>span{font-size:17px;font-weight:400;line-height:1}
.cfj-header-map-toggle.is-collapsed{
  opacity:.5;
  animation:cfj-map-toggle-reveal 2.2s ease-in-out infinite;
}
.cfj-dot{background:#29b77a}
.cfj-layout{grid-template-columns:minmax(0,1.04fr) 18px minmax(410px,.96fr);gap:0}
.cfj-form-column{gap:12px}
.cfj-card,.cfj-map-panel{border-color:#e1ebf2;border-radius:16px;background:#fff;box-shadow:0 10px 28px rgba(36,72,101,.055)}
.cfj-card{padding:22px}
.cfj-section-head{gap:11px;margin-bottom:18px}
.cfj-section-n{width:30px;height:30px;border-radius:50%;background:#eaf8f2;color:#24966a}
.cfj-section-head h2{color:#1c405d;font-size:17px;font-weight:800}
.cfj-section-head p{color:#899bab;font-size:11px}
.cfj-start-toggle{margin-bottom:14px;color:#476177;font-size:11px}
.cfj-start-toggle input{accent-color:#f58a1f}
.cfj-stops{gap:8px}
.cfj-stop{gap:11px;padding:11px 12px;border:1px solid #eaf0f4;border-radius:13px;background:#fff}
.cfj-stop:not(:last-child)::after{top:45px;bottom:-10px;left:27px;border-left:2px solid #d5e6ef}
.cfj-stop-number{width:32px;height:32px;flex-basis:32px;border:0;border-radius:50%;background:#2bb879;color:#fff;font-size:12px;box-shadow:0 4px 10px rgba(38,183,120,.18)}
.cfj-stop-number.is-waypoint{background:#f58a1f;color:#fff;border:0;box-shadow:0 4px 10px rgba(245,138,31,.2)}
.cfj-stop-number.is-end{background:#eef1f4;color:#748594;border:0}
.cfj-stop-body>label{margin:1px 0 8px;color:#294761;font-size:12px}
.cfj-address-row{border-color:#dfe9ef;border-radius:9px;background:#fbfdff}
.cfj-stop.is-active .cfj-address-row{border-color:#f6b16f;background:#fffaf5;box-shadow:0 0 0 3px #fff2e5}
.cfj-address-row input{padding:11px 10px;color:#365168;font-size:11px}
.cfj-stop-details{margin-top:10px;border-color:#e6eef3;border-radius:11px;background:#f7fbfd}
.cfj-recipient-card{border-color:#e7eef3;border-radius:10px;box-shadow:0 3px 10px rgba(34,68,91,.035)}
.cfj-contact-grid{grid-template-columns:1.2fr 1fr 1fr 1fr}
.cfj-cargo-item{border-color:#e6eef3;border-radius:10px;background:#fff}
.cfj-cargo-head button,.cfj-route-actions button{border-color:#f2c18f;background:#fff8f1;color:#d0711e}
.cfj-route-actions{margin-top:14px}
.cfj-route-summary{border:1px solid #e5eef3;background:#f6fafc;color:#5d7487}
.cfj-schedule-grid{gap:10px}
.cfj-field{color:#3a566d;font-size:11px}
.cfj-field input,.cfj-field textarea,.cfj-field select{border-color:#dfe9ef;border-radius:9px;padding:11px;background:#fbfdff;color:#294761;font-size:12px}
.cfj-driver-search{height:40px;border-color:#e0eaf0;background:#f8fbfd}
.cfj-member-chip{color:#708597}
.cfj-assignment-card{border-color:#e3edf2;border-radius:14px;box-shadow:0 5px 15px rgba(35,70,96,.04)}
.cfj-map-column{top:22px}
.cfj-map-wrap{height:clamp(440px,62vh,650px);border:1px solid #dce8ef;border-radius:16px;box-shadow:none}
.cfj-map-stop-switch{bottom:14px}
.cfj-map-stop-button{border-color:#d9e7ee;border-radius:999px}
.cfj-map-stop-button.is-active{border-color:#f4ad68;background:#fff7ee;color:#b65f16}
.cfj-map-tools button,.cfj-custom-zoom button{border-color:#dbe7ed}
.cfj-trip{border:1px solid #dce8ef;border-top:0;border-radius:0 0 16px 16px;background:#fff;box-shadow:0 10px 28px rgba(36,72,101,.055);padding:18px 8px}
.cfj-trip strong{color:#1d405c;font-size:23px;font-weight:750}
.cfj-trip small{color:#8295a5;font-size:10px}
.cfj-footer{margin-top:16px;border-color:#e0eaf1;border-radius:14px;background:rgba(255,255,255,.96);padding:14px 18px;box-shadow:0 -8px 24px rgba(35,70,96,.06)}
.cfj-footer b{color:#284963;font-size:12px}
.cfj-footer small{color:#8698a7;font-size:10px}
.cfj-submit{min-height:42px;border-radius:10px;background:#f58a1f;box-shadow:0 7px 16px rgba(245,138,31,.22);font-size:12px}
.cfj-submit:hover:not(:disabled){background:#e87910}
.cfj-notice{border-radius:12px}

.cfj-cargo-placement-overlay{padding:24px;background:rgba(21,47,68,.30);backdrop-filter:blur(6px)}
.cfj-cargo-placement-modal{width:min(770px,100%);max-height:min(790px,calc(100vh - 48px));border:1px solid rgba(255,255,255,.9);border-radius:15px;background:#fff;box-shadow:0 28px 80px rgba(22,48,70,.27)}
.cfj-cargo-placement-head{padding:25px 28px 17px;border-bottom:0}
.cfj-cargo-placement-head small{color:#d7771e;font-size:10px;letter-spacing:.08em}
.cfj-cargo-placement-head h2{color:#183d5b;font-size:24px;letter-spacing:-.04em}
.cfj-cargo-placement-head p{max-width:520px;margin-top:6px;color:#8a9baa;font-size:10px}
.cfj-binding-close{width:34px;height:34px;border:1px solid #edf1f4;border-radius:50%;background:#fff;color:#758696;font-size:22px;line-height:1}
.cfj-cargo-placement-summary{display:flex;align-items:center;gap:12px;margin:0 28px 18px;min-height:64px;border:1px solid #e1edf4;border-radius:11px;padding:10px 13px;background:linear-gradient(110deg,#f5fbff,#eef7fb)}
.cfj-cargo-placement-summary-icon{width:38px;height:38px;display:grid;place-items:center;border-radius:10px;background:#fff;color:#47728f;box-shadow:0 4px 10px rgba(57,104,133,.08)}
.cfj-cargo-placement-summary-copy{display:grid;gap:3px;min-width:0;flex:1}
.cfj-cargo-placement-summary-copy b{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#244761;font-size:14px}
.cfj-cargo-placement-summary-copy small{color:#8295a4;font-size:10px}
.cfj-cargo-placement-status{display:inline-flex;align-items:center;gap:6px;border-radius:999px;padding:7px 10px;background:#eaf8ef;color:#2b9a64;font-size:10px;font-weight:800;white-space:nowrap}
.cfj-cargo-placement-status-dot{width:7px;height:7px;border-radius:50%;background:#27b779}
.cfj-cargo-placement-body{grid-template-columns:minmax(0,.96fr) minmax(300px,1.04fr);gap:22px;padding:0 28px 22px}
.cfj-cargo-placement-fields{gap:14px}
.cfj-cargo-placement-field{gap:6px;color:#39556c;font-size:11px}
.cfj-cargo-placement-field input,.cfj-cargo-placement-field textarea{border-color:#dfe9ef;border-radius:9px;padding:11px;background:#fff;color:#294761;font-size:12px}
.cfj-cargo-placement-field input{min-height:42px}
.cfj-cargo-placement-field textarea{min-height:92px}
.cfj-cargo-use-stop-button{height:42px;border-color:#f2c18f;border-radius:9px;background:#fff8f1;color:#cb6e1c;font-size:10px}
.cfj-cargo-coordinates{border:1px solid #e4edf2;background:#f7fbfd;color:#6b8293}
.cfj-placement-map{min-height:348px;border-color:#dbe8ef;border-radius:13px}
.cfj-placement-map .cfj-map{min-height:348px}
.cfj-placement-map-hint{left:12px;bottom:12px;border:1px solid #e6edf1;color:#5f788a;font-size:10px}
.cfj-cargo-placement-footer{padding:16px 28px 24px;border-top:1px solid #edf2f5}
.cfj-cargo-secondary-button,.cfj-cargo-primary-button{min-height:40px;border-radius:9px;padding:0 18px;font-size:11px}
.cfj-cargo-secondary-button{border-color:#dfe8ed;color:#687e8f}
.cfj-cargo-primary-button{border-color:#f58a1f;background:#f58a1f;box-shadow:0 7px 15px rgba(245,138,31,.2)}
.cfj-cargo-primary-button:hover{background:#e87910}

@media(max-width:1120px){
  .cfj-root{padding-left:18px;padding-right:18px}
  .cfj-layout{grid-template-columns:minmax(0,1fr) 14px minmax(340px,.9fr)}
  .cfj-map-wrap{height:430px}
}
@media(max-width:760px){
  .cfj-root{padding-left:18px;padding-right:18px}
  .cfj-header{align-items:flex-start}
  .cfj-page-back-title{font-size:24px}
  .cfj-header h1{font-size:23px}
  .cfj-header-meta{display:none}
  .cfj-layout{display:grid;grid-template-columns:1fr;gap:12px}
  .cfj-form-column,.cfj-map-column{grid-column:1}
  .cfj-map-column{grid-row:auto;position:static}
  .cfj-layout-gap{display:none}
  .cfj-card{padding:17px}
  .cfj-contact-grid{grid-template-columns:1fr 1fr}
  .cfj-cargo-placement-head{padding:20px 18px 14px}
  .cfj-cargo-placement-summary{margin-left:18px;margin-right:18px}
  .cfj-cargo-placement-body{grid-template-columns:1fr;padding:0 18px 16px}
  .cfj-placement-map,.cfj-placement-map .cfj-map{min-height:260px}
  .cfj-cargo-placement-footer{padding:13px 18px 18px}
}

/* Dense logistics workspace: keep station details visible without long scrolling */
.cfj-root{padding:12px 18px 78px}
.cfj-header{
  position:sticky;
  top:0;
  z-index:1200;
  margin-bottom:12px;
  padding-top:8px;
  padding-bottom:8px;
  background:rgba(246,250,255,.94);
  backdrop-filter:blur(10px);
}
.cfj-header p,.cfj-section-head p{display:none}
.cfj-breadcrumb{margin-bottom:7px;font-size:10px}
.cfj-header h1{font-size:22px}
.cfj-form-column{gap:8px}
.cfj-card{padding:14px 15px;border-radius:13px}
.cfj-section-head{margin-bottom:10px}
.cfj-section-head h2{font-size:14px}
.cfj-section-n{width:26px;height:26px;font-size:10px}
.cfj-stops{gap:4px}
.cfj-stop{gap:8px;padding:8px 9px;border-radius:10px}
.cfj-stop:not(:last-child)::after{top:40px;bottom:-5px;left:21px;border-left-width:1px}
.cfj-stop-number{width:26px;height:26px;flex-basis:26px;font-size:10px}
.cfj-stop-body>label{margin:0 0 5px;font-size:11px}
.cfj-address-row input{height:34px;padding:7px 9px;font-size:10px}
.cfj-address-row button{padding:7px 8px}
.cfj-common-actions{gap:7px;margin-top:3px;min-height:18px}
.cfj-common-actions button{padding:3px 6px;font-size:9px}
.cfj-common-actions span{font-size:9px}
.cfj-stop-details{margin-top:6px;padding:8px;border-radius:9px}
.cfj-stop-detail-head{margin-bottom:6px;font-size:10px}
.cfj-recipient-title{gap:1px}
.cfj-recipient-title small{font-size:8px}
.cfj-recipient-card{margin:6px 0 7px;padding:8px;border-radius:8px}
.cfj-recipient-head{margin-bottom:6px}
.cfj-recipient-head span{font-size:8px}
.cfj-recipient-head b{margin-top:2px;font-size:11px}
.cfj-recipient-actions{gap:4px}
.cfj-common-picker-button{padding:4px 6px;font-size:9px}
.cfj-contact-grid{gap:5px}
.cfj-contact-grid input,.cfj-cargo-row input,.cfj-delivery-note{min-height:33px;padding:7px 8px;border-radius:7px;font-size:10px}
.cfj-recipient-card .cfj-cargo-head{margin-top:8px}
.cfj-cargo-head{margin-bottom:6px;font-size:10px}
.cfj-cargo-head span small{font-size:8px}
.cfj-cargo-head button{padding:4px 6px;font-size:9px}
.cfj-cargo-item{margin-bottom:6px;padding:7px 8px;border-radius:8px}
.cfj-cargo-row{grid-template-columns:72px minmax(0,1fr) 56px 48px 23px;gap:5px;margin-bottom:5px}
.cfj-cargo-row input{height:32px;min-height:32px}
.cfj-icon-button{width:23px;height:32px}
.cfj-cargo-control-row{grid-template-columns:minmax(0,1fr) 27px;gap:6px;margin-top:5px;padding-top:5px}
.cfj-cargo-status-field{gap:4px;font-size:8px}
.cfj-cargo-status-field select{height:27px;padding:4px 18px 4px 6px;font-size:9px}
.cfj-cargo-action-button{width:27px;height:27px}
.cfj-cargo-action-menu{top:31px;width:156px}
.cfj-cargo-action-menu button{padding:7px 8px;font-size:9px}
.cfj-cargo-photo-head{margin:7px 0 5px;font-size:9px}
.cfj-cargo-photo-head small{font-size:8px}
.cfj-cargo-photos{gap:5px}
.cfj-cargo-photo,.cfj-cargo-photo-add{width:48px;height:42px}
.cfj-cargo-photo-add{font-size:0}
.cfj-delivery-note{margin-top:3px}
.cfj-photo-head{margin:8px 0 5px;font-size:9px}
.cfj-photos{gap:5px}
.cfj-photo,.cfj-photo-add{width:54px;height:48px}
.cfj-stop-footer{margin-top:7px;font-size:8px}
.cfj-stop-footer button{padding:4px 5px;font-size:9px}
.cfj-route-actions{margin-top:9px}
.cfj-route-actions button{padding:5px 7px;font-size:9px}
.cfj-route-summary{gap:12px;margin-top:8px;padding:8px;font-size:9px}
.cfj-map-wrap{height:clamp(390px,58vh,600px)}
.cfj-trip{padding:12px 6px}
.cfj-trip strong{font-size:20px}
.cfj-footer{margin-top:9px;padding:10px 13px}
.cfj-submit{min-height:38px;padding:0 17px}
.cfj-edit-lock-note{margin-bottom:8px;padding:7px 9px;font-size:9px}
.cfj-driver-toolbar{margin-bottom:9px}
.cfj-member-scroll{gap:10px;padding-bottom:8px}
.cfj-member-avatar{width:42px;height:42px;font-size:14px}
.cfj-member-chip{width:58px;flex-basis:58px;font-size:9px}
.cfj-member-chip>span:last-child{margin-top:5px}
.cfj-assignment-card{margin-top:8px;padding:10px;border-radius:10px}
.cfj-stop-details>.cfj-stop-detail-head{padding-bottom:6px;border-bottom:1px solid #dfeaf0}
.cfj-stop-details>.cfj-stop-detail-head .cfj-recipient-title b{color:#21455e;font-size:11px}
.cfj-recipient-card{background:#fff;border-color:#dce8ee}
.cfj-recipient-card+.cfj-recipient-card{margin-top:9px;border-top:3px solid #eef5f8}
.cfj-recipient-card .cfj-recipient-head{padding-bottom:5px;border-bottom:1px solid #eef3f5}
.cfj-recipient-card .cfj-cargo-head{padding-bottom:4px;border-bottom:1px dashed #e5edf1}
.cfj-cargo-item+.cfj-cargo-item{margin-top:6px;border-top:2px solid #f0f5f7}
.cfj-recipient-actions .cfj-common-picker-button{max-width:136px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;border:1px solid #f0c895;border-radius:7px;padding:5px 8px;background:#fff8f0;color:#c87324;font-size:10px}
.cfj-recipient-actions .cfj-common-picker-button:hover{background:#ffecda;border-color:#eaa165}
.cfj-recipient-notifications{display:flex;align-items:center;flex-wrap:wrap;gap:6px 10px;margin-top:7px;padding-top:6px;border-top:1px solid #edf2f5}
.cfj-notification-option{display:inline-flex;align-items:center;gap:5px;color:#63798a;font-size:9px;font-weight:750;cursor:pointer}
.cfj-notification-option input{width:14px;height:14px;margin:0;accent-color:#2bb879}
.cfj-email-badge{display:grid;place-items:center;width:16px;height:16px;border-radius:50%;background:#edf3f7;color:#47728f;font-size:10px;font-weight:850;line-height:1}
.cfj-email-template-button{margin-left:auto;border:1px solid #dce8ee;border-radius:7px;padding:4px 7px;background:#f8fbfd;color:#547187;font-size:9px;font-weight:750}
.cfj-email-template-button:hover{border-color:#f0bd8b;background:#fff8f1;color:#c66d24}
.cfj-email-template-overlay{position:fixed;inset:0;z-index:3300;display:grid;place-items:center;padding:24px;background:rgba(21,47,68,.3);backdrop-filter:blur(6px)}
.cfj-email-template-modal{width:min(680px,100%);max-height:min(760px,calc(100vh - 48px));overflow:auto;border:1px solid rgba(255,255,255,.9);border-radius:15px;background:#fff;box-shadow:0 28px 80px rgba(22,48,70,.27)}
.cfj-email-template-head{display:flex;align-items:flex-start;justify-content:space-between;gap:18px;padding:25px 28px 17px;border-bottom:1px solid #edf2f5}
.cfj-email-template-head small{color:#d7771e;font-size:10px;font-weight:850;letter-spacing:.08em}
.cfj-email-template-head h2{margin:5px 0 0;color:#183d5b;font-size:22px;letter-spacing:-.04em}
.cfj-email-template-head p{max-width:520px;margin:7px 0 0;color:#8a9baa;font-size:10px;line-height:1.6}
.cfj-email-template-body{display:grid;gap:10px;padding:20px 28px}
.cfj-email-template-body textarea{width:100%;min-height:300px;resize:vertical;border:1px solid #dfe9ef;border-radius:9px;padding:12px;background:#fbfdff;color:#294761;font:inherit;font-size:12px;line-height:1.65;outline:0}
.cfj-email-template-body textarea:focus{border-color:#efaa73;background:#fff;box-shadow:0 0 0 3px rgba(239,170,115,.12)}
.cfj-email-template-footer{display:flex;justify-content:flex-end;gap:8px;padding:15px 28px 24px;border-top:1px solid #edf2f5}
.cfj-email-template-modal{width:min(760px,100%);max-height:min(900px,calc(100vh - 28px));border-radius:18px}
.cfj-email-template-body{gap:16px;padding:18px 24px 22px;background:#f3f7fa}
.cfj-email-preview{overflow:hidden;border:1px solid #e1eaf0;border-radius:13px;background:#fff;box-shadow:0 8px 20px rgba(31,67,91,.06)}
.cfj-email-preview-brand{padding:19px 24px 16px;border-bottom:1px solid #edf2f5;background:#fff}
.cfj-email-preview-brand img{display:block;width:178px;height:42px;object-fit:contain;object-position:left center}
.cfj-email-preview-hero{padding:22px 24px 20px;background:#eef7ff}
.cfj-email-preview-hero small{display:block;color:#123d63;font-size:14px;font-weight:750}
.cfj-email-preview-hero strong{display:block;margin-top:7px;color:#123d63;font-size:27px;line-height:1.22;letter-spacing:-.04em}
.cfj-email-preview-hero p{max-width:540px;margin:9px 0 0;color:#627c93;font-size:12px;line-height:1.7}
.cfj-email-preview-content{padding:22px 24px 24px}
.cfj-email-preview-content h3{margin:0 0 13px;color:#123d63;font-size:19px;letter-spacing:-.03em}
.cfj-email-preview-summary{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));padding:5px 0;border-radius:9px;background:#f7fbfe}
.cfj-email-preview-summary>div{display:grid;gap:5px;min-width:0;padding:10px 8px;text-align:center;border-right:1px solid #dfeaf2}
.cfj-email-preview-summary>div:last-child{border-right:0}
.cfj-email-preview-summary small{color:#70869a;font-size:9px}
.cfj-email-preview-summary b{color:#123d63;font-size:13px;line-height:1.4;overflow-wrap:anywhere}
.cfj-email-preview-panel{margin-top:13px;padding:0 15px;border:1px solid #e2ebf1;border-radius:9px;background:#fff}
.cfj-email-preview-panel h4{margin:0;padding:13px 0 7px;color:#123d63;font-size:14px}
.cfj-email-preview-addresses{display:grid}
.cfj-email-preview-address{display:flex;align-items:flex-start;gap:5px;padding:11px 0;border-top:1px solid #e8eef3;color:#64788c;font-size:12px;line-height:1.55;overflow-wrap:anywhere}
.cfj-email-preview-address strong{flex:none;color:#163e63}
.cfj-email-preview-stop{display:flex;align-items:flex-start;gap:10px;padding:10px 0;border-top:1px solid #e8eef3}
.cfj-email-preview-stop>span{display:grid;place-items:center;flex:none;width:24px;height:24px;border-radius:50%;background:#f58220;color:#fff;font-size:11px;font-weight:800}
.cfj-email-preview-stop>div{display:grid;gap:3px;min-width:0}
.cfj-email-preview-stop strong{color:#163e63;font-size:12px}
.cfj-email-preview-stop small{overflow:hidden;color:#64788c;font-size:11px;line-height:1.45;text-overflow:ellipsis;white-space:nowrap}
.cfj-email-preview-stations{display:flex;gap:7px;overflow-x:auto;padding:4px 0 10px;scrollbar-width:thin}
.cfj-email-preview-stations button{display:flex;align-items:center;gap:6px;flex:0 0 auto;border:1px solid #e2ebf1;border-radius:8px;padding:7px 9px;background:#fff;color:#718497;font:inherit;font-size:10px;cursor:pointer}
.cfj-email-preview-stations button>span{display:grid;place-items:center;width:20px;height:20px;border-radius:50%;background:#eef3f7;color:#526e84;font-size:10px;font-weight:800}
.cfj-email-preview-stations button.is-active{border-color:#f3a15b;background:#fff8f1;color:#c66d24;box-shadow:0 2px 8px rgba(245,130,32,.12)}
.cfj-email-preview-stations button.is-active>span{background:#f58220;color:#fff}
.cfj-email-preview-station-detail{display:grid;gap:4px;margin:2px 0 8px;padding:10px 0;border-top:1px solid #e8eef3}
.cfj-email-preview-station-detail strong{color:#163e63;font-size:12px}
.cfj-email-preview-station-detail span{overflow:hidden;color:#64788c;font-size:11px;line-height:1.5;text-overflow:ellipsis;white-space:nowrap}
.cfj-email-preview-cargo{display:flex;justify-content:space-between;gap:12px;padding:9px 0;border-top:1px solid #e8eef3;color:#365a77;font-size:11px}
.cfj-email-preview-cargo b{color:#163e63}
.cfj-email-preview-empty{margin:0;padding:10px 0 14px;color:#8093a3;font-size:11px}
.cfj-email-preview-button{margin-top:16px;padding:12px 16px;border-radius:7px;background:#f58220;color:#fff;text-align:center;font-size:14px;font-weight:800}
.cfj-email-preview-footer{padding:14px 24px 17px;border-top:1px solid #edf2f6;color:#8a9aaa;text-align:center;font-size:10px}

.cfj-schedule-review{position:fixed;inset:0;z-index:3400;display:grid;place-items:center;padding:22px;background:rgba(15,23,42,.34);backdrop-filter:blur(6px)}
.cfj-schedule-review-card{width:min(680px,100%);max-height:min(780px,calc(100vh - 44px));overflow:auto;border:1px solid rgba(255,255,255,.85);border-radius:24px;padding:24px;background:#fff;box-shadow:0 24px 70px rgba(15,23,42,.2)}
.cfj-schedule-review-head{display:flex;align-items:flex-start;justify-content:space-between;gap:20px;padding-bottom:18px;border-bottom:1px solid #edf1f4}
.cfj-schedule-review-kicker{color:#e9781e;font-size:10px;font-weight:950;letter-spacing:.08em}
.cfj-schedule-review-head h2{margin:7px 0 5px;color:#16212a;font-size:22px;font-weight:950}
.cfj-schedule-review-head p{margin:0;color:#87939b;font-size:12px}
.cfj-schedule-review-close{width:32px;height:32px;border:0;border-radius:10px;background:#f5f7f8;color:#66737c;font-size:22px;line-height:1;cursor:pointer}
.cfj-schedule-review-selected{display:grid;gap:9px;padding:18px 0}
.cfj-schedule-review-selected>strong{color:#34424c;font-size:13px}
.cfj-schedule-review-row{display:grid;grid-template-columns:1fr 1fr 1.2fr;gap:9px;padding:10px 12px;border-radius:11px;background:#fff8f1;color:#59656d;font-size:11px}
.cfj-schedule-review-row>div{display:grid;gap:4px;min-width:0}
.cfj-schedule-review-row small{color:#9aa5ad;font-size:10px;font-weight:700}
.cfj-schedule-review-row strong{overflow:hidden;color:#26333b;font-size:12px;text-overflow:ellipsis;white-space:nowrap}
.cfj-review-vehicle strong{color:#e9781e;font-size:16px}
.cfj-schedule-review-list{padding-top:4px}
.cfj-schedule-review-list-head{padding-bottom:9px;color:#34424c;font-size:13px}
.cfj-schedule-review-existing{display:grid;gap:7px;margin-top:8px;border-left:3px solid #f1a064;border-radius:10px;background:#fbfcfd;padding:12px 14px}
.cfj-schedule-review-existing>div{display:flex;justify-content:space-between;gap:12px;min-width:0}
.cfj-schedule-review-existing strong{overflow:hidden;color:#26333b;font-size:13px;text-overflow:ellipsis;white-space:nowrap}
.cfj-schedule-review-existing span{color:#697983;font-size:11px}
.cfj-schedule-review-existing>div:last-child{justify-content:flex-start;flex-wrap:wrap;gap:5px 18px}
.cfj-schedule-review-empty{padding:16px;border-radius:10px;background:#f8fafb;color:#87939b;font-size:11px;text-align:center}
.cfj-schedule-review-actions{display:flex;justify-content:flex-end;gap:9px;padding-top:18px}
.cfj-schedule-review-actions button{height:38px;border-radius:10px;padding:0 15px;font-size:11px;font-weight:900;cursor:pointer}
.cfj-schedule-review-cancel{border:1px solid #e1e7eb;background:#fff;color:#66737c}
.cfj-schedule-review-confirm{border:0;background:#f18432;color:#fff;box-shadow:0 7px 16px rgba(241,132,50,.2)}
.cfj-email-test-actions{display:grid;justify-items:start;gap:6px;min-width:0}
.cfj-email-test-button{border:1px solid #ef9a55;border-radius:8px;padding:8px 11px;background:#fff8f1;color:#cf721f;font-size:11px;font-weight:850;cursor:pointer}
.cfj-email-test-button:disabled{cursor:wait;opacity:.6}
.cfj-email-test-message{max-width:290px;color:#7c8e9b;font-size:10px;line-height:1.4;text-align:left}
.cfj-email-test-message.is-success{color:#21865c}
.cfj-email-test-message.is-error{color:#c45b50}

.cfj-cargo-table-head{display:grid;grid-template-columns:90px minmax(0,1fr) 60px 54px 92px 25px;gap:6px;margin:0 0 4px;padding:0 10px;color:#9aa6ae;font-size:9px;font-weight:800;letter-spacing:.02em}
  .cfj-cargo-table-head span:nth-child(2),.cfj-cargo-table-head span:nth-child(3){text-align:center}
.cfj-cargo-number-badge{padding:5px 10px 0;color:#82919b;font-size:9px;font-weight:750;letter-spacing:.02em}
.cfj-cargo-row{grid-template-columns:90px minmax(0,1fr) 60px 54px 92px 25px;align-items:center;gap:6px;padding:9px 10px;margin:0;border-bottom:1px solid #f0f3f5}
.cfj-cargo-row input,.cfj-cargo-row select{min-width:0;height:32px;border:1px solid #e3e8ec;border-radius:7px;background:#fff;color:#34424c;font-size:10px}
.cfj-cargo-row input{padding:0 8px}
.cfj-cargo-row select{padding:0 20px 0 8px}
.cfj-cargo-operation-select{font-weight:800;color:#345875!important}
.cfj-cargo-row .cfj-icon-button{width:25px;height:32px}
.cfj-cargo-item{padding:0 0 5px;border:1px solid #edf0f2;border-radius:9px;background:#fff;overflow:hidden}
.cfj-cargo-item .cfj-cargo-control-row{padding:0 10px 8px}
.cfj-cargo-operation-field{display:flex;align-items:center;gap:4px;min-width:0;color:#7d8991;font-size:9px;font-weight:750}
.cfj-cargo-operation-field select{min-width:0;flex:1;border:1px solid #e5eaed;border-radius:7px;padding:6px 20px 6px 7px;background:#fff;color:#53616b;font-size:10px;outline:0}

@media(max-width:760px){
  .cfj-root{padding:8px 8px 76px}
  .cfj-card{padding:11px 10px}
  .cfj-contact-grid{grid-template-columns:1.15fr 1fr 1fr 1fr}
  .cfj-email-template-head{padding:20px 18px 14px}
  .cfj-email-template-body{padding:16px 18px}
  .cfj-email-template-footer{align-items:flex-end;justify-content:space-between;padding:13px 18px 18px}
  .cfj-email-test-message{max-width:180px}
  .cfj-email-preview-brand{padding:15px 17px 13px}
  .cfj-email-preview-brand img{width:145px;height:34px}
  .cfj-email-preview-hero{padding:18px 17px}
  .cfj-email-preview-hero strong{font-size:23px}
  .cfj-email-preview-content{padding:17px}
  .cfj-email-preview-summary{grid-template-columns:repeat(2,minmax(0,1fr))}
  .cfj-email-preview-summary>div:nth-child(2){border-right:0}
  .cfj-map-wrap{height:360px}
}
@media(max-width:700px){
  .cfj-cargo-table-head{grid-template-columns:72px minmax(0,1fr) 50px 45px 70px 24px;gap:4px;padding:0 6px}
  .cfj-cargo-row{grid-template-columns:72px minmax(0,1fr) 50px 45px 70px 24px;gap:4px;padding:7px 6px}
  .cfj-cargo-control-row{grid-template-columns:minmax(0,1fr) minmax(0,1fr) 27px}
  .cfj-cargo-action-wrap{grid-column:3;grid-row:1}
  .cfj-cargo-operation-field select,.cfj-cargo-status-field select{height:27px;padding:4px 16px 4px 6px;font-size:9px}
}
@media(max-width:480px){
  .cfj-contact-grid{grid-template-columns:1fr 1fr}
  .cfj-recipient-notifications{align-items:flex-start}
  .cfj-email-template-button{margin-left:0}
}

/* 地圖可收起／滑回：只改版面呈現，不改路線與站點資料。 */
@keyframes cfj-map-toggle-reveal{
  0%,100%{transform:translateX(0);box-shadow:0 5px 14px rgba(35,70,96,.12)}
  50%{transform:translateX(-4px);box-shadow:0 7px 18px rgba(35,70,96,.2)}
}
@media(prefers-reduced-motion:reduce){
  .cfj-header-map-toggle.is-collapsed{animation:none}
}
.cfj-layout{transition:grid-template-columns .34s cubic-bezier(.22,.61,.36,1)}
.cfj-form-column,.cfj-map-column,.cfj-layout-gap{transition:transform .34s cubic-bezier(.22,.61,.36,1),opacity .28s ease,width .34s ease,max-width .34s ease}
.cfj-layout.is-map-collapsed{grid-template-columns:minmax(0,1fr) 0 minmax(0,0fr)}
.cfj-layout.is-map-collapsed .cfj-map-column{
  min-width:0;
  width:0;
  max-width:0;
  overflow:hidden;
  opacity:0;
  pointer-events:none;
  transform:translateX(34px);
}
.cfj-layout.is-map-collapsed .cfj-layout-gap{width:0;opacity:0;transform:translateX(18px)}
.cfj-layout.is-map-collapsed .cfj-form-column{width:100%;transform:translateX(0)}

.cfj-cargo-table-head{grid-template-columns:minmax(0,1fr) 60px 54px 92px 25px}
.cfj-cargo-row{grid-template-columns:minmax(0,1fr) 60px 54px 92px 25px}
@media(max-width:760px){
  .cfj-map-column{
    max-height:2000px;
    overflow:hidden;
    transition:max-height .34s cubic-bezier(.22,.61,.36,1),transform .34s cubic-bezier(.22,.61,.36,1),opacity .28s ease;
  }
  .cfj-layout.is-map-collapsed .cfj-map-column{
    width:100%;
    max-width:100%;
    max-height:0;
    transform:translateY(24px);
  }
  .cfj-layout.is-map-collapsed .cfj-layout-gap{display:none}
}
@media(max-width:700px){
  .cfj-cargo-table-head{grid-template-columns:minmax(0,1fr) 50px 45px 70px 24px}
  .cfj-cargo-row{grid-template-columns:minmax(0,1fr) 50px 45px 70px 24px}
}

/* Cargo row: status and ellipsis stay inline with name, quantity, unit and operation. */
.cfj-cargo-table-head{grid-template-columns:minmax(0,1fr) 60px 54px 84px 84px 29px;align-items:center}
.cfj-cargo-row{grid-template-columns:minmax(0,1fr) 60px 54px 84px 84px 29px;align-items:center}
.cfj-cargo-row input,.cfj-cargo-row select{min-width:0}
.cfj-cargo-status-select{font-weight:750;color:#52616b!important}
.cfj-cargo-status-select:invalid{color:#9aa6ae!important}
.cfj-cargo-row>.cfj-cargo-action-wrap{justify-self:end;grid-column:auto;grid-row:auto}
@media(max-width:700px){
  .cfj-cargo-table-head{grid-template-columns:minmax(0,1fr) 50px 45px 62px 62px 25px;gap:4px;padding:0 6px}
  .cfj-cargo-row{grid-template-columns:minmax(0,1fr) 50px 45px 62px 62px 25px;gap:4px;padding:7px 6px}
  .cfj-cargo-row select{padding-left:6px;padding-right:14px;font-size:9px}
  .cfj-cargo-status-select{height:27px}
  .cfj-cargo-action-button{width:25px;height:27px}
}

/* Recipient header actions: preview, contact picker and Email toggle stay together. */
.cfj-recipient-actions{align-items:center;justify-content:flex-end;flex-wrap:wrap;gap:5px}
.cfj-recipient-actions .cfj-email-template-button{order:1;margin-left:0;border:1px solid #dce8ee;border-radius:7px;padding:5px 8px;background:#f8fbfd;color:#547187;font-size:10px;font-weight:750;white-space:nowrap}
.cfj-recipient-actions .cfj-common-picker-button{order:2}
.cfj-recipient-actions .cfj-notification-option{order:3;min-height:28px;padding:4px 7px;border:1px solid #dce8ee;border-radius:7px;background:#f8fbfd}
.cfj-recipient-actions .cfj-icon-button{order:4}
@media(max-width:700px){.cfj-recipient-actions{justify-content:flex-start;width:100%}.cfj-recipient-actions .cfj-notification-option{margin-left:0}}

/* Keep an opened cargo menu above neighboring rows and outside the cargo-card clip. */
.cfj-cargo-item.is-action-open{position:relative;z-index:80;overflow:visible}
.cfj-cargo-item.is-action-open .cfj-cargo-action-wrap{z-index:90}
.cfj-cargo-item.is-action-open .cfj-cargo-action-menu{z-index:100}
.cfj-category-field{margin:2px 0 20px;padding:15px;border:1px solid #e2edf2;border-radius:14px;background:linear-gradient(135deg,#fbfdff,#f7fbfd)}
.cfj-category-field-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:11px}
.cfj-category-field-head>div{display:grid;gap:4px;min-width:0}
.cfj-category-field-head b{color:#244b65;font-size:12px}
.cfj-category-field-head small{color:#8b9ca8;font-size:10px;line-height:1.5;font-weight:400}
.cfj-category-field-head>span{flex:none;border-radius:999px;padding:5px 9px;background:#fff1e5;color:#c86d22;font-size:10px;font-weight:850}
.cfj-category-option{display:grid;grid-template-columns:40px minmax(0,1fr) 16px;align-items:center;gap:8px;min-width:0;min-height:56px;padding:8px 9px;border:1px solid #dfeaf0;border-radius:10px;background:#fff;text-align:left;color:#49677a}
.cfj-category-option:hover{border-color:#f0ae76;background:#fffaf6;transform:translateY(-1px)}
.cfj-category-option.is-active{border-color:#ef9b59;background:#fff7ef;box-shadow:0 0 0 3px rgba(239,155,89,.12)}
.cfj-category-icon{width:38px;height:38px;border-radius:9px;padding:2px;background:#f5f9fb;object-fit:contain}
.cfj-category-option.is-active .cfj-category-icon{background:#fff0e3}
.cfj-category-copy{display:grid;gap:2px;min-width:0}
.cfj-category-copy b{overflow:hidden;color:#2e5269;font-size:10px;line-height:1.35;text-overflow:ellipsis;white-space:nowrap}
.cfj-category-copy small{overflow:hidden;color:#8a9ba6;font-size:8px;line-height:1.3;text-overflow:ellipsis;white-space:nowrap}
.cfj-category-option>svg{color:#e37e2a}
.cfj-category-note{display:flex;align-items:flex-start;gap:6px;margin-top:10px;padding-top:10px;border-top:1px dashed #dce8ee;color:#8496a2;font-size:9px;line-height:1.5}
.cfj-category-note svg{flex:none;color:#6d9ab0;margin-top:1px}
@media(max-width:480px){.cfj-category-field{padding:12px}.cfj-category-option{min-height:48px}}
.cfj-category-picker-trigger{width:100%;display:grid;grid-template-columns:42px minmax(0,1fr) auto;align-items:center;gap:10px;padding:9px 12px;border:1px solid #dfeaf0;border-radius:11px;background:#fff;text-align:left;color:#315a73}
.cfj-category-picker-trigger:hover{border-color:#efaa73;background:#fffaf6;box-shadow:0 0 0 3px rgba(239,170,115,.1)}
.cfj-category-picker-icon{width:42px;height:42px;border-radius:10px;padding:2px;background:#fff0e3;object-fit:contain}
.cfj-category-picker-copy{display:grid;gap:3px;min-width:0}
.cfj-category-picker-copy b{overflow:hidden;color:#2e5269;font-size:12px;text-overflow:ellipsis;white-space:nowrap}
.cfj-category-picker-copy small{overflow:hidden;color:#8a9ba6;font-size:9px;text-overflow:ellipsis;white-space:nowrap}
.cfj-category-picker-action{display:flex;align-items:center;gap:4px;color:#c66d24;font-size:10px;font-weight:800;white-space:nowrap}
.cfj-category-picker-action svg{flex:none}
.cfj-category-overlay{position:fixed;inset:0;z-index:3400;display:grid;place-items:center;padding:24px;background:rgba(21,47,68,.36);backdrop-filter:blur(7px)}
.cfj-category-modal{width:min(780px,100%);max-height:min(760px,calc(100vh - 48px));overflow:hidden;border:1px solid rgba(255,255,255,.9);border-radius:18px;background:#fff;box-shadow:0 30px 90px rgba(22,48,70,.3)}
.cfj-category-modal-head{display:flex;align-items:flex-start;justify-content:space-between;gap:18px;padding:25px 28px 18px;border-bottom:1px solid #edf2f5}
.cfj-category-modal-head small{color:#d7771e;font-size:9px;font-weight:900;letter-spacing:.12em}
.cfj-category-modal-head h2{margin-top:5px;color:#183d5b;font-size:22px;letter-spacing:-.04em}
.cfj-category-modal-head p{margin-top:7px;color:#8a9baa;font-size:10px;line-height:1.6}
.cfj-category-modal-close{width:34px;height:34px;display:grid;place-items:center;border:0;border-radius:50%;background:#f5f7f8;color:#7e8a92;font-size:22px}
.cfj-category-modal-close:hover{background:#fff0e3;color:#c66d24}
.cfj-category-modal-body{max-height:calc(100vh - 210px);overflow:auto;padding:22px 28px 28px}
.cfj-category-modal-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}
.cfj-category-modal-grid .cfj-category-option{min-height:76px;padding:11px 12px}
.cfj-category-modal-grid .cfj-category-icon{width:48px;height:48px}
.cfj-category-modal-grid .cfj-category-copy b{font-size:11px}
.cfj-category-modal-grid .cfj-category-copy small{font-size:9px}
@media(max-width:700px){.cfj-category-modal{max-height:calc(100vh - 24px);border-radius:15px}.cfj-category-modal-head{padding:20px 18px 15px}.cfj-category-modal-body{padding:16px 18px 20px}.cfj-category-modal-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media(max-width:480px){.cfj-category-picker-trigger{grid-template-columns:38px minmax(0,1fr) 24px}.cfj-category-picker-icon{width:38px;height:38px}.cfj-category-picker-action{font-size:0}.cfj-category-picker-action svg{width:17px;height:17px}.cfj-category-modal-grid{grid-template-columns:1fr}}
@container (max-width: 800px){.cfj-section-head-actions{margin-left:0}.cfj-import-overlay{padding:8px}.cfj-import-head,.cfj-import-summary,.cfj-import-list,.cfj-import-footer{padding-left:14px;padding-right:14px}.cfj-import-station-fields,.cfj-import-recipient-fields,.cfj-import-cargo-row{grid-template-columns:1fr 1fr}.cfj-import-cargo-row .cfj-import-remove{justify-self:start}.cfj-import-station-head{flex-wrap:wrap}.cfj-import-station-head select{margin-left:38px}.cfj-import-summary{gap:12px;flex-wrap:wrap}.cfj-import-errors,.cfj-import-ready{margin-left:14px;margin-right:14px}}


.cfj-category-confirm-overlay{position:fixed;inset:0;z-index:3500;display:grid;place-items:center;padding:20px;background:rgba(21,47,68,.38);backdrop-filter:blur(7px)}
.cfj-category-confirm-modal{width:min(430px,100%);padding:26px;border:1px solid rgba(255,255,255,.95);border-radius:18px;background:#fff;box-shadow:0 28px 80px rgba(22,48,70,.28);text-align:center}
.cfj-category-confirm-icon{width:46px;height:46px;display:grid;place-items:center;margin:0 auto 12px;border-radius:50%;background:#fff1e5;color:#d7771e}
.cfj-category-confirm-modal h2{color:#183d5b;font-size:18px;letter-spacing:-.03em}
.cfj-category-confirm-modal p{margin-top:10px;color:#718694;font-size:11px;line-height:1.75;text-align:left}
.cfj-category-confirm-danger-text{color:#bd4641;font-weight:850}
.cfj-category-confirm-actions{display:flex;justify-content:flex-end;gap:8px;margin-top:20px}
.cfj-category-confirm-actions button{min-height:36px;border-radius:9px;padding:0 15px;font-size:11px;font-weight:800}
.cfj-category-confirm-danger{border:1px solid #e58b53;background:#fff1e8;color:#bb5f22}
.cfj-category-confirm-danger:hover{border-color:#cf6d2c;background:#ffe5d4;color:#a95415}


/* Candidate route preview: the current form remains unchanged until the user applies a route. */
.cfj-route-head-actions{display:flex;align-items:center;justify-content:flex-end;gap:7px;flex-wrap:wrap}.cfj-route-optimize-button,.cfj-route-import-button{display:inline-flex;align-items:center;justify-content:center;gap:6px;min-height:34px;border-radius:9px;padding:0 11px;font-size:10px;font-weight:800;white-space:nowrap}.cfj-route-optimize-button{border:1px solid #f0b27d;background:#fff6ed;color:#c86d20}.cfj-route-optimize-button:hover:not(:disabled){border-color:#e88b45;background:#ffead8;color:#a95415}.cfj-route-optimize-button:disabled{opacity:.5;cursor:not-allowed}.cfj-route-recalculate-button{display:inline-flex;align-items:center;justify-content:center;min-height:34px;border:1px solid #dce7ed;border-radius:9px;padding:0 10px;background:#f8fbfd;color:#5f7e91;font-size:10px;font-weight:750;white-space:nowrap}.cfj-route-recalculate-button:hover:not(:disabled){border-color:#9fc0d0;background:#eef7fb;color:#315e77}.cfj-route-recalculate-button:disabled{opacity:.5;cursor:not-allowed}.cfj-route-head-actions .cfj-route-import-button{min-height:34px;padding:0 11px}.cfj-route-candidates-overlay{position:fixed;inset:0;z-index:3600;display:grid;place-items:center;padding:20px;background:rgba(21,47,68,.36);backdrop-filter:blur(7px)}.cfj-route-candidates-modal{position:relative;width:min(1280px,100%);height:min(850px,calc(100vh - 40px));display:block;overflow:hidden;border:1px solid rgba(255,255,255,.9);border-radius:18px;background:#fff;box-shadow:0 30px 100px rgba(22,48,70,.3)}.cfj-route-candidates-head{position:absolute;z-index:3;top:0;left:0;width:32%;height:108px;box-sizing:border-box;display:flex;align-items:flex-start;justify-content:space-between;gap:18px;padding:21px 25px 15px;border-bottom:1px solid #eaf0f4;background:#fff}.cfj-route-candidates-head .cfj-binding-close{position:absolute;top:18px;right:18px}.cfj-route-candidates-head small{color:#d7771e;font-size:9px;font-weight:900;letter-spacing:.12em}.cfj-route-candidates-head h2{margin-top:5px;color:#183d5b;font-size:21px;letter-spacing:-.04em}.cfj-route-candidates-head p{margin-top:5px;color:#8799a8;font-size:11px}.cfj-route-candidates-error{margin:12px 25px 0;border:1px solid #f0c8bb;border-radius:9px;padding:10px 12px;background:#fff6f2;color:#a75f50;font-size:11px;line-height:1.5}.cfj-route-candidates-body{position:absolute;z-index:1;inset:0;display:grid;grid-template-columns:32% minmax(0,68%);gap:0;min-height:0;height:100%;padding-bottom:58px;box-sizing:border-box;overflow:hidden}.cfj-route-candidate-list{grid-column:1;grid-row:1;min-width:0;min-height:0;height:100%;overflow-y:auto;overflow-x:hidden;padding:145px 14px 18px 22px;border-right:1px solid #eaf0f4;background:#f8fbfd}.cfj-route-candidate-card{width:100%;display:grid;grid-template-columns:minmax(0,1fr) 22px;align-items:start;gap:10px;margin-bottom:8px;border:1px solid #e2ebf1;border-radius:12px;padding:12px 10px;background:#fff;text-align:left;color:#415f73}.cfj-route-candidate-card:hover{border-color:#efb27f;background:#fffaf6}.cfj-route-candidate-card.is-selected{border-color:#f0a05d;background:#fff8f0;box-shadow:0 0 0 3px rgba(240,160,93,.12)}.cfj-route-candidate-card.is-invalid{background:#fffdfc}.cfj-route-candidate-rank{display:grid;place-items:center;width:29px;height:29px;border-radius:9px;background:#eaf3f8;color:#47718c;font-size:10px;font-weight:900}.cfj-route-candidate-card.is-selected .cfj-route-candidate-rank{background:#f18432;color:#fff}.cfj-route-candidate-copy{display:grid;gap:8px;min-width:0}.cfj-route-candidate-title-row{display:flex;align-items:center;gap:6px;min-width:0}.cfj-route-candidate-title-row strong{overflow:hidden;color:#234962;font-size:12px;text-overflow:ellipsis;white-space:nowrap}.cfj-route-candidate-title-row em{flex:none;border-radius:999px;padding:3px 6px;background:#eaf8ef;color:#27865c;font-size:8px;font-style:normal;font-weight:850}.cfj-route-candidate-copy>small{display:block;overflow-x:auto;overflow-y:hidden;color:#8b9ba7;font-size:9px;line-height:1.45;text-overflow:clip;white-space:nowrap;scrollbar-width:none;-webkit-overflow-scrolling:touch}.cfj-route-candidate-copy>small::-webkit-scrollbar{display:none}.cfj-route-candidate-metrics{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));border-top:1px solid #edf2f5;color:#6e8494;font-size:9px}.cfj-route-candidate-metric{display:grid;gap:3px;min-width:0;padding:2px 8px 1px 0;border-right:1px solid #edf2f5}.cfj-route-candidate-metric:not(:first-child){padding-left:8px}.cfj-route-candidate-metric:last-child{border-right:0;padding-right:0}.cfj-route-candidate-metric small{color:#8b9ba7;font-size:8px;line-height:1.2}.cfj-route-candidate-metric b{overflow:hidden;color:#315a73;font-size:10px;line-height:1.25;text-overflow:ellipsis;white-space:nowrap}.cfj-route-candidate-metric-late.is-late small,.cfj-route-candidate-metric-late.is-late b{color:#ba6258}.cfj-route-candidate-metric-late.is-on-time small,.cfj-route-candidate-metric-late.is-on-time b{color:#31815e}.cfj-route-candidate-valid,.cfj-route-candidate-warning,.cfj-route-candidate-invalid{font-size:9px;line-height:1.35}.cfj-route-candidate-valid{color:#31815e}.cfj-route-candidate-warning{color:#b87626}.cfj-route-candidate-invalid{color:#ba6258}.cfj-route-candidate-radio{display:grid;place-items:center;width:20px;height:20px;border:1px solid #dce7ed;border-radius:50%;color:#fff}.cfj-route-candidate-card.is-selected .cfj-route-candidate-radio{border-color:#f18432;background:#f18432}.cfj-route-candidate-preview{grid-column:2;grid-row:1;display:grid;grid-template-rows:auto auto minmax(0,1fr);min-width:0;min-height:0;height:100%;overflow:hidden;padding:18px 22px 20px;background:#fff}.cfj-route-candidate-preview-head{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;margin-bottom:12px}.cfj-route-candidate-preview-head>div:first-child{display:grid;gap:4px}.cfj-route-candidate-preview-head small{color:#d7771e;font-size:9px;font-weight:850;letter-spacing:.08em}.cfj-route-candidate-preview-head strong{color:#1f4863;font-size:18px;letter-spacing:-.03em}.cfj-route-candidate-preview-stats{display:grid;grid-template-columns:repeat(5,minmax(64px,1fr));align-items:stretch;min-width:min(100%,470px);border:1px solid #e1ebf0;border-radius:11px;background:#f8fbfd;overflow:hidden}.cfj-route-candidate-preview-stats span{display:grid;align-content:center;gap:3px;min-width:0;padding:7px 9px;border-left:1px solid #e1ebf0}.cfj-route-candidate-preview-stats span:first-child{border-left:0}.cfj-route-candidate-preview-stats small{color:#8b9ba7;font-size:8px;line-height:1.2}.cfj-route-candidate-preview-stats b{overflow:hidden;color:#315a73;font-size:10px;line-height:1.25;text-overflow:ellipsis;white-space:nowrap}.cfj-route-candidate-preview-stats .is-late small,.cfj-route-candidate-preview-stats .is-late b{color:#ba6258}.cfj-route-candidate-preview-stats .is-on-time small,.cfj-route-candidate-preview-stats .is-on-time b{color:#31815e}.cfj-route-candidate-map{position:relative;min-width:0;min-height:0;height:100%;flex:none;overflow:hidden;border:1px solid #dce8ef;border-radius:13px;background:#e7edf0}.cfj-route-candidate-map .cfj-map{height:100%;min-height:0}.cfj-route-candidate-map-label{position:absolute;left:12px;bottom:12px;z-index:800;display:flex;align-items:center;gap:5px;border:1px solid #e3ebef;border-radius:999px;padding:7px 10px;background:rgba(255,255,255,.94);color:#587287;font-size:9px;box-shadow:0 4px 13px rgba(25,35,45,.12)}.cfj-route-candidate-sequence{display:grid;gap:8px;margin-bottom:14px}.cfj-route-candidate-sequence-head{display:flex;align-items:baseline;justify-content:space-between;gap:10px}.cfj-route-candidate-sequence-head strong{color:#385a70;font-size:11px}.cfj-route-candidate-sequence-head small{color:#9aaab5;font-size:9px}.cfj-route-candidate-sequence-track{display:flex;align-items:stretch;gap:7px;overflow-x:auto;overflow-y:hidden;padding:1px 0 5px;scrollbar-width:none;-webkit-overflow-scrolling:touch}.cfj-route-candidate-sequence-track::-webkit-scrollbar{display:none}.cfj-route-candidate-sequence-track>i{display:grid;place-items:center;flex:0 0 auto;color:#a7b7c1;font-size:18px;font-style:normal}.cfj-route-station-card{display:grid;gap:5px;min-width:122px;max-width:170px;flex:0 0 auto;border:1px solid #e1ebf0;border-radius:10px;padding:7px 8px;background:#fbfdff}.cfj-route-station-number{display:grid;place-items:center;width:24px;height:24px;border-radius:50%;background:#eaf3f8;color:#426b83;font-size:9px;font-weight:900}.cfj-route-station-card>strong{overflow:hidden;color:#365a70;font-size:10px;line-height:1.35;text-overflow:ellipsis;white-space:nowrap}.cfj-route-station-eta{color:#d7771e;font-size:9px;font-weight:850;white-space:nowrap}.cfj-route-station-cargo{display:flex;flex-wrap:wrap;gap:4px;min-height:18px}.cfj-route-station-cargo em{border-radius:999px;padding:3px 6px;font-size:8px;font-style:normal;font-weight:850;white-space:nowrap}.cfj-route-station-cargo .is-pickup{background:#fff0e4;color:#d66f1d}.cfj-route-station-cargo .is-delivery{background:#e8f2ff;color:#2472cf}.cfj-route-station-cargo .is-empty{background:#f1f4f6;color:#9aa8b0}.cfj-route-candidates-loading{display:grid;place-items:center;align-content:center;gap:9px;flex:1;padding:40px;color:#688095}.cfj-route-candidates-loading strong{color:#315a73;font-size:14px}.cfj-route-candidates-loading>span:last-child{font-size:10px}.cfj-route-candidates-spinner{width:28px;height:28px;border:3px solid #e5edf2;border-top-color:#f18432;border-radius:50%;animation:cfj-route-spin .8s linear infinite}.cfj-route-candidates-empty{display:grid;place-items:center;min-height:160px;color:#8b9aa5;font-size:11px}.cfj-route-candidates-footer{position:absolute;z-index:4;left:0;right:0;bottom:0;display:flex;align-items:center;justify-content:space-between;gap:14px;padding:13px 22px 15px;border-top:1px solid #eaf0f4;background:#fff;color:#718899;font-size:10px}.cfj-route-candidates-footer>div{display:flex;gap:8px}.cfj-route-candidates-footer .cfj-cargo-primary-button:disabled{opacity:.45;cursor:not-allowed}@keyframes cfj-route-spin{to{transform:rotate(360deg)}}
@media(max-width:860px){.cfj-route-candidates-overlay{padding:8px}.cfj-route-candidates-modal{height:calc(100vh - 16px);border-radius:14px;display:flex;flex-direction:column}.cfj-route-candidates-head{position:relative;top:auto;left:auto;width:auto;height:auto;min-height:0}.cfj-route-candidates-body{position:static;inset:auto;display:grid;grid-template-columns:1fr;flex:1;height:auto;padding-bottom:0}.cfj-route-candidate-list,.cfj-route-candidate-preview{grid-column:auto;grid-row:auto}.cfj-route-candidate-list{max-height:270px;border-right:0;border-bottom:1px solid #eaf0f4;padding:12px 14px}.cfj-route-candidate-preview{min-height:430px;padding:14px}.cfj-route-candidate-map,.cfj-route-candidate-map .cfj-map{min-height:310px}.cfj-route-candidates-footer{padding-left:14px;padding-right:14px}.cfj-route-head-actions{width:100%;justify-content:flex-end}}
@media(max-width:560px){.cfj-section-head{align-items:flex-start;flex-wrap:wrap}.cfj-section-head-actions{width:100%;margin-left:0}.cfj-route-head-actions{justify-content:stretch}.cfj-route-head-actions>*{flex:1}.cfj-route-candidate-preview-head{display:grid}.cfj-route-candidate-preview-stats{width:100%;min-width:0;grid-template-columns:repeat(2,minmax(0,1fr))}.cfj-route-candidates-footer{position:static;align-items:stretch;flex-direction:column}.cfj-route-candidates-footer>div{width:100%}.cfj-route-candidates-footer button{flex:1}.cfj-route-candidate-card{grid-template-columns:minmax(0,1fr) 20px}}


/* Cargo detail editor: summary stays in the stop; full category fields live in a drawer. */
.cfj-cargo-action-wrap{display:flex;align-items:center;justify-content:flex-end;gap:5px;min-width:0}
.cfj-cargo-detail-button{height:32px;display:inline-flex;align-items:center;justify-content:center;gap:5px;min-width:0;flex:1;border:2px solid #df6f22;border-radius:8px;padding:0 8px;background:#fffaf5;color:#c9631c;font-size:10px;font-weight:850;white-space:nowrap}
.cfj-cargo-detail-button:hover{border-color:#c85a14;background:#fff0e2;color:#b44e0d;box-shadow:0 0 0 3px rgba(223,111,34,.1)}
.cfj-cargo-detail-button:active,.cfj-cargo-primary-button:active,.cfj-cargo-secondary-button:active{transform:scale(.97)}
.cfj-cargo-detail-button svg{flex:none}
.cfj-cargo-action-button{flex:0 0 29px}
.cfj-cargo-table-head{grid-template-columns:minmax(0,1fr) 60px 54px 84px 84px minmax(155px,1.25fr)}
.cfj-cargo-row{grid-template-columns:minmax(0,1fr) 60px 54px 84px 84px minmax(155px,1.25fr)}
.cfj-cargo-detail-overlay{position:fixed;inset:0;z-index:3500;display:flex;justify-content:flex-end;background:rgba(21,47,68,.34);backdrop-filter:blur(6px)}
.cfj-cargo-detail-drawer{width:min(570px,100%);height:100%;display:flex;flex-direction:column;min-width:0;background:#fff;box-shadow:-18px 0 55px rgba(22,48,70,.2);animation:cfj-cargo-detail-in .22s cubic-bezier(.23,1,.32,1)}
.cfj-cargo-detail-head{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;padding:22px 24px 15px;border-bottom:1px solid #edf2f5}
.cfj-cargo-detail-heading{min-width:0}
.cfj-cargo-detail-title-row{display:flex;align-items:center;gap:9px;color:#183d5b}
.cfj-cargo-detail-title-row h2{margin:0;color:#183d5b;font-size:21px;letter-spacing:-.04em}
.cfj-cargo-detail-heading p{margin:6px 0 0;color:#899baa;font-size:10px;line-height:1.5}
.cfj-cargo-detail-close{width:32px;height:32px;display:grid;place-items:center;flex:none;border:1px solid #e4ebef;border-radius:50%;background:#fff;color:#718496}
.cfj-cargo-detail-close:hover{border-color:#f1b17c;background:#fff7ef;color:#c66d24}

.cfj-cargo-sender-editor{display:grid;gap:11px;margin:0 20px 11px;border:1px solid #f1d9c5;border-radius:11px;padding:12px;background:linear-gradient(110deg,#fffaf5,#fff)}
.cfj-cargo-sender-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}
.cfj-cargo-sender-head>div{display:grid;gap:3px}
.cfj-cargo-sender-head strong{color:#9b5427;font-size:12px}
.cfj-cargo-sender-head small{color:#9a8b80;font-size:9px}
.cfj-cargo-sender-head .cfj-common-picker-button{display:inline-flex;align-items:center;justify-content:center;gap:6px;border:1px solid #ead7c7;border-radius:8px;padding:7px 10px;background:#fff;color:#a85c2e;font-size:10px;font-weight:750;white-space:nowrap}
.cfj-cargo-sender-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}
.cfj-cargo-sender-grid input{width:100%;min-width:0;height:34px;border:1px solid #eadfd7;border-radius:8px;padding:8px 9px;background:#fff;color:#294761;font-size:10px;outline:0}
.cfj-cargo-sender-grid input:focus{border-color:#efaa73;box-shadow:0 0 0 3px rgba(239,170,115,.12)}
.cfj-cargo-sender-grid .is-wide{grid-column:1 / -1}
@media(max-width:700px){.cfj-cargo-sender-editor{margin-left:14px;margin-right:14px}.cfj-cargo-sender-grid{grid-template-columns:1fr}.cfj-cargo-sender-grid .is-wide{grid-column:auto}.cfj-cargo-sender-head{align-items:stretch;flex-direction:column}}
.cfj-cargo-detail-summary{display:grid;grid-template-columns:42px minmax(0,1fr) auto auto auto;align-items:center;gap:9px;margin:14px 20px 10px;padding:10px 12px;border:1px solid #e1edf4;border-radius:11px;background:linear-gradient(110deg,#f5fbff,#eef7fb)}
.cfj-cargo-detail-summary>img{width:38px;height:38px;border-radius:9px;padding:2px;background:#fff0e3;object-fit:contain}
.cfj-cargo-detail-summary>div{display:grid;gap:3px;min-width:0}
.cfj-cargo-detail-summary strong{overflow:hidden;color:#244761;font-size:13px;text-overflow:ellipsis;white-space:nowrap}
.cfj-cargo-detail-summary small{color:#8295a4;font-size:10px}
.cfj-cargo-detail-summary>span{border-radius:999px;padding:5px 8px;background:#eaf4fb;color:#3e6e8b;font-size:9px;font-weight:850;white-space:nowrap}
.cfj-cargo-detail-summary>b{border-radius:999px;padding:5px 8px;background:#fff0e3;color:#d36d1d;font-size:9px;white-space:nowrap}
.cfj-cargo-detail-summary>em{border-radius:999px;padding:5px 8px;background:#f1f4f6;color:#7a8994;font-size:9px;font-style:normal;white-space:nowrap}
.cfj-cargo-detail-scroll{min-height:0;flex:1;overflow:auto;padding:0 20px 18px;background:#fbfdff}
.cfj-cargo-detail-section{margin-top:10px;border:1px solid #e2ebf1;border-radius:11px;background:#fff;overflow:hidden}
.cfj-cargo-detail-section-head{width:100%;display:flex;align-items:center;justify-content:space-between;gap:10px;border:0;padding:11px 13px;background:#f7fbfd;color:#244b65;text-align:left;font-size:12px;font-weight:850}
.cfj-cargo-detail-section-head:hover{background:#fff8f1;color:#bd621a}
.cfj-cargo-detail-section-head>span{display:flex;align-items:center;gap:7px;min-width:0}
.cfj-cargo-detail-section-head>span svg{flex:none;color:#4a7590}
.cfj-cargo-detail-section-head>svg{flex:none;color:#8da0ad}
.cfj-cargo-detail-fields{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:11px;padding:13px}
.cfj-cargo-detail-field{display:grid;align-content:start;gap:5px;min-width:0;color:#496276;font-size:10px;font-weight:750}
.cfj-cargo-detail-field.is-wide{grid-column:1 / -1}
.cfj-cargo-detail-field>span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cfj-cargo-detail-field input,.cfj-cargo-detail-field select,.cfj-cargo-detail-field textarea{width:100%;min-width:0;border:1px solid #dfe9ef;border-radius:8px;padding:9px;background:#fbfdff;color:#294761;font-size:11px;font-weight:400;outline:0}
.cfj-cargo-detail-field input,.cfj-cargo-detail-field select{height:36px}
.cfj-cargo-detail-field textarea{min-height:68px;resize:vertical;line-height:1.55}
.cfj-cargo-detail-field input:focus,.cfj-cargo-detail-field select:focus,.cfj-cargo-detail-field textarea:focus{border-color:#efaa73;background:#fff;box-shadow:0 0 0 3px rgba(239,170,115,.12)}
.cfj-cargo-detail-input-wrap{position:relative;min-width:0}
.cfj-cargo-detail-input-wrap input{padding-right:42px}
.cfj-cargo-detail-input-wrap small{position:absolute;right:10px;top:50%;transform:translateY(-50%);color:#8a9eab;font-size:10px;pointer-events:none}
.cfj-cargo-detail-footer{display:flex;justify-content:flex-end;gap:9px;padding:13px 20px 18px;border-top:1px solid #e7eef3;background:#fff}
.cfj-cargo-detail-footer .cfj-cargo-secondary-button{min-width:96px}
.cfj-cargo-detail-footer .cfj-cargo-primary-button{min-width:190px}
@keyframes cfj-cargo-detail-in{from{opacity:.6;transform:translateX(22px)}to{opacity:1;transform:translateX(0)}}
@media(max-width:960px){.cfj-cargo-detail-summary{grid-template-columns:38px minmax(0,1fr) auto auto}.cfj-cargo-detail-summary>em{grid-column:3 / -1;justify-self:end}.cfj-cargo-detail-summary>span{grid-column:2}.cfj-cargo-detail-summary>b{grid-column:4;grid-row:2}}
@media(max-width:700px){.cfj-cargo-detail-button{flex:0 0 27px;width:27px;padding:0}.cfj-cargo-detail-button span{display:none}.cfj-cargo-action-wrap{gap:4px}.cfj-cargo-table-head{grid-template-columns:minmax(0,1fr) 50px 45px 62px 62px 60px}.cfj-cargo-row{grid-template-columns:minmax(0,1fr) 50px 45px 62px 62px 60px}.cfj-cargo-action-button{flex-basis:25px;width:25px}.cfj-cargo-detail-head{padding:18px 16px 13px}.cfj-cargo-detail-summary{margin:11px 14px 8px}.cfj-cargo-detail-scroll{padding-left:14px;padding-right:14px}.cfj-cargo-detail-footer{padding:11px 14px 15px}.cfj-cargo-detail-footer button{flex:1;min-width:0!important}.cfj-cargo-detail-fields{grid-template-columns:1fr;gap:9px;padding:11px}.cfj-cargo-detail-field.is-wide{grid-column:auto}}

 .cfj-cargo-head-actions{display:flex;align-items:center;gap:7px;flex-wrap:wrap}
 .cfj-cargo-source-button{display:inline-flex;align-items:center;gap:5px;border:1px solid #bad6e4;border-radius:8px;padding:7px 9px;background:#f4fbff;color:#3c7694;font-size:10px;font-weight:800}
 .cfj-cargo-source-button:hover{border-color:#76b3ca;background:#eaf7fd;color:#24627e}
 .cfj-cargo-table-head{grid-template-columns:minmax(0,1fr) 150px 155px 30px;gap:8px}
 .cfj-cargo-table-head span:nth-child(2),.cfj-cargo-table-head span:nth-child(3){text-align:left}
 .cfj-cargo-row{grid-template-columns:minmax(0,1fr) 150px 155px 30px;gap:8px;align-items:start}
 .cfj-cargo-name-cell{display:grid;gap:4px;min-width:0}
 .cfj-cargo-name-cell input{width:100%;min-width:0}
 .cfj-cargo-name-cell small{overflow:hidden;color:#6d91a3;font-size:9px;line-height:1.35;text-overflow:ellipsis;white-space:nowrap}
 .cfj-cargo-quantity-cell{display:grid;grid-template-columns:minmax(0,1fr) 58px;gap:5px;min-width:0}
 .cfj-cargo-quantity-cell input{min-width:0}
 .cfj-cargo-action-wrap{align-items:start;padding-top:0}
 .cfj-cargo-detail-button{flex:1;min-width:0}
 .cfj-cargo-source-overlay{position:fixed;inset:0;z-index:3450;display:grid;place-items:center;padding:22px;background:rgba(21,47,68,.34);backdrop-filter:blur(6px)}
 .cfj-cargo-source-modal{width:min(590px,100%);max-height:min(720px,calc(100vh - 44px));display:flex;flex-direction:column;overflow:hidden;border:1px solid rgba(255,255,255,.9);border-radius:18px;background:#fff;box-shadow:0 30px 90px rgba(22,48,70,.28)}
 .cfj-cargo-source-head{display:flex;align-items:flex-start;justify-content:space-between;gap:18px;padding:23px 25px 17px;border-bottom:1px solid #edf2f5}
 .cfj-cargo-source-head small{color:#3b7896;font-size:9px;font-weight:900;letter-spacing:.12em}
 .cfj-cargo-source-head h2{margin:5px 0 0;color:#183d5b;font-size:21px;letter-spacing:-.04em}
 .cfj-cargo-source-head p{margin:7px 0 0;color:#899baa;font-size:10px;line-height:1.6}
 .cfj-cargo-source-list{display:grid;gap:8px;overflow:auto;padding:17px 20px;background:#f8fbfd}
 .cfj-cargo-source-option{display:grid;grid-template-columns:42px minmax(0,1fr) 18px;align-items:center;gap:11px;width:100%;border:1px solid #deebf1;border-radius:12px;padding:11px 12px;background:#fff;color:#345970;text-align:left}
 .cfj-cargo-source-option:hover{border-color:#efaa73;background:#fffaf5;box-shadow:0 0 0 3px rgba(239,170,115,.1)}
 .cfj-cargo-source-option-icon{display:grid;place-items:center;width:42px;height:42px;border-radius:10px;background:#eaf6fb;color:#397b99}
 .cfj-cargo-source-option-copy{display:grid;gap:3px;min-width:0}
 .cfj-cargo-source-option-copy strong{overflow:hidden;color:#244761;font-size:12px;text-overflow:ellipsis;white-space:nowrap}
 .cfj-cargo-source-option-copy small{overflow:hidden;color:#8095a3;font-size:9px;text-overflow:ellipsis;white-space:nowrap}
 .cfj-cargo-source-option-copy em{color:#d16f22;font-size:9px;font-style:normal;font-weight:800}
 .cfj-cargo-source-option>svg{color:#9aabb5}
 .cfj-cargo-source-empty{display:grid;justify-items:center;gap:7px;padding:42px 20px;color:#88a0ae;text-align:center}
 .cfj-cargo-source-empty svg{color:#5b91a7}
 .cfj-cargo-source-empty strong{color:#42667a;font-size:13px}
 .cfj-cargo-source-empty span{font-size:10px}
 .cfj-cargo-source-footer{padding:13px 20px 16px;border-top:1px solid #e9eff3;background:#fff;color:#8799a6;font-size:10px;text-align:center}
 .cfj-cargo-source-summary{display:flex;align-items:center;gap:8px;margin:0 20px 7px;border:1px solid #dcecf3;border-radius:10px;padding:9px 11px;background:#f4fbff;color:#42718a}
 .cfj-cargo-source-summary>div{display:grid;gap:2px;min-width:0;flex:1}
 .cfj-cargo-source-summary span{font-size:9px}
 .cfj-cargo-source-summary strong{overflow:hidden;color:#2c5870;font-size:11px;text-overflow:ellipsis;white-space:nowrap}
 .cfj-cargo-source-summary>b{border-radius:999px;padding:5px 7px;background:#e5f4fb;color:#36738e;font-size:9px;white-space:nowrap}
 @media(max-width:700px){.cfj-cargo-head-actions{width:100%;justify-content:flex-end}.cfj-cargo-table-head{grid-template-columns:minmax(0,1fr) 112px 27px 25px;gap:5px;padding-left:6px;padding-right:6px}.cfj-cargo-row{grid-template-columns:minmax(0,1fr) 112px 27px 25px;gap:5px;padding-left:6px;padding-right:6px}.cfj-cargo-quantity-cell{grid-template-columns:minmax(0,1fr) 45px;gap:3px}.cfj-cargo-detail-button{flex:0 0 27px;width:27px;padding:0}.cfj-cargo-detail-button span{display:none}.cfj-cargo-source-overlay{padding:8px}.cfj-cargo-source-modal{max-height:calc(100vh - 16px);border-radius:14px}.cfj-cargo-source-head{padding:19px 17px 14px}.cfj-cargo-source-list{padding:13px 12px}.cfj-cargo-source-footer{padding:11px 13px 14px}.cfj-cargo-source-summary{margin-left:14px;margin-right:14px}}
.cfj-delivery-basic-summary{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin:0 8px 8px;padding:7px 9px;border:1px solid #e8eef2;border-radius:8px;background:#fbfdff;color:#718797;font-size:9px;line-height:1.4}
.cfj-delivery-basic-summary span{border-right:1px solid #dce6eb;padding-right:7px;white-space:nowrap}
.cfj-delivery-basic-summary span:last-child{border-right:0;padding-right:0;color:#d27327;font-weight:800}
.cfj-delivery-detail-overlay{z-index:3460}
.cfj-delivery-detail-overlay .cfj-cargo-detail-summary{background:linear-gradient(110deg,#fff9f2,#fff3e6);border-color:#f4dbc3}
.cfj-delivery-detail-summary-icon{display:grid;place-items:center;width:38px;height:38px;border-radius:9px;background:#fff0df;color:#d47728}
.cfj-delivery-detail-overlay .cfj-cargo-detail-summary>span{background:#fff0e3;color:#c66d24}
.cfj-delivery-detail-overlay .cfj-cargo-detail-summary>b{background:#eaf5fb;color:#39738e}
.cfj-cargo-table-head{grid-template-columns:minmax(0,1fr) 150px 92px minmax(155px,1.25fr);gap:8px}
.cfj-cargo-row{grid-template-columns:minmax(0,1fr) 150px 92px minmax(155px,1.25fr);gap:8px}
.cfj-cargo-row>.cfj-cargo-operation-select{min-width:0;width:100%;height:32px;border:1px solid #e3e8ec;border-radius:7px;background:#fff;color:#345875;font-size:10px;font-weight:800;padding:0 20px 0 8px}
@media(max-width:700px){.cfj-delivery-basic-summary{margin-left:6px;margin-right:6px;gap:5px}.cfj-delivery-basic-summary span{max-width:100%;overflow:hidden;text-overflow:ellipsis}.cfj-delivery-detail-overlay .cfj-cargo-detail-summary{grid-template-columns:38px minmax(0,1fr) auto}.cfj-delivery-detail-overlay .cfj-cargo-detail-summary>span{grid-column:2}.cfj-delivery-detail-overlay .cfj-cargo-detail-summary>b{grid-column:3;grid-row:2}.cfj-delivery-detail-overlay .cfj-cargo-detail-summary>em{grid-column:2 / -1;justify-self:start}}
@media(max-width:700px){.cfj-cargo-table-head{grid-template-columns:minmax(0,1fr) 112px 72px minmax(84px,1fr);gap:5px;padding:0 6px}.cfj-cargo-row{grid-template-columns:minmax(0,1fr) 112px 72px minmax(84px,1fr);gap:5px;padding:7px 6px}.cfj-cargo-row>.cfj-cargo-operation-select{height:27px;padding:0 14px 0 6px;font-size:9px}.cfj-cargo-detail-button span{display:none}.cfj-cargo-detail-button{flex:0 0 27px;width:27px;padding:0}}
.cfj-document-proof-editor{display:grid;gap:11px;padding:13px;background:#fbfdff}
.cfj-document-proof-intro{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;padding:10px 11px;border:1px solid #e7eef3;border-radius:10px;background:#fff}
.cfj-document-proof-intro>div{display:grid;gap:3px;min-width:0}
.cfj-document-proof-intro strong{color:#315b73;font-size:11px}
.cfj-document-proof-intro span{color:#8b9ca7;font-size:9px;line-height:1.5}
.cfj-document-proof-intro button{display:inline-flex;align-items:center;gap:4px;flex:0 0 auto;border:1px solid #efb477;border-radius:7px;padding:6px 8px;background:#fff8f1;color:#c66d24;font-size:10px;font-weight:800}
.cfj-document-proof-list{display:grid;gap:8px}
.cfj-document-proof-card{display:grid;gap:8px;padding:10px;border:1px solid #e4edf2;border-radius:10px;background:#fff}
.cfj-document-proof-card-head{display:flex;align-items:center;justify-content:space-between;color:#5c7381;font-size:10px;font-weight:800}
.cfj-document-proof-card-head button{display:grid;place-items:center;width:25px;height:25px;border:0;border-radius:7px;background:#fff0ef;color:#c85b53}
.cfj-document-proof-card textarea{width:100%;min-height:54px;resize:vertical;border:1px solid #dfe9ef;border-radius:8px;padding:8px;background:#fbfdff;color:#294761;font-size:10px;line-height:1.5;outline:0}
.cfj-document-proof-card textarea:focus{border-color:#efaa73;background:#fff;box-shadow:0 0 0 3px rgba(239,170,115,.12)}
.cfj-document-proof-file-row{display:flex;align-items:center;gap:8px;min-width:0}
.cfj-document-proof-upload{display:inline-flex;align-items:center;justify-content:center;gap:5px;min-height:30px;flex:0 0 auto;border:1px solid #cbdfe9;border-radius:7px;padding:5px 8px;background:#f5fbfe;color:#39738e;font-size:9px;font-weight:800;cursor:pointer}
.cfj-document-proof-upload input{display:none}
.cfj-document-proof-file-meta{display:flex;align-items:center;gap:6px;min-width:0;color:#738894;font-size:9px}
.cfj-document-proof-file-meta>svg{flex:0 0 auto;color:#6b9ab0}
.cfj-document-proof-file-meta>img{width:32px;height:32px;flex:0 0 32px;object-fit:cover;border-radius:6px;border:1px solid #dfe9ef;background:#f5f8fa}
.cfj-document-proof-file-meta span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cfj-document-proof-empty{display:grid;justify-items:center;gap:5px;border:1px dashed #cbdfe9;border-radius:10px;padding:20px 12px;background:#fff;color:#7392a2;text-align:center}
.cfj-document-proof-empty:hover{border-color:#efb477;background:#fffaf5;color:#c66d24}
.cfj-document-proof-empty strong{font-size:11px}
.cfj-document-proof-empty span{font-size:9px}
@media(max-width:700px){.cfj-document-proof-editor{padding:10px}.cfj-document-proof-intro{display:grid;gap:8px}.cfj-document-proof-intro button{justify-self:start}.cfj-document-proof-file-row{align-items:flex-start;flex-direction:column}.cfj-document-proof-upload{width:100%}.cfj-document-proof-file-meta{width:100%}}
/* Route editor visual refresh: orange mission timeline, no decorative house/truck artwork. */
.cfj-stops{position:relative;display:grid;gap:10px;padding:5px 0 5px 43px}
.cfj-stops::before{content:"";position:absolute;top:22px;bottom:24px;left:17px;width:2px;background:linear-gradient(#42b88c 0 20%,#dce7eb 20% 100%)}
.cfj-stop{position:relative;min-width:0;border:1px solid #e3ebef;border-radius:13px;background:#fff;box-shadow:0 4px 16px rgba(25,55,72,.035);transition:border-color .18s ease,box-shadow .18s ease,background .18s ease}
.cfj-stop:hover{border-color:#efb27f;box-shadow:0 8px 24px rgba(25,55,72,.07)}
.cfj-stop.is-active{border-color:#f0a05d;background:#fffaf5;box-shadow:0 0 0 3px rgba(240,160,93,.13),0 10px 28px rgba(25,55,72,.07)}
.cfj-stop-number{position:absolute;z-index:2;top:18px;left:-43px;display:grid;place-items:center;width:34px;height:34px;border:1px solid #dce7eb;border-radius:50%;background:#f4f8fa;color:#617988;font-size:11px;font-weight:900}
.cfj-stop:first-child .cfj-stop-number,.cfj-stop-number.is-waypoint{border-color:#40b88d;background:#36ae83;color:#fff}
.cfj-stop.is-active .cfj-stop-number{border-color:#ef8737;background:#f18432;color:#fff;box-shadow:0 0 0 5px rgba(241,132,50,.12)}
.cfj-stop-body{min-width:0;padding:16px 17px 14px}
.cfj-stop-body>label{display:flex;align-items:center;gap:6px;min-width:0;color:#254a62;font-size:13px;font-weight:900}
.cfj-stop-body>label::before{content:"";width:7px;height:7px;flex:0 0 7px;border-radius:50%;background:#aabac2}
.cfj-stop.is-active .cfj-stop-body>label::before{background:#f18432}
.cfj-stop-body .cfj-selecting{border-radius:999px;padding:4px 7px;background:#fff0df;color:#d7771e;font-size:9px;font-weight:850}
.cfj-address-row{display:grid;grid-template-columns:minmax(0,1fr) 34px;gap:6px;margin-top:10px}
.cfj-address-row input{height:36px;border:1px solid #dfe9ee;border-radius:8px;padding:0 11px;background:#fff;color:#294761;font-size:11px;outline:0}
.cfj-stop.is-active .cfj-address-row input{border-color:#efb27f;background:#fff}
.cfj-address-row input:focus{border-color:#ef9b59;box-shadow:0 0 0 3px rgba(239,155,89,.12)}
.cfj-address-row button{display:grid;place-items:center;border:1px solid #dfe9ee;border-radius:8px;background:#fff;color:#59788a}
.cfj-address-row button:hover{border-color:#efaa73;background:#fff5eb;color:#c66d24}
.cfj-common-actions{display:flex;align-items:center;gap:8px;margin-top:7px}
.cfj-common-actions button{border:0;border-radius:6px;padding:4px 6px;background:#fff0df;color:#c66d24;font-size:9px;font-weight:850}
.cfj-common-actions span{color:#7ca18e;font-size:9px}
.cfj-stop-details{margin-top:13px;border:1px solid #e8eef1;border-radius:10px;background:#fff;overflow:visible}
.cfj-stop-detail-head{padding:11px 12px 8px;border-bottom:1px solid #edf2f4}
.cfj-recipient-title b{color:#315a73;font-size:12px}
.cfj-recipient-card{margin:9px;border:1px solid #e8eef1;border-radius:9px;background:#fcfefe}
.cfj-recipient-card:last-child{margin-bottom:9px}
.cfj-recipient-head{padding:9px 10px;border-bottom:1px solid #edf2f4}
.cfj-recipient-head>div:first-child{gap:2px}
.cfj-recipient-head>div:first-child span{color:#91a0a8;font-size:9px}
.cfj-recipient-head>div:first-child b{color:#31556c;font-size:11px}
.cfj-contact-grid{gap:6px;padding:9px 10px}
.cfj-contact-grid input{height:32px;border-radius:7px;font-size:10px}
.cfj-cargo-head{padding:0 10px 7px}
.cfj-cargo-table-head{margin:0 10px;padding:6px 7px;background:#f7fafb;color:#9aa7ad}
.cfj-cargo-row{margin:0 10px;padding:7px 0;border-bottom:1px solid #edf2f4}
.cfj-cargo-row input,.cfj-cargo-row select{height:32px;border-radius:7px;font-size:10px}
.cfj-delivery-basic-summary{margin:7px 10px 8px}
.cfj-cargo-photo-head,.cfj-cargo-photos{margin-left:10px;margin-right:10px}
.cfj-stop-footer{padding-top:10px;border-top:1px solid #edf2f4}
.cfj-stop-extra{margin:8px 9px 0;border-top:1px solid #e8f1f5;color:#4e7895}
.cfj-stop-extra summary{display:flex;align-items:center;justify-content:space-between;gap:8px;cursor:pointer;padding:9px 2px;color:#4c7895;font-size:9px;font-weight:850;list-style:none}
.cfj-stop-extra summary::-webkit-details-marker{display:none}
.cfj-stop-extra summary::after{content:"＋";color:#83a6bc;font-size:14px;font-weight:500}
.cfj-stop-extra[open] summary::after{content:"－"}
.cfj-stop-extra summary small{overflow:hidden;color:#9aabb5;font-size:8px;font-weight:500;text-overflow:ellipsis;white-space:nowrap}
.cfj-stop-extra-body{padding:0 0 8px}
.cfj-stop-extra-body textarea{width:100%;min-height:58px;resize:vertical;border:1px solid #dfeaf1;border-radius:7px;padding:8px;background:#fbfdff;color:#34566e;font-size:10px}
.cfj-route-summary{display:flex;justify-content:flex-end;gap:8px;margin:11px 0 0;padding:0;border:0;background:transparent}
.cfj-route-summary>span{display:grid;gap:2px;min-width:96px;border:1px solid #e0eaee;border-radius:9px;padding:8px 10px;background:#f8fbfc;color:#315a73;font-size:10px;font-weight:850}
.cfj-route-summary>span small{color:#8b9ba5;font-size:8px;font-weight:750}
.cfj-route-actions{margin-top:10px}
.cfj-route-actions button{min-height:46px;padding:0 18px;border:1px dashed #cddfe6;border-radius:10px;background:#fff;color:#4b788d;font-size:12px;font-weight:800}
.cfj-route-actions button:hover{border-color:#efaa73;background:#fff8f1;color:#c66d24}
@media(max-width:700px){.cfj-stops{padding-left:35px}.cfj-stops::before{left:13px}.cfj-stop-number{left:-35px;width:29px;height:29px;font-size:10px}.cfj-stop-body{padding:13px 12px}.cfj-stop-body>label{font-size:12px}.cfj-route-summary{justify-content:stretch}.cfj-route-summary>span{flex:1;min-width:0}}
.cfj-cargo-row>.cfj-cargo-action-wrap{display:flex;align-items:center;gap:5px;justify-self:stretch}
.cfj-cargo-row>.cfj-cargo-action-wrap .cfj-cargo-detail-button{flex:1;min-width:0;height:32px}
.cfj-cargo-row>.cfj-cargo-operation-select{border:2px solid #183d5b;border-radius:8px;background:#fff;color:#183d5b;font-weight:850;box-shadow:none}
.cfj-cargo-row>.cfj-cargo-operation-select:focus{border-color:#ef8737;box-shadow:0 0 0 3px rgba(239,135,55,.12)}
.cfj-cargo-placement-inline{display:flex;align-items:center;justify-content:space-between;gap:12px;margin:0 20px 10px;padding:11px 13px;border:1px solid #deebf1;border-radius:11px;background:linear-gradient(105deg,#f5fbfe,#fffaf4)}
.cfj-cargo-placement-inline>div{display:grid;gap:4px;min-width:0}
.cfj-cargo-placement-inline span{color:#78909d;font-size:9px;font-weight:750}
.cfj-cargo-placement-inline strong{overflow:hidden;color:#315a73;font-size:11px;text-overflow:ellipsis;white-space:nowrap}
.cfj-cargo-placement-inline button{display:inline-flex;align-items:center;gap:5px;flex:0 0 auto;border:1px solid #efaa73;border-radius:8px;padding:8px 10px;background:#fff8f1;color:#c66d24;font-size:10px;font-weight:850}
.cfj-cargo-placement-inline button:hover{border-color:#e78435;background:#fff0e1;color:#ae5112}
@media(max-width:700px){.cfj-cargo-placement-inline{align-items:flex-start;flex-direction:column;margin-left:14px;margin-right:14px}.cfj-cargo-placement-inline button{width:100%;justify-content:center}.cfj-cargo-row>.cfj-cargo-action-wrap .cfj-cargo-detail-button{font-size:9px}}
.cfj-cargo-placement-overlay{z-index:3600}
/* Mockup UI refresh: route station > recipient > cargo hierarchy. */
.cfj-route-head-actions{align-items:center;gap:8px}
.cfj-compact-action:active,.cfj-stop-tools button:active,.cfj-submit:active{transform:scale(.97)}
.cfj-stop-body{padding:18px 19px 16px}
.cfj-stop-topline{display:flex;align-items:center;justify-content:space-between;gap:12px;min-width:0;margin-bottom:14px}
.cfj-stop-title{display:flex;align-items:center;gap:8px;min-width:0;flex-wrap:wrap}
.cfj-stop-title>strong{overflow:hidden;color:#183d5b;font-size:16px;font-weight:900;letter-spacing:-.02em;text-overflow:ellipsis;white-space:nowrap}
.cfj-stop-tools{display:flex;align-items:center;gap:5px;flex:0 0 auto}
.cfj-stop-move-wrap{position:relative;flex:0 0 auto}
.cfj-stop-move-button{display:inline-flex;align-items:center;gap:3px;border:1px solid #cbdde8;border-radius:7px;padding:5px 7px;background:#f7fbfe;color:#397697;font-size:10px;font-weight:800;white-space:nowrap}
.cfj-stop-move-button:hover{border-color:#8bb7ce;background:#eef8fc;color:#24627f}
.cfj-stop-move-menu{position:absolute;top:calc(100% + 6px);right:0;z-index:120;display:grid;gap:8px;min-width:170px;padding:10px;border:1px solid #dce8ef;border-radius:10px;background:#fff;box-shadow:0 12px 28px rgba(24,61,91,.16)}
.cfj-stop-move-menu>span{color:#6e8491;font-size:9px;font-weight:800;white-space:nowrap}
.cfj-stop-move-options{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:5px}
.cfj-stop-move-options button{height:28px;border:1px solid #d8e6ee;border-radius:7px;background:#f8fbfd;color:#39708f;font-size:10px;font-weight:850}
.cfj-stop-move-options button:hover{border-color:#efaa73;background:#fff6ed;color:#c66d24}
.cfj-stop-delete{display:inline-flex;align-items:center;gap:4px;border:0;background:transparent;color:#df3c3c;font-size:10px;font-weight:800;white-space:nowrap}
.cfj-stop-delete:hover{color:#b92020}
.cfj-stop-toggle{display:grid;place-items:center;width:28px;height:28px;border:0;border-radius:7px;background:transparent;color:#315a73}
.cfj-stop-toggle:hover{background:#edf6fb;color:#1f78b9}
.cfj-stop-toggle svg,.cfj-cargo-chevron svg,.cfj-recipient-summary>svg,.cfj-child-summary>svg{transform:rotate(90deg)}
.cfj-stop-collapsed-summary{display:none;min-width:0}
.cfj-stop.is-collapsed .cfj-stop-body> *:not(.cfj-stop-topline):not(.cfj-stop-collapsed-summary){display:none!important}
.cfj-stop.is-collapsed .cfj-stop-topline{margin-bottom:8px}
.cfj-stop.is-collapsed .cfj-stop-collapsed-summary{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:10px;padding-top:8px;border-top:1px solid #edf1f3}
.cfj-stop-collapsed-address{overflow:hidden;color:#587180;font-size:11px;font-weight:650;text-overflow:ellipsis;white-space:nowrap}
.cfj-stop-collapsed-counts{display:flex;align-items:center;gap:6px;flex:0 0 auto;color:#82929d;font-size:10px;font-weight:650;white-space:nowrap}
.cfj-stop-collapsed-counts i{color:#c0cbd1;font-style:normal}
.cfj-stop-address-label{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:6px;color:#315a73;font-size:11px;font-weight:850}
.cfj-stop-address-label b{color:#e97928}
.cfj-stop-address-label small{color:#97a7b0;font-size:9px;font-weight:600}
.cfj-stop-address-label .cfj-stop-eta{flex:0 0 auto;padding:3px 8px;border:1px solid #f3d4b8;border-radius:999px;background:#fff7ef;color:#c66b20;font-size:10px;font-weight:850;white-space:nowrap}
.cfj-address-row{display:grid;grid-template-columns:30px minmax(0,1fr) 32px;align-items:center;gap:0;min-height:40px;border:1px solid #dce6ec;border-radius:9px;background:#fff;overflow:hidden}
.cfj-stop.is-active .cfj-address-row{border-color:#efaa73;background:#fffaf5;box-shadow:0 0 0 3px rgba(239,170,115,.1)}
.cfj-address-leading{display:grid;place-items:center;color:#4d88aa}
.cfj-address-row input{width:100%;height:38px;min-width:0;border:0!important;border-radius:0!important;outline:0!important;box-shadow:none!important;padding:0 5px 0 0;background:transparent!important;color:#294761;font-size:11px}
.cfj-address-row button.cfj-address-clear{display:grid;place-items:center;width:100%;height:38px;border:0;padding:0;background:transparent;color:#91a1aa}
.cfj-address-row button.cfj-address-clear:hover{color:#c65b50;background:#fff1ef}
.cfj-common-actions{justify-content:flex-end;margin-top:7px}
.cfj-common-actions button{border:1px solid #cbdde8;border-radius:8px;padding:6px 10px;background:#fff;color:#3b708d;font-size:10px;font-weight:800}
.cfj-common-actions button:hover{border-color:#eeaa73;background:#fff8f1;color:#c66d24}
.cfj-stop-details{margin-top:16px;border:1px solid #dcebf4;border-radius:12px;background:#fff;overflow:visible}
.cfj-hierarchy-head{padding:14px 15px;background:linear-gradient(105deg,#f8fcff,#fffaf6)}
.cfj-recipient-title b{color:#183d5b;font-size:16px;font-weight:900}
.cfj-recipient-title b em{color:#e97e2e;font-style:normal}
.cfj-recipient-title small{margin-top:3px;color:#93a2ad;font-size:9px}
.cfj-hierarchy-head-actions{gap:8px}
.cfj-compact-action{min-height:34px;padding:0 12px;border-color:#cbdde8;background:#fff;color:#2c7199;font-size:10px}
.cfj-compact-action.is-primary{border-color:#ed7e2d;background:#f57f20;color:#fff;box-shadow:0 5px 12px rgba(239,112,37,.18)}
.cfj-compact-action.is-primary:hover{border-color:#d96517;background:#e96f16;color:#fff}
.cfj-recipient-card{margin:10px;border-color:#dbe9f1;border-radius:11px;background:#fff}
.cfj-recipient-summary{min-height:82px;align-items:flex-start;padding:13px 14px;background:linear-gradient(105deg,#fbfdff,#f4faff)}
.cfj-recipient-summary-copy{display:grid;gap:8px;min-width:0;flex:1}
.cfj-recipient-summary .cfj-tree-label strong{font-size:14px;color:#183d5b}
.cfj-recipient-summary .cfj-tree-label>small{font-size:10px}
.cfj-tree-meta{align-self:flex-start;margin-left:8px}
.cfj-tree-meta em{border:1px solid #d5e5ee;background:#fff;color:#39728f}
.cfj-tree-meta em:last-child{background:#fff0e4;color:#ca6b22;border-color:#f2d3b6}
.cfj-progressive-body{margin:0 10px 10px;padding:10px;border-left:2px solid #4a94df;background:linear-gradient(90deg,#f8fcff 0,#fff 38%)}
.cfj-progressive-section{border-color:#e3edf3}
.cfj-cargo-section{background:#fafdff}
.cfj-cargo-list-title strong{font-size:13px;color:#183d5b}
.cfj-cargo-list-title small{font-size:9px}
.cfj-cargo-summary{min-height:56px;gap:8px;padding:8px 10px;background:#fff}
.cfj-cargo-index{display:grid;place-items:center;width:25px;height:25px;flex:0 0 25px;border:1px solid #f1c99e;border-radius:50%;background:#fff8ef;color:#d67625;font-size:11px;font-weight:900}
.cfj-cargo-summary-trigger .cfj-tree-label strong{font-size:11px;color:#183d5b}
.cfj-cargo-summary-trigger .cfj-tree-label>small{font-size:9px;color:#8d9ca6}
.cfj-compact-operation{height:32px!important;border-color:#bdd8ea!important;background:#fff!important;color:#2a709a!important}
.cfj-cargo-inline-detail{background:#fbfdff}
.cfj-cargo-inline-grid{grid-template-columns:minmax(0,1.25fr) 88px 74px minmax(0,1fr);gap:8px}
.cfj-cargo-inline-grid input{height:36px;background:#fff;border-color:#d9e7ef;font-size:10px}
.cfj-cargo-inline-actions{justify-content:space-between}
.cfj-cargo-detail-button{min-height:34px;background:#fff;color:#d46d22}
.cfj-cargo-photos-panel{margin:0 9px 10px;padding:11px 12px;border:1px solid #e0ebf1;border-radius:9px;background:#fff}
.cfj-cargo-photos-heading{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:8px;color:#315a73;font-size:10px;font-weight:850}
.cfj-cargo-photos-heading small{color:#9aa9b2;font-size:8px;font-weight:500}
.cfj-cargo-photos-grid{display:flex;flex-wrap:wrap;gap:7px}
.cfj-cargo-photo-thumb{position:relative;width:58px;height:52px;overflow:hidden;border:1px solid #dfe9ef;border-radius:8px;background:#f4f8fa}
.cfj-cargo-photo-thumb img{width:100%;height:100%;object-fit:cover}
.cfj-cargo-photo-thumb button{position:absolute;top:3px;right:3px;display:grid;place-items:center;width:17px;height:17px;border:0;border-radius:50%;background:rgba(24,61,91,.8);color:#fff}
.cfj-cargo-photo-add-large{width:82px;height:52px;cursor:pointer;font-size:9px}
.cfj-cargo-photo-add-large span{font-size:8px}
.cfj-cargo-bottom-actions{padding:0 9px 11px}
.cfj-outline-action{min-height:34px;border-color:#bcd7ec;border-style:solid;background:#fff;color:#2f7fba}
.cfj-outline-action.is-soft{border-color:#c6dce9;background:#f7fbfe;color:#3d7898}
.cfj-stop-note-block{margin:14px 0 2px;padding:0 2px}
.cfj-stop-note-heading{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:6px;color:#315a73;font-size:11px;font-weight:850}
.cfj-stop-note-heading label small{color:#8ea0aa;font-size:9px;font-weight:500}
.cfj-stop-note-heading>span{color:#9aa9b2;font-size:9px;font-weight:600}
.cfj-stop-note-block textarea{width:100%;min-height:74px;resize:vertical;border:1px solid #dce7ed;border-radius:9px;padding:10px 11px;background:#fff;color:#294761;font-size:10px;line-height:1.5}
.cfj-stop-note-block textarea:focus{border-color:#efaa73;outline:0;box-shadow:0 0 0 3px rgba(239,170,115,.1)}
@media(max-width:760px){
  .cfj-route-head-actions{width:100%;justify-content:flex-end;flex-wrap:wrap}
  .cfj-stop-body{padding:14px 12px}
  .cfj-stop-title>strong{font-size:14px}
  .cfj-stop-delete{font-size:9px}
  .cfj-hierarchy-head{align-items:flex-start;flex-direction:column}
  .cfj-hierarchy-head-actions{width:100%;justify-content:flex-start}
  .cfj-recipient-summary{min-height:76px;padding:11px 9px}
  .cfj-tree-meta{margin-left:3px}
  .cfj-tree-meta em{padding:4px 5px;font-size:8px}
  .cfj-cargo-inline-grid{grid-template-columns:1fr 76px 64px}
  .cfj-cargo-inline-grid .is-wide{grid-column:1/-1}
}
@media(max-width:520px){
  .cfj-route-head-actions{justify-content:flex-start}
  .cfj-stop-topline{align-items:flex-start}
  .cfj-stop-tools{gap:2px}
  .cfj-stop-delete{max-width:90px;overflow:hidden;text-overflow:ellipsis}
  .cfj-address-row{grid-template-columns:27px minmax(0,1fr) 30px}
  .cfj-recipient-summary{gap:5px}
  .cfj-recipient-summary-copy{gap:6px}
  .cfj-tree-meta{display:grid;justify-items:end}
  .cfj-cargo-summary{gap:5px;padding-left:6px;padding-right:6px}
  .cfj-cargo-index{width:22px;height:22px;flex-basis:22px;font-size:9px}
  .cfj-cargo-summary-trigger{gap:5px}
  .cfj-compact-operation{width:68px!important;padding-left:5px!important;padding-right:12px!important;font-size:9px!important}
  .cfj-cargo-photo-add-large{width:74px}
  .cfj-cargo-bottom-actions{flex-direction:column}
  .cfj-cargo-bottom-actions>*{width:100%}
}
/* Authoritative hierarchy UI: station > recipient > sender/cargo. */
.cfj-hierarchy-panel{border:1px solid #dce8ef;background:#fff}
.cfj-hierarchy-head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 16px;border-bottom:1px solid #e6eef3;background:#fff}
.cfj-hierarchy-head-actions,.cfj-inline-actions{display:flex;align-items:center;justify-content:flex-end;gap:8px;flex-wrap:wrap}
.cfj-compact-action,.cfj-outline-action{display:inline-flex;align-items:center;justify-content:center;gap:6px;height:38px;min-height:38px;padding:0 14px;border:1px solid #b9d6ed;border-radius:8px;background:#fff;color:#247bc5;font-size:11px;font-weight:800;white-space:nowrap;cursor:pointer}
.cfj-compact-action.is-primary{border-color:#ef7521;background:#f57c20;color:#fff;box-shadow:none}
.cfj-compact-action:hover,.cfj-outline-action:hover{border-color:#78afd9;background:#f5faff}
.cfj-compact-action.is-primary:hover{border-color:#d96517;background:#ed711b;color:#fff}
.cfj-hierarchy-card{overflow:hidden;transition:border-color .16s ease,box-shadow .16s ease,background .16s ease}
.cfj-hierarchy-card.is-open{border-color:#9fc8e8;box-shadow:0 3px 14px rgba(40,115,177,.07);background:#fff}
.cfj-tree-row{width:100%;display:flex;align-items:center;gap:9px;border:0;text-align:left;background:transparent;color:#234a64;cursor:pointer}
.cfj-recipient-card{margin:10px;border:1px solid #dce7ef;border-radius:12px;background:#fff;box-shadow:none}
.cfj-recipient-card.cfj-hierarchy-card.is-open{border-color:#c6dceb;box-shadow:0 2px 12px rgba(46,115,169,.06)}
.cfj-recipient-summary{min-height:78px;align-items:flex-start;padding:13px 15px;background:#fff}
.cfj-recipient-summary:hover{background:#fbfdff}
.cfj-tree-node{width:30px;height:30px;display:grid;place-items:center;flex:0 0 30px;border-radius:50%;background:#eaf5ff;color:#247fd0}
.cfj-tree-node.is-recipient{background:#fff2e5;color:#ed7a23;box-shadow:0 0 0 5px #fff9f3}
.cfj-tree-node.is-sender{width:27px;height:27px;flex-basis:27px;background:#eef7ff;color:#398bd1}
.cfj-tree-node.is-cargo{width:29px;height:29px;flex-basis:29px;background:#e9f4ff;color:#2b83d5}
.cfj-tree-node.is-cargo-small{width:27px;height:27px;flex-basis:27px;background:#eef6ff;color:#2d83d1}
.cfj-tree-label{display:grid;gap:3px;min-width:0;flex:1}
.cfj-tree-label strong{overflow:hidden;color:#183d5b;font-size:12px;font-weight:850;text-overflow:ellipsis;white-space:nowrap}
.cfj-tree-label strong small{margin-left:4px;color:#8da0ad;font-size:9px;font-weight:600}
.cfj-tree-label>small{overflow:hidden;color:#8d9da8;font-size:9px;text-overflow:ellipsis;white-space:nowrap}
.cfj-recipient-summary .cfj-tree-label strong{font-size:15px}
.cfj-recipient-summary .cfj-tree-label>small{font-size:10px}
.cfj-recipient-summary-copy{display:grid;gap:8px;min-width:0;flex:1}
.cfj-tree-meta{display:flex;align-items:center;gap:6px;align-self:flex-start;margin-left:auto;flex:0 0 auto;color:#7893a4;font-size:9px;font-weight:800}
.cfj-tree-meta em{border:1px solid #cfe2ef;border-radius:999px;padding:7px 10px;background:#f5faff;color:#3179b2;font-style:normal;white-space:nowrap}
.cfj-tree-meta em:last-child{border-color:#f2cfb0;background:#fff4ea;color:#d56d1d}
.cfj-recipient-summary>svg,.cfj-child-summary>svg,.cfj-cargo-chevron svg,.cfj-stop-toggle svg{transform:rotate(90deg)}
.cfj-progressive-body{position:relative;margin:0 12px 14px 34px;padding:0 0 0 28px;border-left:2px solid #73aef0;border-radius:0;background:transparent}
.cfj-progressive-section{position:relative;margin:0 0 14px;padding:16px;border:1px solid #dce7ef;border-radius:11px;background:#fff;box-shadow:none}
.cfj-progressive-section:last-child{margin-bottom:0}
.cfj-progressive-section::before{content:"";position:absolute;left:-29px;top:27px;width:28px;height:1.5px;background:#73aef0}
.cfj-progressive-section::after{content:"";position:absolute;left:-34px;top:22px;width:9px;height:9px;border:2px solid #378ce3;border-radius:50%;background:#fff}
.cfj-progressive-section-head{display:flex;align-items:center;justify-content:space-between;gap:10px;min-height:30px;margin-bottom:12px;color:#234a66;font-size:14px;font-weight:850}
.cfj-progressive-section-head>span:first-child{display:flex;align-items:center;gap:8px;min-width:0}
.cfj-common-picker-button,.cfj-link-action{display:inline-flex;align-items:center;justify-content:center;gap:6px;min-height:36px;height:36px;padding:0 12px;border-radius:8px;font-size:11px;font-weight:800;white-space:nowrap;cursor:pointer}
.cfj-common-picker-button{border:1px solid #b9d6ed;background:#fff;color:#237bc6}
.cfj-common-picker-button:hover{border-color:#7db4df;background:#f5faff}
.cfj-link-action{border:0;background:transparent;color:#2b79b5}
.cfj-link-action:hover{text-decoration:underline}
.cfj-link-action.is-danger{color:#c55b55}
.cfj-progressive-grid.cfj-contact-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px 14px}
.cfj-progressive-grid input,.cfj-party-grid input{width:100%;height:42px;min-height:42px;padding:0 13px;border:1px solid #d8e3ea;border-radius:8px;background:#fff;color:#243f55;font-size:12px;outline:0;transition:border-color .15s ease,box-shadow .15s ease}
.cfj-progressive-grid input:focus,.cfj-party-grid input:focus{border-color:#80afd3;box-shadow:0 0 0 3px rgba(73,145,204,.09)}
.cfj-contact-grid.cfj-progressive-grid input:last-child{grid-column:1/-1}
.cfj-inline-notification{display:flex;align-items:center;gap:6px;margin-top:12px;padding-top:11px;border-top:1px solid #edf1f3;color:#496678;font-size:11px}
.cfj-inline-notification input{width:auto;height:auto;min-height:0}
.cfj-sender-section{padding:0;overflow:hidden;background:#fbfdff}
.cfj-sender-section .cfj-child-summary{min-height:52px;padding:10px 12px;background:#fbfdff}
.cfj-child-summary .cfj-tree-label strong{font-size:13px;color:#244c68}
.cfj-child-summary .cfj-tree-label>small{font-size:10px}
.cfj-sender-fields{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px 14px;padding:15px;border-top:1px solid #dcebf5;background:#fff}
.cfj-sender-fields .cfj-party-field-actions{grid-column:1/-1;display:flex;justify-content:flex-end}
.cfj-cargo-section{padding:0;overflow:hidden;background:#fff}
.cfj-cargo-section>.cfj-progressive-section-head{padding-bottom:12px;border-bottom:1px solid #e7eef3}
.cfj-cargo-list-title{display:flex;align-items:center;gap:9px;min-width:0}
.cfj-cargo-list-title>span:last-child{display:grid;gap:3px;min-width:0}
.cfj-cargo-list-title strong{color:#183d5b;font-size:15px}
.cfj-cargo-list-title small{color:#8c9ca7;font-size:10px;font-weight:500}
.cfj-cargo-section .cfj-inline-actions button{height:38px;min-height:38px}
.cfj-cargo-list{display:grid;gap:10px;padding:0}
.cfj-cargo-item{margin:0;padding:0;border:1px solid #dce8ef;border-radius:10px;background:#fff;box-shadow:none;overflow:hidden}
.cfj-cargo-summary{min-height:58px;padding:9px 12px;background:#fff}
.cfj-cargo-summary-trigger{display:flex;align-items:center;gap:9px;min-width:0;flex:1;border:0;background:transparent;text-align:left;cursor:pointer}
.cfj-cargo-summary-trigger .cfj-tree-label strong{font-size:12px}
.cfj-cargo-summary-trigger .cfj-tree-label>small{font-size:10px}
.cfj-cargo-index{display:grid;place-items:center;width:27px;height:27px;flex:0 0 27px;border:1px solid #f1c99e;border-radius:50%;background:#fff8ef;color:#d67625;font-size:11px;font-weight:900}
.cfj-compact-operation{width:100px!important;height:36px!important;min-height:36px!important;padding:0 10px!important;border:1px solid #b8d3e8!important;border-radius:8px!important;background:#fff!important;color:#2875a8!important;font-size:11px!important;font-weight:800!important}
.cfj-cargo-chevron{width:28px;height:30px;display:grid;place-items:center;flex:0 0 28px;border:0;background:transparent;color:#6f8da2;cursor:pointer}
.cfj-cargo-inline-detail{width:100%;min-width:0;max-width:100%;box-sizing:border-box;padding:14px;border-top:1px solid #e5edf2;background:#fbfdff}
.cfj-cargo-inline-grid{display:grid;width:100%;min-width:0;max-width:100%;box-sizing:border-box;grid-template-columns:minmax(0,1.4fr) minmax(58px,.7fr) minmax(58px,.65fr) minmax(0,1fr);gap:10px}
.cfj-cargo-inline-grid input{width:100%;min-width:0;max-width:100%;box-sizing:border-box;height:38px;min-height:38px;padding:0 10px;border:1px solid #dce6ec;border-radius:8px;background:#fff;color:#294761;font-size:11px}
.cfj-cargo-inline-grid input:focus{border-color:#80afd3;outline:0;box-shadow:0 0 0 3px rgba(73,145,204,.09)}
.cfj-cargo-inline-grid .is-wide{grid-column:auto}
.cfj-cargo-inline-actions{display:flex;align-items:center;justify-content:space-between;gap:8px;width:100%;min-width:0;margin-top:10px}
.cfj-cargo-detail-button{display:inline-flex;align-items:center;justify-content:center;gap:5px;min-height:36px;height:36px;border:1px solid #efaa73;border-radius:8px;padding:0 12px;background:#fff8f1;color:#d46d22;font-size:11px;font-weight:850;cursor:pointer}
.cfj-cargo-detail-button:hover{background:#fff0e1}
.cfj-cargo-photos-panel{margin:14px 0 0;padding:0;border:0;background:transparent}
.cfj-cargo-photos-heading{display:flex;align-items:center;gap:8px;margin-bottom:8px;color:#294c64;font-size:11px;font-weight:800}
.cfj-cargo-photos-heading small{color:#95a4ae;font-size:9px;font-weight:500}
.cfj-cargo-photos-grid{display:flex;flex-wrap:wrap;align-items:center;gap:8px}
.cfj-cargo-photo-thumb,.cfj-cargo-photo-add-large{width:76px;height:64px;border-radius:9px}
.cfj-cargo-photo-thumb{position:relative;overflow:hidden;border:1px solid #dbe5eb;background:#f5f8fa}
.cfj-cargo-photo-thumb img{width:100%;height:100%;object-fit:cover}
.cfj-cargo-photo-thumb button{position:absolute;top:3px;right:3px;display:grid;place-items:center;width:17px;height:17px;border:0;border-radius:50%;background:rgba(24,61,91,.8);color:#fff;cursor:pointer}
.cfj-cargo-photo-add-large{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;border:1px dashed #b7cfe1;background:#fff;color:#467a9c;cursor:pointer;font-size:9px;font-weight:700}
.cfj-cargo-photo-add-large:hover{border-color:#ef9a5b;background:#fffaf5;color:#d96f20}
.cfj-cargo-bottom-actions{display:none!important}
.cfj-stop-note-block{margin:14px 0 2px;padding:0 2px}
.cfj-stop-note-heading{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:6px;color:#315a73;font-size:11px;font-weight:850}
.cfj-stop-note-heading label small{color:#8ea0aa;font-size:9px;font-weight:500}
.cfj-stop-note-heading>span{color:#9aa9b2;font-size:9px;font-weight:600}
.cfj-stop-note-block textarea{width:100%;min-height:74px;resize:vertical;border:1px solid #dce7ed;border-radius:9px;padding:10px 11px;background:#fff;color:#294761;font-size:10px;line-height:1.5}
@media(max-width:760px){
  .cfj-hierarchy-head{align-items:flex-start;flex-direction:column}
  .cfj-hierarchy-head-actions{width:100%;justify-content:flex-start}
  .cfj-progressive-body{margin-left:22px;padding-left:20px}
  .cfj-progressive-section::before{left:-21px;width:20px}
  .cfj-progressive-section::after{left:-26px}
  .cfj-progressive-grid.cfj-contact-grid,.cfj-sender-fields{grid-template-columns:1fr 1fr}
  .cfj-cargo-inline-grid{grid-template-columns:1fr 90px 75px}
  .cfj-cargo-inline-grid .is-wide{grid-column:1/-1}
}
@media(max-width:520px){
  .cfj-hierarchy-head-actions,.cfj-inline-actions{width:100%;justify-content:flex-start}
  .cfj-recipient-summary{min-height:74px;padding:11px 9px}
  .cfj-tree-meta{gap:3px}
  .cfj-tree-meta em{padding:6px 8px;font-size:9px}
  .cfj-progressive-body{margin-left:22px;padding-left:16px}
  .cfj-progressive-section::before{left:-17px;width:16px}
  .cfj-progressive-section::after{left:-22px}
  .cfj-progressive-grid.cfj-contact-grid,.cfj-sender-fields{grid-template-columns:1fr}
  .cfj-contact-grid.cfj-progressive-grid input:last-child{grid-column:auto}
  .cfj-progressive-section-head{align-items:flex-start;flex-wrap:wrap}
  .cfj-cargo-section .cfj-inline-actions button{flex:1}
  .cfj-cargo-inline-grid{grid-template-columns:1fr 72px}
  .cfj-cargo-inline-grid .is-wide{grid-column:1/-1}
  .cfj-compact-operation{width:78px!important}
}

/* Fluid common-info columns: widths follow the available card space. */
.cfj-progressive-grid.cfj-contact-grid,
.cfj-sender-fields{
  grid-template-columns:repeat(auto-fit,minmax(min(100%,260px),1fr));
}
.cfj-contact-grid.cfj-progressive-grid input:last-child{
  grid-column:1/-1;
}
.cfj-contact-grid.cfj-progressive-grid input.cfj-required-field{
  border-color:#E98A45;
}
.cfj-contact-grid.cfj-progressive-grid input.cfj-required-field:valid{
  border-color:#3BA66B;
}
.cfj-required-field{
  border-color:#E98A45;
}
.cfj-required-field:valid{
  border-color:#3BA66B;
}
.cfj-required-field.is-filled{
  border-color:#3BA66B;
}
.cfj-field input.cfj-required-field{
  border-color:#E98A45;
}
.cfj-field input.cfj-required-field:valid,
.cfj-field input.cfj-required-field.is-filled{
  border-color:#3BA66B;
}
.cfj-cargo-inline-grid input.cfj-required-field{
  border-color:#E98A45;
}
.cfj-cargo-inline-grid input.cfj-required-field:valid,
.cfj-cargo-inline-grid input.cfj-required-field.is-filled{
  border-color:#3BA66B;
}
.cfj-address-row.cfj-required-field{
  border-color:#E98A45;
}
.cfj-address-row.cfj-required-field.is-filled{
  border-color:#3BA66B;
}
.cfj-sender-fields .cfj-party-field-actions{
  grid-column:1/-1;
}
@media(max-width:760px){
  .cfj-progressive-grid.cfj-contact-grid,
  .cfj-sender-fields{
    grid-template-columns:repeat(auto-fit,minmax(min(100%,220px),1fr));
  }
}
@media(max-width:520px){
  .cfj-progressive-grid.cfj-contact-grid,
  .cfj-sender-fields{
    grid-template-columns:1fr;
  }
  .cfj-contact-grid.cfj-progressive-grid input:last-child{
    grid-column:auto;
  }
}

/* Clean collapsed summaries: keep only the decision-ready information. */
.cfj-recipient-summary-copy{
  display:flex;
  align-items:center;
  gap:0;
}
.cfj-recipient-summary .cfj-tree-label{
  display:block;
}
.cfj-recipient-summary .cfj-tree-label>strong{
  display:block;
  line-height:1.25;
}
.cfj-cargo-summary{
  gap:12px;
  min-height:58px;
  padding:9px 13px;
}
.cfj-cargo-summary-trigger{
  gap:0;
}
.cfj-cargo-summary-trigger .cfj-tree-label{
  display:block;
}
.cfj-cargo-summary-trigger .cfj-tree-label>strong{
  display:inline-flex;
  align-items:baseline;
  gap:0;
  color:#183d5b;
  font-size:14px;
  line-height:1.25;
}
.cfj-cargo-summary-trigger .cfj-cargo-quantity{
  margin-left:2px;
  color:#183d5b;
  font-size:14px;
  font-weight:800;
  white-space:nowrap;
}
.cfj-compact-operation{
  width:108px!important;
  height:38px!important;
  min-height:38px!important;
  padding:0 28px 0 14px!important;
  border:1px solid #f2c79f!important;
  border-radius:999px!important;
  background:#fff5e9!important;
  color:#d97527!important;
  font-size:13px!important;
  font-weight:850!important;
  box-shadow:none!important;
}
.cfj-cargo-summary .cfj-cargo-chevron{
  display:none;
}
@media(max-width:520px){
  .cfj-cargo-summary{
    gap:7px;
    padding-left:9px;
    padding-right:9px;
  }
  .cfj-cargo-summary-trigger .cfj-tree-label>strong,
  .cfj-cargo-summary-trigger .cfj-cargo-quantity{
    font-size:12px;
  }
  .cfj-compact-operation{
    width:92px!important;
    height:34px!important;
    min-height:34px!important;
    padding-left:11px!important;
    padding-right:23px!important;
    font-size:11px!important;
  }
}

/* Fluid layout: follow the available workspace like the job list. */
.cfj-root{
  width:100%;
  max-width:none;
  min-width:0;
  overflow-x:hidden;
}
.cfj-root form{
  width:100%;
  max-width:none;
  min-width:0;
}
.cfj-layout{
  grid-template-columns:
    minmax(0,1.04fr)
    clamp(12px,1.4vw,24px)
    minmax(0,.96fr);
}
.cfj-form-column,
.cfj-map-column,
.cfj-card,
.cfj-map-panel{
  min-width:0;
  max-width:100%;
}
@container(max-width:760px){
  .cfj-layout{
    grid-template-columns:minmax(0,1fr);
    gap:14px;
  }
  .cfj-form-column{
    grid-column:1;
    grid-row:1;
  }
  .cfj-layout-gap{
    display:none;
  }
  .cfj-map-column{
    grid-column:1;
    grid-row:2;
    position:static;
  }
}

/* Desktop map viewport: keep the map visible while the route form scrolls. */
@container(min-width:761px){
  .cfj-root{
    padding-bottom:20px;
  }
  .cfj-map-column{
    align-self:start;
    height:calc(100vh - 110px);
    min-height:480px;
    position:sticky;
    top:18px;
  }
  .cfj-map-column>div{
    height:100%;
    min-height:0;
    display:flex;
    flex-direction:column;
  }
  .cfj-map-wrap{
    flex:1 1 auto;
    height:auto!important;
    min-height:0;
  }
  .cfj-trip{
    flex:0 0 auto;
  }
  .cfj-footer{
    position:fixed;
    left:clamp(220px,15.3vw,300px);
    right:18px;
    bottom:0;
    margin:0;
    min-height:84px;
    border-radius:14px 14px 0 0;
    padding:14px 22px;
  }
}

@container(max-width:760px){
  .cfj-footer{
    position:sticky;
    left:auto;
    right:auto;
    bottom:0;
    margin-top:16px;
    min-height:0;
  }
}

/* Keep the action footer inside the main workspace, not over the sidebar list. */
@container(min-width:761px){
  .cfj-footer{
    left:clamp(276px,15.3vw,300px);
  }
}

@container(min-width:761px){
  .cfj-root{
    padding-top:0;
  }
  .cfj-header{
    padding-top:0;
  }
  .cfj-root.is-header-collapsed .cfj-layout{
    padding-top:14px;
  }
  .cfj-map-column{
    transition:height .36s cubic-bezier(.22,.61,.36,1),top .36s ease;
  }
  .cfj-map-wrap{
    display:flex;
    flex-direction:column;
  }
  .cfj-map-wrap .cfj-map{
    flex:1 1 auto;
    height:auto!important;
    min-height:0!important;
  }
  .cfj-root.is-header-collapsed .cfj-map-column{
    height:calc(100vh - 126px);
  }
}

.cfj-section-head.is-collapsible{
  cursor:pointer;
  border-radius:10px;
  transition:background .16s ease;
}
.cfj-section-head.is-collapsible:hover{background:#f8fbfd}
.cfj-section-head.is-collapsible:focus-visible{
  outline:2px solid #f0a05d;
  outline-offset:3px;
}
.cfj-section-summary{
  display:flex;
  align-items:center;
  min-width:0;
  margin-top:5px;
  color:#6f8595;
  font-size:10px;
  font-weight:700;
  line-height:1.35;
}
.cfj-summary-items{
  display:flex;
  align-items:center;
  flex-wrap:wrap;
  gap:5px 0;
  min-width:0;
}
.cfj-summary-items span{
  display:inline-flex;
  align-items:center;
  min-width:0;
  max-width:220px;
  overflow:hidden;
  padding:0 8px;
  border-right:1px solid #dbe6ec;
  text-overflow:ellipsis;
  white-space:nowrap;
}
.cfj-summary-items span:first-child{padding-left:0}
.cfj-summary-items span:last-child{border-right:0}
.cfj-summary-items-route span{max-width:none}
.cfj-route-summary-pill{
  display:inline-flex;
  align-items:center;
  max-width:100%;
  min-height:26px;
  overflow:hidden;
  padding:3px 5px;
  border:1px solid #e5edf1;
  border-radius:8px;
  background:#f8fbfc;
  color:#647c8b;
  font-size:10px;
  font-weight:700;
  line-height:1.2;
  white-space:nowrap;
}
.cfj-route-summary-pill span{
  display:inline-flex;
  align-items:center;
  min-width:0;
  padding:0 8px;
  border-right:1px solid #dce7ec;
  overflow:hidden;
  text-overflow:ellipsis;
}
.cfj-route-summary-pill span:first-child{padding-left:4px}
.cfj-route-summary-pill span:last-child{
  padding-right:4px;
  border-right:0;
}
.cfj-card.is-collapsed .cfj-route-optimize-button,
.cfj-card.is-collapsed .cfj-route-recalculate-button{
  display:none;
}
.cfj-section-chevron{
  display:grid;
  place-items:center;
  flex:0 0 24px;
  width:24px;
  height:24px;
  margin-left:2px;
  border-radius:50%;
  background:#f3f7f9;
  color:#648095;
  font-size:15px;
  line-height:1;
}
.cfj-card.is-collapsed .cfj-section-head{margin-bottom:0}

@container(min-width:761px){
  .cfj-layout{
    height:calc(100dvh - 190px);
    min-height:0;
    align-items:stretch;
  }
  .cfj-left-column{
    height:100%;
    min-height:0;
    overflow:hidden;
  }
  .cfj-form-scroll{
    min-height:0;
    overflow-y:auto;
  }
  .cfj-left-column>.cfj-footer{
    position:relative;
    left:auto;
    right:auto;
    bottom:auto;
    margin:0;
    flex:0 0 auto;
    border-radius:14px 14px 0 0;
  }
  .cfj-map-column{
    height:100%;
    min-height:0;
    top:0;
  }
  .cfj-map-column>div{
    height:100%;
    min-height:0;
  }
}

@supports not (height: 100dvh){
  @container(min-width:761px){
    .cfj-layout{height:calc(100vh - 190px)}
    .cfj-map-column{height:calc(100vh - 190px);max-height:calc(100vh - 190px)}
  }
}

@container(min-width:761px){
  /* The action footer is section 04 in the left scroll, not a fixed page footer. */
  .cfj-form-scroll{
    display:flex;
    flex-direction:column;
    overflow-y:auto;
  }
  .cfj-left-column>.cfj-footer{
    position:relative;
    inset:auto;
    flex:0 0 auto;
    width:100%;
    margin:16px 0 20px;
    border-radius:14px;
  }
  /* Keep the map bounded to the viewport instead of content height. */
  .cfj-map-column{
    height:calc(100dvh - 190px);
    max-height:calc(100dvh - 190px);
    min-height:0;
    overflow:hidden;
    top:0;
  }
  .cfj-map-column>div,
  .cfj-map-panel{
    height:100%;
    max-height:100%;
    min-height:0;
    overflow:hidden;
  }
  .cfj-map-wrap{
    height:auto!important;
    min-height:0;
    flex:1 1 auto;
  }
}

@container(min-width:761px){
  .cfj-form-scroll>.cfj-footer{
    position:relative;
    inset:auto;
    flex:0 0 auto;
    width:100%;
    margin:16px 0 20px;
    border-radius:14px;
  }
}
.cfj-footer.is-ready{
  border-color:#f2a15b;
  box-shadow:0 0 0 3px rgba(245,138,31,.14),0 10px 28px rgba(245,138,31,.22);
  animation:cfj-footer-ready-pulse 1.8s ease-in-out infinite;
}
@keyframes cfj-footer-ready-pulse{
  0%,100%{box-shadow:0 0 0 3px rgba(245,138,31,.12),0 8px 22px rgba(245,138,31,.16)}
  50%{box-shadow:0 0 0 6px rgba(245,138,31,.2),0 12px 30px rgba(245,138,31,.3)}
}
@media(prefers-reduced-motion:reduce){
  .cfj-footer.is-ready{animation:none}
}

@container(min-width:761px){
  /* Compact center gutter; keep breathing room on the map's outer right edge. */
  .cfj-layout{
    grid-template-columns:minmax(0,1fr) clamp(12px,1vw,18px) minmax(0,1fr);
    padding-right:clamp(14px,1.4vw,24px);
    box-sizing:border-box;
  }
  .cfj-map-column{
    margin-right:0;
  }
}

.cfj-layout:not(.is-map-collapsed){
  grid-template-columns:minmax(0,calc(100% - var(--cfj-map-width,30%) - clamp(12px,1vw,18px))) clamp(12px,1vw,18px) minmax(0,var(--cfj-map-width,30%));
}
.cfj-layout-resize-handle{
  position:relative;
  z-index:10;
  display:flex;
  align-items:stretch;
  justify-content:center;
  min-width:12px;
  cursor:col-resize;
  touch-action:none;
}
.cfj-layout-resize-handle>span{
  width:3px;
  border-radius:999px;
  background:#dce7ed;
  transition:width .16s,background .16s,box-shadow .16s;
}
.cfj-layout-resize-handle:hover>span,
.cfj-layout-resize-handle:focus-visible>span{
  width:5px;
  background:#f0a05d;
  box-shadow:0 0 0 4px rgba(240,160,93,.13);
}
@media(max-width:760px){
  .cfj-layout-resize-handle{display:none}
  .cfj-layout:not(.is-map-collapsed){grid-template-columns:1fr}
}


/* A concept: clear visual hierarchy for station > role group > contact > cargo. */
.cfj-contact-groups-intro{padding:18px 20px 14px;background:#fff;border-bottom:1px solid #e8eef2}
.cfj-contact-groups-intro .cfj-recipient-title b{font-size:17px;line-height:1.35;letter-spacing:-.02em}
.cfj-contact-groups-intro .cfj-recipient-title small{margin-top:5px;font-size:10px;line-height:1.6}
.cfj-contact-role-group{margin:0 20px 16px;border:1px solid #e3edf2;border-radius:12px;background:#fff;overflow:hidden}
.cfj-contact-role-group:last-child{margin-bottom:18px}
.cfj-contact-role-head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:13px 14px;background:#f8fbfd;border-bottom:1px solid #e8eef2}
.cfj-contact-role-head .cfj-recipient-title b{font-size:15px;line-height:1.4;letter-spacing:-.015em}
.cfj-contact-role-head .cfj-recipient-title b em{font-size:15px;color:#e97e2e;font-style:normal}
.cfj-contact-role-head .cfj-hierarchy-head-actions{gap:7px}
.cfj-contact-role-head .cfj-compact-action{height:32px;min-height:32px;padding:0 11px;border-radius:8px;font-size:10px}
.cfj-contact-role-list{padding:0 2px 1px}
.cfj-contact-role-list .cfj-recipient-card{margin:10px 10px 12px;border-color:#dfeaf0;border-radius:10px;background:#fff}
.cfj-contact-role-list .cfj-recipient-summary{min-height:72px;padding:12px 13px;background:#fff}
.cfj-contact-role-list .cfj-recipient-summary .cfj-tree-label strong{font-size:15px;line-height:1.35}
.cfj-contact-role-list .cfj-recipient-summary .cfj-tree-label>small{font-size:10px;line-height:1.5}
.cfj-contact-role-list .cfj-tree-meta em{padding:6px 9px;font-size:9px}
.cfj-contact-role-list .cfj-progressive-body{margin-left:32px;padding-left:24px}
@media(max-width:760px){
  .cfj-contact-role-group{margin-left:12px;margin-right:12px}
  .cfj-contact-role-head{align-items:flex-start;flex-direction:column}
  .cfj-contact-role-head .cfj-hierarchy-head-actions{width:100%;justify-content:flex-start}
}

/* Compact workbench UI: summary first, details only when expanded. */
.cfj-stop-details.cfj-hierarchy-panel{
  margin-top:10px;
  padding:0;
  border:1px solid #e1eaee;
  border-radius:10px;
  background:#fff;
  overflow:hidden;
}
.cfj-stop-details .cfj-contact-groups-intro{
  min-height:0;
  padding:9px 13px 7px;
  border-bottom:1px solid #edf2f4;
  background:#fff;
}
.cfj-stop-details .cfj-contact-groups-intro .cfj-recipient-title{
  display:flex;
  align-items:baseline;
  flex-direction:row;
  gap:8px;
}
.cfj-stop-details .cfj-contact-groups-intro .cfj-recipient-title b{
  color:#315a73;
  font-size:11px;
  line-height:1.35;
  letter-spacing:0;
}
.cfj-stop-details .cfj-contact-groups-intro .cfj-recipient-title small{
  margin:0;
  color:#99a7ae;
  font-size:8px;
  line-height:1.35;
}
.cfj-stop-details .cfj-contact-role-group,
.cfj-stop-details .cfj-contact-role-group:last-child{
  margin:0;
  border:0;
  border-radius:0;
  background:#fff;
  overflow:visible;
}
.cfj-stop-details .cfj-contact-role-group + .cfj-contact-role-group{
  border-top:1px solid #e8eff2;
}
.cfj-stop-details .cfj-contact-role-head{
  min-height:44px;
  padding:7px 11px 6px 13px;
  border:0;
  background:#f8fbfc;
}
.cfj-stop-details .cfj-contact-role-head .cfj-recipient-title b{
  color:#234a64;
  font-size:12px;
  line-height:1.3;
  letter-spacing:0;
}
.cfj-stop-details .cfj-contact-role-head .cfj-recipient-title b em{
  color:#e87927;
  font-size:12px;
}
.cfj-stop-details .cfj-contact-role-head .cfj-hierarchy-head-actions{
  gap:5px;
}
.cfj-stop-details .cfj-contact-role-head .cfj-compact-action{
  height:28px;
  min-height:28px;
  padding:0 8px;
  border-radius:7px;
  font-size:9px;
  box-shadow:none;
}
.cfj-stop-details .cfj-contact-role-head .cfj-compact-action svg{
  width:12px;
  height:12px;
}
.cfj-stop-details .cfj-contact-role-list{
  padding:0 9px 7px;
}
.cfj-stop-details .cfj-contact-role-list .cfj-recipient-card,
.cfj-stop-details .cfj-contact-role-list .cfj-recipient-card.cfj-hierarchy-card.is-open{
  margin:0;
  border:0;
  border-radius:0;
  box-shadow:none;
  background:#fff;
}
.cfj-stop-details .cfj-contact-role-list .cfj-recipient-summary{
  min-height:52px;
  padding:8px 9px;
  border:1px solid #e2ebef;
  border-radius:8px;
  background:#fff;
}
.cfj-stop-details .cfj-contact-role-list .cfj-recipient-summary:hover{
  background:#fbfdfe;
}
.cfj-stop-details .cfj-contact-role-list .cfj-tree-node.is-recipient{
  width:25px;
  height:25px;
  flex-basis:25px;
  box-shadow:none;
}
.cfj-stop-details .cfj-contact-role-list .cfj-recipient-summary-copy{
  gap:0;
}
.cfj-stop-details .cfj-contact-role-list .cfj-recipient-summary .cfj-tree-label strong{
  color:#183d5b;
  font-size:14px;
  line-height:1.25;
}
.cfj-stop-details .cfj-contact-role-list .cfj-recipient-summary .cfj-tree-label>small{
  font-size:9px;
  line-height:1.25;
}
.cfj-stop-details .cfj-contact-role-list .cfj-tree-meta{
  gap:4px;
}
.cfj-stop-details .cfj-contact-role-list .cfj-tree-meta em{
  padding:4px 7px;
  font-size:9px;
}
.cfj-stop-details .cfj-contact-role-list .cfj-recipient-summary>svg{
  width:13px;
  height:13px;
}
.cfj-stop-details .cfj-contact-role-list .cfj-progressive-body{
  margin:6px 0 2px 18px;
  padding:0 0 0 16px;
  border-left:1px solid #9dc6dd;
}
.cfj-stop-details .cfj-contact-role-list .cfj-progressive-section{
  margin:0 0 7px;
  padding:9px;
  border:1px solid #e0ebf0;
  border-radius:8px;
  background:#fbfdfe;
}
.cfj-stop-details .cfj-contact-role-list .cfj-progressive-section-head{
  min-height:28px;
  margin-bottom:9px;
  font-size:12px;
}
.cfj-stop-details .cfj-contact-role-list .cfj-progressive-grid input{
  height:38px;
  min-height:38px;
  padding:0 10px;
  font-size:12px;
}
.cfj-stop-details .cfj-contact-role-list .cfj-sender-section,
.cfj-stop-details .cfj-contact-role-list .cfj-cargo-section{
  border-radius:8px;
}
.cfj-stop-details .cfj-contact-role-list .cfj-contact-owned-cargo{
  margin:9px 0 2px;
  padding:11px 12px 10px;
  border:1px solid #efdfcf;
  border-left:3px solid #e58b3e;
  border-radius:10px;
  background:#fffaf5;
}
.cfj-stop-details .cfj-contact-role-list .cfj-contact-owned-cargo>.cfj-progressive-section-head{
  min-height:30px;
  margin-bottom:9px;
  padding-bottom:8px;
  border-bottom:1px solid #f0e3d7;
  color:#754a29;
  font-size:12px;
}
.cfj-stop-details .cfj-contact-role-list .cfj-contact-owned-cargo .cfj-cargo-list-title strong{
  color:#5d452f;
  font-size:13px;
  line-height:1.35;
}
.cfj-stop-details .cfj-contact-role-list .cfj-contact-owned-cargo .cfj-cargo-list-title small{
  color:#8c7b6c;
  font-size:10px;
}
.cfj-stop-details .cfj-contact-role-list .cfj-contact-owned-cargo .cfj-cargo-list{
  gap:7px;
}
.cfj-stop-details .cfj-contact-role-list .cfj-contact-owned-cargo .cfj-cargo-item{
  border-color:#e8e5e1;
  border-radius:8px;
  background:#fff;
}
.cfj-stop-details .cfj-contact-role-list .cfj-contact-owned-cargo .cfj-cargo-summary{
  min-height:50px;
  padding:8px 10px;
}
.cfj-stop-details .cfj-contact-role-list .cfj-contact-owned-cargo .cfj-cargo-summary-trigger .cfj-tree-label strong{
  font-size:13px;
}
.cfj-stop-details .cfj-contact-role-list .cfj-contact-owned-cargo .cfj-cargo-summary-trigger .cfj-tree-label>small{
  font-size:11px;
}
.cfj-stop-details .cfj-stop-note-block{
  margin:8px 11px 10px;
}
.cfj-stop-details .cfj-stop-note-block textarea{
  min-height:55px;
  padding:8px 9px;
  font-size:10px;
}
@media(max-width:760px){
  .cfj-stop-details .cfj-contact-role-head{
    align-items:center;
    flex-direction:row;
  }
  .cfj-stop-details .cfj-contact-role-head .cfj-hierarchy-head-actions{
    width:auto;
    justify-content:flex-end;
  }
  .cfj-stop-details .cfj-contact-role-head .cfj-compact-action{
    padding:0 6px;
  }
  .cfj-stop-details .cfj-contact-role-head .cfj-compact-action:not(.is-primary){
    width:30px;
    overflow:hidden;
    white-space:nowrap;
  }
}
@media(max-width:520px){
  .cfj-stop-details .cfj-contact-groups-intro .cfj-recipient-title small{
    display:none;
  }
  .cfj-stop-details .cfj-contact-role-head{
    min-height:42px;
    padding-left:9px;
  }
  .cfj-stop-details .cfj-contact-role-head .cfj-compact-action.is-primary{
    width:29px;
    padding:0;
    overflow:hidden;
    white-space:nowrap;
  }
  .cfj-stop-details .cfj-contact-role-list{
    padding-left:6px;
    padding-right:6px;
  }
  .cfj-stop-details .cfj-contact-role-list .cfj-tree-meta em{
    padding:3px 5px;
  }
}

/* Coincident station markers: one map pin, separate station actions. */
.cfj-map-marker-group{
  min-width:54px;
  width:max-content;
  height:32px;
  padding:0 11px;
  border:2px solid #fff;
  border-radius:6px;
  background:#f18432;
  color:#fff;
  font-size:12px;
  font-weight:900;
  line-height:28px;
  letter-spacing:.02em;
  white-space:nowrap;
  box-shadow:0 4px 10px rgba(38,75,98,.24);
}
.cfj-map-marker-group::after{display:none}
.cfj-map-marker-group.is-active{
  background:#d96819;
  box-shadow:0 0 0 3px rgba(245,138,31,.22),0 5px 13px rgba(38,75,98,.28);
}
.cfj-map-marker-group.is-active::after{display:none}
.cfj-coincident-stop-popup .leaflet-popup-content-wrapper{
  border:1px solid #e3edf2;
  border-radius:12px;
  box-shadow:0 10px 28px rgba(28,61,84,.16);
}
.cfj-coincident-stop-popup .leaflet-popup-content{
  width:245px!important;
  margin:12px;
}
.cfj-stop-details .cfj-contact-role-group{
  margin:0 0 12px;
  overflow:hidden;
  border:1px solid #e2e9ed;
  border-radius:12px;
  background:#fff;
  box-shadow:0 2px 9px rgba(33,62,80,.04);
}
.cfj-stop-details .cfj-contact-role-head{
  min-height:58px;
  padding:10px 14px;
  border-bottom:1px solid #edf1f3;
  background:#fff;
}
.cfj-stop-details .cfj-contact-role-head .cfj-recipient-title b{
  color:#213b4c;
  font-size:16px;
  font-weight:850;
}
.cfj-stop-details .cfj-contact-role-head .cfj-hierarchy-head-actions{
  width:auto!important;
  justify-content:flex-end;
  flex-wrap:nowrap;
  gap:8px;
}
.cfj-stop-details .cfj-contact-role-head .cfj-compact-action{
  width:auto!important;
  min-width:0;
  height:36px;
  min-height:36px;
  padding:0 11px!important;
  overflow:visible!important;
  border-radius:8px;
  font-size:11px;
  white-space:nowrap;
}
.cfj-stop-details .cfj-contact-owned-cargo .cfj-inline-actions .cfj-compact-action{
  height:36px;
  min-height:36px;
  padding:0 11px!important;
  border-radius:8px;
  font-size:11px;
  font-weight:800;
  box-shadow:none;
}
.cfj-stop-details .cfj-contact-role-list{padding:0!important}
.cfj-stop-details .cfj-contact-card{
  margin:0!important;
  padding:0;
  border:0!important;
  border-radius:0!important;
  background:#fff!important;
  box-shadow:none!important;
}
.cfj-stop-details .cfj-contact-card+.cfj-contact-card{
  border-top:1px solid #edf1f3!important;
}
.cfj-contact-record-head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  min-height:42px;
  padding:7px 16px 0;
  color:#536875;
  font-size:12px;
}
.cfj-contact-record-toggle{
  display:flex;
  align-items:center;
  gap:8px;
  min-width:0;
  padding:4px 0;
  border:0;
  background:transparent;
  color:#405b6b;
  text-align:left;
  cursor:pointer;
}
.cfj-contact-record-toggle strong,.cfj-contact-record-head>strong{
  overflow:hidden;
  color:#405b6b;
  font-size:12px;
  font-weight:800;
  text-overflow:ellipsis;
  white-space:nowrap;
}
.cfj-contact-cargo-count{flex:0 0 auto;color:#82929d;font-size:11px;font-weight:600;white-space:nowrap}
.cfj-contact-record-remove,.cfj-contact-remove{
  flex:0 0 auto;
  padding:5px 8px;
  border:0;
  border-radius:7px;
  background:#fff5f2;
  color:#c65d50;
  font-size:11px;
  font-weight:750;
  cursor:pointer;
}
.cfj-contact-record-body{padding:16px 18px 14px;background:#fff}
.cfj-contact-fields{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
.cfj-contact-fields>.cfj-contact-field:last-child{grid-column:1/-1}
.cfj-contact-field{display:grid;gap:6px;color:#344b58;font-size:12px;font-weight:750}
.cfj-contact-name-label{display:flex;align-items:center;justify-content:space-between;gap:8px;min-height:26px}
.cfj-contact-name-label label{display:inline-flex;align-items:center;gap:3px;color:inherit;font:inherit}
.cfj-contact-common-button{display:inline-flex;align-items:center;justify-content:center;gap:4px;flex:0 0 auto;min-height:25px;padding:2px 8px;border:1px solid #d7e5ec;border-radius:7px;background:#fff;color:#4384a5;font-size:10px;font-weight:650;white-space:nowrap;cursor:pointer}
.cfj-contact-common-button:hover{border-color:#b8d4e1;background:#f7fbfd;color:#2e718f}
.cfj-contact-field>span b{color:#e97825;font-weight:850}
.cfj-contact-field input,.cfj-contact-field textarea{
  box-sizing:border-box;
  width:100%;
  border:1px solid #d9e2e7;
  border-radius:8px;
  background:#fff;
  color:#294656;
  font:inherit;
  font-size:13px;
  font-weight:500;
}
.cfj-contact-field input{height:42px;min-height:42px;padding:0 11px}
.cfj-contact-field textarea{min-height:70px;padding:10px 11px;resize:vertical;line-height:1.45}
.cfj-contact-field input:focus,.cfj-contact-field textarea:focus{
  outline:none;
  border-color:#e97825;
  box-shadow:0 0 0 3px rgba(233,120,37,.1);
}
@media(max-width:520px){
  .cfj-contact-fields{grid-template-columns:minmax(0,1fr)}
  .cfj-contact-fields>.cfj-contact-field:last-child{grid-column:auto}
}
.cfj-contact-email-row{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  margin-top:12px;
  padding:10px 0;
  border-top:1px solid #edf1f3;
}
.cfj-contact-email-row .cfj-inline-notification{
  min-height:34px;
  margin:0;
  padding:0;
  border:0;
  background:transparent;
}
.cfj-contact-email-preview{
  display:inline-flex;
  flex:0 0 auto;
  align-items:center;
  gap:6px;
  min-height:34px;
  padding:0 5px;
  border:0;
  background:transparent;
  color:#526b7b;
  font-size:12px;
  font-weight:750;
  cursor:pointer;
}
.cfj-contact-email-preview:hover{color:#d96f20}
.cfj-contact-owned-cargo{margin-top:13px!important}
@media(max-width:760px){
  .cfj-stop-details .cfj-contact-role-head{align-items:center;flex-direction:row}
  .cfj-stop-details .cfj-contact-role-head .cfj-hierarchy-head-actions{width:auto!important}
}
@media(max-width:520px){
  .cfj-stop-details .cfj-contact-role-head{min-height:52px;padding:8px 10px}
  .cfj-stop-details .cfj-contact-role-head .cfj-hierarchy-head-actions{gap:5px}
  .cfj-stop-details .cfj-contact-role-head .cfj-compact-action{height:34px;min-height:34px;padding:0 7px!important;font-size:10px}
  .cfj-stop-details .cfj-contact-owned-cargo .cfj-inline-actions .cfj-compact-action{height:34px;min-height:34px;padding:0 7px!important;font-size:10px}
  .cfj-contact-record-body{padding:13px 12px 12px}
  .cfj-contact-record-head{padding-right:12px;padding-left:12px}
}
.cfj-coincident-stop-popup-head{
  display:grid;
  gap:3px;
  padding:0 2px 9px;
  border-bottom:1px solid #edf2f5;
}
.cfj-coincident-stop-popup-head strong{
  color:#234a64;
  font-size:12px;
  line-height:1.3;
}
.cfj-coincident-stop-popup-head span{
  overflow:hidden;
  color:#8798a4;
  font-size:9px;
  line-height:1.35;
  text-overflow:ellipsis;
  white-space:nowrap;
}
.cfj-coincident-stop-list{
  display:grid;
  gap:6px;
  padding-top:9px;
}
.cfj-coincident-stop-item{
  display:flex;
  align-items:center;
  gap:7px;
  width:100%;
  min-height:43px;
  padding:6px 7px;
  border:1px solid #e2ebef;
  border-radius:8px;
  background:#fff;
  color:#234a64;
  text-align:left;
  cursor:pointer;
}
.cfj-coincident-stop-item:hover,
.cfj-coincident-stop-item.is-active{
  border-color:#f1ad70;
  background:#fff8f1;
}
.cfj-coincident-stop-order{
  display:grid;
  place-items:center;
  width:25px;
  height:25px;
  flex:0 0 25px;
  border-radius:50%;
  background:#f18432;
  color:#fff;
  font-size:9px;
  font-weight:900;
}
.cfj-coincident-stop-copy{
  display:grid;
  min-width:0;
  flex:1;
  gap:2px;
}
.cfj-coincident-stop-copy strong{
  overflow:hidden;
  color:#234a64;
  font-size:10px;
  line-height:1.25;
  text-overflow:ellipsis;
  white-space:nowrap;
}
.cfj-coincident-stop-copy small{
  color:#8b9ba5;
  font-size:8px;
  line-height:1.25;
}
.cfj-coincident-stop-action{
  color:#d87521;
  font-size:9px;
  font-weight:800;
  white-space:nowrap;
}
@media(max-width:520px){
  .cfj-coincident-stop-popup .leaflet-popup-content{width:210px!important}
}

.cfj-cargo-detail-readonly-operation{display:flex;align-items:center;min-height:36px;padding:0 11px;border:1px solid #dce7ed;border-radius:8px;background:#f5f8fa;color:#315e77;font-size:11px;font-weight:900;}
/* Keep the create-job workspace flush with the available viewport. */
@container(min-width:761px){
  .cfj-root{padding-bottom:20px}
  .cfj-layout{height:calc(100dvh - 120px)}
  .cfj-map-column{height:calc(100dvh - 120px);max-height:calc(100dvh - 120px)}
}
@supports not (height: 100dvh){
  @container(min-width:761px){
    .cfj-layout{height:calc(100vh - 120px)}
    .cfj-map-column{height:calc(100vh - 120px);max-height:calc(100vh - 120px)}
  }
}

/* 單點聯絡人貨物：扁平資訊列與緊湊照片帶 */
.cfj-contact-owned-cargo .cfj-cargo-list{
  display:grid;
  gap:0;
  padding:0 12px 4px;
}
.cfj-contact-owned-cargo .cfj-cargo-item{
  overflow:visible;
  border:0;
  border-bottom:1px solid #e9eef1;
  border-radius:0;
  background:transparent;
  box-shadow:none;
}
.cfj-contact-owned-cargo .cfj-cargo-item:last-child{border-bottom:0}
.cfj-contact-owned-cargo .cfj-cargo-summary{
  display:flex;
  min-height:0;
  gap:8px;
  padding:12px 0 8px;
  background:transparent;
}
.cfj-contact-owned-cargo .cfj-cargo-summary-trigger{
  display:flex;
  align-items:center;
  gap:8px;
  width:100%;
  min-width:0;
  flex:1;
  padding:0;
}
.cfj-contact-owned-cargo .cfj-cargo-summary-trigger .cfj-tree-label{
  display:grid;
  gap:3px;
  min-width:0;
  flex:1;
}
.cfj-contact-owned-cargo .cfj-cargo-summary-trigger .cfj-tree-label>strong{
  display:flex;
  align-items:baseline;
  flex-wrap:wrap;
  gap:7px;
  color:#183d5b;
  font-size:14px;
  line-height:1.3;
}
.cfj-contact-owned-cargo .cfj-cargo-summary-trigger .cfj-cargo-quantity{
  margin:0;
  color:#627b8d;
  font-size:12px;
  font-weight:700;
}
.cfj-contact-owned-cargo .cfj-cargo-note-preview{
  display:-webkit-box;
  overflow:hidden;
  color:#748692;
  font-size:10px;
  line-height:1.4;
  -webkit-box-orient:vertical;
  -webkit-line-clamp:2;
}
.cfj-contact-owned-cargo .cfj-compact-operation{
  width:auto!important;
  min-width:46px!important;
  height:24px!important;
  min-height:24px!important;
  flex:0 0 auto;
  padding:0 8px!important;
  border:1px solid #f0d3b8!important;
  border-radius:999px!important;
  background:#fff7ef!important;
  color:#bd682b!important;
  font-size:10px!important;
  font-weight:800!important;
}
.cfj-contact-owned-cargo .cfj-cargo-summary .cfj-cargo-chevron{
  display:grid;
  width:22px;
  height:22px;
  flex:0 0 22px;
  place-items:center;
  color:#80929d;
}
.cfj-contact-owned-cargo .cfj-cargo-inline-detail{
  padding:10px 0;
  border-top:1px solid #edf0f2;
  background:transparent;
}
.cfj-contact-owned-cargo .cfj-cargo-inline-grid{
  grid-template-columns:minmax(0,1.3fr) 80px 70px minmax(0,1fr);
  gap:7px;
}
.cfj-contact-owned-cargo .cfj-cargo-inline-actions{margin-top:8px}
.cfj-contact-owned-cargo .cfj-cargo-photos-panel{
  margin:0 0 9px;
  padding:0;
  border:0;
  background:transparent;
}
.cfj-contact-owned-cargo .cfj-cargo-photos-heading{
  justify-content:space-between;
  gap:8px;
  margin-bottom:5px;
  color:#71818b;
  font-size:9px;
  font-weight:750;
}
.cfj-contact-owned-cargo .cfj-cargo-photos-heading small{font-size:9px}
.cfj-contact-owned-cargo .cfj-cargo-photos-grid{
  display:flex;
  flex-wrap:nowrap;
  align-items:center;
  gap:6px;
  max-width:100%;
  overflow-x:auto;
  overflow-y:hidden;
  padding:0 0 3px;
  scrollbar-width:thin;
}
.cfj-contact-owned-cargo .cfj-cargo-photo-thumb,
.cfj-contact-owned-cargo .cfj-cargo-photo-add-large{
  width:54px;
  height:46px;
  flex:0 0 54px;
  border-radius:7px;
}
.cfj-contact-owned-cargo .cfj-cargo-photo-add-large{
  gap:2px;
  padding:2px;
  font-size:9px;
}
.cfj-contact-owned-cargo .cfj-cargo-photo-add-large span{font-size:9px}
@media(max-width:600px){
  .cfj-contact-owned-cargo .cfj-cargo-inline-grid{
    grid-template-columns:minmax(0,1fr) 74px;
  }
  .cfj-contact-owned-cargo .cfj-cargo-inline-grid .is-wide{grid-column:1/-1}
  .cfj-contact-owned-cargo .cfj-cargo-inline-actions{flex-wrap:wrap}
  .cfj-contact-owned-cargo .cfj-cargo-detail-button{
    min-height:32px;
    height:32px;
    padding:0 10px;
    font-size:10px;
  }
}

/* Driver roster scrollbar: reveal only while hovered or keyboard-focused. */
.cfj-member-scroll{
  scrollbar-width:none;
  -webkit-overflow-scrolling:touch;
}
.cfj-member-scroll::-webkit-scrollbar{height:6px}
.cfj-member-scroll::-webkit-scrollbar-track{background:transparent}
.cfj-member-scroll::-webkit-scrollbar-thumb{
  border:2px solid transparent;
  border-radius:999px;
  background:transparent;
  background-clip:padding-box;
  transition:background-color .18s ease;
}
.cfj-member-scroll:hover,
.cfj-member-scroll:focus-within{
  scrollbar-width:thin;
  scrollbar-color:#c4d2db transparent;
}
.cfj-member-scroll:hover::-webkit-scrollbar-thumb,
.cfj-member-scroll:focus-within::-webkit-scrollbar-thumb{background-color:#c4d2db}

`;

/* 單點工作固定為兩個站點：SRT 取貨站 -> END 送貨站。 */
const INSPECTION_ASSET_PICKER_CSS = `
.cfj-asset-picker-trigger{display:flex;align-items:center;justify-content:space-between;gap:16px;margin-top:15px;padding:15px 16px;border:1px solid #e5eaed;border-radius:13px;background:linear-gradient(135deg,#fbfcfd 0%,#f7f9fa 100%)}
.cfj-asset-picker-trigger-copy{display:flex;align-items:center;gap:11px;min-width:0}
.cfj-asset-picker-trigger-icon{width:38px;height:38px;display:grid;place-items:center;flex:0 0 38px;border:1px solid #f0decf;border-radius:11px;background:#fff6ee;color:#dd782d}
.cfj-asset-picker-trigger-copy>span:last-child{display:grid;gap:4px;min-width:0}
.cfj-asset-picker-trigger-copy strong{color:#2b3942;font-size:18px;font-weight:850}
.cfj-asset-picker-trigger-copy small{overflow:hidden;color:#859199;font-size:15px;text-overflow:ellipsis;white-space:nowrap}
.cfj-asset-picker-open{display:inline-flex;align-items:center;justify-content:center;gap:7px;flex:0 0 auto;min-height:37px;border:1px solid #e4b994;border-radius:9px;padding:0 13px;background:#fff;color:#bd682b;font-size:16px;font-weight:800;cursor:pointer;transition:background .16s,border-color .16s,transform .16s}
.cfj-asset-picker-open:hover:not(:disabled){border-color:#dd8c4d;background:#fff8f1;transform:translateY(-1px)}
.cfj-asset-picker-open:disabled{opacity:.5;cursor:not-allowed}
.cfj-asset-picker-overlay{position:fixed;inset:0;z-index:3600;display:grid;place-items:center;padding:22px;background:rgba(20,29,36,.4);backdrop-filter:blur(6px)}
.cfj-asset-picker-modal{display:flex;flex-direction:column;width:min(1120px,100%);max-height:min(820px,calc(100vh - 44px));overflow:hidden;border:1px solid rgba(255,255,255,.82);border-radius:18px;background:#fff;box-shadow:0 26px 84px rgba(18,30,38,.27)}
.cfj-asset-picker-header{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;padding:20px 22px 15px;border-bottom:1px solid #e9edef}
.cfj-asset-picker-header h2{margin:0;color:#25343d;font-size:24px;font-weight:850;letter-spacing:-.025em}
.cfj-asset-picker-header p{margin:5px 0 0;color:#849098;font-size:16px}
.cfj-asset-picker-close{width:31px;height:31px;display:grid;place-items:center;border:0;border-radius:9px;background:#f4f6f7;color:#69767e;font-size:26px;line-height:1;cursor:pointer}
.cfj-asset-picker-tabs{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:7px;padding:12px 20px 0}
.cfj-asset-picker-tab{display:flex;align-items:center;justify-content:center;gap:8px;min-width:0;min-height:40px;border:1px solid #e7ebee;border-radius:9px;background:#f8f9fa;color:#66747c;font-size:16px;font-weight:800;cursor:pointer}
.cfj-asset-picker-tab small{padding:3px 6px;border-radius:999px;background:#edf0f2;color:#87939a;font-size:14px;font-weight:750;white-space:nowrap}
.cfj-asset-picker-tab.is-active{border-color:#e97825;background:#e97825;color:#fff;box-shadow:0 3px 9px rgba(233,120,37,.18)}
.cfj-asset-picker-tab.is-active small{background:rgba(255,255,255,.2);color:#fff}
.cfj-asset-picker-body{display:grid;grid-template-columns:minmax(0,1.8fr) minmax(270px,.88fr);gap:14px;min-height:0;flex:1;padding:15px 20px 16px;overflow:hidden}
.cfj-asset-picker-browser{display:flex;flex-direction:column;min-width:0;min-height:0;border:1px solid #e8ecef;border-radius:12px;overflow:hidden}
.cfj-asset-picker-filters{display:grid;grid-template-columns:minmax(180px,1fr) minmax(100px,.42fr) minmax(100px,.42fr);gap:8px;padding:11px;background:#fafbfc;border-bottom:1px solid #edf0f2}
.cfj-asset-picker-search{display:flex;align-items:center;gap:8px;min-width:0;height:36px;padding:0 10px;border:1px solid #e1e6e9;border-radius:8px;background:#fff;color:#87949b}
.cfj-asset-picker-search:focus-within{border-color:#e9a36f;box-shadow:0 0 0 3px rgba(233,120,37,.09)}
.cfj-asset-picker-search input{width:100%;min-width:0;border:0;outline:0;background:transparent;color:#34434b;font-size:16px}
.cfj-asset-picker-select{display:grid;grid-template-columns:auto minmax(0,1fr);align-items:center;gap:6px;min-width:0;height:36px;padding:0 8px;border:1px solid #e1e6e9;border-radius:8px;background:#fff;color:#849098;font-size:15px}
.cfj-asset-picker-select select{width:100%;min-width:0;border:0;outline:0;background:transparent;color:#3f4d55;font-size:15px}
.cfj-asset-picker-list-head{display:grid;grid-template-columns:32px 44px minmax(100px,1fr) minmax(100px,1fr) minmax(85px,.7fr);align-items:center;gap:9px;padding:9px 11px;background:#f7f8f9;color:#69767e;font-size:15px;font-weight:800}
.cfj-asset-picker-list{min-height:0;overflow:auto;overscroll-behavior:contain}
.cfj-asset-picker-row{width:100%;display:grid;grid-template-columns:32px 44px minmax(100px,1fr) minmax(100px,1fr) minmax(85px,.7fr);align-items:center;gap:9px;min-height:59px;padding:7px 11px;border:0;border-bottom:1px solid #edf0f2;background:#fff;color:#34434b;text-align:left;cursor:pointer;transition:background .14s}
.cfj-asset-picker-row:hover:not(:disabled){background:#fff8f1}
.cfj-asset-picker-row.is-selected{background:#fff5eb;box-shadow:inset 3px 0 #e97825}
.cfj-asset-picker-row:disabled{cursor:not-allowed}
.cfj-asset-picker-row.is-blocked:not(.is-selected){opacity:.62}
.cfj-asset-picker-choice{width:17px;height:17px;display:grid;place-items:center;margin:auto;border:1.5px solid #ccd5da;background:#fff;color:#fff}
.cfj-asset-picker-choice.is-check{border-radius:5px}
.cfj-asset-picker-choice.is-radio{border-radius:50%}
.cfj-asset-picker-row.is-selected .cfj-asset-picker-choice{border-color:#e97825;background:#e97825;color:#fff}
.cfj-asset-picker-image,.cfj-asset-picker-selected-image{display:grid;place-items:center;overflow:hidden;border:1px solid #edf0f2;border-radius:8px;background:#f7f8f9;color:#84929a}
.cfj-asset-picker-image{width:40px;height:40px}
.cfj-asset-picker-image img,.cfj-asset-picker-selected-image img{width:100%;height:100%;object-fit:cover}
.cfj-asset-picker-identity,.cfj-asset-picker-type{display:grid;gap:3px;min-width:0}
.cfj-asset-picker-identity strong{overflow:hidden;color:#263742;font-size:18px;font-weight:850;text-overflow:ellipsis;white-space:nowrap}
.cfj-asset-picker-identity small,.cfj-asset-picker-type small{overflow:hidden;color:#89949b;font-size:15px;text-overflow:ellipsis;white-space:nowrap}
.cfj-asset-picker-type strong{overflow:hidden;color:#4b5b64;font-size:16px;font-weight:750;text-overflow:ellipsis;white-space:nowrap}
.cfj-asset-picker-status{justify-self:start;max-width:100%;overflow:hidden;border-radius:999px;padding:5px 8px;background:#f0f2f3;color:#758189;font-size:14px;font-weight:800;text-overflow:ellipsis;white-space:nowrap}
.cfj-asset-picker-status.is-in_use{background:#fff2df;color:#b96b1f}
.cfj-asset-picker-status.is-maintenance,.cfj-asset-picker-status.is-retired{background:#fcefed;color:#b65a4e}
.cfj-asset-picker-empty{padding:34px 16px;color:#8b969d;font-size:16px;text-align:center}
.cfj-asset-picker-selection{display:flex;flex-direction:column;min-width:0;min-height:0;border:1px solid #e8ecef;border-radius:12px;background:#fafbfc;overflow:hidden}
.cfj-asset-picker-selection-head{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:12px 13px;border-bottom:1px solid #e8ecef;background:#fff}
.cfj-asset-picker-selection-head strong{color:#34434b;font-size:18px;font-weight:850}
.cfj-asset-picker-selection-head span{border-radius:999px;padding:4px 7px;background:#fff2e6;color:#c66a27;font-size:14px;font-weight:850}
.cfj-asset-picker-selected-list{display:grid;align-content:start;gap:7px;min-height:0;overflow:auto;padding:9px}
.cfj-asset-picker-selected-item{display:flex;align-items:center;gap:9px;min-width:0;padding:9px;border:1px solid #edf0f2;border-radius:9px;background:#fff}
.cfj-asset-picker-selected-image{width:38px;height:38px;flex:0 0 38px}
.cfj-asset-picker-selected-copy{display:grid;gap:2px;flex:1;min-width:0}
.cfj-asset-picker-selected-copy small{color:#d17631;font-size:14px;font-weight:800}
.cfj-asset-picker-selected-copy strong{overflow:hidden;color:#33434c;font-size:17px;font-weight:850;text-overflow:ellipsis;white-space:nowrap}
.cfj-asset-picker-selected-copy em{overflow:hidden;color:#8c979e;font-size:14px;font-style:normal;text-overflow:ellipsis;white-space:nowrap}
.cfj-asset-picker-remove{width:25px;height:25px;display:grid;place-items:center;flex:0 0 25px;border:0;border-radius:7px;background:#f4f6f7;color:#818d94;font-size:21px;cursor:pointer}
.cfj-asset-picker-remove:hover{background:#fff0ed;color:#bd574a}
.cfj-asset-picker-locked{flex:0 0 auto;border-radius:999px;padding:4px 6px;background:#fff2e6;color:#c66a27;font-size:14px;font-weight:800}
.cfj-asset-picker-selection-empty{display:grid;place-items:center;min-height:120px;border:1px dashed #dfe5e8;border-radius:9px;background:#fff;color:#929da4;font-size:16px}
.cfj-asset-picker-footer{display:flex;align-items:center;justify-content:space-between;gap:15px;padding:12px 20px;border-top:1px solid #e9edef;background:#fff}
.cfj-asset-picker-footer>small{color:#87939a;font-size:14px}
.cfj-asset-picker-footer>div{display:flex;gap:8px}
.cfj-asset-picker-footer button{min-height:35px;border-radius:8px;padding:0 14px;font-size:15px;font-weight:850;cursor:pointer}
.cfj-asset-picker-cancel{border:1px solid #e2e7ea;background:#fff;color:#66747c}
.cfj-asset-picker-confirm{border:1px solid #e97825;background:#e97825;color:#fff;box-shadow:0 3px 8px rgba(233,120,37,.15)}
.cfj-asset-picker-confirm:disabled{opacity:.5;cursor:not-allowed}
@media(max-width:760px){.cfj-asset-picker-overlay{padding:10px}.cfj-asset-picker-modal{max-height:calc(100vh - 20px);border-radius:14px}.cfj-asset-picker-header{padding:15px 15px 12px}.cfj-asset-picker-tabs{padding:10px 12px 0;gap:5px}.cfj-asset-picker-tab{gap:5px;min-height:38px;font-size:15px}.cfj-asset-picker-tab small{display:none}.cfj-asset-picker-body{grid-template-columns:1fr;gap:9px;padding:10px 12px;overflow:auto}.cfj-asset-picker-browser{min-height:250px;max-height:45vh}.cfj-asset-picker-filters{grid-template-columns:1fr 1fr}.cfj-asset-picker-search{grid-column:1/-1}.cfj-asset-picker-list-head{grid-template-columns:24px 36px minmax(80px,1fr) minmax(78px,.8fr);gap:6px;padding:8px}.cfj-asset-picker-list-head span:nth-child(2),.cfj-asset-picker-list-head span:nth-child(4){display:none}.cfj-asset-picker-list-head span:nth-child(3){grid-column:3}.cfj-asset-picker-list-head span:nth-child(5){grid-column:4}.cfj-asset-picker-row{grid-template-columns:24px 36px minmax(80px,1fr) minmax(78px,.8fr);gap:6px;min-height:54px;padding:6px 8px}.cfj-asset-picker-image{width:34px;height:34px}.cfj-asset-picker-type{display:none}.cfj-asset-picker-status{font-size:14px;padding:4px 6px}.cfj-asset-picker-selection{max-height:23vh}.cfj-asset-picker-selected-list{grid-template-columns:1fr 1fr}.cfj-asset-picker-selected-item{padding:6px;gap:6px}.cfj-asset-picker-selected-image{width:30px;height:30px;flex-basis:30px}.cfj-asset-picker-selected-copy small{font-size:13px}.cfj-asset-picker-selected-copy strong{font-size:15px}.cfj-asset-picker-selected-copy em{display:none}.cfj-asset-picker-footer{padding:10px 12px}.cfj-asset-picker-footer>small{max-width:42%;line-height:1.4}}
@media(max-width:430px){.cfj-asset-picker-list-head{grid-template-columns:20px 32px minmax(0,1fr) 75px}.cfj-asset-picker-row{grid-template-columns:20px 32px minmax(0,1fr) 75px}.cfj-asset-picker-image{width:30px;height:30px}.cfj-asset-picker-identity strong{font-size:16px}.cfj-asset-picker-tab{gap:3px}.cfj-asset-picker-tab svg{width:13px;height:13px}}
`;

const SINGLE_POINT_FIXED_ROUTE_CSS = `.cfj-route-optimize-button,.cfj-route-recalculate-button,.cfj-route-actions,.cfj-map-optimize-button,.cfj-map-stop-add,.cfj-stop-move-wrap,.cfj-stop-delete{display:none!important}.cfj-cargo-source-option{grid-template-columns:26px 42px minmax(0,1fr) 18px}.cfj-cargo-source-checkbox{width:20px;height:20px;display:grid;place-items:center;border:1px solid #cbdde6;border-radius:6px;background:#fff;color:#fff}.cfj-cargo-source-option.is-selected{border-color:#efa76f;background:#fff8f1;box-shadow:0 0 0 3px rgba(239,167,111,.12)}.cfj-cargo-source-option.is-selected .cfj-cargo-source-checkbox{border-color:#df812e;background:#df812e}.cfj-cargo-source-footer{display:flex;align-items:center;justify-content:space-between;gap:12px}.cfj-cargo-source-footer .cfj-cargo-primary-button{display:inline-flex;align-items:center;gap:5px;border:0;border-radius:8px;padding:8px 12px;background:#e97825;color:#fff;font-size:10px;font-weight:800}.cfj-cargo-source-footer .cfj-cargo-primary-button:disabled{opacity:.45;cursor:not-allowed}.cfj-work-mode-options{display:grid;grid-template-columns:1fr 1fr;gap:8px}.cfj-work-mode-option{min-height:40px;border:1px solid #dce7ed;border-radius:10px;background:#fff;color:#5e707b;font-weight:800;cursor:pointer}.cfj-work-mode-option.is-active{border-color:#e97825;background:#fff6ed;color:#d46618;box-shadow:0 0 0 3px rgba(233,120,37,.1)}.cfj-category-icon.is-inspection-icon,.cfj-category-picker-icon.is-inspection-icon{display:grid;place-items:center;color:#e97825;background:#fff5e9}.cfj-inspection-addresses{display:grid;gap:14px;padding:4px 0}.cfj-inspection-address-row{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:end;gap:10px;padding:14px;border:1px solid #e1ebf0;border-radius:12px;background:#fbfdfe}.cfj-inspection-address-row label{display:grid;gap:7px;color:#466273;font-size:11px;font-weight:800}.cfj-inspection-address-row input{height:42px;border:1px solid #d8e4ea;border-radius:9px;padding:0 12px;background:#fff;color:#263943}.cfj-inspection-address-row small{color:#7b8c95;font-size:10px;white-space:nowrap}.cfj-inspection-address-input-row{display:flex;align-items:center;gap:8px}.cfj-inspection-address-input-row input{flex:1;min-width:0}.cfj-inspection-address-input-row .cfj-inspection-common-address{flex:0 0 auto;white-space:nowrap}.cfj-inspection-common-address{display:inline-flex;align-items:center;gap:5px;width:max-content;border:0;border-radius:7px;padding:6px 8px;background:#fff4e9;color:#d16c1d;font-size:10px;font-weight:800;cursor:pointer}.cfj-inspection-hint{margin:0;color:#7a8992;font-size:11px;line-height:1.6}`;
